/**
 * Fleet admin dashboard (bus tracking).
 *
 * This dashboard shows:
 * - Post-trip survey data: "Needs refuel" count and fuel estimation (from driver surveys after each trip).
 * - Refuel events: from "I refueled" in driver settings; used for estimated L/100 km.
 * - Distance: from trip completion (distanceKm on busAttendance).
 * - Suspend / maintenance: one flow; bus is marked maintenance (drivers/parents see "under maintenance").
 * - Route deviation: alerts from route learning (trip start/end vs saved routes); attendance shows "Route deviation" pill when routeAnomaly.
 * - Generate temporary code: 4-digit signup code (15 min) for drivers/parents (Settings modal).
 */
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
  Timestamp,
  addDoc,
  FieldValue,
  deleteField,
  deleteDoc,
  arrayUnion,
} from "firebase/firestore";
import { db, auth, functions, functionsEu, httpsCallable } from "../firebase";
import { busPhrase } from "../fleet/busPhrase";
import { adminInviteActionSettings } from "./selfServeSignup";
import { customToast } from "../utils/customToast";
import { materializeBusDoc, openLatLng } from "../utils/locationCrypto";
import { loadTripTrailRaw } from "../utils/tripTrail";
import { useAuth } from "../contexts/AuthContext";
import { Card as FleetCard } from "./ui/card";
import { Badge } from "./ui/badge";
import SchoolTripComposer from "./SchoolTripComposer";
import FuelPricesPage, { currentMonthKey } from "./FuelPricesPage";
import MaintenancePage from "./MaintenancePage";
import SavingsLedgerCard from "./SavingsLedgerCard";
import { spotlight, clearSpotlight } from "../services/zanaTakeover";
import FleetShell, { FleetNavItem } from "./FleetShell";
import CreateDriverButton from "./CreateDriverButton";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { getFleetCollections, fleetHasFullProductFeatures as isFullFeatureFleet } from "../fleet/fleetPortal";
import { rawProductType } from "../fleet/fleetConstants";
import { severityOf, severityRank, kindOf, REVIEW_REASON_COPY } from "../fleet/alertMeta";
import { logBusStatusEvent } from "../fleet/busStatusEvents";
import { sendFleetInvite } from "./FleetJoinPage";
import { FaSearch, FaBell, FaCog, FaBus, FaArrowLeft, FaExclamationTriangle, FaMapPin, FaCheckCircle, FaGasPump, FaDollarSign, FaChartBar } from "react-icons/fa";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import {
  generateReport as generateEfficiencyReport,
  calculateL100km,
  getEfficiencyRatingFromInput,
  efficiencyScorePercent,
  ageAdjustedBenchmarkKmPerL,
  DEFAULT_TANK_CAPACITY_L,
  type BusFuelInput,
} from "../utils/busEfficiencyAnalyzer";
import "./BusTracking.css";
import "../styles/fleet-design.css";
import HighRiskScreen, { type HighRiskTrip } from "./fleet/HighRiskScreen";
import NotificationsPopover from "./fleet/NotificationsPopover";
import ParentsBulkInvite from "./fleet/ParentsBulkInvite";
import FuelReportScreen from "./fleet/FuelReportScreen";
import MaintenanceScreen from "./fleet/MaintenanceScreen";
import BusDetailHeader from "./fleet/BusDetailHeader";
import BusMiniMap from "./fleet/BusMiniMap";
import RunTimeline from "./fleet/RunTimeline";
import BusCostEstimatePage from "./BusCostEstimatePage";
import BusAnalyticsPage, { FuelOverview, SubTab as AnalyticsSubTab } from "./BusAnalyticsPage";
import ZanaDock from "./ZanaDock";
import ZanaChatPage from "./ZanaChatPage";
import ZanaTakeoverPanel from "./ZanaTakeoverPanel";
import { ZanaAgentsDrawer } from "./ZanaAgentsDrawer";
import { useZanaTakeover } from "./useZanaTakeover";
import type { TakeoverRequest } from "../services/zanaTakeover";
import ZanaModeUpsell from "./ZanaModeUpsell";
import ZanaSummaryCard from "./ZanaSummaryCard";
import ErrorBoundary from './ErrorBoundary';
import FleetAdminSettings from "./FleetAdminSettings";
import TermNextBanner from "./TermNextBanner";
import { shouldRemindNextTerm, subscriptionAllowsReminders } from "../utils/termDates";
import type { ZanaIntent } from "../services/zanaActions";
import { executeZanaAdminIntent } from "../services/zanaAdminActions";
import {
  generateReport, saveReportFile, scheduleReport,
  listReportSchedules, cancelReportSchedule, describeCadence,
  requestFuelReadings,
} from "../services/zanaReports";
import type { AskPageContext } from "../services/briefingsApi";
import { sendAlertDigest, invalidateZanaContext, runZanaConnectionTest, notifyBusDriver, checkRoadsWithGoogle } from "../services/briefingsApi";
import { announceToBusParents } from "../services/fleetContactApi";
import BusLiveMapPanel from "./BusLiveMapPanel";
import SchoolFamilyLinksPanel from "./SchoolFamilyLinksPanel";
import SubscriptionManager from './SubscriptionManager';
import PremiumUpgradeFlow from './PremiumUpgradeFlow';
import EnterpriseBranchSwitcher from './EnterpriseBranchSwitcher';
import { useOrgSubscription } from '../services/useOrgSubscription';
import { switchBranch } from '../services/paymentsApi';
import { cancelTrip as cancelTripApi, notifyTripParents as notifyTripApi, isTripsApiConfigured, disableTripConductor, reissueTripParent, reissueTripConductor } from '../services/tripsApi';

type DateLike = Date | number | string | null | undefined;
type StatusKey = "picked_up" | "dropped_off" | "not_picked" | "absent";
type CrashSevKey = "minor" | "major";

// UI shape (used by the dashboard)
interface BusChild {
  id: string;
  name: string;
  parentPhone: string;
  status: string;
  parentId?: string;
  /** Class / year group from signup or roster. */
  classLabel?: string;
  /** Outside-trip child temporarily arrayUnion'd onto a fleet bus — not a permanent roster kid. */
  temporaryTripChild?: boolean;
  /** A1/A2: transport hold — parent still logs in; child not picked up. */
  transportSuspended?: boolean;
  /** When the status last changed — the driver app stamps `lastUpdated` on
   *  every pickup / drop / undo. Feeds the run timeline. */
  time?: Date | null;
}
interface BusRoute {
  type: string | null;
  status: string | null;
  started: Date | null;
}
interface Bus {
  id: string;
  plate: string;
  name: string;
  drivers: string[];
  children: BusChild[];
  route: BusRoute;
  morningTime: string;
  eveningTime: string;
  school: string;
  trafficDetected?: boolean;
  status: "active" | "maintenance" | "suspended" | "for_hire";
  /** `reportId` is the busIssueReports doc id — present on merged reports, absent on legacy seeds; it is what the Resolve button writes to. */
  crashes: { date: Date; severity: CrashSevKey; description: string; reported: boolean; reportId?: string }[];
  kmHistory: number[];
  /** Fuel tank capacity in liters; used for efficiency calculation. */
  fuelTankCapacityL?: number | null;
  /** User-set efficiency benchmark in km per litre. Required for a fuel efficiency score. */
  benchmarkKmPerL?: number | null;
  /** Year of manufacture — used to relax the benchmark for an aging engine (fairer score). */
  yearOfManufacture?: number | null;
  /** Last known dash reading in km (driver-typed at refill — not set here). */
  odometerKm?: number | null;
  /** The bus's FIRST odometer reading, in km — written once by whichever came
   *  first, the driver's trip-start prompt or the first refuel. Until it
   *  exists no fill on this bus can produce a km/L, because km/L is the
   *  distance between two readings. Drives the "No odometer reading yet" row
   *  on High Risk. */
  odometerBaselineKm?: number | null;
  /** What the dash shows. Driver types the number; we only store the unit. */
  odometerUnit?: "km" | "mi" | "miles";
  /** Latest driver GPS fix (live tracking). */
  liveLocation?: { lat: number; lng: number } | null;
  /** Epoch ms from `liveLocation.timestamp` when present. */
  liveLocationUpdatedAt?: number | null;
  /** Set by driver app when GPS/network fails (`buses.locationError`). */
  locationError?: string | null;
  /** Current trip polyline on the bus document (cleared when route ends). */
  gpsRoutePoints?: { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[];
}
interface AttendanceChild {
  id?: string;
  name: string;
  status: string;
  time: Date | null;
  /** Where the pickup/drop was marked — our own recorded pins, used to
   *  estimate a trip's distance when the phone died mid-route (Gabriel,
   *  2026-08-17: "calculate the geofences for children… just for safety"). */
  lat?: number | null;
  lng?: number | null;
}
interface AttendanceRecord {
  id: string;
  busId: string;
  date: Date;
  type: string;
  children: AttendanceChild[];
  started: Date | null;
  finished: Date | null;
  duration: number | null;
  km: number | null;
  /** busAttendance `status` — 'completed' | 'cancelled' | … Recent runs says
      "Cancelled" instead of a misleading 0/5 (Gabriel, 2026-08-22). */
  status?: string | null;
  routeAnomaly?: boolean;
  needsRefuelSoon?: boolean;
  /** Pinned by the office so it can be found again under Analytics → Routes (2026-09-29). */
  pinned?: boolean;
  pinnedName?: string | null;
  /** Driver "Start a refuel trip" run — not a school morning/evening. */
  isRefuelTrip?: boolean;
  visitedStation?: boolean;
  loggedRefuel?: boolean;
  /** Stamped by the app at refuel-trip end (2026-08-22): every station stop in
      the trip's window, and how many of them produced a logged fill — so the
      Fuel report can say "1 of 2 stations logged" instead of a bare yes/no. */
  stationVisits?: number | null;
  refuelsLogged?: number | null;
  driverId?: string | null;
  /** Completed trip path (copied at route finish). */
  routePoints?: { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[];
  /** The UNOPENED trail. Every point since 2026-08-10 is a sealed `{v,iv,c}`
   *  blob, which the sync parser above cannot read — so `routePoints` is empty
   *  for exactly the trips an owner most wants to see. Kept raw here and
   *  opened (async, with the DEK) only when a run is selected on the map. */
  rawRoutePoints?: unknown[];
  /** Bug 216: a long trip's trail spills into `{trip}/trail/{seq}` chunks and
   *  `routePoints` becomes the recent window. `trailChunks` > 0 means the
   *  whole run is chunks + window — see utils/tripTrail.ts. */
  trailChunks?: number;
  trailWindowStart?: number | null;
  trailSpilled?: number;
  /** Road-snapped line the reconcile stored (plain lat/lng) — preferred over
      the raw GPS scatter wherever a trail is drawn (2026-08-24). */
  snappedRoutePoints?: { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[];
  /** Semantic trip ends for fleet map (from driver pickup/drop + finishRoute). */
  mapStart?: { lat: number; lng: number } | null;
  mapEnd?: { lat: number; lng: number } | null;
}

function isRefuelAttendance(r: AttendanceRecord): boolean {
  return r.isRefuelTrip === true || r.type === "refuel";
}

interface FleetAlert {
  id: string;
  busId: string;
  busName: string;
  type: string;
  message: string;
  timestamp: Date;
  read: boolean;
  /* Optional extras on the safety alerts added 2026-07-30 — mirrors
     `FleetAlertRow` in useFleetAlerts. Every other alert type leaves them
     undefined. */
  childName?: string;
  triage?: string;
  dropLat?: number;
  dropLng?: number;
  note?: string;
  placeId?: string;
  stationName?: string;
  fuelType?: string;
  observedPerL?: number;
  listedPerL?: number;
}

/** GPS breadcrumbs from `buses.routePoints` or `busAttendance.routePoints`. */
function parseGpsPoints(raw: unknown): { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[] {
  if (!Array.isArray(raw)) return [];
  const out: { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[] = [];
  for (const p of raw) {
    if (!p || typeof p !== "object") continue;
    const o = p as Record<string, unknown>;
    const lat = typeof o.lat === "number" ? o.lat : Number(o.lat);
    const lng = typeof o.lng === "number" ? o.lng : Number(o.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    // `gapBefore` marks the first breadcrumb after an app-kill resume — keep it so
    // the map can lift the pen there instead of drawing a straight line, and never
    // let the dedupe below drop it. `estimated` marks inferred gap-fill points.
    const gapBefore = o.gapBefore === true;
    if (!gapBefore && out.length > 0) {
      const last = out[out.length - 1];
      if (Math.abs(last.lat - lat) < 1e-5 && Math.abs(last.lng - lng) < 1e-5) continue;
    }
    const estimated = o.estimated === true;
    out.push(estimated ? { lat, lng, gapBefore, estimated } : (gapBefore ? { lat, lng, gapBefore } : { lat, lng }));
  }
  return out;
}

/** Bus age in whole years from its year of manufacture, or undefined if not set (→ no age adjustment). */
function busAgeYearsFrom(yearOfManufacture?: number | null): number | undefined {
  if (yearOfManufacture == null || yearOfManufacture <= 0) return undefined;
  const age = new Date().getFullYear() - yearOfManufacture;
  return age > 0 ? age : 0;
}

/** Map analyzer rating + 30d inputs to a single % for donut / tables (aligned with busEfficiencyAnalyzer). */
function efficiencyPercentFrom30dInput(input: BusFuelInput): { pct: number; rating: string; color: string } {
  const rating = getEfficiencyRatingFromInput(input);
  const score = efficiencyScorePercent(input);
  if (score != null) {
    const pct = Math.round(score);
    const color = pct <= 30 ? "#dc2626" : pct <= 70 ? "#f97316" : "#16a34a";
    return { pct, rating, color };
  }
  const pctByRating: Record<string, number> = { Excellent: 92, Good: 78, Fair: 58, Poor: 36 };
  const pct = pctByRating[rating] ?? 40;
  const color = pct <= 30 ? "#dc2626" : pct <= 70 ? "#f97316" : "#16a34a";
  return { pct, rating, color };
}

/**
 * Fuel efficiency estimation with benchmark-gated scoring.
 *
 * Scoring requires a user-set benchmark (km/L). Without it, we show the raw
 * consumption figure but no score, and display a red "set benchmark" prompt.
 *
 * Confidence levels:
 *  "no_benchmark"  — benchmark not set; no score possible (red callout)
 *  "measured"      — actual litres + benchmark → exact score
 *  "estimated"     — tank-capacity estimate + benchmark → estimated score
 *  "early"         — benchmark set but not enough refuel data yet
 */
function estimateFuelEfficiency(input: BusFuelInput): {
  pct: number | null;
  kmPerL: number | null;
  l100km: number | null;
  confidence: "no_benchmark" | "measured" | "estimated" | "early";
  confidenceLabel: string;
  tooltipNote: string;
} {
  const FILL_FRACTION = 0.8;
  /* Below this fraction of the bus's OWN benchmark, a measured figure is very
     unlikely to be fuel economy and very likely to be a short denominator.
     A quarter leaves plenty of room for a genuinely bad bus to score badly. */
  const MIN_FRACTION_OF_BENCHMARK = 0.25;
  const hasBenchmark = input.benchmarkKmPerL != null && input.benchmarkKmPerL > 0;
  // The km/L target is relaxed for the bus's age so older buses are scored fairly.
  const benchmark = ageAdjustedBenchmarkKmPerL(input.benchmarkKmPerL ?? 0, input.busAgeYears);

  // Compute actual km/L from real litres when available
  const hasActualLitres = input.totalLitresAdded != null && input.totalLitresAdded > 0 && input.totalKm30 > 0;
  const actualKmPerL = hasActualLitres ? input.totalKm30 / input.totalLitresAdded! : null;
  const actualL100km = actualKmPerL != null ? (1 / actualKmPerL) * 100 : null;

  /* ── Odometer-measured — the PREFERRED source (Gabriel, 2026-08-17) ────────
     The odometer counts ALL driving; GPS counts only tracked trips. When
     clean odometer-bracketed fills exist in the window, km/L comes from them
     and the GPS-vs-litres arithmetic below is only the fallback. */
  const odoKm = input.odoWindowKm ?? 0;
  const odoLitres = input.odoWindowLitres ?? 0;
  const hasOdo = odoKm > 0 && odoLitres > 0;
  const odoKmPerL = hasOdo ? odoKm / odoLitres : null;
  const bestKmPerL = odoKmPerL ?? actualKmPerL;
  const bestL100km = bestKmPerL != null ? (1 / bestKmPerL) * 100 : null;

  // ── No benchmark set ───────────────────────────────────────────────────────
  if (!hasBenchmark) {
    return {
      pct: null,
      kmPerL: bestKmPerL,
      l100km: bestL100km,
      confidence: "no_benchmark",
      confidenceLabel: "No benchmark set",
      tooltipNote:
        "Click 'Edit' on the fuel capacity tile to set a km/L benchmark for this bus. " +
        "The benchmark is the efficiency target you want this bus to achieve. " +
        "The score = (actual km/L ÷ benchmark) × 100.",
    };
  }

  // ── Benchmark set + odometer-measured fills (preferred) ───────────────────
  if (hasOdo && odoKmPerL != null) {
    // Same honesty guard as below: an impossible number is a data problem to
    // name, never a score. With the odometer the usual cause is a flagged
    // reading that slipped the clean-fill filter.
    if (odoKmPerL < 0.5 || odoKmPerL > 40) {
      return {
        pct: null,
        kmPerL: null,
        l100km: null,
        confidence: "early",
        confidenceLabel: "Odometer data needs review",
        tooltipNote:
          `${odoKm.toFixed(0)} km on the odometer across ${odoLitres.toFixed(1)} L implies ` +
          `${odoKmPerL.toFixed(2)} km/L, which no bus does. Check the flagged readings on ` +
          `the Fuel review list before trusting this window.`,
      };
    }
    const pct = Math.round(Math.min(100, (odoKmPerL / benchmark) * 100));
    return {
      pct,
      kmPerL: odoKmPerL,
      l100km: bestL100km,
      confidence: "measured",
      confidenceLabel: "Measured · odometer",
      tooltipNote:
        `${odoKm.toFixed(0)} km between odometer readings · ${odoLitres.toFixed(1)} L across those fills ` +
        `→ ${odoKmPerL.toFixed(2)} km/L vs benchmark ${benchmark} km/L · Score: ${pct}%. ` +
        `Odometer-based: counts ALL driving, not only tracked trips.`,
    };
  }

  // ── Benchmark set + actual litres logged (GPS fallback) ────────────────────
  if (hasActualLitres && actualKmPerL != null) {
    /* Refuse to score an impossible number (Gabriel's screenshot, 2026-08-17:
       0.63 km/L → "14% · Poor", in red). Every litre ever logged divided by
       only the GPS-TRACKED km is not fuel economy — trips that never tracked
       leave the km side short while the litres side stays complete, so the bus
       reads thirstier than physics allows and the card accuses a driver on
       arithmetic. The backend already rejects exactly this band as garbage
       (engine/fuel junk check, 0.3–40 km/L); the customer-facing card must not
       print what the backend would refuse to compute with. Say what is missing
       instead of scoring it. */
    /* ⚠️ THE 0.5 FLOOR WAS TOO LOW TO CATCH THE VERY NUMBER IT WAS WRITTEN FOR.
       Gabriel's screenshot showed 0.63 km/L — which is INSIDE 0.5–40, so the
       guard above never fired and the card printed "14% · Poor" in red anyway
       (2026-08-18, on live LEAF data, KCE999Z).

       An absolute band cannot do this job. 0.5 km/L is not a number any road
       vehicle produces; it only ever means the denominator is incomplete. What
       makes a figure impossible is how far it sits BELOW THIS BUS'S OWN
       BENCHMARK: at a 5 km/L benchmark, 0.63 is 13% — that is not a thirsty
       bus, it is a bus whose tracked kilometres are missing while every litre
       was logged.

       A quarter of benchmark is the line. A bus genuinely doing 2 km/L against
       a 5 km/L target still scores 40% and still reads "Poor" — which is a real
       finding an owner should act on. Below a quarter, arithmetic is the more
       likely explanation than physics, and a wrong "Poor" blames a driver. */
    const implausiblyLow = actualKmPerL < benchmark * MIN_FRACTION_OF_BENCHMARK;
    if (actualKmPerL < 0.5 || actualKmPerL > 40 || implausiblyLow) {
      /* Plain words first (Gabriel, 2026-08-23: "it should have a message
         saying we aren't showing anything since the numbers don't add up"). */
      return {
        pct: null,
        kmPerL: null,
        l100km: null,
        confidence: "early",
        confidenceLabel: "Not scored — the numbers don't add up",
        tooltipNote:
          `We are not showing a score because the numbers don't add up: ` +
          `${input.totalLitresAdded!.toFixed(0)} L logged against ${input.totalKm30.toFixed(0)} tracked km ` +
          `is ${actualKmPerL.toFixed(2)} km/L, and no bus does that against a ${benchmark} km/L target. ` +
          `It almost always means trips ran without tracking (or test litres were typed), so the km side ` +
          `is incomplete while every litre was logged. Real fills at real pumps will score; ` +
          `a wrong "Poor" would blame a driver on arithmetic.`,
      };
    }
    const l100km = actualL100km!;
    const pct = Math.round(Math.min(100, (actualKmPerL / benchmark) * 100));
    return {
      pct,
      kmPerL: actualKmPerL,
      l100km,
      confidence: "measured",
      confidenceLabel: "Measured",
      tooltipNote:
        `${input.totalLitresAdded!.toFixed(1)} L added · ${input.totalKm30.toFixed(0)} km ` +
        `→ ${actualKmPerL.toFixed(2)} km/L vs benchmark ${benchmark} km/L · Score: ${pct}%.`,
    };
  }

  // ── Benchmark set + refuel count (estimated litres from tank size) ─────────
  if (input.totalKm30 > 0 && input.refuelCount30 >= 1) {
    const estimatedLitres = input.refuelCount30 * input.tankCapacityL * FILL_FRACTION;
    const kmPerL = input.totalKm30 / estimatedLitres;
    const l100km = (estimatedLitres / input.totalKm30) * 100;
    const pct = Math.round(Math.min(100, (kmPerL / benchmark) * 100));
    return {
      pct,
      kmPerL,
      l100km,
      confidence: "estimated",
      confidenceLabel: "Estimated",
      tooltipNote:
        `Estimated from ${input.refuelCount30} refuel event(s) and a ${input.tankCapacityL} L tank ` +
        `(80% fill assumed) · ${input.totalKm30.toFixed(0)} km. ` +
        `Benchmark: ${benchmark} km/L · Score: ${pct}%. ` +
        `Ask drivers to enter actual litres for a precise reading.`,
    };
  }

  // ── Benchmark set, no refuel data yet ─────────────────────────────────────
  return {
    pct: null,
    kmPerL: null,
    l100km: null,
    confidence: "early",
    confidenceLabel: "Need refuel data",
    tooltipNote:
      `Benchmark set at ${benchmark} km/L. ` +
      "Ask drivers to log refuel events and enter litres added to calculate a score.",
  };
}

function buildFuelBenchmarkUpdates(
  fuelCapacityInput: string,
  benchmarkInput: string,
  busYearInput: string,
  odometerUnit?: "km" | "mi" | "miles",
): Record<string, FieldValue | number | string | null> {
  const updates: Record<string, FieldValue | number | string | null> = { updatedAt: serverTimestamp() };
  const cap = parseFloat(fuelCapacityInput);
  if (!isNaN(cap) && cap >= 50 && cap <= 500) {
    updates.fuelTankCapacityL = cap;
    updates.fuelCapacityLiters = cap;
  }
  const bench = parseFloat(benchmarkInput);
  if (!isNaN(bench) && bench > 0) {
    updates.benchmarkKmPerL = bench;
    updates.benchmarkKmPerLiter = bench;
  } else if (benchmarkInput.trim() === "") {
    updates.benchmarkKmPerL = null;
    updates.benchmarkKmPerLiter = null;
  }
  const yr = parseInt(busYearInput, 10);
  if (!isNaN(yr) && yr >= 1980 && yr <= new Date().getFullYear()) updates.yearOfManufacture = yr;
  else if (busYearInput.trim() === "") updates.yearOfManufacture = null;
  if (odometerUnit === "mi" || odometerUnit === "miles") updates.odometerUnit = "miles";
  else if (odometerUnit === "km") updates.odometerUnit = "km";
  return updates;
}

const detailsFieldStyle: React.CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  fontSize: 15,
  color: "#111",
  outline: "none",
  background: "#fff",
  fontFamily: "inherit",
};

function BusDetailsEditor(props: {
  tank: string;
  benchmark: string;
  year: string;
  unit: "km" | "mi";
  saving: boolean;
  saved: boolean;
  onTank: (v: string) => void;
  onBenchmark: (v: string) => void;
  onYear: (v: string) => void;
  onUnit: (u: "km" | "mi") => void;
  onSave: () => void;
}) {
  const { tank, benchmark, year, unit, saving, saved, onTank, onBenchmark, onYear, onUnit, onSave } = props;
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Tank, benchmark &amp; dash units</div>
      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16, lineHeight: 1.5 }}>
        You set tank size and whether this dash reads kilometres or miles. The driver types the actual reading in the app at each refill — there is no odometer number here.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <label style={{ display: "block" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>Tank size (litres)</div>
          <input type="number" min={50} max={500} value={tank} onChange={(e) => onTank(e.target.value)} placeholder="e.g. 200" style={detailsFieldStyle} />
        </label>
        <label style={{ display: "block" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>Benchmark (km per litre)</div>
          <input type="number" min={0.1} step={0.1} value={benchmark} onChange={(e) => onBenchmark(e.target.value)} placeholder="e.g. 8" style={detailsFieldStyle} />
        </label>
        <label style={{ display: "block" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>Year of manufacture</div>
          <input type="number" min={1980} max={new Date().getFullYear()} value={year} onChange={(e) => onYear(e.target.value)} placeholder="e.g. 2015" style={detailsFieldStyle} />
        </label>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>This dash reads</div>
          <div style={{ display: "flex", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", height: 42 }}>
            {(["km", "mi"] as const).map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => onUnit(u)}
                style={{
                  flex: 1,
                  border: "none",
                  background: unit === u ? "#0f172a" : "#fff",
                  color: unit === u ? "#fff" : "#64748b",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {u === "km" ? "Kilometres" : "Miles"}
              </button>
            ))}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        style={{
          padding: "10px 22px",
          background: saved ? "#16a34a" : "#0f172a",
          border: "none",
          borderRadius: 9,
          color: "#fff",
          fontWeight: 600,
          fontSize: 13,
          cursor: saving ? "default" : "pointer",
          fontFamily: "Geist, sans-serif",
        }}
      >
        {saving ? "Saving…" : saved ? "✓ Saved" : "Save details"}
      </button>
    </Card>
  );
}

function mapBusDoc(id: string, data: Record<string, unknown>, kmHistory: number[]): Bus {
  const trafficDetected = data.trafficDetected as boolean | undefined;
  const children = (data.children as Record<string, unknown>[] || []).map((c: Record<string, unknown>) => {
    const cn = [c.className, c.yearGroup].map((x) => (typeof x === "string" ? x.trim() : "")).find((s) => s.length > 0);
    return {
      id: (c.childId as string) || "",
      name: (c.childName as string) || "",
      parentPhone: (c.parentPhone as string) || "",
      status: (c.status as string) || "not_picked",
      parentId: c.parentId as string | undefined,
      classLabel: cn || undefined,
      temporaryTripChild: c.temporaryTripChild === true || String(c.childId || "").startsWith("otrip_"),
      transportSuspended: c.transportSuspended === true,
      /* `lastUpdated` is what the driver app writes on the bus roster;
         `timestamp` is the attendance copy's name for the same moment. Read
         both so a row from either source has a time. */
      time: ((c.lastUpdated ?? c.timestamp) as Timestamp | undefined)?.toDate?.() ?? null,
    };
  });
  const currentRoute = (data.currentRoute as Record<string, unknown>) || {};
  const startedAt = currentRoute.startedAt as Timestamp | undefined;
  return {
    id,
    plate: (data.numberPlate as string) || id,
    name: (data.nickname as string) || id,
    drivers: (data.driverIds as string[]) || [],
    children,
    route: {
      type: (currentRoute.type as string) || null,
      status: (currentRoute.status as string) || null,
      started: startedAt?.toDate?.() ?? null,
    },
    morningTime: (data.morningPickupTime as string) || "",
    eveningTime: (data.eveningPickupTime as string) || "",
    school: (data.schoolCode as string) || "",
    trafficDetected,
    status: (data.status as Bus["status"]) || "active",
    crashes: [],
    kmHistory,
    /* TWO FIELDS FOR ONE TANK (found 2026-08-02).
     *
     * The dashboard has always written and read `fuelTankCapacityL`. The mobile
     * app has always written and read `fuelCapacityLiters` — signup, the driver
     * Settings editor, and the Fuel Engine's anchor all use that name. Neither
     * side could see the other's value.
     *
     * Nothing had caught fire only because production has 22 buses and NOT ONE
     * carries either field, so both sides were falling back to the same guess.
     * The moment a driver entered a tank size at signup — which `59681fe` made
     * compulsory — the dashboard would have carried on costing fuel against
     * DEFAULT_TANK_CAPACITY_L and looked precise while doing it.
     *
     * Read either, prefer the app's: that is the one the Engine anchors on, so
     * it is the number the litres in `vehicleFuelState` were actually derived
     * from. Writes now set both (see the tank editor). */
    fuelTankCapacityL:
      (data.fuelCapacityLiters as number | undefined)
      ?? (data.fuelTankCapacityL as number | undefined)
      ?? null,
    benchmarkKmPerL: typeof data.benchmarkKmPerL === "number" ? data.benchmarkKmPerL : null,
    yearOfManufacture: typeof data.yearOfManufacture === "number" ? data.yearOfManufacture : null,
    odometerKm: typeof data.odometerKm === "number" ? data.odometerKm : null,
    odometerBaselineKm:
      typeof data.odometerBaselineKm === "number" && data.odometerBaselineKm > 0
        ? data.odometerBaselineKm
        : null,
    odometerUnit: data.odometerUnit === "mi" || data.odometerUnit === "miles" ? "mi" : "km",
    liveLocation: (() => {
      const live = data.liveLocation as Record<string, unknown> | undefined;
      if (!live) return null;
      const lat = typeof live.lat === "number" ? live.lat : Number(live.lat);
      const lng = typeof live.lng === "number" ? live.lng : Number(live.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng };
    })(),
    liveLocationUpdatedAt: (() => {
      const docTs = data.liveLocationUpdatedAt as { toMillis?: () => number } | undefined;
      if (docTs && typeof docTs.toMillis === "function") return docTs.toMillis();
      const live = data.liveLocation as Record<string, unknown> | undefined;
      const ts = live?.timestamp as { toMillis?: () => number } | undefined;
      if (ts && typeof ts.toMillis === "function") return ts.toMillis();
      return null;
    })(),
    locationError: typeof data.locationError === "string" ? data.locationError : null,
    gpsRoutePoints: parseGpsPoints(data.routePoints),
  };
}

function mapAttendanceDoc(id: string, data: Record<string, unknown>): AttendanceRecord {
  const children = (data.children as Record<string, unknown>[] || []).map((c: Record<string, unknown>) => ({
    id: (c.childId as string) || undefined,
    name: (c.childName as string) || "",
    status: (c.status as string) || "not_picked",
    time: (c.timestamp as Timestamp)?.toDate?.() ?? null,
    // Morning marks write pickupLat/…; EVENING drop marks write dropLat/… —
    // either is a served-stop pin, and an evening trip with a dead phone was
    // exactly the case this estimate exists for.
    lat: typeof c.pickupLat === "number" ? (c.pickupLat as number)
      : typeof c.dropLat === "number" ? (c.dropLat as number) : null,
    lng: typeof c.pickupLng === "number" ? (c.pickupLng as number)
      : typeof c.dropLng === "number" ? (c.dropLng as number) : null,
  }));
  const routeStartedAt = data.routeStartedAt as Timestamp | undefined;
  const routeFinishedAt = data.routeFinishedAt as Timestamp | undefined;
  const msl = data.mapStartLat as number | undefined;
  const msng = data.mapStartLng as number | undefined;
  const mel = data.mapEndLat as number | undefined;
  const meng = data.mapEndLng as number | undefined;
  return {
    id,
    busId: (data.busId as string) || "",
    date: (data.date as Timestamp)?.toDate?.() ?? (routeStartedAt?.toDate?.() ?? new Date()),
    type: (data.routeType as string) || "morning",
    children,
    started: routeStartedAt?.toDate?.() ?? null,
    finished: routeFinishedAt?.toDate?.() ?? null,
    duration: (data.durationMinutes as number) ?? null,
    km: (data.distanceKm as number) ?? null,
    status: typeof data.status === "string" ? (data.status as string) : null,
    routeAnomaly: (data.routeAnomaly as boolean) ?? false,
    pinned: data.pinned === true,
    pinnedName: typeof data.pinnedName === "string" ? (data.pinnedName as string) : null,
    needsRefuelSoon: (data.needsRefuelSoon as boolean) ?? false,
    isRefuelTrip: data.isRefuelTrip === true || data.routeType === "refuel",
    visitedStation: data.visitedStation === true,
    loggedRefuel: data.loggedRefuel === true,
    stationVisits: typeof data.stationVisits === "number" ? (data.stationVisits as number) : null,
    refuelsLogged: typeof data.refuelsLogged === "number" ? (data.refuelsLogged as number) : null,
    driverId: typeof data.driverId === "string" ? data.driverId : null,
    routePoints: parseGpsPoints(data.routePoints),
    rawRoutePoints: Array.isArray(data.routePoints) ? (data.routePoints as unknown[]) : [],
    trailChunks: typeof data.trailChunks === "number" ? (data.trailChunks as number) : 0,
    trailWindowStart: typeof data.trailWindowStart === "number" ? (data.trailWindowStart as number) : null,
    trailSpilled: typeof data.trailSpilled === "number" ? (data.trailSpilled as number) : 0,
    snappedRoutePoints: parseGpsPoints(data.routePointsSnapped),
    mapStart:
      msl != null && msng != null && Number.isFinite(msl) && Number.isFinite(msng)
        ? { lat: msl, lng: msng }
        : null,
    mapEnd:
      mel != null && meng != null && Number.isFinite(mel) && Number.isFinite(meng)
        ? { lat: mel, lng: meng }
        : null,
  };
}

/* A trip that lost its trail (dead phone, killed app) still marked its
 * children — and those marks carry OUR pins. Chain the served stops in time
 * order, sum the straight-line legs, and widen by a road factor: Nairobi
 * roads are not straight lines, and 1.3 keeps the figure a fair ESTIMATE
 * rather than a flattering floor. Never presented as measured. */
function estimateKmFromStops(r: AttendanceRecord): number | null {
  const pts = (r.children || [])
    .filter((c) => c.lat != null && c.lng != null && c.time != null)
    .sort((a, b) => (a.time as Date).getTime() - (b.time as Date).getTime());
  if (pts.length < 2) return null;
  const rad = (d: number) => (d * Math.PI) / 180;
  let sum = 0;
  for (let i = 1; i < pts.length; i += 1) {
    const la1 = rad(pts[i - 1].lat as number); const la2 = rad(pts[i].lat as number);
    const dLa = la2 - la1; const dLo = rad((pts[i].lng as number) - (pts[i - 1].lng as number));
    const a = Math.sin(dLa / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLo / 2) ** 2;
    sum += 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  const est = sum * 1.3;
  return est > 0.2 ? est : null;
}

/** Show distance as "X m" when < 1 km, else "X km". Attendance from app may be short routes. */
function formatDistance(km: number | null | undefined): string {
  if (km == null || Number.isNaN(km)) return "—";
  if (km < 1 && km >= 0) return `${Math.round(km * 1000)} m`;
  return `${Number(km).toFixed(1)} km`;
}

/** Last N trips as one row each. Unique labels so same-day morning/evening
 *  never collapse into one Recharts category (that is what mixed the dates). */
function lastRouteDistanceRows(records: AttendanceRecord[], n = 8): {
  id: string;
  day: string;
  when: string;
  km: number | null;
  kind: "measured" | "estimated" | "none";
}[] {
  return [...records].slice(0, n).reverse().map((r) => {
    const day = r.date
      ? new Date(r.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
      : "—";
    const t = (r.type || "").toLowerCase();
    const leg = t === "evening" ? "evening" : t === "morning" ? "morning" : t || "trip";
    const time = r.started
      ? r.started.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
      : "";
    const measured = r.km != null && r.km > 0;
    const est = !measured ? estimateKmFromStops(r) : null;
    return {
      id: r.id,
      day,
      when: time ? `${leg} · ${time}` : leg,
      km: measured ? (r.km as number) : est,
      kind: (measured ? "measured" : est != null ? "estimated" : "none") as "measured" | "estimated" | "none",
    };
  });
}

const STATUS: Record<string, { label: string; color: string; bg: string; border: string }> = {
  picked_up: { label: "Picked Up", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  dropped_off: { label: "Dropped Off", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  not_picked: { label: "Not Picked", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  absent: { label: "Absent", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
};

const CRASH_SEV = {
  minor: { label: "Minor", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  major: { label: "Major", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
};

/** Stored in `reassignTargets` when the destination is a new temporary hired bus (created on confirm). */
const REASSIGN_HIRED_SENTINEL = "__hired__";

const FULL_FEATURE_ONLY_MSG =
  "School trips are available on school-bus accounts. This account doesn't support them — contact PEAR.";

function formatCallableFailure(e: unknown): string {
  const fe = e as { code?: string; message?: string };
  const msg = fe.message || (e instanceof Error ? e.message : String(e));
  if (fe.code === "functions/internal" || msg === "internal") {
    if (process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === "true") {
      return `${msg} — Is the Functions emulator running? From pear_mobile: npm run build --prefix functions && firebase emulators:start --only functions (port 5001).`;
    }
    return `${msg} — Production: enable Firebase Blaze on the project, then deploy: firebase deploy --only functions:createTemporaryHireBus (from pear_mobile). Local dev without Blaze: set REACT_APP_USE_FUNCTIONS_EMULATOR=true in .env and run the emulator as above.`;
  }
  return msg;
}

/** Match if every token appears in normalized name + class (flexible: "class 3", "year6blue"). */
function studentMatchesPlannerFilter(name: string, classLabel: string | undefined, filterRaw: string): boolean {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const ft = norm(filterRaw);
  if (!ft) return true;
  const tokens = ft.split(/\s+/).filter(Boolean);
  const hay = norm(`${name} ${classLabel ?? ""}`);
  return tokens.every((t) => hay.includes(t));
}

function newTripLegId(): string {
  return `leg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

// School-bus docs + busAttendance.busId use UPPERCASE plate (Flutter normalizePlate).
// Fleet plate keys are lowercase (fleetPlateKey). This helper is for case-insensitive
// matching when resolving a bus from a mixed-case id/plate string.
function normalizePlateKey(plate: string): string {
  return plate.toLowerCase().replace(/[-\s]/g, "");
}

function parseTripChildKey(key: string): { busId: string; childId: string } | null {
  const i = key.indexOf("::");
  if (i <= 0) return null;
  return { busId: key.slice(0, i), childId: key.slice(i + 2) };
}

function firestoreTimestampToDate(v: unknown): Date | null {
  if (v == null) return null;
  if (v instanceof Timestamp) return v.toDate();
  const t = v as { toDate?: () => Date; toMillis?: () => number };
  if (typeof t.toDate === "function") return t.toDate();
  if (typeof t.toMillis === "function") return new Date(t.toMillis());
  return null;
}

type TripLegFleetForm = {
  lid: string;
  kind: "fleet";
  busId: string;
  useFleetCrew: boolean;
  childKeys: Set<string>;
  condName: string;
  condEmail: string;
  condPassword: string;
};

type TripLegHiredForm = {
  lid: string;
  kind: "hired";
  nickname: string;
  plate: string;
  childKeys: Set<string>;
  c1Name: string;
  c1Email: string;
  c1Password: string;
  c2Name: string;
  c2Email: string;
  c2Password: string;
};

type TripLegForm = TripLegFleetForm | TripLegHiredForm;

const ft = (d: DateLike, mode: "time" | "date" | "short" = "time"): string => {
  if (!d) return "—";
  const date = d instanceof Date ? d : new Date(d as string | number);
  if (mode === "date") return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  if (mode === "short") return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const Pill = ({ label, color, bg, border }: { label: string; color: string; bg: string; border: string }) => (
  <span style={{ fontSize: 11, fontWeight: 600, color, background: bg, border: `1px solid ${border}`, borderRadius: 20, padding: "3px 10px", whiteSpace: "nowrap", fontFamily: "Geist, sans-serif", letterSpacing: "0.01em" }}>
    {label}
  </span>
);

const Dot = ({ color }: { color: string }) => <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 }} />;

const SectionHead = ({ children, sub }: { children: React.ReactNode; sub?: React.ReactNode }) => (
  <div style={{ marginBottom: 20 }}>
    <h2 style={{ fontFamily: "Instrument Serif, serif", fontSize: 22, fontWeight: 400, color: "#111", letterSpacing: "-0.02em", margin: 0 }}>{children}</h2>
    {sub && <p style={{ fontFamily: "Geist, sans-serif", fontSize: 13, color: "#94a3b8", margin: "4px 0 0", fontWeight: 400 }}>{sub}</p>}
  </div>
);

const Card = ({ children, style = {} }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ background: "#fff", border: "1px solid #f1f5f9", borderRadius: 14, padding: 24, ...style }}>{children}</div>
);

const StatTile = ({ label, value, sub, accent = "#111" }: { label: string; value: React.ReactNode; sub?: React.ReactNode; accent?: string }) => (
  <div style={{ background: "#fff", border: "1px solid #f1f5f9", borderRadius: 12, padding: "18px 20px" }}>
    <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 32, color: accent, letterSpacing: "-0.03em", lineHeight: 1 }}>{value}</div>
    <div style={{ fontFamily: "Geist, sans-serif", fontSize: 12, fontWeight: 600, color: "#64748b", marginTop: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
    {sub != null && sub !== "" && <div style={{ fontFamily: "Geist, sans-serif", fontSize: 11, color: "#cbd5e1", marginTop: 3 }}>{sub}</div>}
  </div>
);


const MiniBar = ({ value, max, color = "#e2e8f0", fill = "#0f172a" }: { value: number; max: number; color?: string; fill?: string }) => (
  <div style={{ height: 3, background: color, borderRadius: 2, overflow: "hidden", marginTop: 6 }}>
    <div style={{ height: "100%", width: `${Math.min(100, (value / max) * 100)}%`, background: fill, borderRadius: 2, transition: "width 0.5s ease" }} />
  </div>
);

type FleetPeriodStats = {
  trips: number;
  totalKm: number;
  absences: number;
  morning: number;
  evening: number;
};

type FleetBusPeriodRow = {
  busId: string;
  trips: number;
  totalKm: number;
  absences: number;
  lastTripAt: Date | null;
};

/** Rolling 30-day trip/absence stats from attendance rows (single bus or whole fleet). */
function computeFleetPeriodStats(rows: AttendanceRecord[]): FleetPeriodStats {
  const cutoff30 = new Date();
  cutoff30.setDate(cutoff30.getDate() - 30);
  const rows30 = rows.filter((r) => {
    if (isRefuelAttendance(r)) return false;
    const t = r.finished ?? r.date;
    return t && new Date(t) >= cutoff30;
  });
  const trips = rows30.length;
  const totalKm = rows30.reduce((s, r) => s + (r.km ?? 0), 0);
  let absences = 0;
  let morning = 0;
  let evening = 0;
  for (const r of rows30) {
    if (r.type === "morning") morning++;
    else if (r.type === "evening") evening++;
    for (const c of r.children) {
      if (c.status === "absent" || c.status === "not_picked") absences++;
    }
  }
  return { trips, totalKm, absences, morning, evening };
}

/** Per-bus slice of the same 30-day window — scales to a full fleet, unlike a day list. */
function computeFleetByBusPeriodStats(
  rows: AttendanceRecord[],
  busIds: string[],
): FleetBusPeriodRow[] {
  const cutoff30 = new Date();
  cutoff30.setDate(cutoff30.getDate() - 30);
  const map = new Map<string, FleetBusPeriodRow>();
  for (const id of busIds) {
    map.set(id, { busId: id, trips: 0, totalKm: 0, absences: 0, lastTripAt: null });
  }
  for (const r of rows) {
    if (isRefuelAttendance(r)) continue;
    const t = r.finished ?? r.date;
    if (!t || new Date(t) < cutoff30) continue;
    const cur = map.get(r.busId) ?? {
      busId: r.busId,
      trips: 0,
      totalKm: 0,
      absences: 0,
      lastTripAt: null,
    };
    cur.trips += 1;
    cur.totalKm += r.km ?? 0;
    for (const c of r.children) {
      if (c.status === "absent" || c.status === "not_picked") cur.absences += 1;
    }
    const when = new Date(t);
    if (!cur.lastTripAt || when > cur.lastTripAt) cur.lastTripAt = when;
    map.set(r.busId, cur);
  }
  return Array.from(map.values()).sort((a, b) => {
    if (b.absences !== a.absences) return b.absences - a.absences;
    if (b.trips !== a.trips) return b.trips - a.trips;
    return a.busId.localeCompare(b.busId);
  });
}

export default function FleetDashboard() {
  const { fleetOrgCode, logout, currentUser, refreshAdminStatus } = useAuth();
  // Premium gating: locked-out orgs only. Pilots on Premium/Enterprise get the
  // full plan (incl. Analytics / Fuel / trips) during the free window — same
  // product rule as isPremiumOrg / isZanaTier (2026-08-21).
  const sub = useOrgSubscription(fleetOrgCode);
  const premiumLocked = !sub.isPremiumUnlocked;
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeStart, setUpgradeStart] = useState<'tour' | 'checkout'>('tour');
  const [tryAnalyticsCoach, setTryAnalyticsCoach] = useState(false);
  // start 'checkout' jumps past the tour (used by the payment-retry banner).
  const openUpgrade = useCallback((start?: 'tour' | 'checkout') => {
    setUpgradeStart(start === 'checkout' ? 'checkout' : 'tour');
    setTryAnalyticsCoach(false);
    setShowUpgrade(true);
  }, []);
  const [cancelTripBusy, setCancelTripBusy] = useState(false);

  /**
   * Admin deletes/cancels a trip. The status flip is written client-side so EVERY
   * participant reverts out of Trip mode immediately from Firestore (parents live,
   * drivers on next resume / time-backstop) — no backend needed. Best-effort: also
   * disable the conductor logins and push a "trip cancelled" notice to everyone.
   */
  const handleCancelTrip = useCallback(async (tripId: string, data: Record<string, unknown>) => {
    if (!fleetOrgCode) return;
    // window.confirm's buttons are browser-fixed as OK/Cancel — and "Cancel"
    // KEEPS the trip, which read as "nothing happened" when Gabriel pressed
    // it expecting it to cancel (09-04). The message now names the buttons.
    if (!window.confirm('Cancel this trip? Press OK to cancel it — everyone on it is taken out of Trip mode and notified, and the children go back to their normal bus. (Cancel keeps the trip running.)')) return;
    setCancelTripBusy(true);
    try {
      await updateDoc(doc(db, 'schoolTrips', tripId), { status: 'cancelled', cancelledAt: new Date(), updatedAt: serverTimestamp() });
      const parentIds = Array.isArray(data.parentIds) ? (data.parentIds as string[]) : [];
      const crew = Array.isArray(data.tripConductorUids) ? (data.tripConductorUids as string[]) : [];
      if (isTripsApiConfigured()) {
        // Backend cancelTrip already pushes + writes the inbox row for every
        // parentId — notifying them from here too sent every parent TWO pushes
        // and TWO Recent Activity rows per cancellation. The backend does not
        // cover the conductor crew, so that notice stays ours; parents are
        // re-covered from here only when the backend's own parent notice never
        // ran (notified.total = 0 means its helper threw before writing any
        // row — sent:0 alone just means stale tokens, rows already written).
        let parentsCovered = false;
        try {
          const r = await cancelTripApi({ orgCode: fleetOrgCode, tripId });
          parentsCovered = parentIds.length === 0 || (r?.notified?.total ?? 0) > 0;
        } catch { /* conductor disable needs backend */ }
        const clientRecipients = parentsCovered
          ? [...new Set(crew)]
          : [...new Set([...parentIds, ...crew])];
        if (clientRecipients.length) {
          try { await notifyTripApi({ orgCode: fleetOrgCode, tripId, parentIds: clientRecipients, title: 'School trip cancelled', body: `${(data.title as string) || 'The school trip'} has been cancelled. Your child is back on their normal bus.` }); } catch { /* push needs backend */ }
        }
      }
      customToast.show('Trip cancelled — everyone has been taken out of Trip mode.', 'success');
      setTripsDetailTripId(null);
    } catch {
      customToast.show('Could not cancel the trip. Please try again.', 'error');
    } finally {
      setCancelTripBusy(false);
    }
  }, [fleetOrgCode]);
  const [showSettings, setShowSettings] = useState(false);
  const [inviteAdminEmail, setInviteAdminEmail] = useState('');
  const [adminInviteSending, setAdminInviteSending] = useState(false);
  const [adminInviteStatus, setAdminInviteStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [adminInviteError, setAdminInviteError] = useState('');
  const [parentMapEnabled, setParentMapEnabled] = useState(true);
  /* Term boundary (Gabriel, 2026-08-17): a new term is a fresh count — the
   * rings and fuel sums must not carry last term's driving across a holiday
   * in which anything could have happened. History stays stored and visible;
   * only the CURRENT-health windows clip to the term. Null = no term set. */
  const [termStartMs, setTermStartMs] = useState<number | null>(null);
  /* Approving a Zana-proposed odometer fix. She PROPOSES (anchored to the
   * previous reading + what GPS proved); the admin types/confirms the number
   * and this ships it to the backend, which preserves the original on the
   * event. She never applies one herself. */
  const [odoFixBusy, setOdoFixBusy] = useState<string | null>(null);
  /* `gpsProven` is the difference between "GPS says the reading is too low"
     and "these two numbers disagree and nobody knows which is wrong".
     ──────────────────────────────────────────────────────────────────────
     This dialog always claimed the first, and always PREFILLED previous +
     tracked km. On 2026-08-24 Gabriel typed KCE999Z's real dashboard reading
     (333,642) after five SEEDED test fills in the 48,xxx range, with nothing
     tracked between them. Accepting the default here would have overwritten
     the only true reading in the chain with 48,385 and left the fakes
     standing. With no tracked kilometres nothing is proven — so say so, and
     prefill the number the driver actually read off the dash. */
  const approveOdometerFix = async (
    eventId: string, proposedMinKm: number, typedKm?: number, gpsProven = true,
  ) => {
    const base = (process.env.REACT_APP_PAYMENTS_URL || "").replace(/\/$/, "");
    if (!base) { customToast.show("Payments API URL is not configured.", "error"); return; }
    const prefill = gpsProven ? proposedMinKm : (typedKm ?? proposedMinKm);
    const answer = window.prompt(
      gpsProven
        ? `Corrected odometer reading (km). GPS proves at least ${proposedMinKm.toLocaleString()} km`
          + (typeof typedKm === "number" ? ` — the driver typed ${typedKm.toLocaleString()}.` : ".")
        : "Correct odometer reading (km). Nothing was tracked between these two"
          + ` readings, so neither is proven: the driver typed ${(typedKm ?? 0).toLocaleString()}`
          + ` and the one before it was ${proposedMinKm.toLocaleString()}. Type whatever the`
          + " dashboard actually shows.",
      String(prefill),
    );
    if (answer == null) return;
    // Strict: digits only after stripping thousands separators. A stray dot
    // ("118.540") is REJECTED rather than guessed at — parseInt would silently
    // truncate it to 118 and ship a wrong correction with an honest face.
    const cleaned = answer.replace(/[,\s]/g, "");
    if (!/^\d+$/.test(cleaned)) { customToast.show("Digits only, please — e.g. 118540.", "error"); return; }
    const km = parseInt(cleaned, 10);
    if (!Number.isFinite(km) || km <= 0) { customToast.show("That is not a valid km reading.", "error"); return; }
    setOdoFixBusy(eventId);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${base}/api/premium/fuel/odometer-correction`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orgCode: fleetOrgCode, eventId, approvedKm: km }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.ok) customToast.show("Odometer corrected — the original stays on the record.", "success");
      else customToast.show(body.message || "Could not apply the correction.", "error");
    } catch {
      customToast.show("Could not reach the payments API.", "error");
    } finally {
      setOdoFixBusy(null);
    }
  };
  // When on, this org's drivers get the old fuel system in its entirety — the
  // post-trip survey, the "Did you refuel the vehicle?" check and the weekend
  // prompt — instead of the Fuel Engine. Off is the default and the field is
  // absent on every org, so nobody is on the old system until an admin asks.
  const [legacyFuelFlowEnabled, setLegacyFuelFlowEnabled] = useState(false);
  // Set when Zana proposes a takeover. The dock picks this up, fetches the
  // resolved script from the engine and drives — the client never builds steps.
  const [tripCompleteSaving, setTripCompleteSaving] = useState(false);
  // Add-ons declared at signup. Each is { wanted, status }; status 'connected' means a real
  // feed is wired (Connections console), otherwise the app falls back to default values.
  const [orgAddons, setOrgAddons] = useState<Record<string, { wanted?: boolean; status?: string; verified?: boolean }> | null>(null);
  const [orgIsPilot, setOrgIsPilot] = useState(false);
  const [orgDisplayName, setOrgDisplayName] = useState('');
  const [parentMapSaving, setParentMapSaving] = useState(false);
  const [buses, setBuses] = useState<Bus[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  /* The bus map shows a TRIP, not a blank. Gabriel, 2026-08-19: "on that place
     for no position, that's where I want you to be showing the trip on the map
     — by default the most recent, and when one selects a different trip it
     shows the selected one." null = nothing clicked yet → newest run. A LIVE
     run still wins until a row is explicitly clicked: watching the bus now
     beats re-reading yesterday. */
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  /* A run asked for by name (a pinned run opened from Analytics → Routes)
     survives the bus-change reset below, which would otherwise clear it. */
  const pendingRunIdRef = useRef<string | null>(null);
  const [runTrail, setRunTrail] = useState<{ forId: string; pts: { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[] } | null>(null);
  const [selectedBus, setSelectedBus] = useState<Bus | null>(null);
  /* Bus detail sections. Seven tabs became four on 2026-07-30: Children,
     Attendance and Absentees were the same roster asked three ways, so they are
     one tab with a segmented filter; Incidents and Settings became Record.
     The old tab bodies are untouched — only which section owns them changed. */
  const [tab, setTab] = useState("today");
  const [peopleSub, setPeopleSub] = useState<"children" | "attendance" | "absentees">("children");
  const [search, setSearch] = useState("");
  const [mTime, setMTime] = useState("");
  const [eTime, setETime] = useState("");
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fleetAlerts, setFleetAlerts] = useState<FleetAlert[]>([]);
  const [fleetAlertsReady, setFleetAlertsReady] = useState(false);
  const [fleetAttendance, setFleetAttendance] = useState<AttendanceRecord[]>([]);
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [announceMsg, setAnnounceMsg] = useState("");
  const [announceSending, setAnnounceSending] = useState(false);
  const [announceResult, setAnnounceResult] = useState<string | null>(null);
  const [signupCode, setSignupCode] = useState<string | null>(null);
  const [signupCodeExpiresAt, setSignupCodeExpiresAt] = useState<number | null>(null);
  const [refuelEvents, setRefuelEvents] = useState<{ id: string; timestamp: Date; litersAdded?: number; costEstimate?: number; kmSinceLastRefuel?: number; efficiencyFlag?: string; reviewReason?: string; odometerKm?: number; odometerUnknown?: boolean; previousOdometerKm?: number; gpsKmSincePrevRefuel?: number; odometerCorrected?: boolean; correctedAt?: Date | null; odometerOriginalKm?: number | null }[]>([]);
  /** Number of days for the Fuel Cost window shown in the Analytics tab (10 / 30 / 50). */
  const [fuelCostDays, setFuelCostDays] = useState<10 | 30 | 50>(30);
  const [benchmarkInput, setBenchmarkInput] = useState<string>("");
  const [busYearInput, setBusYearInput] = useState<string>("");
  const [odometerUnit, setOdometerUnit] = useState<"km" | "mi">("km");
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [editingFuelCapacity, setEditingFuelCapacity] = useState(false);
  const [fuelCapacityInput, setFuelCapacityInput] = useState<string>("");
  /* NOTE on "maintenance": this is the SCHEDULE tab (service intervals, expiry,
     burn drift). It is a different thing from `showMaintenanceFullScreen`
     below, which is the overlay listing buses whose STATUS is "maintenance".
     Same word, two concepts — the status one is reached from Fleet. */
  type TopLevelTab = "dashboard" | "fleet" | "accounts" | "monthlyReport" | "trips" | "liveMap" | "analytics" | "fuel" | "fuelPrices" | "maintenance" | "zanaChat";
  /* /fleet/classic?tab=trips opens straight on that tab: the v3 console hands
     the jobs it does not redo itself (planning a trip, adding a bus…) to this
     dashboard by link (2026-10-23). */
  const [topLevelTab, setTopLevelTab] = useState<TopLevelTab>(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    const allowed: TopLevelTab[] = ["dashboard", "fleet", "accounts", "monthlyReport", "trips", "liveMap", "analytics", "fuel", "fuelPrices", "maintenance", "zanaChat"];
    return allowed.includes(t as TopLevelTab) ? (t as TopLevelTab) : "dashboard";
  });
  const [analyticsInitialBusId, setAnalyticsInitialBusId] = useState<string | null>(null);
  /** Analytics sub-tab a takeover step needs open, with a nonce so asking for
   *  the same one twice still lands after the owner has clicked elsewhere. */
  /** Which High Risk row to land on when the screen opens from a notification. */
  const [highRiskInitialKey, setHighRiskInitialKey] = useState<string | null>(null);
  const [zanaAnalyticsSubTab, setZanaAnalyticsSubTab] = useState<AnalyticsSubTab | null>(null);
  const [zanaAnalyticsSubTabNonce, setZanaAnalyticsSubTabNonce] = useState(0);
  const [fleetTrips, setFleetTrips] = useState<Array<{ id: string; data: Record<string, unknown> }>>([]);
  const [tripsDetailTripId, setTripsDetailTripId] = useState<string | null>(null);
  const [tripHireLabels, setTripHireLabels] = useState<Record<string, string>>({});
  /** Conductors/crew resolved for the open trip detail (incl. hired-bus temps). */
  const [tripDetailCrew, setTripDetailCrew] = useState<{
    id: string;
    displayName: string;
    email: string;
    username?: string;
    busName?: string;
  }[]>([]);
  const [tripReissueBusy, setTripReissueBusy] = useState<string | null>(null);
  const [tripReissuedCred, setTripReissuedCred] = useState<{
    kind: "parent" | "conductor";
    displayName: string;
    username: string;
    password: string;
    revealUrl?: string;
    smsNote?: string;
  } | null>(null);
  /** For Monthly Report: selected month (1-based) and year. */
  const [reportMonth, setReportMonth] = useState<{ year: number; month: number }>(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [fleetUsers, setFleetUsers] = useState<{ id: string; email: string; displayName: string; role: string; busId?: string | null; busName?: string; suspended?: boolean; childNames?: string[]; username?: string; temporaryTripConductor?: boolean; temporaryTripParent?: boolean }[]>([]);
  const [accountsSubTab, setAccountsSubTab] = useState<"drivers" | "parents">("drivers");
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; email: string; displayName: string; role: string; busId?: string | null; busName?: string; suspended?: boolean } | null>(null);
  const [accountActionBusy, setAccountActionBusy] = useState(false);
  const [moveBusInput, setMoveBusInput] = useState("");
  const [moveBusSaving, setMoveBusSaving] = useState(false);
  /** Per-child target-plate inputs in the parent account modal (keyed by childId). */
  const [childMoveInputs, setChildMoveInputs] = useState<Record<string, string>>({});

  /**
   * Move one or more of a parent's children to a different bus (by plate).
   * Removes each child from its current bus's `children` array and adds it to
   * the target bus's array (one atomic batch), then repoints the parent's
   * `busChildren` map so their app and the driver both update. Org-scoped:
   * the target must be a bus in this fleet.
   */
  const moveChildrenToBus = useCallback(
    async (childIds: string[], targetPlateRaw: string) => {
      if (!selectedAccount || !fleetOrgCode || childIds.length === 0) return;
      const targetPlate = targetPlateRaw.trim().toUpperCase().replace(/\s/g, "");
      if (!targetPlate) return;
      const target = buses.find(
        (b) => (b.plate || "").toUpperCase().replace(/\s/g, "") === targetPlate
      );
      if (!target) {
        customToast.show("Bus not found in your fleet. Check the plate.", "error");
        return;
      }
      setMoveBusSaving(true);
      try {
        const targetRef = doc(db, "buses", target.id);
        const targetSnap = await getDoc(targetRef);
        const targetChildren: any[] = targetSnap.exists() && Array.isArray((targetSnap.data() as any).children)
          ? [...((targetSnap.data() as any).children as any[])]
          : [];

        const sourceChildrenById: Record<string, any[]> = {};
        const movedNames: string[] = [];

        for (const childId of childIds) {
          const meta = selectedAccount && buses
            .flatMap((b) => b.children.map((c) => ({ bus: b, child: c })))
            .find((x) => x.child.id === childId && x.child.parentId === selectedAccount.id);
          if (!meta || meta.bus.id === target.id) continue;
          const sBusId = meta.bus.id;
          if (!sourceChildrenById[sBusId]) {
            const sSnap = await getDoc(doc(db, "buses", sBusId));
            sourceChildrenById[sBusId] = sSnap.exists() && Array.isArray((sSnap.data() as any).children)
              ? [...((sSnap.data() as any).children as any[])]
              : [];
          }
          const arr = sourceChildrenById[sBusId];
          const idx = arr.findIndex(
            (c: any) => c.childId === childId || (c.childName && c.childName === meta.child.name)
          );
          if (idx < 0) continue;
          const entry = { ...arr[idx], status: "not_picked", lastUpdated: Timestamp.now() };
          arr.splice(idx, 1);
          if (!targetChildren.some((c: any) => (entry.childId && c.childId === entry.childId) || (c.childName && c.childName === entry.childName))) {
            targetChildren.push(entry);
          }
          const nm = (entry.childName as string) || meta.child.name || "";
          if (nm) movedNames.push(nm);
        }

        if (movedNames.length === 0) {
          customToast.show("Nothing to move — already on that bus.", "error");
          return;
        }

        const batch = writeBatch(db);
        Object.entries(sourceChildrenById).forEach(([bid, children]) =>
          batch.update(doc(db, "buses", bid), { children, updatedAt: serverTimestamp() })
        );
        batch.update(targetRef, { children: targetChildren, updatedAt: serverTimestamp() });

        // Repoint the parent's busChildren map so their app shows the new bus.
        const parentRef = doc(db, "users", selectedAccount.id);
        const parentSnap = await getDoc(parentRef);
        const bc: Record<string, string> = parentSnap.exists() && (parentSnap.data() as any).busChildren && typeof (parentSnap.data() as any).busChildren === "object"
          ? { ...((parentSnap.data() as any).busChildren as Record<string, string>) }
          : {};
        movedNames.forEach((nm) => { bc[nm] = target.id; });
        batch.update(parentRef, { busChildren: bc, updatedAt: serverTimestamp() });

        await batch.commit();
        customToast.show(
          `Moved ${movedNames.length} child${movedNames.length > 1 ? "ren" : ""} to ${target.name || targetPlate}`,
          "success"
        );
        setChildMoveInputs({});
        setMoveBusInput("");
      } catch (e) {
        customToast.show("Failed to move children", "error");
      } finally {
        setMoveBusSaving(false);
      }
    },
    [selectedAccount, fleetOrgCode, buses]
  );

  /** A1/A2: set transportSuspended on roster children (parent keeps login). */
  const setChildrenTransportSuspend = useCallback(
    async (childIds: string[], suspended: boolean) => {
      if (childIds.length === 0) return;
      const idSet = new Set(childIds);
      setAccountActionBusy(true);
      try {
        const busIds = new Set<string>();
        for (const b of buses) {
          if (b.children.some((c) => idSet.has(c.id))) busIds.add(b.id);
        }
        if (busIds.size === 0) {
          customToast.show("No children on a bus roster to update", "error");
          return;
        }
        const batch = writeBatch(db);
        let ops = 0;
        for (const bid of busIds) {
          const ref = doc(db, "buses", bid);
          const snap = await getDoc(ref);
          if (!snap.exists()) continue;
          const raw = (snap.data() as { children?: Record<string, unknown>[] }).children;
          if (!Array.isArray(raw)) continue;
          const next = raw.map((c) => {
            const cid = String(c.childId || "");
            if (!idSet.has(cid)) return c;
            const copy = { ...c } as Record<string, unknown>;
            if (suspended) copy.transportSuspended = true;
            else delete copy.transportSuspended;
            return copy;
          });
          batch.update(ref, { children: next, updatedAt: serverTimestamp() });
          ops += 1;
        }
        // Firestore rejects commit() on a batch with zero writes.
        if (ops === 0) {
          customToast.show("Could not load bus roster to update", "error");
          return;
        }
        await batch.commit();
        setBuses((prev) =>
          prev.map((b) => ({
            ...b,
            children: b.children.map((c) =>
              idSet.has(c.id) ? { ...c, transportSuspended: suspended } : c
            ),
          }))
        );
        setSelectedBus((prev) =>
          prev
            ? {
                ...prev,
                children: prev.children.map((c) =>
                  idSet.has(c.id) ? { ...c, transportSuspended: suspended } : c
                ),
              }
            : prev
        );
        customToast.show(
          suspended
            ? `Transport hold on ${childIds.length} child${childIds.length > 1 ? "ren" : ""} — parent can still sign in`
            : `Transport restored for ${childIds.length} child${childIds.length > 1 ? "ren" : ""}`,
          "success"
        );
      } catch {
        customToast.show("Failed to update transport hold", "error");
      } finally {
        setAccountActionBusy(false);
      }
    },
    [buses]
  );

  const [detailView, setDetailView] = useState(false);
  // Bulk invite (Settings panel)
  const [inviteRole, setInviteRole] = useState<'parent' | 'bus_driver'>('parent');
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);
  const [inviteDraft, setInviteDraft] = useState('');
  const [bulkInviting, setBulkInviting] = useState(false);
  // Live list of invites already sent for this org (for the "who's pending / joined" panel).
  const [sentInvites, setSentInvites] = useState<Array<{ id: string; email: string; role: string; used: boolean; usedAt: any; createdAt: any; superseded?: boolean }>>([]);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  /** Full-screen views (instead of modals) for dashboard cards. */
  const [showHighRiskFullScreen, setShowHighRiskFullScreen] = useState(false);
  const [showFuelReportFullScreen, setShowFuelReportFullScreen] = useState(false);
  const [showMaintenanceFullScreen, setShowMaintenanceFullScreen] = useState(false);
  const [showCostEstimateModal, setShowCostEstimateModal] = useState(false);
  const [showCostEstimateFullScreen, setShowCostEstimateFullScreen] = useState(false);
  /** Header bell → notifications panel. */
  const [showNotifications, setShowNotifications] = useState(false);
  /* Which full-screen page opened the bus detail view, so Back returns there
     instead of dumping the user on the dashboard. Cleared by any sidebar
     navigation, because at that point "where you were" is somewhere else. */
  const [detailReturnTo, setDetailReturnTo] =
    useState<null | "highRisk" | "fuelReport" | "maintenance" | "serviceMaintenance">(null);
  /** Selected period in cost full screen: "last30" or specific { year, month }. */
  const [costScreenPeriod, setCostScreenPeriod] = useState<"last30" | { year: number; month: number }>("last30");
  /** Per-bus fuel cost for cost full screen when a specific month is selected. */
  const [costScreenFuelByBus, setCostScreenFuelByBus] = useState<Record<string, number>>({});
  /** Whether the "Reset expenditure" confirm dialog is open. */
  const [resetCostConfirm, setResetCostConfirm] = useState(false);
  const [resetCostBusy, setResetCostBusy] = useState(false);
  const [fleetRefuelCountByBusId, setFleetRefuelCountByBusId] = useState<Record<string, number>>({});
  /** Per-bus litres added (last 30 days) summed from busRefuelEvents.litersAdded. */
  const [fleetLitresAddedByBusId, setFleetLitresAddedByBusId] = useState<Record<string, number>>({});
  /** Per-bus fuel cost (last 30 days) from driver cost estimates in busRefuelEvents. */
  const [costEstimateByBusId, setCostEstimateByBusId] = useState<Record<string, number>>({});
  /** Newest refuel we have ever seen per bus, in ms — not just inside the 30-day
   *  window. It is what tells us an acknowledgement has been overtaken by events. */
  const [fleetLastRefuelAtByBusId, setFleetLastRefuelAtByBusId] = useState<Record<string, number>>({});
  const [fleetLastRefuelReady, setFleetLastRefuelReady] = useState(false);
  /** busId → true when at least one refuel on file carries an odometer.
   *  Only meaningful once `fleetLastRefuelReady`; before that we do not know,
   *  which is a different answer from "no". */
  const [busHasRefuelOdometerById, setBusHasRefuelOdometerById] = useState<Record<string, boolean>>({});
  /** The refuel-history read THREW (rules, network). Not the same as an empty
   *  fleet, and anything that would accuse a bus must treat it as unknown. */
  const [fleetRefuelHistoryFailed, setFleetRefuelHistoryFailed] = useState(false);
  /** busId → when its missing-refuel condition was acknowledged, in ms.
   *  An ack survives a reload; marking the alert read never did. */
  const [refuelAckAtByBusId, setRefuelAckAtByBusId] = useState<Record<string, number>>({});
  const [showFuelEfficiencyModal, setShowFuelEfficiencyModal] = useState(false);
  /** When set, efficiency modal shows for this bus (e.g. opened from fleet list). Otherwise uses selectedBus. */
  const [efficiencyModalBus, setEfficiencyModalBus] = useState<Bus | null>(null);
  const [efficiencyTankL, setEfficiencyTankL] = useState<string>(String(DEFAULT_TANK_CAPACITY_L));
  const [efficiencyCostPerL, setEfficiencyCostPerL] = useState<string>("");
  /** Bus pending delete confirmation; modal shows when set. */
  const [deleteBusToConfirm, setDeleteBusToConfirm] = useState<Bus | null>(null);
  const [busDeleteBusy, setBusDeleteBusy] = useState(false);
  /** Step 1: explain hire / suspend / maintenance before opening reassignment. */
  const [reassignIntro, setReassignIntro] = useState<{
    bus: Bus;
    mode: "maintenance" | "for_hire" | "suspended" | "delete_bus";
  } | null>(null);
  /** Move students off a bus, then set source to maintenance (or for_hire / delete; legacy suspended still supported). */
  const [reassignModal, setReassignModal] = useState<{
    bus: Bus;
    mode: "maintenance" | "for_hire" | "suspended" | "delete_bus";
  } | null>(null);
  const [reassignTargets, setReassignTargets] = useState<Record<string, string>>({});
  const [reassignBusy, setReassignBusy] = useState(false);
  /** Bulk UI: which students are selected for "apply destination". */
  const [reassignSelection, setReassignSelection] = useState<Set<string>>(() => new Set());
  const [reassignFilter, setReassignFilter] = useState("");
  const [reassignBulkDest, setReassignBulkDest] = useState("");
  /** When some students go to a new hired bus (suspend / hire / maintenance / delete flow). */
  const [reassignHireNickname, setReassignHireNickname] = useState("");
  const [reassignHirePlate, setReassignHirePlate] = useState("");
  const [reassignHireExpiresLocal, setReassignHireExpiresLocal] = useState("");
  const [reassignHireCondEmail, setReassignHireCondEmail] = useState("");
  const [reassignHireCondPassword, setReassignHireCondPassword] = useState("");
  const [reassignHireCondName, setReassignHireCondName] = useState("");

  const [showSchoolTripPlanner, setShowSchoolTripPlanner] = useState(false);
  const [tripTitle, setTripTitle] = useState("");
  const [tripDestination, setTripDestination] = useState("");
  const [tripWhenLocal, setTripWhenLocal] = useState("");
  /** When any hired vehicle is on the trip: bus hire expiry + conductor login window end. */
  const [tripEndsLocal, setTripEndsLocal] = useState("");
  /** One or more fleet buses and/or temporary hired buses, each with its own students and crew rules. */
  const [tripLegs, setTripLegs] = useState<TripLegForm[]>([]);
  /** Which leg receives toggles from the shared student picker below. */
  const [tripStudentTargetLegId, setTripStudentTargetLegId] = useState<string>("");
  const [tripSaving, setTripSaving] = useState(false);
  /** Temporary hired bus wizard (callable + student moves). */
  const [showTempHireWizard, setShowTempHireWizard] = useState(false);
  const [tempHireNickname, setTempHireNickname] = useState("");
  const [tempHirePlate, setTempHirePlate] = useState("");
  const [tempHireExpiresLocal, setTempHireExpiresLocal] = useState("");
  const [tempHireChildKeys, setTempHireChildKeys] = useState<Set<string>>(() => new Set());
  const [tempHireCondEmail, setTempHireCondEmail] = useState("");
  const [tempHireCondPassword, setTempHireCondPassword] = useState("");
  const [tempHireCondName, setTempHireCondName] = useState("");
  const [tempHireSaving, setTempHireSaving] = useState(false);
  const [tripPlannerStudentFilter, setTripPlannerStudentFilter] = useState("");
  const [tempHireStudentFilter, setTempHireStudentFilter] = useState("");
  /** Monthly Report: show efficiency % column (same month window as km/refuel). */
  const [monthlyShowEfficiencyPercent, setMonthlyShowEfficiencyPercent] = useState(true);
  /** When true, show full-screen Incidents summary (from Monthly Report): all buses + incident count for selected month. */
  const [showIncidentsSummaryScreen, setShowIncidentsSummaryScreen] = useState(false);
  const [showSubManager, setShowSubManager] = useState(false);
  const [orgDocData, setOrgDocData] = useState<Record<string, unknown> | null>(null);
  /** Campus pin from organizations.homeLocations — view-only in Settings. */
  const [schoolHomeLocation, setSchoolHomeLocation] = useState<{ lat: number; lng: number } | null>(null);
  /** When set, show modal with incident details for this bus (filtered by reportMonth). */
  const [incidentsModalBusId, setIncidentsModalBusId] = useState<string | null>(null);
  const [fleetIssueReports, setFleetIssueReports] = useState<{ id: string; busId: string; issueType: string; details: string; timestamp: Date; resolved: boolean }[]>([]);
  /** Attendance for monthly report (last 90 days when tab active). */
  const [monthlyReportAttendance, setMonthlyReportAttendance] = useState<AttendanceRecord[]>([]);
  /** Per-bus fuel cost for selected report month (from busRefuelEvents costEstimate). */
  const [monthlyReportFuelCostByBusId, setMonthlyReportFuelCostByBusId] = useState<Record<string, number>>({});
  /** Refuel event count per bus for selected report month (for L/100km). */
  const [monthlyReportRefuelCountByBusId, setMonthlyReportRefuelCountByBusId] = useState<Record<string, number>>({});
  /** Total litres added per bus for selected report month (from busRefuelEvents.litersAdded). */
  const [monthlyReportLitresAddedByBusId, setMonthlyReportLitresAddedByBusId] = useState<Record<string, number>>({});
  /** YYYY-MM whose fuel/refuel load has finished (empty totals still count). */
  const [monthlyReportFuelReadyFor, setMonthlyReportFuelReadyFor] = useState<string | null>(null);

  /** Fleet org product type — self-tracking / cargo orgs cannot use school trips. */
  const [fleetOrgProductType, setFleetOrgProductType] = useState<string>("FULL_FEATURE");
  const fleetHasFullProductFeatures = isFullFeatureFleet(fleetOrgProductType);
  // School trips (incl. hired buses + temp conductor logins) are available to
  // every school-bus account — bus-only orgs are the primary users — not just
  // the full-feature product. Cargo/lorry orgs don't get it.
  const canPlanSchoolTrips = fleetHasFullProductFeatures || fleetOrgProductType === "BUS_ONLY";
  const fleetCols = useMemo(() => getFleetCollections(fleetOrgProductType), [fleetOrgProductType]);

  // Live invites for this org → drives the "Invited (pending / account created)" panel.
  useEffect(() => {
    if (!fleetOrgCode) { setSentInvites([]); return; }
    const q = query(collection(db, "fleetInvites"), where("orgCode", "==", fleetOrgCode));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setSentInvites(
          snap.docs.map((d) => {
            const v = d.data() as Record<string, unknown>;
            return {
              id: d.id,
              email: (v.email as string) || "",
              role: (v.role as string) || "parent",
              used: v.used === true,
              usedAt: v.usedAt,
              createdAt: v.createdAt,
              superseded: v.superseded === true,
            };
          })
        );

        // Stale-invite sweep (Spark has no Cloud Functions, so we clean up client-side
        // whenever an admin opens this panel): drop superseded invites, and pending invites
        // not accepted within 24h. Used/accepted invites are kept. Errors are non-fatal.
        const DAY_MS = 24 * 60 * 60 * 1000;
        const now = Date.now();
        snap.docs.forEach((d) => {
          const v = d.data() as Record<string, unknown>;
          if (v.used === true) return;
          const createdMs = (v.createdAt as { toMillis?: () => number } | undefined)?.toMillis?.() ?? now;
          const isSuperseded = v.superseded === true;
          const isStalePending = now - createdMs > DAY_MS;
          if (isSuperseded || isStalePending) {
            deleteDoc(doc(db, "fleetInvites", d.id)).catch(() => { /* rules/transient — ignore */ });
          }
        });
      },
      () => setSentInvites([])
    );
    return () => unsub();
  }, [fleetOrgCode]);

  // Single org listener (was two separate onSnapshot subscriptions on the same
  // organizations doc — this one now also derives the product type).
  useEffect(() => {
    if (!fleetOrgCode) {
      setFleetOrgProductType("FULL_FEATURE");
      return;
    }
    const q = query(collection(db, "organizations"), where("code", "==", fleetOrgCode), limit(1));
    const unsub = onSnapshot(q, (snap) => {
      const d = snap.docs[0]?.data();
      // Keep the RAW product type (e.g. BUS_ONLY) so getFleetCollections can pick
      // the correct collections. Normalizing here collapses BUS_ONLY into
      // SELF_TRACKING and makes the dashboard query fleetVehicles instead of buses.
      setFleetOrgProductType(rawProductType(d?.productType as string | undefined) || "FULL_FEATURE");
      if (d) {
        setParentMapEnabled(d.parentMapEnabled !== false); // default true
        setLegacyFuelFlowEnabled(d.legacyFuelFlowEnabled === true); // absent = OFF
        const termParsed = Date.parse(String(d.currentTermStartDate ?? ""));
        setTermStartMs(Number.isFinite(termParsed) ? termParsed : null);
        setOrgAddons((d.addons as Record<string, { wanted?: boolean; status?: string; verified?: boolean }>) || null);
        setOrgIsPilot(!!(d.isPilot));
        setOrgDisplayName((d.name as string) || '');
        setOrgDocData(d as Record<string, unknown>);

        // Auto-apply pending plan change if effectiveDate has passed
        if ((d as Record<string, unknown>)?.pendingPlanChange) {
          const orgData = d as Record<string, unknown>;
          const pending = orgData.pendingPlanChange as Record<string, unknown>;
          const effectiveDate = new Date(pending.effectiveDate as string);
          if (effectiveDate <= new Date()) {
            const { plan, addons: pendingAddons } = pending as { plan: string; addons: Record<string, boolean> };
            const addonUpdates: Record<string, boolean | string> = {};
            Object.entries(pendingAddons || {}).forEach(([k, v]) => {
              addonUpdates[`addons.${k}.wanted`] = v as boolean;
              addonUpdates[`addons.${k}.status`] = (v as boolean) ? 'default' : 'off';
            });
            updateDoc(doc(db, 'organizations', snap.docs[0].id), {
              subscriptionPlan: plan,
              ...addonUpdates,
              pendingPlanChange: null,
              updatedAt: new Date(),
            }).catch(() => {});
          }
        }
      }
    });
    return () => unsub();
  }, [fleetOrgCode]);

  useEffect(() => {
    let cancelled = false;
    const raw = orgDocData?.homeLocations;
    if (!raw) {
      setSchoolHomeLocation(null);
      return;
    }
    void openLatLng(raw).then((opened) => {
      if (!cancelled) setSchoolHomeLocation(opened);
    });
    return () => {
      cancelled = true;
    };
  }, [orgDocData?.homeLocations]);

  // "Try Analytics" coach-mark: show once per browser session for ~18s to a pilot,
  // then never again that session (sessionStorage), regardless of reloads.
  useEffect(() => {
    if (!premiumLocked) return;
    if (sessionStorage.getItem('pear_tryAnalyticsCoach')) return;
    sessionStorage.setItem('pear_tryAnalyticsCoach', '1');
    setTryAnalyticsCoach(true);
    const t = setTimeout(() => setTryAnalyticsCoach(false), 18000);
    return () => clearTimeout(t);
  }, [premiumLocked]);

  // Live vehicles: filter by fleet org when fleet admin (buses | lorries | vehicles by productType)
  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    const vehiclesCol = fleetCols.vehicles;
    const orgField = fleetCols.orgField;
    const q = fleetOrgCode
      ? query(
          collection(db, vehiclesCol),
          where(orgField, "==", fleetOrgCode),
          where("isActive", "==", true)
        )
      : query(collection(db, vehiclesCol), where("isActive", "==", true));
    const unsub = onSnapshot(
      q,
      (snap) => {
        void (async () => {
          const list: Bus[] = [];
          for (const d of snap.docs) {
            const data = d.data() as Record<string, unknown>;
            // Temporary trip ("hired") buses live in the same collection but are NOT
            // real fleet vehicles — they expire after the trip. Keep them out of the
            // fleet list, the live map and the trip vehicle picker.
            if (data.temporaryHire === true) continue;
            const materialized = await materializeBusDoc(data);
            list.push(mapBusDoc(d.id, materialized, []));
          }
          setBuses(list);
          setLoading(false);
          // Never fall back to list[0]. After a delete the gone bus drops out of
          // this query (isActive: false) and swapping in the next one left the
          // admin on that other bus's Settings — Delete then hit the wrong vehicle.
          setSelectedBus((prev) => {
            if (!prev) return null;
            return list.find((b) => b.id === prev.id) ?? null;
          });
        })();
      },
      (err) => {
        setLoadError(err.message || "Failed to load buses");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [fleetOrgCode, fleetCols.vehicles, fleetCols.orgField]);

  useEffect(() => {
    if (!fleetOrgCode || topLevelTab !== "trips") {
      setFleetTrips([]);
      return;
    }
    // NB: no orderBy here — combining where(schoolCode) + orderBy(departureAt)
    // needs a composite index; without it the query errors and the Trips tab shows
    // empty even when trips exist. fleetTripsEnriched already sorts by departure.
    const q = query(
      collection(db, "schoolTrips"),
      where("schoolCode", "==", fleetOrgCode),
      limit(150)
    );
    const unsub = onSnapshot(
      q,
      (snap) => setFleetTrips(snap.docs.map((d) => ({ id: d.id, data: d.data() as Record<string, unknown> }))),
      (err) => { console.error("[trips] schoolTrips query failed", err); setFleetTrips([]); }
    );
    return () => unsub();
  }, [fleetOrgCode, topLevelTab]);

  useEffect(() => {
    if (topLevelTab !== "trips") setTripsDetailTripId(null);
  }, [topLevelTab]);

  const fleetBusIdsKey = useMemo(() => (buses.length > 0 ? buses.map((b) => b.id).join(",") : ""), [buses]);
  const busActiveKey = useMemo(
    () => buses.map((b) => `${b.id}:${b.status ?? "active"}`).join(","),
    [buses],
  );

  // Fleet-wide attendance (last ~60 days in memory). Downstream useMemos still apply 30-day windows where needed.
  // Query by busId only (no orderBy) to avoid composite index; sort and filter in memory.
  // IMPORTANT: this effect keys off fleetBusIdsKey (the stable comma-joined id list),
  // NOT the `buses` array. The live-vehicles listener replaces `buses` with a fresh
  // array on every 14s location tick, so depending on `buses` here re-ran this whole
  // multi-hundred-read prefetch every 14 seconds. The set of bus IDs rarely changes,
  // so this now runs ~once per session instead. fetch limit is 100 (we cap at 100 kept).
  useEffect(() => {
    const busIds = fleetBusIdsKey ? fleetBusIdsKey.split(",").filter(Boolean) : [];
    if (!busIds.length || !fleetOrgCode) {
      setFleetAttendance([]);
      return;
    }
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 60);
    /* LIVE, per bus. This was a one-shot getDocs loop, so a refuel trip that
       ended after the page loaded did not reach the Fuel report until a reload
       (Gabriel, 2026-08-22: "how do you refresh this page?"). One listener per
       bus, same query shape (schoolCode is required — fleet_admin rules gate on
       it), merged into one list. */
    const byBus = new Map<string, AttendanceRecord[]>();
    const publish = () => {
      const all: AttendanceRecord[] = [];
      byBus.forEach((recs) => { all.push(...recs); });
      setFleetAttendance(all);
    };
    const unsubs = busIds.map((bid) => {
      /* NEWEST hundred, not an arbitrary hundred. limit() with no ordering
         returns documents in __name__ order — for the auto-ids this collection
         uses, the OLDEST. The client-side sort below then dutifully ordered a
         window that had already excluded everything recent, so any bus with
         more than 100 runs behind it reported no recent activity at all
         (review, 09-06). The schoolCode+busId+routeStartedAt DESC index already
         exists, so this needs no deploy. */
      const q = query(
        collection(db, "busAttendance"),
        where("schoolCode", "==", fleetOrgCode),
        where("busId", "==", bid),
        orderBy("routeStartedAt", "desc"),
        limit(100)
      );
      return onSnapshot(
        q,
        (snap) => {
          const sorted = [...snap.docs].sort((a, b) => {
            const tA = (a.data().routeStartedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
            const tB = (b.data().routeStartedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
            return tB - tA;
          });
          const recs: AttendanceRecord[] = [];
          for (const d of sorted) {
            const rec = mapAttendanceDoc(d.id, d.data() as Record<string, unknown>);
            const tripEnd = rec.finished ?? rec.date;
            if (tripEnd && new Date(tripEnd) >= cutoff) recs.push(rec);
            if (recs.length >= 100) break;
          }
          byBus.set(bid, recs);
          publish();
        },
        () => {
          /* skip this bus on error — the others still publish */
          byBus.set(bid, []);
          publish();
        }
      );
    });
    return () => { unsubs.forEach((u) => u()); };
  }, [fleetBusIdsKey, fleetOrgCode]);

  const [attendanceError, setAttendanceError] = useState<string | null>(null);

  // Live attendance for selected bus. Must include schoolCode — fleet_admin rules
  // require it, and a busId-only query fails closed (0 rides / 0 km while refuels
  // still load, because refuel rules resolve org via get(buses/{busId})).
  // No orderBy: sort in memory. Limit 500 fetched / 200 kept for totalKm.
  useEffect(() => {
    if (!selectedBus?.id || !fleetOrgCode) {
      setAttendance([]);
      setAttendanceError(null);
      return;
    }
    setAttendanceError(null);
    const q = query(
      collection(db, "busAttendance"),
      where("schoolCode", "==", fleetOrgCode),
      where("busId", "==", selectedBus.id),
      limit(500)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const sorted = [...snap.docs].sort((a, b) => {
          const tA = (a.data().routeStartedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
          const tB = (b.data().routeStartedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
          return tB - tA;
        });
        const list = sorted.slice(0, 200).map((d) => mapAttendanceDoc(d.id, d.data() as Record<string, unknown>));
        setAttendance(list);
        setAttendanceError(null);
      },
      (err) => {
        setAttendanceError(err?.message ?? "Failed to load attendance");
        setAttendance([]);
      }
    );
    return () => unsub();
  }, [selectedBus?.id, fleetOrgCode]);

  // Fleet alerts (traffic, etc.) for fleet org
  useEffect(() => {
    if (!fleetOrgCode) {
      setFleetAlerts([]);
      setFleetAlertsReady(false);
      return;
    }
    /* Sorted server-side on the fleetAlerts(schoolCode ASC, timestamp DESC)
       composite index — deployed to pear-bc51f on 2026-07-25. Before that the
       query was rejected and a silent error handler turned the rejection into
       "0 alerts" for every org, forever.

       The cap is 500, not the old 20: this list also feeds the High-Risk unread
       COUNT, the mark-reviewed sweep and the duplicate-flag guard, so a tight
       cap silently truncates all three. With orderBy these really are the
       newest 500, which no real org reaches. (A `limit` without `orderBy` would
       be worse than useless here — that returns documents in random-id order,
       an arbitrary slice rather than the newest.)

       Safe against dropped rows: orderBy omits documents missing the field, and
       every fleetAlerts writer — dashboard, addonSync, mobile bus and cargo —
       sets `timestamp`. */
    const q = query(
      collection(db, "fleetAlerts"),
      where("schoolCode", "==", fleetOrgCode),
      orderBy("timestamp", "desc"),
      limit(500)
    );
    const mapAlert = (d: { id: string; data: () => Record<string, unknown> }): FleetAlert => {
      const data = d.data();
      const ts = data.timestamp as Timestamp | undefined;
      /* childName / dropLat / note ride on the parent-flagged drop-off alerts.
         Dropping them here is what made Ask Zana say "A child" for every row
         even when the Firestore doc named the student — useFleetAlerts already
         kept these fields; this mapper is what the Zana intent path reads. */
      return {
        id: d.id,
        busId: (data.busId as string) || "",
        busName: (data.busName as string) || "",
        type: (data.type as string) || "info",
        message: (data.message as string) || "",
        timestamp: ts?.toDate?.() ?? new Date(),
        read: (data.read as boolean) ?? false,
        childName: (data.childName as string) || undefined,
        triage: (data.triage as string) || undefined,
        dropLat: typeof data.dropLat === "number" ? data.dropLat : undefined,
        dropLng: typeof data.dropLng === "number" ? data.dropLng : undefined,
        note: (data.note as string) || undefined,
        placeId: (data.placeId as string) || undefined,
        stationName: (data.stationName as string) || undefined,
        fuelType: (data.fuelType as string) || undefined,
        observedPerL: typeof data.observedPerL === "number" ? data.observedPerL : undefined,
        listedPerL: typeof data.listedPerL === "number" ? data.listedPerL : undefined,
      };
    };
    const unsub = onSnapshot(
      q,
      (snap) => {
        // No slice here — the unread count, "mark reviewed" and the
        // duplicate-flag guard all read this list, and trimming it silently
        // caps every one of them. Render sites do their own slicing.
        setFleetAlerts(snap.docs.map(mapAlert));
        setFleetAlertsReady(true);
      },
      (err) => {
        // Never swallow this again: a blind catch here is what hid the bug.
        console.error("[BusTracking] fleetAlerts snapshot error:", err);
      },
    );
    return () => unsub();
  }, [fleetOrgCode]);

  /* Acknowledged conditions. These are the owner's "I know, stop telling me" —
     the only thing that makes a DERIVED problem stay dealt with, because the
     condition itself is recomputed from the fuel logs on every load. */
  useEffect(() => {
    if (!fleetOrgCode) {
      setRefuelAckAtByBusId({});
      return;
    }
    const unsub = onSnapshot(
      query(
        collection(db, "fleetConditionAcks"),
        where("orgCode", "==", fleetOrgCode),
        where("condition", "==", "missing_refuel"),
      ),
      (snap) => {
        const map: Record<string, number> = {};
        snap.docs.forEach((d) => {
          const a = d.data() as { busId?: string; ackedAt?: Timestamp };
          const ms = a.ackedAt?.toDate?.().getTime();
          if (a.busId && ms) map[a.busId] = ms;
        });
        setRefuelAckAtByBusId(map);
      },
      (err) => {
        // Loud, because failing quietly here means Zana keeps re-reporting
        // things the owner already dealt with and looks like she is lying.
        console.error("[BusTracking] fleetConditionAcks snapshot error:", err);
      },
    );
    return () => unsub();
  }, [fleetOrgCode]);

  /* Fleet org users (for Accounts tab), all three org-field spellings.
     The listeners are keyed on fleetOrgCode ONLY. `buses` used to be a dep,
     and its identity changes on every bus-doc write — a GPS tick every ~5s
     per live bus — so all the users listeners were torn down and re-billed
     their full result set every few seconds of every live run, on Blaze
     with no spend cap. Bus names now flow in through a ref, and the small
     republish effect below refreshes the labels for free. */
  const busesForUserMapRef = useRef<typeof buses>([]);
  busesForUserMapRef.current = buses;
  const republishFleetUsersRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    republishFleetUsersRef.current?.();
  }, [buses]);
  useEffect(() => {
    if (!fleetOrgCode) {
      setFleetUsers([]);
      return;
    }

    let schoolSnap: { docs: Array<{ id: string; data: () => Record<string, unknown> }> } | null = null;
    let orgSnap: { docs: Array<{ id: string; data: () => Record<string, unknown> }> } | null = null;
    let orgCodeSnap: { docs: Array<{ id: string; data: () => Record<string, unknown> }> } | null = null;

    const mapFleetUser = (d: { id: string; data: () => Record<string, unknown> }) => {
      const data = d.data();
      const busId = (data.busId as string | null) ?? null;
      const plateKey = busId ? normalizePlateKey(busId) : "";
      const busList = busesForUserMapRef.current;
      const busName = busId
        ? (busList.find((b) => b.id === busId || normalizePlateKey(b.plate) === plateKey || normalizePlateKey(b.id) === plateKey)?.name
          || busList.find((b) => b.drivers.includes(d.id))?.name
          || (data.busNickname as string)
          || busId)
        : busList.find((b) => b.drivers.includes(d.id))?.name;
      const role = data.role as string | undefined;
      let roleLabel = "Parent";
      if (role === "fleet_admin") roleLabel = "Fleet admin";
      else if (role === "bus_driver" || role === "driver" || busId) roleLabel = "Driver";
      else if (data.busChildren && Object.keys(data.busChildren as Record<string, string>).length > 0) roleLabel = "Parent";

      const childNames: string[] = [];
      const seenChild = new Set<string>();
      const takeName = (n: string) => {
        const t = n.trim();
        if (!t) return;
        const key = t.toLowerCase();
        if (seenChild.has(key)) return;
        seenChild.add(key);
        childNames.push(t);
      };
      const rawChildren = data.children;
      if (Array.isArray(rawChildren)) {
        for (const c of rawChildren) {
          if (typeof c === "string") takeName(c);
          else if (c && typeof c === "object") takeName(String((c as { childName?: string }).childName || ""));
        }
      }
      const linked = data.linkedChildren;
      if (Array.isArray(linked)) {
        for (const c of linked) {
          if (c && typeof c === "object") takeName(String((c as { childName?: string }).childName || ""));
        }
      }

      return {
        id: d.id,
        email: (data.email as string) || "",
        displayName: (data.displayName as string) || (data.email as string)?.split("@")[0] || "—",
        role: roleLabel,
        busId: busId || undefined,
        busName,
        suspended: data.suspended === true,
        childNames,
        username: (data.username as string) || undefined,
        temporaryTripConductor: data.temporaryTripConductor === true,
        temporaryTripParent: data.temporaryTripParent === true,
      };
    };

    const publish = () => {
      const seen = new Set<string>();
      const list: ReturnType<typeof mapFleetUser>[] = [];
      for (const snap of [schoolSnap, orgSnap, orgCodeSnap]) {
        if (!snap) continue;
        for (const d of snap.docs) {
          if (seen.has(d.id)) continue;
          seen.add(d.id);
          list.push(mapFleetUser(d));
        }
      }
      setFleetUsers(list);
    };

    // All THREE org-field spellings, like SchoolTripComposer's driver lookup
    // (F12): the rules accept schoolCode, organizationCode and orgCode, so a
    // user doc carrying only orgCode was invisible to Accounts.
    const qSchool = query(collection(db, "users"), where("schoolCode", "==", fleetOrgCode));
    const qOrg = query(collection(db, "users"), where("organizationCode", "==", fleetOrgCode));
    const qOrgCode = query(collection(db, "users"), where("orgCode", "==", fleetOrgCode));
    const unsubSchool = onSnapshot(qSchool, (snap) => {
      schoolSnap = snap;
      publish();
    });
    const unsubOrg = onSnapshot(qOrg, (snap) => {
      orgSnap = snap;
      publish();
    });
    const unsubOrgCode = onSnapshot(qOrgCode, (snap) => {
      orgCodeSnap = snap;
      publish();
    });
    republishFleetUsersRef.current = publish;
    return () => {
      republishFleetUsersRef.current = null;
      unsubOrgCode();
      unsubSchool();
      unsubOrg();
    };
  }, [fleetOrgCode]);

  const namesForDriverIds = useCallback((ids: string[]) => {
    if (!ids.length) return "None assigned";
    const labels = ids.map((id) => {
      const u = fleetUsers.find((x) => x.id === id);
      const name = (u?.displayName || "").trim();
      if (name && name !== "—") return name;
      const email = (u?.email || "").trim();
      const at = email.indexOf("@");
      if (at > 0) return email.slice(0, at);
      return "";
    }).filter(Boolean);
    return labels.length ? labels.join(", ") : `${ids.length} assigned`;
  }, [fleetUsers]);

  // A vanished selected bus must close the detail page. Leaving detailView
  // true with selectedBus null looks like the list, until the next snapshot
  // (or a later select) puts you back inside Settings of a different bus.
  useEffect(() => {
    if (!detailView) return;
    if (selectedBus && buses.some((b) => b.id === selectedBus.id)) return;
    setDetailView(false);
    setDetailReturnTo(null);
  }, [buses, detailView, selectedBus]);

  // Sync schedule + admin details when the selected bus changes
  useEffect(() => {
    if (!selectedBus) return;
    setMTime(selectedBus.morningTime);
    setETime(selectedBus.eveningTime);
    setFuelCapacityInput(selectedBus.fuelTankCapacityL != null ? String(selectedBus.fuelTankCapacityL) : "");
    setBenchmarkInput(selectedBus.benchmarkKmPerL != null ? String(selectedBus.benchmarkKmPerL) : "");
    setBusYearInput(selectedBus.yearOfManufacture != null ? String(selectedBus.yearOfManufacture) : "");
    setOdometerUnit(selectedBus.odometerUnit === "mi" || selectedBus.odometerUnit === "miles" ? "mi" : "km");
  }, [selectedBus?.id]);

  // Refuel events for selected bus (for fuel estimation).
  // NOTE: No orderBy here — combining where+orderBy requires a composite Firestore index that may
  // not exist and would silently return nothing. We fetch without orderBy and sort client-side.
  useEffect(() => {
    if (!selectedBus?.id) {
      setRefuelEvents([]);
      return;
    }
    /* `limit` lands BEFORE the client-side sort below, so this cap decides
       WHICH events exist, not just how many — an arbitrary slice by document
       id. A bus past the cap loses its most recent fills from the odometer
       review card, which is the one place those get corrected. Raised rather
       than ordered, for the reason spelled out in CorrectRefuelPanel. */
    const q = query(
      collection(db, "busRefuelEvents"),
      where("busId", "==", selectedBus.id),
      limit(300)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((d) => {
            const data = d.data() as Record<string, unknown>;
            const ts = data.timestamp as Timestamp | undefined;
            const litersAdded = typeof data.litersAdded === "number" ? data.litersAdded : undefined;
            const costEstimate = typeof data.costEstimate === "number" ? data.costEstimate : undefined;
            const numOr = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
            return {
              id: d.id,
              timestamp: ts?.toDate?.() ?? new Date(),
              litersAdded,
              costEstimate,
              // The odometer facts: what the ring measures from, and what the
              // review list needs to show a flagged reading honestly.
              kmSinceLastRefuel: numOr(data.kmSinceLastRefuel),
              efficiencyFlag: typeof data.efficiencyFlag === "string" ? data.efficiencyFlag : undefined,
              reviewReason: typeof data.reviewReason === "string" ? data.reviewReason : undefined,
              odometerKm: numOr(data.odometerKm),
              odometerUnknown: data.odometerUnknown === true,
              previousOdometerKm: numOr(data.previousOdometerKm),
              gpsKmSincePrevRefuel: numOr(data.gpsKmSincePrevRefuel),
              odometerCorrected: data.odometerCorrected === true,
              correctedAt: (data.correctedAt as Timestamp | undefined)?.toDate?.() ?? null,
              odometerOriginalKm: numOr((data.odometerOriginal as Record<string, unknown> | undefined)?.odometerKm),
            };
          })
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        setRefuelEvents(list);
      },
      (err) => {
        console.error("[BusTracking] refuelEvents snapshot error:", err);
        // Stop the broken listener before falling back to a one-shot fetch so the
        // two sources cannot race to write setRefuelEvents after recovery.
        unsub();
        getDocs(query(collection(db, "busRefuelEvents"), where("busId", "==", selectedBus.id)))
          .then((snap) => {
            const list = snap.docs
              .map((d) => {
                const data = d.data() as Record<string, unknown>;
                const ts = data.timestamp as Timestamp | undefined;
                const litersAdded = typeof data.litersAdded === "number" ? data.litersAdded : undefined;
                const costEstimate = typeof data.costEstimate === "number" ? data.costEstimate : undefined;
                const numOr = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
                return {
                  id: d.id,
                  timestamp: ts?.toDate?.() ?? new Date(),
                  litersAdded,
                  costEstimate,
                  kmSinceLastRefuel: numOr(data.kmSinceLastRefuel),
                  efficiencyFlag: typeof data.efficiencyFlag === "string" ? data.efficiencyFlag : undefined,
                  reviewReason: typeof data.reviewReason === "string" ? data.reviewReason : undefined,
                  odometerKm: numOr(data.odometerKm),
                  odometerUnknown: data.odometerUnknown === true,
                  previousOdometerKm: numOr(data.previousOdometerKm),
                  gpsKmSincePrevRefuel: numOr(data.gpsKmSincePrevRefuel),
                  odometerCorrected: data.odometerCorrected === true,
                  correctedAt: (data.correctedAt as Timestamp | undefined)?.toDate?.() ?? null,
                  odometerOriginalKm: numOr((data.odometerOriginal as Record<string, unknown> | undefined)?.odometerKm),
                };
              })
              .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
            setRefuelEvents(list);
          })
          .catch(() => setRefuelEvents([]));
      }
    );
    return () => unsub();
  }, [selectedBus?.id]);

  // Load bus issue reports (incidents) from mobile — dashboard was never reading busIssueReports.
  //
  // ⚠️ THE QUERY MAY CONTAIN ONLY EXISTING BUS DOCUMENT IDS. Nothing else. Not
  // plates, not casings, not "just in case" variants.
  //
  // The rule authorises each row via get(buses/{busId}), and Firestore judges a
  // list query on what it COULD return, not what it does: every value in the
  // `in` list must be provably readable. One value that is not a real bus doc
  // id — `kce999z` was ours — gives the rules engine a branch it cannot prove,
  // and the WHOLE query is denied. The client then sees the same
  // `Missing or insufficient permissions` a genuine rules bug produces, which
  // is how this survived three rule deploys (bug 73, 2026-08-04): the rule,
  // the data and the account were all verified correct against production, and
  // the query still failed, because the query itself named ids that do not
  // exist.
  //
  // This loader used to expand each bus into id/plate/lower/UPPER "so older
  // lowercase reports still land". That expansion was the entire bug. Legacy
  // lowercase rows are a DATA repair (upper-case busId on the report doc — the
  // one LEAF row was fixed by hand 2026-08-04), not a query-shape problem:
  // under a get()-based rule a query cannot even ask about them safely.
  // BusAnalyticsPage's IncidentsTab and the busAttendance loader have always
  // queried exact ids only — that is why they work and this didn't.
  useEffect(() => {
    // No org means no provable query — the rule compares orgCode, so firing
    // this with a null org would be denied outright (bug 77c).
    if (!buses.length || !fleetOrgCode) {
      setFleetIssueReports([]);
      return;
    }
    const busIds = [...new Set(buses.map((b) => b.id).filter(Boolean))];
    let cancelled = false;
    (async () => {
      const all: { id: string; busId: string; issueType: string; details: string; timestamp: Date; resolved: boolean }[] = [];
      /* Chunks of 8, not Firestore's `in` maximum of 30. The rule spends one
       * get(buses/{id}) per value in the list, plus two account lookups, and
       * the rules engine allows at most 10 document-access calls per query
       * request. Whether that budget is shared across the values of an `in`
       * or granted per value is not documented; at 8 the query is inside the
       * limit under either reading. At 30, the first fleet with 11+ buses may
       * get this exact all-or-nothing denial back — with clean data and a
       * correct rule, which is precisely the shape that ate 2026-08-04. */
      for (let i = 0; i < busIds.length; i += 8) {
        if (cancelled) break;
        const chunk = busIds.slice(i, i + 8);
        /* `orgCode` IS NOT OPTIONAL HERE — it is what makes the rule provable.
           The rule compares resource.data.orgCode, and Firestore proves a list
           query against its POTENTIAL result set, so without this filter it
           cannot know the matches belong to us and denies the whole read
           (bug 77c). The busId filter alone is not enough. */
        const q = query(
          collection(db, "busIssueReports"),
          where("orgCode", "==", fleetOrgCode),
          where("busId", "in", chunk),
        );
        try {
          const snap = await getDocs(q);
          snap.docs.forEach((d) => {
            const data = d.data() as Record<string, unknown>;
            const ts = data.timestamp as Timestamp | undefined;
            const tripTs = data.tripFinishedAt as Timestamp | undefined;
            const t = ts ?? tripTs;
            all.push({
              id: d.id,
              busId: (data.busId as string) ?? "",
              issueType: (data.issueType as string) ?? "",
              details: (data.details as string) ?? "",
              timestamp: t?.toDate?.() ?? new Date(),
              resolved: data.resolved === true,
            });
          });
        } catch (err) {
          /* NEVER swallow this again. A `permission-denied` here renders as
             "No incident reports outstanding" — a confident, wrong all-clear on
             a page whose entire job is telling an owner what needs them. That
             is exactly how the 2026-08-04 12:07 incident stayed invisible: the
             rule could not resolve the bus document, denied the read, and this
             catch turned the denial into a zero. An empty Incidents tab must
             mean "none", never "we were not allowed to look". */
          console.error("[BusTracking] busIssueReports read failed for chunk", chunk, err);
        }
      }
      if (!cancelled) setFleetIssueReports(all);
    })();
    return () => { cancelled = true; };
    // fleetOrgCode is now part of the QUERY, so a change to it must re-run this
    // — otherwise a switched org would keep showing the previous org's reads.
  }, [fleetBusIdsKey, fleetOrgCode]);

  // Buses with incidents merged from busIssueReports (for high-risk and incident UI)
  const busesWithIncidents = useMemo(() => {
    const byBus = new Map<string, { date: Date; severity: CrashSevKey; description: string; reported: boolean; reportId?: string }[]>();
    for (const r of fleetIssueReports) {
      // A closed-out incident is history, not a live risk. Before this, one
      // report kept its bus in the High Risk count permanently.
      if (r.resolved) continue;
      const rk = normalizePlateKey(r.busId);
      const bus = buses.find(
        (b) =>
          b.id === r.busId ||
          normalizePlateKey(b.id) === rk ||
          normalizePlateKey(b.plate) === rk,
      );
      if (!bus) continue;
      const list = byBus.get(bus.id) ?? [];
      list.push({
        date: r.timestamp,
        severity: (r.issueType === "accident" || r.issueType === "major" ? "major" : "minor") as CrashSevKey,
        description: r.details?.trim() ? `${r.issueType}: ${r.details}` : r.issueType,
        reported: true,
        reportId: r.id,
      });
      byBus.set(bus.id, list);
    }
    return buses.map((b) => ({ ...b, crashes: byBus.get(b.id) ?? [] }));
  }, [buses, fleetIssueReports]);

  /* ---- props for the redesigned High Risk screen -------------------------
     Everything below is DERIVED from state this component already holds. No
     new reads, no new writes — the page is a different arrangement of the same
     facts. `lastTripByBusId` is the only slightly costly one, so it is capped
     to the newest finished trip per bus rather than sorting the whole set. */

  const highRiskBuses = useMemo(
    () => buses.map((b) => ({
      id: b.id,
      name: b.name,
      plate: b.plate,
      status: b.status,
      drivers: b.drivers ?? [],
      childCount: b.children?.length ?? 0,
      liveLocationUpdatedAt: b.liveLocationUpdatedAt ?? null,
      /* The High Risk screen derives its "No tank size on this bus" row from
         THIS field — and this projection never carried it, so the row fired
         for every bus forever, whatever the admin saved, and "Mark reviewed"
         could not touch it because it is not an alert (Gabriel, 2026-08-22:
         "I've just saved the tank size and it's still there"). */
      fuelTankCapacityL: b.fuelTankCapacityL ?? null,
      /* Same contract as the line above, and the same failure if it is left
         out: the "No odometer reading yet" row is derived from THIS field, so
         a projection that drops it shows the row on every bus for ever, no
         matter what the driver records. */
      odometerBaselineKm: b.odometerBaselineKm ?? null,
      /* THREE states on purpose. Until the refuel loader has run we do not
         know whether this bus has readings on file, and "don't know" must not
         render as "none" — that is a whole fleet accused of a gap it may not
         have, for as long as the query takes. `null` tells the screen to say
         nothing yet. */
      hasRefuelOdometer: fleetLastRefuelReady && !fleetRefuelHistoryFailed
        ? (busHasRefuelOdometerById[b.id] ?? false)
        : null,
    })),
    [buses, fleetLastRefuelReady, fleetRefuelHistoryFailed, busHasRefuelOdometerById],
  );

  const highRiskIncidents = useMemo(
    () => busesWithIncidents.flatMap((b) =>
      b.crashes.map((c) => ({
        busId: b.id,
        date: c.date,
        severity: c.severity === "major" ? "Major" : "Minor",
        description: c.description,
      })),
    ),
    [busesWithIncidents],
  );

  const unreadAlertCount = useMemo(() => fleetAlerts.filter((a) => !a.read).length, [fleetAlerts]);

  /** Find a bus by document id OR any plate casing/format.
   *
   * Alert documents from older app builds carry `kce999z`-style ids (bug 73's
   * family). An exact-id `buses.find` followed by `if (!b) return;` turns that
   * row into a click that does NOTHING — and the notifications popover marks
   * the alert read before the find, so the tap greys the row and then goes
   * nowhere, which reads as "notifications are broken" (Gabriel, bug 81).
   * Every open-bus handler resolves through this instead. */
  const findBusByAnyId = (busId: string): Bus | undefined => {
    const rk = normalizePlateKey(busId || "");
    return buses.find((b) => b.id === busId)
      ?? buses.find((b) => normalizePlateKey(b.id) === rk || normalizePlateKey(b.plate) === rk);
  };

  const plateByBusId = useMemo(() => {
    const out: Record<string, string> = {};
    for (const b of buses) out[b.id] = b.plate;
    return out;
  }, [buses]);

  /** Bus lookup for the Fuel Report, keyed by id. */
  const fuelBusById = useMemo(() => {
    const out: Record<string, {
      id: string; name: string; plate: string; status: string; drivers: string[];
      fuelTankCapacityL?: number | null; benchmarkKmPerL?: number | null;
    }> = {};
    for (const b of buses) {
      out[b.id] = {
        id: b.id, name: b.name, plate: b.plate, status: b.status, drivers: b.drivers ?? [],
        fuelTankCapacityL: b.fuelTankCapacityL ?? null,
        benchmarkKmPerL: b.benchmarkKmPerL ?? null,
      };
    }
    return out;
  }, [buses]);

  /* The last completed school run per bus (refuel trips excluded). */
  const lastTripRecordByBusId = useMemo(() => {
    const out: Record<string, AttendanceRecord | undefined> = {};
    for (const r of fleetAttendance) {
      if (!r.finished || isRefuelAttendance(r)) continue;
      const prev = out[r.busId];
      const prevFinished = prev?.finished?.getTime() ?? 0;
      if (prevFinished >= r.finished.getTime()) continue;
      out[r.busId] = r;
    }
    return out;
  }, [fleetAttendance]);

  /* Trail points for those runs, DECRYPTED. Release builds seal every trail
     point (LocationCrypto, 2026-08-10); `parseGpsPoints` only reads plain
     lat/lng, so High Risk's "This bus's last completed route" said "No route
     recorded" for every real-phone trip since (Gabriel, 2026-08-23
     screenshot) while Recent runs, which opens the points, drew them fine. */
  const [hrTrails, setHrTrails] = useState<Record<string, { lat: number; lng: number; estimated?: boolean }[]>>({});
  const hrTrailsDone = useRef<Set<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next: Record<string, { lat: number; lng: number; estimated?: boolean }[]> = {};
      for (const r of Object.values(lastTripRecordByBusId)) {
        if (!r || hrTrailsDone.current.has(r.id)) continue;
        if ((r.snappedRoutePoints ?? []).length >= 2) continue; // snapped line wins
        if ((r.routePoints ?? []).length >= 2) continue; // plain points already parsed
        const raw = r.rawRoutePoints ?? [];
        if (raw.length < 2) continue;
        hrTrailsDone.current.add(r.id);
        const pts: { lat: number; lng: number; estimated?: boolean }[] = [];
        for (const rp of raw) {
          if (!rp || typeof rp !== "object") continue;
          const o = rp as Record<string, unknown>;
          // eslint-disable-next-line no-await-in-loop
          const ll = await openLatLng(o);
          if (!ll) continue;
          pts.push(o.estimated === true ? { lat: ll.lat, lng: ll.lng, estimated: true } : { lat: ll.lat, lng: ll.lng });
        }
        next[r.id] = pts;
      }
      if (!cancelled && Object.keys(next).length) setHrTrails((prev) => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [lastTripRecordByBusId]);

  const lastTripByBusId = useMemo(() => {
    const out: Record<string, HighRiskTrip | undefined> = {};
    for (const [busId, r] of Object.entries(lastTripRecordByBusId)) {
      if (!r || !r.finished) continue;
      const kids = r.children ?? [];
      const snapped = (r.snappedRoutePoints ?? []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
      const plain = (r.routePoints ?? []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
      out[busId] = {
        started: r.started,
        finished: r.finished,
        km: r.km,
        childrenTotal: kids.length,
        // "Accounted for" mirrors the driver app: anything that is not still
        // marked as aboard has been resolved one way or the other.
        childrenAccounted: kids.filter((c) => c.status !== "picked_up" && c.status !== "pending").length,
        routePoints: snapped.length >= 2 ? snapped : plain.length >= 2 ? plain : (hrTrails[r.id] ?? plain),
      };
    }
    return out;
  }, [lastTripRecordByBusId, hrTrails]);

  /** School morning/evening only — refuel trips live under Fuel Report → Refuel trips. */
  const busRecords = useMemo(
    () => attendance
      .filter((r) => !isRefuelAttendance(r))
      /* NEWEST FIRST, decided here and not only by the query (Gabriel,
         2026-09-28: "the latest should be the one up top"). The query orders
         on routeStartedAt, but a row without one — an older document, or a
         run whose start never synced — sorts to the END of a descending
         query whatever its date. The day field is the fallback. */
      .sort((a, b) => ((b.started ?? b.date)?.getTime() ?? 0) - ((a.started ?? a.date)?.getTime() ?? 0)),
    [attendance],
  );
  const selectedBusWithKm = useMemo(() => {
    if (!selectedBus) return null;
    const school = attendance.filter((r) => !isRefuelAttendance(r));
    const kmHistory = school.map((r) => r.km).filter((k): k is number => k != null && k > 0).slice(0, 20);
    return { ...selectedBus, kmHistory };
  }, [selectedBus, attendance]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return buses;
    return buses.filter((b) => {
      const id = (b.id || "").toLowerCase();
      const name = (b.name || "").toLowerCase();
      const plate = (b.plate || "").toLowerCase();
      const school = (b.school || "").toLowerCase();
      const nick = name.replace(/^bus\s+/i, "").trim();
      const hay = [id, name, plate, school, nick].filter(Boolean).join(" ");
      return hay.includes(q);
    });
  }, [buses, search]);

  const tempHireRoster = useMemo(() => {
    const rows: { key: string; studentName: string; busName: string; classLabel?: string }[] = [];
    for (const b of buses) {
      for (const c of b.children) {
        rows.push({
          key: `${b.id}\t${c.id}`,
          studentName: c.name,
          busName: b.name,
          classLabel: c.classLabel,
        });
      }
    }
    return rows.sort((a, b) => a.studentName.localeCompare(b.studentName));
  }, [buses]);

  const tempHireFilteredRoster = useMemo(
    () => tempHireRoster.filter((r) => studentMatchesPlannerFilter(r.studentName, r.classLabel, tempHireStudentFilter)),
    [tempHireRoster, tempHireStudentFilter]
  );

  const tripPlannerFleetRoster = useMemo(() => {
    const rows: { key: string; studentName: string; busName: string; classLabel?: string }[] = [];
    for (const b of buses) {
      for (const c of b.children) {
        rows.push({
          key: `${b.id}::${c.id}`,
          studentName: c.name,
          busName: b.name,
          classLabel: c.classLabel,
        });
      }
    }
    return rows.sort((a, b) => a.studentName.localeCompare(b.studentName));
  }, [buses]);

  useEffect(() => {
    if (!showSchoolTripPlanner || tripLegs.length === 0) return;
    if (!tripLegs.some((l) => l.lid === tripStudentTargetLegId)) {
      setTripStudentTargetLegId(tripLegs[0].lid);
    }
  }, [showSchoolTripPlanner, tripLegs, tripStudentTargetLegId]);

  const fleetTripsEnriched = useMemo(() => {
    const now = Date.now();
    const tripHasStarted = (data: Record<string, unknown>) => {
      // Only a real crew start counts — do not trust status:"active" alone
      // (can be leftover / set without departedAt).
      if (data.departedAt) return true;
      const ms = data.milestones;
      if (ms && typeof ms === "object" && (ms as Record<string, unknown>).departedAt) return true;
      return false;
    };
    return fleetTrips
      .map(({ id, data }) => {
        const depD = firestoreTimestampToDate(data.departureAt);
        const dep = depD?.getTime() ?? 0;
        const tripEndD = firestoreTimestampToDate(data.tripEndsAt);
        const tripEndMs = tripEndD?.getTime() ?? null;
        const endEffective = tripEndMs ?? (dep > 0 ? dep + 36 * 3600 * 1000 : now + 1);
        const status = String(data.status ?? "planned");
        const started = tripHasStarted(data);
        let bucket: "upcoming" | "active" | "past";
        if (status === "cancelled" || status === "completed" || now >= endEffective) bucket = "past";
        else if (started) bucket = "active";
        else bucket = "upcoming"; // not started yet — even if departure time has passed
        const phaseLabel =
          bucket === "active" ? "Active"
            : bucket === "past" ? "Past"
              : (dep > now ? "Upcoming" : "Pending");
        const title = String(data.title ?? "");
        const destination = String(data.destination ?? "");
        return { id, data, dep, title, destination, status, bucket, phaseLabel, tripEndMs, depD, started };
      })
      .sort((a, b) => b.dep - a.dep);
  }, [fleetTrips]);

  // Hired buses are excluded from the fleet `buses` list — resolve nicknames + conductors.
  useEffect(() => {
    if (!tripsDetailTripId) {
      setTripHireLabels({});
      setTripDetailCrew([]);
      return;
    }
    const row = fleetTrips.find((t) => t.id === tripsDetailTripId);
    if (!row) return;
    const busIds = Array.isArray(row.data.busIds) ? (row.data.busIds as string[]) : [];
    const conductorUids = Array.isArray(row.data.tripConductorUids)
      ? (row.data.tripConductorUids as string[])
      : [];
    let cancelled = false;
    void (async () => {
      const labels: Record<string, string> = {};
      const crewUids = new Set<string>(conductorUids.filter(Boolean));
      await Promise.all(busIds.map(async (id) => {
        if (buses.some((b) => b.id === id)) return;
        try {
          const snap = await getDoc(doc(db, "buses", id));
          if (!snap.exists()) return;
          const d = snap.data();
          labels[id] = String(d.nickname || d.numberPlate || id);
          const tuid = (d.temporaryConductorUid as string | undefined)?.trim();
          if (tuid) crewUids.add(tuid);
        } catch { /* ignore */ }
      }));
      const crew: typeof tripDetailCrew = [];
      await Promise.all([...crewUids].map(async (uid) => {
        try {
          const snap = await getDoc(doc(db, "users", uid));
          if (!snap.exists()) return;
          const d = snap.data();
          crew.push({
            id: uid,
            displayName: String(d.displayName || d.name || d.username || "Conductor"),
            email: String(d.email || ""),
            username: (d.username as string) || undefined,
            busName: (d.busNickname as string) || undefined,
          });
        } catch { /* ignore */ }
      }));
      if (!cancelled) {
        setTripHireLabels(labels);
        setTripDetailCrew(crew);
      }
    })();
    return () => { cancelled = true; };
  }, [tripsDetailTripId, fleetTrips, buses]);

  // Fleet fuel summary (for Fuel Report modal): per-bus km, needsRefuel, refuel events (last 30 days)
  const fleetFuelSummary = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const byBus = new Map<string, { busName: string; km: number; needsRefuel: number; refuelEvents: number }>();
    for (const r of fleetAttendance) {
      const d = r.finished ? new Date(r.finished) : r.date ? new Date(r.date) : null;
      if (!d || d < cutoff) continue;
      const cur = byBus.get(r.busId) ?? { busName: buses.find((b) => b.id === r.busId)?.name ?? r.busId, km: 0, needsRefuel: 0, refuelEvents: 0 };
      cur.km += r.km ?? 0;
      // Refuel-trip docs are not school surveys — don't count them as "needs refill" flags.
      if (r.needsRefuelSoon && !isRefuelAttendance(r)) cur.needsRefuel++;
      cur.refuelEvents = fleetRefuelCountByBusId[r.busId] ?? 0;
      byBus.set(r.busId, cur);
    }
    // Ensure refuel counts are set from fleetRefuelCountByBusId for buses that have no attendance in window
    for (const b of buses) {
      if (!byBus.has(b.id)) byBus.set(b.id, { busName: b.name, km: 0, needsRefuel: 0, refuelEvents: fleetRefuelCountByBusId[b.id] ?? 0 });
      else (byBus.get(b.id)!).refuelEvents = fleetRefuelCountByBusId[b.id] ?? (byBus.get(b.id)!.refuelEvents);
    }
    /* The MISS window is 7 DAYS (Gabriel, 2026-08-17): a school bus runs every
       weekday, and "no refuel in 30 days" let a dry spell hide for three school
       weeks. `refuelEvents`/litres stay 30-day STATS; only "has this bus
       refuelled recently?" tightens — decided off the newest-ever refuel
       timestamp, which the loader already tracks. This is the same number the
       backend's isMissingRefuel (refuels7d) now computes, so Zana's scripts,
       the insight, and this card finally all name the same buses. */
    const missCutoffMs = Date.now() - 7 * 24 * 3600 * 1000;
    const rows = Array.from(byBus.entries()).map(([busId, v]) => {
      const refuelEvents = v.refuelEvents;
      const hasRefueled = (fleetLastRefuelAtByBusId[busId] ?? 0) >= missCutoffMs;
      /* An acknowledgement holds only until the world moves. Once the bus logs
         a refuel AFTER the ack, the ack is spent — so a fresh dry spell months
         later surfaces again instead of hiding behind an old decision. */
      const ackAt = refuelAckAtByBusId[busId] ?? 0;
      const acked = ackAt > 0 && (fleetLastRefuelAtByBusId[busId] ?? 0) <= ackAt;
      return {
        busId,
        busName: v.busName,
        km: v.km,
        needsRefuel: v.needsRefuel,
        refuelEvents,
        hasRefueled,
        acked,
        ackedAt: acked ? ackAt : null,
        litresAdded: fleetLitresAddedByBusId[busId] ?? 0,
      };
    });
    const totalKm = rows.reduce((s, r) => s + r.km, 0);
    const totalNeedsRefuel = rows.reduce((s, r) => s + r.needsRefuel, 0);
    const totalRefuelEvents = rows.reduce((s, r) => s + r.refuelEvents, 0);
    /** Buses that had at least one trip flagged “needs refuel” in 30d but logged zero refuel events in 30d */
    const busesFlaggedNoRefuelLog = rows.filter((r) => r.needsRefuel > 0 && !r.hasRefueled && !r.acked).length;
    /** Buses with zero refuel events in 30d (dashboard Fuel Report card headline).
     *  Acknowledged buses are out: the owner has already made the call on them,
     *  and re-counting them is what made Zana look like she never finishes. */
    const busesNotRefueledCount = rows.filter((r) => !r.hasRefueled && !r.acked).length;
    /** Still missing a refuel log, but consciously parked. Shown, never counted. */
    const busesAckedCount = rows.filter((r) => !r.hasRefueled && r.acked).length;
    return {
      rows,
      totalKm,
      totalNeedsRefuel,
      totalRefuelEvents,
      busesFlaggedNoRefuelLog,
      busesNotRefueledCount,
      busesAckedCount,
    };
  }, [
    fleetAttendance, buses, fleetRefuelCountByBusId, fleetLitresAddedByBusId,
    refuelAckAtByBusId, fleetLastRefuelAtByBusId,
  ]);

  /** Driver “Start a refuel trip” runs — Fuel Report 4th pill (not school recent runs). */
  const fleetRefuelTrips = useMemo(() => {
    return fleetAttendance
      .filter(isRefuelAttendance)
      .map((r) => {
        const bus = buses.find((b) => b.id === r.busId);
        return {
          id: r.id,
          busId: r.busId,
          busName: bus?.name ?? r.busId,
          plate: bus?.plate,
          started: r.started,
          finished: r.finished,
          km: r.km,
          visitedStation: r.visitedStation === true,
          loggedRefuel: r.loggedRefuel === true,
          stationVisits: r.stationVisits ?? null,
          refuelsLogged: r.refuelsLogged ?? null,
          driverName: bus?.drivers?.length ? namesForDriverIds(bus.drivers) : null,
        };
      })
      .sort((a, b) => {
        const ta = (a.finished ?? a.started)?.getTime?.() ?? 0;
        const tb = (b.finished ?? b.started)?.getTime?.() ?? 0;
        return tb - ta;
      });
  }, [fleetAttendance, buses, namesForDriverIds]);

  const kmByBusId = useMemo(() => {
    const out: Record<string, number> = {};
    for (const r of fleetFuelSummary.rows) out[r.busId] = r.km;
    return out;
  }, [fleetFuelSummary.rows]);

  /* A clock, so the live band can go stale without new data arriving. Freshness
     is a claim about elapsed time; if nothing re-renders, a killed app keeps
     reading "live" forever — which is bug 36 in a different costume. 15s is
     under the 60s threshold, so the flip is never more than a quarter late. */
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!detailView) return;
    const t = setInterval(() => setNowMs(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [detailView]);

  /** Fleet-wide cost per km, so a bus's own figure has something to mean. */
  const fleetCostPerKm = useMemo(() => {
    let cost = 0; let km = 0;
    for (const r of fleetFuelSummary.rows) {
      const c = costEstimateByBusId[r.busId];
      if (typeof c === "number" && c > 0 && r.km > 0) { cost += c; km += r.km; }
    }
    return km > 0 ? cost / km : null;
  }, [fleetFuelSummary.rows, costEstimateByBusId]);

  const detailIncidentCount = useMemo(
    () => {
      const b = selectedBusWithKm ?? selectedBus;
      return b ? (busesWithIncidents.find((x) => x.id === b.id)?.crashes.length ?? 0) : 0;
    },
    [busesWithIncidents, selectedBusWithKm, selectedBus],
  );

  /** Everything the live band and the three concern cells read. All derived
   *  from state this component already holds — no new reads, no new writes. */
  const busDetailHeaderProps = useMemo(() => {
    const b = selectedBusWithKm ?? selectedBus;
    const alerts = b ? fleetAlerts.filter((a) => a.busId === b.id) : [];
    const open = alerts.filter((a) => !a.read);
    const oldest = open.reduce<Date | null>(
      (acc, a) => (!acc || a.timestamp < acc ? a.timestamp : acc), null,
    );
    const kids = b?.children ?? [];
    const aboard = kids.filter((c) => c.status === "picked_up").length;
    const km = b ? (kmByBusId[b.id] ?? 0) : 0;
    const cost = b ? costEstimateByBusId[b.id] : undefined;
    return {
      liveLocationUpdatedAt: b?.liveLocationUpdatedAt ?? null,
      /* Only these two statuses mean "out there right now".
       *
       * This was `status && status !== "completed"`, which treats every OTHER
       * value as on-route — so a CANCELLED route showed "On the evening run"
       * with a live timer, on a bus parked since yesterday (Gabriel,
       * 2026-08-03: buses/KCE999Z carries status 'cancelled'). Blocklisting one
       * value means every status invented later defaults to "driving", which is
       * the wrong direction to be wrong in. Allowlist instead. */
      onRoute: b?.route?.status === "active" || b?.route?.status === "collecting",
      routeLeg: b?.route?.type ?? null,
      routeStartedAt: b?.route?.started ?? null,
      childrenAboard: aboard,
      childrenTotal: kids.length,
      liveDistanceKm: null,
      openAlerts: open.length,
      critAlerts: open.filter((a) => /theft|crash|incident|sos|emergency/i.test(a.type)).length,
      warnAlerts: open.filter((a) => /fuel|refuel|review|delay/i.test(a.type)).length,
      incidents: detailIncidentCount,
      oldestAlertAt: oldest,
      costPerKm: typeof cost === "number" && cost > 0 && km > 0 ? cost / km : null,
      fleetCostPerKm,
      km30d: km,
      now: nowMs,
      lastRun: b ? (lastTripByBusId[b.id] ?? null) : null,
      // Cargo/lorries carry no roster, so the aboard cell would read "0 of 0".
      hasChildren: kids.length > 0 || !!b?.route?.status,
    };
  }, [selectedBusWithKm, selectedBus, fleetAlerts, kmByBusId, costEstimateByBusId, detailIncidentCount, fleetCostPerKm, nowMs, lastTripByBusId]);


  /** Bus rows for the Suspend / maintenance screen. `km30d` and the last
   *  finished trip come from the same two maps the other screens use, so the
   *  three pages can never quote different distances for the same bus. */
  const maintenanceScreenBuses = useMemo(
    () => buses.map((b) => ({
      id: b.id,
      name: b.name,
      plate: b.plate,
      status: b.status,
      drivers: b.drivers ?? [],
      childCount: b.children?.length ?? 0,
      km30d: kmByBusId[b.id] ?? 0,
      lastTripFinished: lastTripByBusId[b.id]?.finished ?? null,
    })),
    [buses, kmByBusId, lastTripByBusId],
  );

  // Load fleet refuel event counts + litres added (last 30d) so Fuel Report and efficiency cards are accurate without opening a modal
  useEffect(() => {
    if (buses.length === 0) {
      setFleetLastRefuelReady(true);
      return;
    }
    setFleetLastRefuelReady(false);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const busIds = buses.map((b) => b.id);
    const load = async () => {
      const counts: Record<string, number> = {};
      const litres: Record<string, number> = {};
      const lastAt: Record<string, number> = {};
      /* Has ANY fill on this bus ever carried an odometer? Free here — this
         loop already reads every refuel document — and it is what stops the
         "No odometer reading yet" row firing on the buses that have had
         readings all along. `odometerBaselineKm` only started being written
         when the trip-start prompt shipped, so history is the other half of
         the answer and a field check alone would libel every older bus. */
      const hasOdo: Record<string, boolean> = {};
      for (let i = 0; i < busIds.length; i += 30) {
        const chunk = busIds.slice(i, i + 30);
        const q = query(collection(db, "busRefuelEvents"), where("busId", "in", chunk));
        const snap = await getDocs(q);
        snap.docs.forEach((d) => {
          const data = d.data() as { busId?: string; timestamp?: Timestamp; litersAdded?: number; odometerKm?: number };
          const busId = data.busId as string | undefined;
          const ts = data.timestamp?.toDate?.();
          if (!busId || !ts) return;
          if (typeof data.odometerKm === "number" && data.odometerKm > 0) hasOdo[busId] = true;
          // Newest-ever refuel, deliberately outside the 30-day cutoff: an
          // acknowledgement is only good until the bus refuels again, and that
          // can happen after the window this loop is counting.
          const ms = ts.getTime();
          if (ms > (lastAt[busId] ?? 0)) lastAt[busId] = ms;
          if (ts >= cutoff) {
            counts[busId] = (counts[busId] ?? 0) + 1;
            const l = typeof data.litersAdded === "number" && data.litersAdded > 0 ? data.litersAdded : 0;
            if (l > 0) litres[busId] = (litres[busId] ?? 0) + l;
          }
        });
      }
      setFleetRefuelCountByBusId(counts);
      setFleetLitresAddedByBusId(litres);
      setFleetLastRefuelAtByBusId(lastAt);
      setBusHasRefuelOdometerById(hasOdo);
      setFleetLastRefuelReady(true);
    };
    setFleetRefuelHistoryFailed(false);
    // Degrade gracefully if refuel reads are denied (e.g. rules not yet deployed)
    load().catch(() => {
      /* Ready, but NOT informed. Every other consumer here degrades to zero
         counts, which reads as "quiet fleet" and is survivable. The odometer
         row would read a failed query as "no bus has ever recorded a reading"
         and accuse the entire fleet — the same mistake as the Luqman FIRST
         READING, which is what a silent catch always eventually produces. */
      setFleetRefuelHistoryFailed(true);
      setFleetLastRefuelReady(true);
    });
  }, [buses.length, fleetBusIdsKey]);

  // 7-day silent-bus alert: running trips, no refill, subscription still on.
  useEffect(() => {
    if (!fleetOrgCode || !fleetAlertsReady || !fleetLastRefuelReady) return;
    if (!subscriptionAllowsReminders(orgDocData)) return;
    const WEEK = 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const weekBucket = Math.floor(now / WEEK);
    let cancelled = false;
    const active = buses.filter((b) => !b.status || b.status === "active");
    (async () => {
      for (const b of active) {
        if (cancelled) return;
        const lastTrip = lastTripByBusId[b.id]?.finished?.getTime() ?? 0;
        if (!lastTrip || now - lastTrip > WEEK) continue;
        const lastFill = fleetLastRefuelAtByBusId[b.id] ?? 0;
        if (lastFill && now - lastFill < WEEK) continue;
        const ackAt = refuelAckAtByBusId[b.id] ?? 0;
        if (ackAt > 0 && lastFill <= ackAt) continue;
        const recent = fleetAlerts.some(
          (a) =>
            a.busId === b.id &&
            /missing_refuel|no_refuel/i.test(a.type || "") &&
            (a.timestamp?.getTime?.() ?? 0) > now - WEEK,
        );
        if (recent) continue;
        const id = `${fleetOrgCode}__${b.id}__no_refuel__${weekBucket}`;
        const ref = doc(db, "fleetAlerts", id);
        try {
          const existing = await getDoc(ref);
          if (existing.exists()) continue;
          const plate = b.plate || b.name || b.id;
          await setDoc(ref, {
            schoolCode: fleetOrgCode,
            busId: b.id,
            busName: plate,
            type: "missing_refuel",
            message: `${plate} has been running for 7 days with no refill logged. Check the tank — no form needed.`,
            timestamp: serverTimestamp(),
            read: false,
            source: "dashboard",
            severity: "warning",
          });
        } catch (e) {
          console.warn("[BusTracking] missing-refuel alert failed:", e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [
    fleetOrgCode,
    fleetAlertsReady,
    fleetLastRefuelReady,
    fleetAlerts,
    fleetLastRefuelAtByBusId,
    lastTripByBusId,
    orgDocData,
    refuelAckAtByBusId,
    fleetBusIdsKey,
    busActiveKey,
  ]);

  // Load per-bus fuel cost (from driver cost estimates) on mount/ when buses load so Cost estimate card shows total without opening the modal
  useEffect(() => {
    if (buses.length === 0) return;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const busIds = buses.map((b) => b.id);
    const load = async () => {
      const sums: Record<string, number> = {};
      busIds.forEach((id) => { sums[id] = 0; });
      for (let i = 0; i < busIds.length; i += 30) {
        const chunk = busIds.slice(i, i + 30);
        const q = query(collection(db, "busRefuelEvents"), where("busId", "in", chunk));
        const snap = await getDocs(q);
        snap.docs.forEach((d) => {
          const data = d.data() as { busId?: string; timestamp?: Timestamp; costEstimate?: number };
          const busId = data.busId as string | undefined;
          const ts = data.timestamp?.toDate?.();
          const rawCost = typeof data.costEstimate === "number" ? data.costEstimate : 0;
          // Single-event cap at 100 million KES — protects totals from clearly erroneous entries.
          const cost = rawCost > 0 && rawCost <= 100_000_000 ? rawCost : 0;
          if (busId && cost > 0 && ts && ts >= cutoff) sums[busId] = (sums[busId] ?? 0) + cost;
        });
      }
      setCostEstimateByBusId(sums);
    };
    // Degrade gracefully if refuel reads are denied (e.g. rules not yet deployed)
    load().catch(() => {});
  }, [buses.length, fleetBusIdsKey]);

  // Load attendance for Monthly Report: covers the 3 months available in the dropdown (up to 95 days).
  useEffect(() => {
    if (topLevelTab !== "monthlyReport" || !buses.length || !fleetOrgCode) {
      setMonthlyReportAttendance([]);
      return;
    }
    const busIds = buses.map((b) => b.id);
    // Extend cutoff to cover the oldest month in the 3-month dropdown (start of 2 months ago).
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 2);
    cutoff.setDate(1);
    cutoff.setHours(0, 0, 0, 0);
    let cancelled = false;
    (async () => {
      const all: AttendanceRecord[] = [];
      for (const bid of busIds) {
        if (cancelled) break;
        const q = query(
          collection(db, "busAttendance"),
          where("schoolCode", "==", fleetOrgCode),
          where("busId", "==", bid),
          limit(300)
        );
        try {
          const snap = await getDocs(q);
          const sorted = [...snap.docs].sort((a, b) => {
            const tA = (a.data().routeStartedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
            const tB = (b.data().routeStartedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
            return tB - tA;
          });
          for (const d of sorted) {
            const rec = mapAttendanceDoc(d.id, d.data() as Record<string, unknown>);
            if (rec.date && new Date(rec.date) >= cutoff) all.push(rec);
            if (all.filter((r) => r.busId === bid).length >= 120) break;
          }
        } catch {
          /* skip */
        }
      }
      if (!cancelled) setMonthlyReportAttendance(all);
    })();
    return () => { cancelled = true; };
  }, [topLevelTab, fleetBusIdsKey, fleetOrgCode, buses]);

  // Load fuel cost, refuel count, and litres added for selected report month when Monthly Report tab is active.
  useEffect(() => {
    if (topLevelTab !== "monthlyReport" || !buses.length || !reportMonth) return;
    const monthKey = `${reportMonth.year}-${String(reportMonth.month).padStart(2, "0")}`;
    setMonthlyReportFuelReadyFor(null);
    const start = new Date(reportMonth.year, reportMonth.month - 1, 1);
    const end = new Date(reportMonth.year, reportMonth.month, 0, 23, 59, 59);
    const busIds = buses.map((b) => b.id);
    let cancelled = false;
    const load = async () => {
      const costByBus: Record<string, number> = {};
      const countByBus: Record<string, number> = {};
      const litresByBus: Record<string, number> = {};
      busIds.forEach((id) => { costByBus[id] = 0; countByBus[id] = 0; litresByBus[id] = 0; });
      for (let i = 0; i < busIds.length; i += 30) {
        const chunk = busIds.slice(i, i + 30);
        const q = query(collection(db, "busRefuelEvents"), where("busId", "in", chunk));
        const snap = await getDocs(q);
        snap.docs.forEach((d) => {
          const data = d.data() as { busId?: string; timestamp?: Timestamp; costEstimate?: number; litersAdded?: number };
          const busId = data.busId as string | undefined;
          const ts = data.timestamp?.toDate?.();
          if (!busId || !ts || ts < start || ts > end) return;
          countByBus[busId] = (countByBus[busId] ?? 0) + 1;
          const rawCost = typeof data.costEstimate === "number" ? data.costEstimate : 0;
          const cost = rawCost > 0 && rawCost <= 100_000_000 ? rawCost : 0;
          if (cost > 0) costByBus[busId] = (costByBus[busId] ?? 0) + cost;
          const l = typeof data.litersAdded === "number" && data.litersAdded > 0 ? data.litersAdded : 0;
          if (l > 0) litresByBus[busId] = (litresByBus[busId] ?? 0) + l;
        });
      }
      if (cancelled) return;
      setMonthlyReportFuelCostByBusId(costByBus);
      setMonthlyReportRefuelCountByBusId(countByBus);
      setMonthlyReportLitresAddedByBusId(litresByBus);
      setMonthlyReportFuelReadyFor(monthKey);
    };
    // Degrade gracefully if refuel reads are denied (e.g. rules not yet deployed)
    load().catch(() => {
      if (!cancelled) setMonthlyReportFuelReadyFor(monthKey);
    });
    return () => { cancelled = true; };
  }, [topLevelTab, reportMonth?.year, reportMonth?.month, buses]);

  // Load per-bus fuel cost for the selected month when cost estimate full screen is open and period is a specific month.
  useEffect(() => {
    if (!showCostEstimateFullScreen || costScreenPeriod === "last30" || !buses.length) {
      if (!showCostEstimateFullScreen) setCostScreenFuelByBus({});
      return;
    }
    const start = new Date(costScreenPeriod.year, costScreenPeriod.month - 1, 1);
    const end = new Date(costScreenPeriod.year, costScreenPeriod.month, 0, 23, 59, 59);
    const busIds = buses.map((b) => b.id);
    const load = async () => {
      const costByBus: Record<string, number> = {};
      busIds.forEach((id) => { costByBus[id] = 0; });
      for (let i = 0; i < busIds.length; i += 30) {
        const chunk = busIds.slice(i, i + 30);
        const q = query(collection(db, "busRefuelEvents"), where("busId", "in", chunk));
        const snap = await getDocs(q);
        snap.docs.forEach((d) => {
          const data = d.data() as { busId?: string; timestamp?: Timestamp; costEstimate?: number };
          const busId = data.busId as string | undefined;
          const ts = data.timestamp?.toDate?.();
          if (!busId || !ts || ts < start || ts > end) return;
          const rawCost = typeof data.costEstimate === "number" ? data.costEstimate : 0;
          const cost = rawCost > 0 && rawCost <= 100_000_000 ? rawCost : 0;
          if (cost > 0) costByBus[busId] = (costByBus[busId] ?? 0) + cost;
        });
      }
      setCostScreenFuelByBus(costByBus);
    };
    // Degrade gracefully if refuel reads are denied (e.g. rules not yet deployed)
    load().catch(() => {});
  }, [showCostEstimateFullScreen, costScreenPeriod, buses]);

  /** Delete the costEstimate field from every busRefuelEvent in the selected period.
   *  Preserves the event documents (liters, timestamp etc.) — only clears cost. */
  const handleResetCostEstimate = useCallback(async () => {
    setResetCostBusy(true);
    try {
      const busIds = buses.map((b) => b.id);
      let start: Date, end: Date;
      if (costScreenPeriod === "last30") {
        end = new Date();
        start = new Date();
        start.setDate(start.getDate() - 30);
      } else {
        start = new Date(costScreenPeriod.year, costScreenPeriod.month - 1, 1);
        end = new Date(costScreenPeriod.year, costScreenPeriod.month, 0, 23, 59, 59);
      }
      // Fetch matching events in chunks of 30 (Firestore "in" limit).
      const docRefs: ReturnType<typeof doc>[] = [];
      for (let i = 0; i < busIds.length; i += 30) {
        const chunk = busIds.slice(i, i + 30);
        const q = query(collection(db, "busRefuelEvents"), where("busId", "in", chunk));
        const snap = await getDocs(q);
        snap.docs.forEach((d) => {
          const ts = (d.data().timestamp as Timestamp | undefined)?.toDate?.();
          if (ts && ts >= start && ts <= end) docRefs.push(doc(db, "busRefuelEvents", d.id));
        });
      }
      // Update in batches of 500 (Firestore write-batch limit).
      for (let i = 0; i < docRefs.length; i += 500) {
        const batch = writeBatch(db);
        docRefs.slice(i, i + 500).forEach((ref) => {
          batch.update(ref, { costEstimate: deleteField(), costUnknown: deleteField() });
        });
        await batch.commit();
      }
      // Clear local state so UI updates immediately.
      setCostEstimateByBusId((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, 0])));
      setCostScreenFuelByBus((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, 0])));
      setResetCostConfirm(false);
      customToast.show(`Cleared fuel cost for ${docRefs.length} refuel event(s).`, "success");
    } catch (e) {
      customToast.show(`Reset failed: ${e}`, "error");
    } finally {
      setResetCostBusy(false);
    }
  }, [buses, costScreenPeriod]);

  const fleetStats = useMemo(() => computeFleetPeriodStats(fleetAttendance), [fleetAttendance]);
  const fleetByBusStats = useMemo(
    () => computeFleetByBusPeriodStats(fleetAttendance, buses.map((b) => b.id)),
    [fleetAttendance, fleetBusIdsKey],
  );

  /** Chronic absentees for the bus detail Absentees tab only (not fleet-wide). */
  const chronicAbsenteesForDetail = useMemo(() => {
    if (!selectedBus) return [];
    const cutoff30 = new Date();
    cutoff30.setDate(cutoff30.getDate() - 30);
    const map = new Map<string, { name: string; busId: string; busName: string; count: number }>();
    for (const rec of fleetAttendance) {
      if (rec.busId !== selectedBus.id) continue;
      const te = rec.finished ?? rec.date;
      if (!te || new Date(te) < cutoff30) continue;
      const busName = buses.find((b) => b.id === rec.busId)?.name ?? rec.busId;
      for (const c of rec.children) {
        if (c.status !== "absent" && c.status !== "not_picked") continue;
        const key = `${c.id ?? c.name}-${rec.busId}`;
        const cur = map.get(key);
        if (cur) cur.count++;
        else map.set(key, { name: c.name, busId: rec.busId, busName, count: 1 });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [fleetAttendance, buses, selectedBus]);

  // Monthly report: per-bus stats for selected month (km, fuel cost, incidents, L/100km, pickup rate).
  const monthlyReportRows = useMemo(() => {
    if (!reportMonth) return [];
    const start = new Date(reportMonth.year, reportMonth.month - 1, 1);
    const end = new Date(reportMonth.year, reportMonth.month, 0, 23, 59, 59);
    const inMonth = (d: Date) => { const t = new Date(d).getTime(); return t >= start.getTime() && t <= end.getTime(); };
    const attendanceInMonth = monthlyReportAttendance.filter((r) => r.date && inMonth(new Date(r.date)));
    const incidentsInMonth = fleetIssueReports.filter((r) => inMonth(r.timestamp));
    const byBus = new Map<string, { busName: string; km: number; fuelCost: number; incidents: number; refuels: number; needsRefuelTrips: number; tankL: number; totalSlots: number; pickedOrDropped: number }>();
    for (const b of buses) {
      byBus.set(b.id, { busName: b.name, km: 0, fuelCost: 0, incidents: 0, refuels: 0, needsRefuelTrips: 0, tankL: b.fuelTankCapacityL ?? DEFAULT_TANK_CAPACITY_L, totalSlots: 0, pickedOrDropped: 0 });
    }
    for (const r of attendanceInMonth) {
      const cur = byBus.get(r.busId);
      if (!cur) continue;
      cur.km += r.km ?? 0;
      if (r.needsRefuelSoon) cur.needsRefuelTrips++;
      for (const c of r.children) {
        cur.totalSlots++;
        if (c.status === "picked_up" || c.status === "dropped_off") cur.pickedOrDropped++;
      }
    }
    for (const r of incidentsInMonth) {
      const cur = byBus.get(r.busId);
      if (cur) cur.incidents++;
    }
    for (const b of buses) {
      const cur = byBus.get(b.id)!;
      cur.fuelCost = monthlyReportFuelCostByBusId[b.id] ?? 0;
      // Use explicit busRefuelEvents count; fall back to needsRefuelSoon survey signals if none logged
      const explicitRefuels = monthlyReportRefuelCountByBusId[b.id] ?? 0;
      cur.refuels = explicitRefuels > 0 ? explicitRefuels : cur.needsRefuelTrips;
    }
    return buses.map((b) => {
      const cur = byBus.get(b.id)!;
      const monthlyLitres = monthlyReportLitresAddedByBusId[b.id] ?? 0;
      const effIn: BusFuelInput = {
        totalKm30: cur.km,
        refuelCount30: cur.refuels,
        needsRefuelCount: cur.needsRefuelTrips,
        tankCapacityL: cur.tankL,
        totalLitresAdded: monthlyLitres,
        benchmarkKmPerL: b.benchmarkKmPerL ?? undefined,
        busAgeYears: busAgeYearsFrom(b.yearOfManufacture),
      };
      // L/100km prefers actual litres, falls back to refuel-count estimate
      const l100 = cur.km > 0 ? calculateL100km(effIn) : null;
      const pickupRate = cur.totalSlots > 0 ? Math.round((cur.pickedOrDropped / cur.totalSlots) * 100) : null;
      // Score requires a benchmark; estimateFuelEfficiency enforces the litres-first, benchmark-gated path
      const effEst = cur.km > 0 ? estimateFuelEfficiency(effIn) : null;
      const effPct = effEst?.pct ?? null;
      const effColor = effPct != null ? (effPct <= 30 ? "#dc2626" : effPct <= 70 ? "#f97316" : "#16a34a") : null;
      return {
        busId: b.id,
        busName: cur.busName,
        km: cur.km,
        fuelCost: cur.fuelCost,
        incidents: cur.incidents,
        needsRefuelTrips: cur.needsRefuelTrips,
        l100km: l100,
        pickupRatePct: pickupRate,
        totalSlots: cur.totalSlots,
        pickedOrDropped: cur.pickedOrDropped,
        efficiencyPct: effPct,
        efficiencyColor: effColor,
      };
    });
  }, [reportMonth, monthlyReportAttendance, fleetIssueReports, buses, monthlyReportFuelCostByBusId, monthlyReportRefuelCountByBusId, monthlyReportLitresAddedByBusId]);

  const handleSelect = useCallback((b: Bus) => {
    setSelectedBus(b);
    setMTime(b.morningTime);
    setETime(b.eveningTime);
    setTab("today");
    setSaved(false);
  }, []);

  /** Zana Action Registry executor. Phase 1 = navigate. Phase 2 = alert
   *  read/write. Phase 5 = money-leak playbook (list/flag missing refuel,
   *  draft coach notes). Write intents only reach here AFTER dock confirm. */
  /** Set once the takeover hook exists below. The executor is defined first —
   *  it is what the hook calls to perform a confirmed write — so the two meet
   *  through a ref rather than a circular dependency. */
  const takeoverRef = useRef<((req: TakeoverRequest) => void) | null>(null);
  /** Land on the subject and stop — what a direct ask gets (2026-09-18). */
  const jumpRef = useRef<((req: { topic: string; ops?: TakeoverRequest['ops']; findingTitle?: string }) => void) | null>(null);
  const zanaLifecycleHelpersRef = useRef<{
    openReassignIntro?: (bus: Bus, mode: "maintenance" | "for_hire" | "suspended" | "delete_bus") => Promise<void>;
    softDeleteEmptyBus?: (busId: string) => Promise<void>;
  }>({});

  // Live snapshot for Zana's async export wait — closures go stale while we
  // poll for the month switch + fuel load to finish.
  const monthlyExportLiveRef = useRef({
    rows: monthlyReportRows,
    reportMonth,
    fuelReadyFor: monthlyReportFuelReadyFor,
    topLevelTab,
  });
  monthlyExportLiveRef.current = {
    rows: monthlyReportRows,
    reportMonth,
    fuelReadyFor: monthlyReportFuelReadyFor,
    topLevelTab,
  };

  const triggerCsvDownload = useCallback((csv: string, filename: string) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, []);

  const exportMonthlyReportCsv = useCallback(async (month: string, format: "csv") => {
    if (format !== "csv") {
      throw new Error("Only CSV download is available right now — I can't mint a PDF yet. Confirm the CSV and I'll download it.");
    }
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
      throw new Error("I need a month like 2026-07 before I can download the report.");
    }
    const year = Number(month.slice(0, 4));
    const mon = Number(month.slice(5, 7));
    setTopLevelTab("monthlyReport");
    setReportMonth({ year, month: mon });

    const ready = await new Promise<boolean>((resolve) => {
      const started = Date.now();
      const tick = () => {
        const live = monthlyExportLiveRef.current;
        const selected = `${live.reportMonth.year}-${String(live.reportMonth.month).padStart(2, "0")}`;
        if (
          live.topLevelTab === "monthlyReport"
          && selected === month
          && live.fuelReadyFor === month
        ) {
          resolve(true);
          return;
        }
        if (Date.now() - started > 20_000) {
          resolve(false);
          return;
        }
        window.setTimeout(tick, 120);
      };
      tick();
    });
    if (!ready) {
      throw new Error(`I opened ${month} in Monthly Report but the rows did not finish loading. Try Download again in a moment.`);
    }

    const live = monthlyExportLiveRef.current;
    const monLabel = new Date(live.reportMonth.year, live.reportMonth.month - 1)
      .toLocaleString("default", { month: "long" });
    const headers = ["bus", "km", "fuelCostKES", "fuelAlerts", "incidents", "l100km", "efficiencyPct", "pickedOrDropped", "totalSlots", "attendancePct"];
    const rows = live.rows.map((r) => [
      r.busName,
      r.km.toFixed(1),
      r.fuelCost > 0 ? r.fuelCost.toFixed(0) : "",
      String(r.needsRefuelTrips),
      String(r.incidents),
      r.l100km != null ? r.l100km.toFixed(1) : "",
      r.efficiencyPct != null ? String(r.efficiencyPct) : "",
      String(r.pickedOrDropped),
      String(r.totalSlots),
      r.pickupRatePct != null ? `${r.pickupRatePct}%` : "",
    ]);
    const summaryRows = [
      ["", "", "", "", "", "", "", "", "", ""],
      ["--- Fleet Summary ---", "", "", "", "", "", "", "", "", ""],
      ["Total km", live.rows.reduce((s, r) => s + r.km, 0).toFixed(1), "", "", "", "", "", "", "", ""],
      ["Total fuel cost (KES)", live.rows.reduce((s, r) => s + r.fuelCost, 0).toFixed(0), "", "", "", "", "", "", "", ""],
      ["Total fuel alerts", String(live.rows.reduce((s, r) => s + r.needsRefuelTrips, 0)), "", "", "", "", "", "", "", ""],
      ["Total incidents", String(live.rows.reduce((s, r) => s + r.incidents, 0)), "", "", "", "", "", "", "", ""],
      ["Period", `${monLabel} ${live.reportMonth.year}`, "", "", "", "", "", "", "", ""],
    ];
    const allRows = [headers, ...rows, ...summaryRows];
    const csv = allRows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
    triggerCsvDownload(csv, `monthly-report-${monLabel}-${live.reportMonth.year}.csv`);
  }, [triggerCsvDownload]);

  const handleZanaIntent = useCallback(async (intent: ZanaIntent): Promise<string | void> => {
    const plateKey = (p: string) => normalizePlateKey(p || "");
    const findBus = (raw?: string) => {
      const key = plateKey(String(raw || ""));
      if (!key) return null;
      return buses.find((b) => plateKey(b.plate) === key || plateKey(b.id) === key || b.id === raw) || null;
    };
    /* Buses still missing a refuel log AND not already acknowledged. Everything
       Zana says, offers or writes about missing refuel goes through this, so
       she can never bring up something the owner has already settled. */
    const missingRefuelRows = () => {
      const weekMs = 7 * 24 * 60 * 60 * 1000;
      const now = Date.now();
      return (fleetFuelSummary.rows || []).filter((r) => {
        if (r.acked) return false;
        const bus = buses.find((b) => b.id === r.busId);
        if (bus?.status && bus.status !== "active") return false;
        const lastFill = fleetLastRefuelAtByBusId[r.busId] ?? 0;
        const lastTrip = lastTripByBusId[r.busId]?.finished?.getTime() ?? 0;
        if (!lastTrip || now - lastTrip > weekMs) return false;
        if (lastFill && now - lastFill < weekMs) return false;
        return true;
      });
    };
    switch (intent.actionId) {
      /* Ranged reports. The document is built and rendered on the server so the
         download and the scheduled 06:00 email come out of one builder — the
         old month-only CSV was assembled in this component from a React ref,
         which is exactly why it could never be scheduled. */
      case "export_report": {
        if (!fleetOrgCode) return "I need your organization before I can build a report.";
        const p = intent.params || {};
        const file = await generateReport(fleetOrgCode, {
          preset: p.preset,
          from: p.from,
          to: p.to,
          format: p.format || "pdf",
        });
        saveReportFile(file);
        const kind = (p.format || "pdf").toUpperCase();
        // Lead with the period actually covered, not the one that was asked
        // for — if they diverge, the owner must be able to see it immediately.
        return `Downloaded ${kind}: ${file.periodLabel} — ${file.vehicles} vehicle(s), ${file.totals.runs} runs.`
          + (file.notes.length ? ` Note: ${file.notes[0]}` : "");
      }
      case "schedule_report": {
        if (!fleetOrgCode) return "I need your organization before I can schedule anything.";
        const p = intent.params || {};
        const res = await scheduleReport(fleetOrgCode, {
          cadence: String(p.cadence || "weekly"),
          hourUtc: p.hourUtc,
          weekday: p.weekday,
          dayOfMonth: p.dayOfMonth,
          format: p.format || "pdf",
        });
        if (res.duplicate) return res.message || "That schedule already exists.";
        const where = res.email ? ` It goes to ${res.email}.` : "";
        return `Done — ${describeCadence({
          cadence: (p.cadence as "daily" | "weekly" | "monthly") || "weekly",
          hourUtc: Number(p.hourUtc ?? 6),
          weekday: p.weekday ?? null,
          dayOfMonth: p.dayOfMonth ?? null,
          format: (p.format as "pdf" | "csv") || "pdf",
        })}.${where} Ask me to stop it any time.`;
      }
      /* Asks every driver for a tank reading. This is the action that turns
         "I need 3 logged refuels before I can judge fuel" from a dead end into
         something the owner can act on in one tap. */
      case "request_fuel_readings": {
        if (!fleetOrgCode) return "I need your organization before I can message your drivers.";
        const only = intent.params?.onlyBuses;
        const res = await requestFuelReadings(fleetOrgCode, Array.isArray(only) ? only : undefined);
        return res.message;
      }
      case "list_report_schedules": {
        if (!fleetOrgCode) return "I need your organization first.";
        const rows = await listReportSchedules(fleetOrgCode);
        if (!rows.length) return "You have no scheduled reports. Ask me to email you one daily, weekly or monthly.";
        return `You have ${rows.length} scheduled report${rows.length === 1 ? "" : "s"}:\n`
          + rows.map((r) => `• ${describeCadence(r)} → ${r.email}`).join("\n");
      }
      case "cancel_report_schedule": {
        if (!fleetOrgCode) return "I need your organization first.";
        const id = String(intent.params?.id || "");
        if (!id) return "I need to know which scheduled report to stop — ask me to list them first.";
        const res = await cancelReportSchedule(fleetOrgCode, id);
        return `Stopped: ${res.describe || "that scheduled report"}. Nothing else changed.`;
      }
      case "list_unsigned_accounts":
      case "contact_late_bus_parents":
      case "prepare_school_trip_draft":
      case "export_monthly_report":
      case "invite_driver": {
        if (!fleetOrgCode) return "I need your organization before I can run that admin action.";
        const result = await executeZanaAdminIntent(intent, {
          orgCode: fleetOrgCode,
          // Every admin write reaches this executor only after the shared
          // Zana confirm chip (takeovers stop at the same gate).
          confirmed: true,
          exportMonthlyReport: exportMonthlyReportCsv,
          resolveInviteEmail: () => null,
          sendDriverInvite: async (email) => {
            if (!currentUser) return { ok: false, error: "Sign in again before inviting a driver." };
            return sendFleetInvite(
              email,
              fleetOrgCode,
              orgDisplayName || fleetOrgCode,
              "bus_driver",
              currentUser.uid,
            );
          },
        });
        return result.message;
      }
      case "test_zana_connection": {
        if (!fleetOrgCode) return "I need your organization to run a connection test.";
        return runZanaConnectionTest(fleetOrgCode);
      }
      case "list_alerts_by_priority": {
        const unread = fleetAlerts.filter((a) => !a.read);
        if (!unread.length) return "No unread alerts right now — the queue is clear. 🎉";
        /* Grouped by kind+bus and ranked by alertMeta's severity — the same
           canonical table High Risk sorts by. The old local regex here was a
           FOURTH independent severity order (a week-old route_timeout pinned
           itself above everything) with no dedupe, so 19 identical traffic
           notices filled all six lines verbatim (Gabriel's screenshot,
           2026-09-04). triage_high_risk was migrated off exactly this class
           of local regex — same reason. */
        type AlertGroup = { top: (typeof unread)[number]; count: number };
        const alertGroups = new Map<string, AlertGroup>();
        for (const a of unread) {
          const key = `${kindOf(a.type)}::${a.busId}`;
          const g = alertGroups.get(key);
          if (!g) alertGroups.set(key, { top: a, count: 1 });
          else {
            g.count++;
            if (a.timestamp > g.top.timestamp) g.top = a;
          }
        }
        const sorted = [...alertGroups.values()].sort((x, y) =>
          severityRank(severityOf(x.top.type)) - severityRank(severityOf(y.top.type))
          || y.top.timestamp.getTime() - x.top.timestamp.getTime());
        const lines = sorted.slice(0, 6).map((g, idx) => {
          const body = (g.top.message || g.top.type || "").slice(0, 140);
          const times = g.count > 1 ? ` ×${g.count}` : "";
          return `${idx + 1}. ${g.top.busName || g.top.busId} — ${body}${times}`;
        });
        const more = sorted.length > 6 ? ` (+${sorted.length - 6} more kinds in High Risk Alerts)` : "";
        return `${unread.length} unread alert(s) in ${sorted.length} group(s), worst first: ${lines.join(" · ")}${more}`;
      }
      case "list_buses_missing_refuel": {
        const missing = missingRefuelRows();
        if (!missing.length) return "Every active bus that has been running has a refill logged in the last 7 days. 👍";
        const lines = missing.slice(0, 8).map((r, idx) => {
          const b = buses.find((x) => x.id === r.busId);
          return `${idx + 1}. ${b?.plate || r.busName || r.busId}`;
        });
        const more = missing.length > 8 ? ` (+${missing.length - 8} more)` : "";
        const noun = missing.length === 1 ? "bus has" : "buses have";
        return `${missing.length} ${noun} no refill logged in 7 days: ${lines.join(" · ")}${more}. Flagging puts them on the High-Risk queue for someone to chase; if you already know about them, I can park them instead so they stop coming back.`;
      }
      case "check_roads_with_google": {
        /* 💸 The one paid chip Zana can offer. Confirm-gated in
           ALWAYS_CONFIRM_IDS, so reaching here means the owner pressed yes.

           The bus is resolved here rather than trusted from the model: a plate
           it invented would spend money on a vehicle that does not exist.
           Falls back to the bus already on screen, which is almost always the
           one being discussed. */
        const plate = String(intent.params?.plate || "").trim();
        const bus = plate
          ? buses.find((b) => b.plate === plate || b.id === plate)
          : selectedBus;
        if (!bus) {
          return plate
            ? `I could not find ${plate} in this fleet, so I did not check anything.`
            : "Open a bus first and I'll check its roads.";
        }
        const rawSide = String(intent.params?.side || "").toLowerCase();
        // Default to the run that is actually happening: before midday the
        // morning one is the live question.
        const side: "morning" | "evening" = rawSide === "evening"
          ? "evening"
          : rawSide === "morning"
            ? "morning"
            : (new Date().getHours() < 12 ? "morning" : "evening");
        if (!fleetOrgCode) return "I need to know which school this is before I can check roads.";
        return await checkRoadsWithGoogle(fleetOrgCode, bus.id, side);
      }
      case "list_parent_dropoff_flags": {
        /* Parents who pressed "that's not right" on a drop-off.

           This is what "Zana looks at it first on paid schools" actually means:
           she gathers them, puts the unread ones first, names the child and says
           how far from the usual stop the bus was — and then stops. She does not
           resolve them, and there is no write intent that lets her. A model is a
           reasonable thing to have rank a queue and a poor thing to have decide
           a child ended up somewhere acceptable.

           Basic schools never reach here at all: their flags are stamped
           `triage: 'admin'` on the phone and go straight to the alert list. */
        const flags = fleetAlerts
          .filter((a) => a.type === "dropoff_disputed_by_parent")
          .sort((x, y) => Number(x.read) - Number(y.read) || y.timestamp.getTime() - x.timestamp.getTime());
        if (!flags.length) {
          return "No parent has disputed a drop-off. That is the good outcome — parents are told where their child got off, and none of them has come back to say it was wrong.";
        }
        const open = flags.filter((a) => !a.read);
        const lines = flags.slice(0, 6).map((a, idx) => {
          const b = buses.find((x) => x.id === a.busId);
          const where = a.dropLat != null && a.dropLng != null
            ? ` · dropped at ${a.dropLat.toFixed(4)}, ${a.dropLng.toFixed(4)}`
            : "";
          const said = a.note ? ` · parent said: "${a.note}"` : "";
          return `${idx + 1}. ${a.childName || "A child"} on ${b?.plate || a.busName || a.busId}${where}${said}${a.read ? " (already reviewed)" : ""}`;
        });
        const more = flags.length > 6 ? ` (+${flags.length - 6} more)` : "";
        setShowFuelReportFullScreen(false);
        setShowMaintenanceFullScreen(false);
        setShowHighRiskFullScreen(true);
        setTopLevelTab("dashboard");
        const head = open.length
          ? `${open.length} ${open.length === 1 ? "drop-off is" : "drop-offs are"} still waiting on someone`
          : "Nothing outstanding — every one has been looked at";
        return `${head}. ${lines.join(" · ")}${more}. I have put them on screen; ring the parent or the driver to settle each one. I won't close these myself — where a child ended up is not mine to decide.`;
      }
      case "flag_buses_missing_refuel": {
        if (!fleetOrgCode) return "I can't flag buses without an organization context.";
        const missing = missingRefuelRows();
        if (!missing.length) {
          /* Reached from a takeover that just said otherwise, this used to be a
             flat contradiction. The live count is now sent up with the compose
             request so the two should agree — but if they ever drift again,
             say so rather than calling the owner's own screen a liar. */
          return intent.confirmRequired
            ? "I can't flag anything — by the time you confirmed, every active bus had a refuel logged in the last 7 days. Nothing is wrong; the list moved while we were talking."
            : "Nothing to flag — every active bus already has a refuel log in 7 days.";
        }
        /* Read OR unread. Looking only at unread ones is what made this mint a
           fresh duplicate every time: flag → 2 unread alerts → owner clears the
           queue → read:true → the guard stops seeing them → flag again. The
           queue grew on every pass and nothing was ever "already handled".
           A month is the horizon: after that the nag has earned a repeat. */
        const REFLAG_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
        const freshEnough = Date.now() - REFLAG_COOLDOWN_MS;
        const already = new Set(
          fleetAlerts
            .filter((a) => /missing_refuel|no_refuel/i.test(a.type || ""))
            .filter((a) => (a.timestamp?.getTime?.() ?? 0) >= freshEnough)
            .map((a) => a.busId),
        );
        const toAdd = missing.filter((r) => !already.has(r.busId));
        if (!toAdd.length) {
          return `Already flagged in the last 7 days — raising it again would just duplicate the queue. Say "park them" and I'll acknowledge them instead so they stop resurfacing.`;
        }
        await Promise.all(
          toAdd.map(async (r) => {
            const b = buses.find((x) => x.id === r.busId);
            const plate = b?.plate || r.busName || r.busId;
            await addDoc(collection(db, "fleetAlerts"), {
              schoolCode: fleetOrgCode,
              busId: r.busId,
              busName: plate,
              type: "missing_refuel",
              message: `${plate} has no refuel logged in 7 days — have the driver log litres so we can see what the tank is doing.`,
              timestamp: serverTimestamp(),
              read: false,
              source: "zana",
              severity: "warning",
            });
          }),
        );
        setShowFuelReportFullScreen(false);
        setShowMaintenanceFullScreen(false);
        setShowHighRiskFullScreen(true);
        setTopLevelTab("dashboard");
        const flagged = toAdd.length === 1 ? "1 bus" : `${toAdd.length} buses`;
        if (fleetOrgCode) void invalidateZanaContext(fleetOrgCode);
        return `Flagged ${flagged} on High Risk Alerts so someone chases the log. That raises it — it doesn't settle it: the bus still has no refuel on file until a driver logs litres and odometer. If you already know why, tell me to park it and it stops coming back.`;
      }
      case "triage_high_risk": {
        const unread = fleetAlerts.filter((a) => !a.read);
        if (!unread.length) return "The high-risk queue is already clear — nothing to triage.";
        /* Severity comes from `alertMeta`, the same table High Risk and the
           header bell rank with — so what Zana clears and what the office sees
           as serious can never disagree.

           It used to be a local regex, and the comment above it claimed to be
           "the same ranking the priority list uses". It was not, and the gap was
           not cosmetic: `fuel_theft` matched none of `incident|route|traffic`,
           landed in the "noise" bucket, and was silently marked read by a
           triage whose own reply said it had cleared "traffic and delays". The
           single loudest money alert in the product was being swallowed by the
           feature meant to surface it. Every fuel warning went the same way.

           Only `info` is cleared now — which is exactly what the reply has
           always promised. Clearing less is the safe direction to be wrong in:
           an alert left on the queue costs someone a glance, and one cleared by
           mistake costs them the thing it was warning about. */
        const keep = unread.filter((a) => severityOf(a.type) !== "info");
        const routine = unread.filter((a) => severityOf(a.type) === "info");

        // Only the routine ones get cleared. The serious ones stay unread on
        // purpose — that is the whole difference from bulk mark-reviewed.
        for (let i = 0; i < routine.length; i += 450) {
          const batch = writeBatch(db);
          routine.slice(i, i + 450).forEach((a) => batch.update(doc(db, "fleetAlerts", a.id), { read: true }));
          await batch.commit();
        }

        let emailed = false;
        if (fleetOrgCode) {
          const res = await sendAlertDigest(
            fleetOrgCode,
            keep.slice(0, 25).map((a) => ({
              busId: a.busId, busName: a.busName, type: a.type, message: a.message,
            })),
            routine.length,
          );
          emailed = res.emailed;
        }

        setShowFuelReportFullScreen(false);
        setShowMaintenanceFullScreen(false);
        setShowHighRiskFullScreen(true);
        setTopLevelTab("dashboard");

        const clearedPart = routine.length
          ? `Cleared ${routine.length} routine ${routine.length === 1 ? "notice" : "notices"} — traffic and delays.`
          : "Nothing in there was routine — everything on the queue is a warning or worse, so I cleared nothing.";
        const keptPart = keep.length
          ? ` ${keep.length === 1 ? "1 alert needs" : `${keep.length} alerts need`} you, and ${keep.length === 1 ? "it is" : "they are"} still on the queue: ${keep.slice(0, 4).map((a) => a.busName || a.busId).join(", ")}${keep.length > 4 ? ` +${keep.length - 4} more` : ""}.`
          : " Nothing left in the unread alert queue.";
        const mailPart = emailed
          ? " I've emailed you the shortlist."
          : " I couldn't get the email out, so the shortlist is here on screen only.";
        // High Risk card = incidents ∪ unread. Triage only touches unread
        // fleetAlerts — incident history stays, so the red number can stay lit.
        const stillIncidentBuses = new Set(
          busesWithIncidents.filter((b) => b.crashes.length > 0).map((b) => b.id),
        );
        const stillUnreadBuses = new Set(keep.map((a) => a.busId).filter(Boolean));
        const cardStill = new Set([...stillIncidentBuses, ...stillUnreadBuses]).size;
        /* The incident clause has to earn its place (bug 80b, 2026-08-04).
           `cardStill > 0` was the only gate, but the sentence it guarded talks
           about `stillIncidentBuses` — a DIFFERENT set. One unread alert and no
           incidents at all produced "that count includes 0 bus(es) carrying
           open incident reports… ask me to close out resolved incidents and the
           number drops": an offer to act on nothing. Say it only when there is
           actually an incident behind the number. */
        const incidentCount = stillIncidentBuses.size;
        const incidentPart = incidentCount > 0
          ? ` — that count includes ${incidentCount === 1 ? "1 bus" : `${incidentCount} buses`} carrying open incident reports, which triage does not touch. Ask me to close out resolved incidents and the number drops.`
          : ", which is the unread queue rather than anything I've missed.";
        const cardPart = cardStill > 0
          ? ` Your High Risk card will still show about ${cardStill}${incidentPart}`
          : " Your High Risk card should drop once this refreshes.";
        if (fleetOrgCode) void invalidateZanaContext(fleetOrgCode);
        return `${clearedPart}${keptPart}${mailPart}${cardPart}`;
      }
      case "ack_missing_refuel": {
        if (!fleetOrgCode) return "I can't park anything without an organization context.";
        const missing = missingRefuelRows();
        if (!missing.length) return "Nothing to park — no bus is showing a missing refuel log right now.";
        const scope = findBus(intent.params?.busId);
        const targets = scope ? missing.filter((r) => r.busId === scope.id) : missing;
        if (!targets.length) return `${scope?.plate} isn't on the missing-refuel list, so there's nothing to park.`;
        const uid = auth.currentUser?.uid || "";
        await Promise.all(
          targets.map(async (r) => {
            const b = buses.find((x) => x.id === r.busId);
            const plate = b?.plate || r.busName || r.busId;
            /* Deterministic id: acking the same bus twice overwrites instead of
               piling up a second row — the exact bug the alerts themselves had. */
            await setDoc(
              doc(db, "fleetConditionAcks", `${fleetOrgCode}__missing_refuel__${r.busId}`),
              {
                orgCode: fleetOrgCode,
                condition: "missing_refuel",
                busId: r.busId,
                busName: plate,
                ackedAt: serverTimestamp(),
                ackedBy: uid,
                source: "zana",
              },
              { merge: true },
            );
          }),
        );
        const what = targets.length === 1
          ? (buses.find((x) => x.id === targets[0].busId)?.plate || targets[0].busName)
          : `${targets.length} buses`;
        return `Parked ${what}. They're off the fuel report and out of my count, and they stay off — until one of them logs a refuel, which retires the note and lets a fresh dry spell surface again.`;
      }
      case "unack_missing_refuel": {
        if (!fleetOrgCode) return "I can't un-park anything without an organization context.";
        const scope = findBus(intent.params?.busId);
        const acked = (fleetFuelSummary.rows || []).filter((r) => r.acked && (!scope || r.busId === scope.id));
        if (!acked.length) return "Nothing is parked right now.";
        await Promise.all(
          acked.map((r) => deleteDoc(doc(db, "fleetConditionAcks", `${fleetOrgCode}__missing_refuel__${r.busId}`))),
        );
        return `Back on the list — ${acked.length === 1 ? "1 bus" : `${acked.length} buses`} will show up in the fuel report again.`;
      }
      case "draft_driver_coach_note": {
        const driverId = String(intent.params?.driverId || "").trim();
        const note = String(intent.params?.note || "").trim().slice(0, 400);
        const label = String(intent.params?.driverLabel || driverId).trim();
        if (!driverId || !note) return "I need a driver and a note to draft — ask me again with a driver in mind.";
        if (!fleetOrgCode) return "I can't save a coach note without an organization context.";
        await setDoc(
          doc(db, "driverInsights", driverId),
          {
            orgCode: fleetOrgCode,
            driverId,
            draftNote: note,
            draftNoteLabel: label,
            draftNoteSource: "zana",
            draftNoteAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        return `Saved a coaching draft for ${label}: "${note.slice(0, 120)}${note.length > 120 ? "…" : ""}". You'll find it on their coaching card.`;
      }
      case "email_driver": {
        if (!fleetOrgCode) return "I need your organization to reach a driver.";
        const scope = findBus(intent.params?.busId);
        if (!scope) return "Tell me which bus and I'll write to whoever drives it.";
        const note = String(intent.params?.note || "").trim();
        if (!note) return `I need something to say to ${scope.plate}'s driver.`;
        const out = await notifyBusDriver(fleetOrgCode, scope.id, note);
        if (out.ok) return `Sent — ${out.driverName || "the driver"} on ${out.plate || scope.plate} has it, and replies come back to you.`;
        if (out.reason === "no_driver") return `${scope.plate} has no driver assigned yet, so there's nobody to write to. Assign one in Accounts and ask me again.`;
        if (out.reason === "no_email") return `${scope.plate}'s driver has no email on file — that's the only channel we have today. Add one in Accounts and I'll send it.`;
        if (out.reason === "email_not_configured") return "Email isn't configured on the server, so I couldn't send that.";
        // The strict org gate says why it refused (2026-09-15); show that, not a bare code.
        if (out.reason === "http_403" && out.message) return out.message;
        return `I couldn't get that email out (${out.reason || "unknown error"}).`;
      }
      case "resolve_incidents": {
        // The missing half of "sort out my high-risk alerts". High Risk counts
        // any bus carrying an incident report, and until now a report could
        // never be closed — so the card kept its number no matter how many
        // alerts got marked read. This closes them out for good.
        const scope = findBus(intent.params?.busId);
        const open = fleetIssueReports.filter((r) => !r.resolved && (!scope || r.busId === scope.id));
        if (!open.length) {
          return scope
            ? `${scope.plate} has no open incident reports.`
            : "No open incident reports — nothing to close out.";
        }
        for (let i = 0; i < open.length; i += 450) {
          const batch = writeBatch(db);
          open.slice(i, i + 450).forEach((r) => batch.update(doc(db, "busIssueReports", r.id), {
            resolved: true,
            resolvedAt: serverTimestamp(),
            resolvedBy: currentUser?.uid || null,
            resolvedNote: "Closed from Zana",
          }));
          await batch.commit();
        }
        const resolvedIds = new Set(open.map((r) => r.id));
        setFleetIssueReports((prev) => prev.map((r) => (resolvedIds.has(r.id) ? { ...r, resolved: true } : r)));
        if (fleetOrgCode) void invalidateZanaContext(fleetOrgCode);
        const busCount = new Set(open.map((r) => r.busId)).size;
        return `Closed out ${open.length} incident report(s) across ${busCount} bus(es)${scope ? ` — ${scope.plate}` : ""}. They drop off High Risk; the reports stay on each bus's history.`;
      }
      case "mark_alerts_reviewed": {
        const scope = findBus(intent.params?.busId);
        const targets = fleetAlerts.filter((a) => !a.read && (!scope || a.busId === scope.id));
        if (!targets.length) {
          return scope
            ? `${scope.plate} has no unread alerts — nothing to mark.`
            : "No unread alerts to mark — the queue is already clear.";
        }
        // Chunked: the alert list is no longer capped, so a busy org can exceed
        // Firestore's 500-write batch ceiling.
        for (let i = 0; i < targets.length; i += 450) {
          const batch = writeBatch(db);
          targets.slice(i, i + 450).forEach((a) => batch.update(doc(db, "fleetAlerts", a.id), { read: true }));
          await batch.commit();
        }
        if (fleetOrgCode) void invalidateZanaContext(fleetOrgCode);
        // Tell them what the card will actually say. High Risk counts recorded
        // incidents as well as unread alerts, so promising a reset and leaving
        // a red 3 on screen is how she loses their trust in one move.
        const markedIds = new Set(targets.map((a) => a.id));
        const stillUnread = fleetAlerts.filter((a) => !a.read && !markedIds.has(a.id));
        const incidentBusIds = busesWithIncidents.filter((b) => b.crashes.length > 0).map((b) => b.id);
        const stillHighRisk = new Set([
          ...incidentBusIds,
          ...stillUnread.map((a) => a.busId).filter(Boolean),
        ]).size;
        const done = `Done — marked ${targets.length} alert(s) reviewed${scope ? ` for ${scope.plate}` : ""}.`;
        if (stillHighRisk === 0) return `${done} High Risk drops to 0.`;
        return `${done} High Risk still reads ${stillHighRisk} — ${incidentBusIds.length ? "that count includes recorded incidents, which only clear when the incident itself is resolved, not by marking alerts read" : "there are still unread alerts on other buses"}.`;
      }
      case "open_high_risk_alerts":
        setShowFuelReportFullScreen(false);
        setShowMaintenanceFullScreen(false);
        setShowHighRiskFullScreen(true);
        setTopLevelTab("dashboard");
        break;
      case "open_fuel_report":
        setShowHighRiskFullScreen(false);
        setShowMaintenanceFullScreen(false);
        setShowFuelReportFullScreen(true);
        setTopLevelTab("dashboard");
        break;
      case "open_zana_mode":
        setShowHighRiskFullScreen(false);
        setShowFuelReportFullScreen(false);
        setAnalyticsInitialBusId(null);
        setTopLevelTab("analytics");
        break;
      case "open_tab": {
        const tab = String(intent.params?.tab || "").trim();
        const allowed = new Set(["dashboard", "fleet", "accounts", "monthlyReport", "trips", "liveMap", "analytics", "fuel", "fuelPrices"]);
        /* An unknown tab used to `break` in silence — the chip was clickable,
           did nothing, and explained nothing. That is how the mission chips
           emitting 'routes' and 'drivers' (tabs that do not exist; fixed at
           source in premium/journey.js) went unnoticed. Say so instead, so the
           next bad tab name is visible the first time somebody clicks it
           rather than years later in an audit (review, 09-06). */
        if (!allowed.has(tab)) {
          customToast.show(`I can't open "${tab || 'that'}" — it isn't a tab on this dashboard.`, 'error');
          break;
        }
        if (tab === "fuel" && !premiumLocked) break;
        setShowHighRiskFullScreen(false);
        setShowFuelReportFullScreen(false);
        if (tab === "trips") setTripsDetailTripId(null);
        if (tab === "analytics") setAnalyticsInitialBusId(null);
        if (tab === "fleet") setDetailView(false);
        setTopLevelTab(tab as typeof topLevelTab);
        break;
      }
      case "spotlight": {
        // The real spotlight: glow a named element on the page. Until now this
        // case did the same thing as open_bus — it opened a bus detail view and
        // seeded an analytics id. No highlight, no scroll, no overlay.
        const targetId = intent.params?.targetId;
        if (targetId) {
          spotlight([targetId]);
          window.setTimeout(() => clearSpotlight(), 4000);
          break;
        }
        // Legacy form, kept so anything still emitting a busId keeps working.
        const sb = findBus(intent.params?.busId || intent.params?.entityId);
        if (!sb) break;
        setShowHighRiskFullScreen(false);
        setShowFuelReportFullScreen(false);
        handleSelect(sb);
        setDetailView(true);
        setTopLevelTab("fleet");
        setAnalyticsInitialBusId(sb.id);
        break;
      }
      case "delete_bus": {
        const target = findBus(intent.params?.busId);
        if (!target) return "I need a plate I know in this fleet before I can delete a bus.";
        const helpers = zanaLifecycleHelpersRef.current;
        if (target.children.length > 0) {
          if (helpers.openReassignIntro) void helpers.openReassignIntro(target, "delete_bus");
          else setTopLevelTab("fleet");
          return `${target.plate} still has students aboard — move them, then confirm delete on the next screen.`;
        }
        if (helpers.softDeleteEmptyBus) await helpers.softDeleteEmptyBus(target.id);
        else {
          await updateDoc(doc(db, "buses", target.id), { isActive: false, updatedAt: serverTimestamp() });
        }
        setBuses((prev) => prev.filter((b) => b.id !== target.id));
        if (fleetOrgCode) void invalidateZanaContext(fleetOrgCode);
        return `Deleted ${target.plate} from the active fleet.`;
      }
      case "suspend_bus": {
        const target = findBus(intent.params?.busId);
        if (!target) return "I need a plate I know in this fleet before I can suspend a bus.";
        const helpers = zanaLifecycleHelpersRef.current;
        if (target.children.length > 0) {
          if (helpers.openReassignIntro) void helpers.openReassignIntro(target, "suspended");
          else setTopLevelTab("fleet");
          return `${target.plate} still has students — reassignment is open; confirm there to suspend.`;
        }
        await updateDoc(doc(db, "buses", target.id), { status: "suspended", updatedAt: serverTimestamp() });
        if (fleetOrgCode) {
          void logBusStatusEvent({
            busId: target.id,
            orgCode: fleetOrgCode,
            plate: target.plate,
            busName: target.name,
            fromStatus: target.status,
            toStatus: "suspended",
            byUid: currentUser?.uid ?? null,
            byEmail: currentUser?.email ?? null,
            source: "zana",
            childCount: target.children.length,
          });
        }
        setBuses((prev) => prev.map((b) => (b.id === target.id ? { ...b, status: "suspended" as Bus["status"] } : b)));
        if (fleetOrgCode) void invalidateZanaContext(fleetOrgCode);
        return `${target.plate} is now suspended / maintenance.`;
      }
      case "create_bus": {
        if (!fleetOrgCode) return "I need your organization before I can create a bus.";
        const plate = String(intent.params?.plate || "").toUpperCase().replace(/[\s-]/g, "");
        if (!/^[A-Z0-9]{3,16}$/.test(plate)) return "I need a valid plate (e.g. KDA123A) to create a bus.";
        const existing = buses.find((b) => plateKey(b.plate) === plate || plateKey(b.id) === plate);
        if (existing) return `${plate} already exists in this fleet — open it from Fleet instead of creating a duplicate.`;
        const nick = String(intent.params?.nickname || "").trim().slice(0, 40) || `Bus ${plate}`;
        const busRef = doc(db, "buses", plate);
        const snap = await getDoc(busRef);
        if (snap.exists()) return `${plate} is already registered — I won't overwrite it from chat.`;
        await setDoc(busRef, {
          ownerId: currentUser?.uid || null,
          driverIds: [],
          nickname: nick,
          numberPlate: plate,
          schoolCode: fleetOrgCode,
          organizationCode: fleetOrgCode,
          orgCode: fleetOrgCode,
          children: [],
          currentRoute: { type: null, startedAt: null, finishedAt: null, status: null },
          isActive: true,
          status: "active",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          source: "zana",
        });
        if (fleetOrgCode) void invalidateZanaContext(fleetOrgCode);
        setTopLevelTab("fleet");
        return `Created ${plate}${nick !== `Bus ${plate}` ? ` (${nick})` : ""} as an active bus. Assign a driver from Accounts when you're ready.`;
      }
      case "apply_station_pump_price": {
        if (!fleetOrgCode) return "I need your organization before I can update a station price.";
        const unread = fleetAlerts.find((a) => a.type === "station_pump_price" && !a.read);
        const any = unread || fleetAlerts.find((a) => a.type === "station_pump_price");
        const placeId = String(intent.params?.placeId || any?.placeId || "").trim();
        const observed = Number(intent.params?.pricePerL ?? any?.observedPerL);
        const fuelType = String(intent.params?.fuelType || any?.fuelType || "diesel").toLowerCase() === "petrol"
          ? "petrol"
          : "diesel";
        const stationName = String(intent.params?.stationName || any?.stationName || "").trim();
        if (!placeId || !Number.isFinite(observed) || observed < 50 || observed > 500) {
          setTopLevelTab("fuelPrices");
          return "I don't have a pump reading to put on the list yet. When a driver corrects litres at a station, I'll get one — or add the station under Fuel prices.";
        }
        const month = currentMonthKey();
        const priceRef = doc(db, "fuelPrices", `${fleetOrgCode}__${month}`);
        const snap = await getDoc(priceRef);
        const data = (snap.data() || {}) as Record<string, unknown>;
        const stations = Array.isArray(data.stations)
          ? (data.stations as Array<Record<string, unknown>>).map((s) => ({ ...s }))
          : [];
        const idx = stations.findIndex(
          (s) => String(s.placeId || "") === placeId
            && (String(s.fuelType || "diesel").toLowerCase() === fuelType)
        );
        const row = {
          placeId,
          name: stationName || String(stations[idx]?.name || placeId),
          pricePerL: Number(observed.toFixed(2)),
          fuelType,
          ...(idx >= 0 && stations[idx].primary === true ? { primary: true } : {}),
        };
        if (idx >= 0) stations[idx] = { ...stations[idx], ...row };
        else stations.push(row);
        await setDoc(priceRef, {
          orgCode: fleetOrgCode,
          month,
          source: "zana",
          stations,
          updatedBy: currentUser?.uid ?? null,
          updatedAt: serverTimestamp(),
        }, { merge: true });
        const matching = fleetAlerts.filter(
          (a) => a.type === "station_pump_price"
            && !a.read
            && (a.placeId === placeId || a.id.includes(placeId))
        );
        await Promise.all(matching.map((a) => updateDoc(doc(db, "fleetAlerts", a.id), { read: true })));
        void invalidateZanaContext(fleetOrgCode);
        setTopLevelTab("fuelPrices");
        const label = stationName || placeId;
        return `Put ${label} on this month's list at Ksh ${observed.toFixed(2)}/L. Next fill there will use that price.`;
      }
      case "suspend_driver": {
        const driverId = String(intent.params?.driverId || "").trim();
        const accountQuery = String(intent.params?.accountQuery || "").trim().toLowerCase();
        let user = driverId ? fleetUsers.find((u) => u.id === driverId) : null;
        if (!user && accountQuery) {
          const hits = fleetUsers.filter((u) => {
            const name = String(u.displayName || "").toLowerCase();
            const email = String(u.email || "").toLowerCase();
            const role = String(u.role || "").toLowerCase();
            if (!(role.includes("driver") || role === "bus_driver" || role === "transport")) return false;
            return name.includes(accountQuery) || email.includes(accountQuery) || accountQuery.includes(name);
          });
          if (hits.length === 1) user = hits[0];
          else if (hits.length > 1) {
            return `I found ${hits.length} drivers matching "${intent.params?.accountQuery}" — open Accounts and pick the right one, or use their D-label.`;
          }
        }
        if (!user) {
          setTopLevelTab("accounts");
          return "I couldn't uniquely match that driver — open Accounts and suspend them there, or ask me with D1 / D2.";
        }
        if ((user as { suspended?: boolean }).suspended) {
          return `${user.displayName || user.email || "That driver"} is already suspended.`;
        }
        await updateDoc(doc(db, "users", user.id), {
          suspended: true,
          suspendedAt: serverTimestamp(),
          suspendReason: "Your account has been suspended by your fleet admin. Contact them to restore access.",
          isLoggedOut: true,
          logoutReason: "Your account has been suspended by your fleet admin.",
          logoutTimestamp: serverTimestamp(),
        });
        setTopLevelTab("accounts");
        return `Suspended ${user.displayName || user.email || "the driver"} — they're logged out until you unsuspend.`;
      }
      case "start_takeover": {
        // Either a named job, or a subject the engine composes a walkthrough
        // for. The console drives it (see useZanaTakeover) because the console
        // owns the tabs and the writes she may ask to make.
        const scriptId = intent.params?.scriptId;
        const topic = intent.params?.topic;
        const ops = intent.params?.ops || undefined;
        const skipChooser = !!intent.params?.skipChooser;
        // Unattended only when asked for in so many words — see useZanaTakeover.
        const auto = skipChooser && intent.params?.auto === true;
        const kind = intent.params?.kind === 'do' || intent.params?.kind === 'show'
          ? intent.params.kind
          : undefined;
        /* 2026-09-18: a composed subject goes STRAIGHT there unless a walk was
           asked for (the dock decides and sends `mode`). A named script keeps
           its walk — it holds the write confirms. */
        if (scriptId) {
          void takeoverRef.current?.({
            scriptId, skipChooser, auto, ...(kind ? { kind } : {}), ...(ops ? { ops } : {}),
          });
        } else if (topic && intent.params?.mode === 'jump') {
          void jumpRef.current?.({
            topic,
            ...(ops ? { ops } : {}),
            ...(intent.params?.findingTitle ? { findingTitle: intent.params.findingTitle } : {}),
          });
        } else if (topic) {
          void takeoverRef.current?.({
            topic, skipChooser, auto, ...(kind ? { kind } : {}), ...(ops ? { ops } : {}),
          });
        }
        break;
      }
      case "open_bus": {
        const b = findBus(intent.params?.busId || intent.params?.entityId);
        if (!b) break;
        setShowHighRiskFullScreen(false);
        setShowFuelReportFullScreen(false);
        handleSelect(b);
        setDetailView(true);
        setTopLevelTab("fleet");
        /* A takeover may ask for a SECTION of the bus page — an incident
           finding lands on Incidents, the list it is about (Gabriel,
           2026-09-28: "it should open either that alert or incident itself").
           Only the page's own sections are honoured. */
        const wanted = typeof intent.params?.tab === "string" ? intent.params.tab : "";
        if (wanted === "today" || wanted === "people" || wanted === "money" || wanted === "incidents" || wanted === "settings") {
          setTab(wanted);
        }
        // A pinned run from Analytics → Routes: land on that run in Recent runs.
        const runId = typeof intent.params?.runId === "string" ? intent.params.runId : "";
        if (runId) { pendingRunIdRef.current = runId; setSelectedRunId(runId); }
        break;
      }
      default:
        break;
    }
  }, [buses, busesWithIncidents, currentUser, exportMonthlyReportCsv, fleetAlerts, fleetFuelSummary.rows, fleetLastRefuelAtByBusId, fleetOrgCode, fleetUsers, handleSelect, lastTripByBusId, orgDisplayName, premiumLocked]);

  /** A takeover step asking for a tab — or the High Risk overlay. */
  /** Which Analytics sub-tab a spotlight target actually lives on. Anything not
   *  listed sits on Fuel, which is where the page opens anyway. */
  const ANALYTICS_SUBTAB_FOR_TARGET: Record<string, AnalyticsSubTab> = useMemo(() => ({
    driverTable: "drivers",
    routeTable: "routes",
    fuelTable: "fuel",
    barWorst: "fuel",
    kpiCpk: "fuel",
  }), []);

  const handleZanaTakeoverTab = useCallback((tab: string, spotlight?: string[]) => {
    if (tab === "highRisk" || tab === "alerts") {
      void handleZanaIntent({ actionId: "open_high_risk_alerts" });
      return;
    }
    /* Fuel Report is an overlay, not a tab — the same shape as High Risk above.
       `open_tab: "fuel"` refuses outright on a premium org (that tab only
       exists on the locked plan), so a takeover step aiming at the fuel screen
       silently did nothing and the owner stayed wherever the walk had opened.
       That is why the "open Fuel Report" chip never opened the Fuel Report. */
    if (tab === "fuel" || tab === "fuelReport") {
      void handleZanaIntent({ actionId: "open_fuel_report" });
      return;
    }
    // "analytics" is a page with its own tabs, and it opens on Fuel. A step
    // aiming at driverTable has to say so, or she narrates the drivers story
    // over the fuel screen with nothing lit.
    if (tab === "analytics") {
      const wanted = (spotlight || [])
        .map((t) => ANALYTICS_SUBTAB_FOR_TARGET[t])
        .find(Boolean);
      if (wanted) {
        setZanaAnalyticsSubTab(wanted);
        setZanaAnalyticsSubTabNonce((n) => n + 1);
      }
    }
    void handleZanaIntent({ actionId: "open_tab", params: { tab } });
  }, [handleZanaIntent, ANALYTICS_SUBTAB_FOR_TARGET]);

  /** Rich Ask + takeover context so "this one" / High Risk hops resolve to the
   *  overlay or bus the owner is actually looking at — not only the shell tab. */
  const zanaPageContext = useMemo((): AskPageContext => {
    const plate = selectedBus?.plate ? String(selectedBus.plate) : undefined;
    // Full-screen fleet overlays are fleet-wide. `selectedBus` can be a stale
    // card selection from minutes ago; attaching it here made Zana describe
    // High Risk as being "around KAA123A" when the owner had selected no row.
    if (showHighRiskFullScreen) return { page: "highRiskAlerts" };
    if (showFuelReportFullScreen) return { page: "fuelReport" };
    if (showMaintenanceFullScreen) return { page: "maintenance" };
    if (detailView && plate) return { page: "busDetail", entityType: "bus", entityId: plate };
    return { page: topLevelTab };
  }, [
    showHighRiskFullScreen,
    showFuelReportFullScreen,
    showMaintenanceFullScreen,
    detailView,
    selectedBus?.plate,
    topLevelTab,
  ]);

  /** Same live High Risk math the red card uses — takeover compose must see it. */
  const zanaLiveCounts = useMemo(() => {
    const unread = fleetAlerts.filter((a) => !a.read);
    const unreadBusIds = [...new Set(unread.map((a) => a.busId).filter(Boolean))];
    const incidentIds = new Set(
      busesWithIncidents.filter((b) => b.crashes.length > 0).map((b) => b.id),
    );
    const highRiskBusCount = new Set([...incidentIds, ...unreadBusIds]).size;
    /* Sent up so the takeover NARRATES the same number the write will act on.
       Zana used to announce the cached server figure and then flag from this
       one; when they disagreed she promised buses she could not flag, and the
       "nothing to flag" reply flashed past before anyone read it. Same
       reasoning as unreadAlertCount above — the open console is the truth. */
    const missingRefuelCount =
      (fleetFuelSummary.rows || []).filter((r) => !r.hasRefueled && !r.acked).length;
    const openIncidentsByBus: Record<string, number> = {};
    fleetIssueReports.forEach((r) => {
      if (r.resolved || !r.busId) return;
      openIncidentsByBus[r.busId] = (openIncidentsByBus[r.busId] || 0) + 1;
    });
    return {
      unreadAlertCount: unread.length,
      highRiskBusCount,
      unreadBusIds: unreadBusIds.map(String).slice(0, 40),
      missingRefuelCount,
      /* Open (unresolved) incident reports per bus — the figure the day-old
         "KCE999Z has 2 logged incidents" finding is checked against, so a
         cluster the owner has closed out stops being offered (2026-09-29). */
      openIncidentsByBus,
    };
  }, [fleetAlerts, busesWithIncidents, fleetFuelSummary, fleetIssueReports]);

  // Remove legacy "fleet" (Summary) tab if it was persisted in state on mount only.
  // Using [] instead of [tab] so this cleanup runs once; [tab] would redirect every
  // time tab changed, making "fleet" permanently unreachable.
  useEffect(() => {
    if (tab === "fleet") setTab("today");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Which run the map shows: the clicked one, else the newest. Reset the
     click whenever the bus changes so bus B never opens on bus A's run. */
  /* PIN A RUN (2026-09-29, route analytics item 2). A run the office wants to
     find again — "the Tsavo trip" — is named and pinned here, and listed under
     Analytics → Routes → Pinned runs. Plain fields on the run's own record;
     the org's fleet admin may update it (rules: schoolCode stays as it is). */
  const togglePinRun = async (r: AttendanceRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (r.pinned) {
        if (!window.confirm(`Unpin "${r.pinnedName || "this run"}"? It stays in Recent runs.`)) return;
        await updateDoc(doc(db, "busAttendance", r.id), { pinned: false, pinnedName: null, pinnedAt: null });
        customToast.show("Run unpinned", "success");
        return;
      }
      const when = r.date ? r.date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "";
      const suggested = `${(r.type || "run").replace(/_/g, " ")}${when ? ` · ${when}` : ""}`;
      const name = window.prompt("Name this run so you can find it later under Analytics → Routes → Pinned runs:", suggested);
      if (name == null) return;
      await updateDoc(doc(db, "busAttendance", r.id), {
        pinned: true,
        pinnedName: name.trim() || suggested,
        pinnedAt: serverTimestamp(),
        pinnedBy: auth.currentUser?.uid || null,
      });
      customToast.show("Pinned — find it under Analytics → Routes", "success");
    } catch (err) {
      console.error("[BusTracking] pin run failed:", err);
      customToast.show("Could not pin that run", "error");
    }
  };

  const shownRun = useMemo(() => {
    const byId = selectedRunId ? busRecords.find((r) => r.id === selectedRunId) : null;
    return byId ?? busRecords[0] ?? null;
  }, [busRecords, selectedRunId]);

  useEffect(() => { setSelectedRunId(pendingRunIdRef.current); pendingRunIdRef.current = null; }, [selectedBus?.id]);

  /* Open the shown run's sealed trail. One async pass per selection — the
     same openLatLng + DEK path the live map uses, so a v1 pin and a rotated
     one both draw. Flags (gapBefore/estimated) ride along so the pen still
     lifts across app-kill gaps. */
  useEffect(() => {
    if (!shownRun) { setRunTrail(null); return; }
    const forId = shownRun.id;
    // Fast path: legacy plaintext trails already parsed sync.
    if ((shownRun.routePoints ?? []).length >= 2) {
      setRunTrail({ forId, pts: shownRun.routePoints! });
      return;
    }
    // The road-snapped line, when the reconcile stored one, beats raw scatter.
    const snapped = shownRun.snappedRoutePoints ?? [];
    if (snapped.length >= 2) { setRunTrail({ forId, pts: snapped }); return; }
    const windowRaw = shownRun.rawRoutePoints ?? [];
    if (windowRaw.length < 2) { setRunTrail({ forId, pts: [] }); return; }
    let cancelled = false;
    (async () => {
      // A spilled trip's array is only its last stretch — fetch the chunks
      // and draw the whole run (bug 216).
      const raw = (shownRun.trailChunks ?? 0) > 0
        ? await loadTripTrailRaw(db, "busAttendance", forId, {
            routePoints: windowRaw, trailChunks: shownRun.trailChunks, trailWindowStart: shownRun.trailWindowStart,
          })
        : windowRaw;
      const pts: { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[] = [];
      for (const rp of raw) {
        if (!rp || typeof rp !== 'object') continue;
        const o = rp as Record<string, unknown>;
        const ll = await openLatLng(o);
        if (!ll) continue;
        pts.push({
          lat: ll.lat, lng: ll.lng,
          ...(o.gapBefore === true ? { gapBefore: true } : {}),
          ...(o.estimated === true ? { estimated: true } : {}),
        });
      }
      if (!cancelled) setRunTrail({ forId, pts });
    })();
    return () => { cancelled = true; };
  }, [shownRun]);

  const totalKm = busRecords.reduce((s, r) => s + (r.km || 0), 0);
  const avgDur = busRecords.length ? Math.round(busRecords.reduce((s, r) => s + (r.duration || 0), 0) / busRecords.length) : "—";

  const fuelStats = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - fuelCostDays);
    const recent = busRecords.filter((r) => r.finished && new Date(r.finished) >= cutoff);
    const totalKm30 = recent.reduce((s, r) => s + (r.km ?? 0), 0);
    const needsRefuelCount = recent.filter((r) => r.needsRefuelSoon).length;
    const recentRefuelEvents = refuelEvents.filter((e) => e.timestamp >= cutoff);
    const refuelCount30 = recentRefuelEvents.length;
    // Sum actual litres reported by drivers; events where driver ticked "I don't know" contribute 0.
    const totalLitresAdded30 = recentRefuelEvents.reduce((s, e) => s + (e.litersAdded ?? 0), 0);
    // Sum refuel cost entries. Events where cost is unknown contribute 0.
    // Per-event cap at 100 million KES prevents clearly erroneous entries from blowing the total.
    const totalCost30 = recentRefuelEvents.reduce((s, e) => {
      const c = e.costEstimate ?? 0;
      return s + (c > 0 && c <= 100_000_000 ? c : 0);
    }, 0);
    const TANK_LITERS = selectedBus?.fuelTankCapacityL ?? DEFAULT_TANK_CAPACITY_L;
    const FILL_FRACTION = 0.8;
    // Prefer actual litres; fall back to tank-capacity estimate when litres are unknown.
    const estimatedL100 = totalKm30 > 0
      ? totalLitresAdded30 > 0
        ? ((totalLitresAdded30 / totalKm30) * 100).toFixed(1)
        : refuelCount30 >= 1
          ? ((refuelCount30 * TANK_LITERS * FILL_FRACTION) / totalKm30 * 100).toFixed(1)
          : null
      : null;
    return { totalKm30, needsRefuelCount, refuelCount30, totalLitresAdded30, totalCost30, estimatedL100 };
  }, [busRecords, refuelEvents, selectedBus?.fuelTankCapacityL, fuelCostDays]);

  /**
   * Same on-screen payload Ask and takeover both use. Without cards and focus,
   * "take over on this" walks the generic topic lens; with them she opens on
   * the plate and figures the owner is pointing at.
   */
  const zanaAskContext = useMemo((): AskPageContext => {
    const plateOf = (busId: string) => buses.find((b) => b.id === busId)?.plate;
    const cards: { label: string; value: string }[] = [
      { label: "Fleet buses", value: String(buses.length) },
      { label: "Active buses", value: String(buses.filter((b) => b.status === "active").length) },
      { label: "High-risk buses", value: String(zanaLiveCounts.highRiskBusCount) },
      { label: "Unread alerts", value: String(zanaLiveCounts.unreadAlertCount) },
    ];
    // The Analytics tab's Fuel SUBTAB is topLevelTab==="analytics", not "fuel",
    // so the fuel stat tiles used to be absent there — and Zana, told only the
    // dashboard cards, recited "Fleet buses 4…" as "the cards in front of you"
    // on a screen showing fuel spend. Include the fuel cards whenever the fuel
    // FIGURES are on screen, top tab or analytics subtab alike.
    const onAnalyticsFuel = topLevelTab === "analytics" && (zanaAnalyticsSubTab || "fuel") === "fuel";
    const onFuel = showFuelReportFullScreen || topLevelTab === "fuel" || onAnalyticsFuel;
    if (onFuel) {
      cards.push(
        { label: `Fuel spend (last ${fuelCostDays}d)`, value: `Ksh ${Math.round(fuelStats.totalCost30).toLocaleString()}` },
        { label: "Refuels logged", value: String(fuelStats.refuelCount30) },
        { label: "Buses needing refuel", value: String(fuelStats.needsRefuelCount) },
      );
    }
    if (topLevelTab === "analytics") {
      const visible = zanaAnalyticsSubTab || "fuel";
      cards.push({
        label: "Visible analytics tab",
        value: visible === "advanced"
          ? "Zana Mode"
          : `${visible.charAt(0).toUpperCase()}${visible.slice(1)}`,
      });
    }
    /* Bus detail — mirror the fuel tile the owner is reading so Zana never
       quotes 8 km/L when the screen says 5 km/L target · Need data. */
    if (detailView && selectedBus) {
      if (selectedBus.benchmarkKmPerL != null && selectedBus.benchmarkKmPerL > 0) {
        cards.push({
          label: "Fuel benchmark (this bus)",
          value: `${selectedBus.benchmarkKmPerL} km/L target`,
        });
      } else {
        cards.push({ label: "Fuel benchmark (this bus)", value: "Not set" });
      }
      cards.push({
        label: "Refuel events logged (this bus)",
        value: String(fleetRefuelCountByBusId[selectedBus.id] ?? 0),
      });
      cards.push({
        label: "Fuel efficiency score (this bus)",
        value: selectedBus.benchmarkKmPerL
          ? "Not scored yet — see the Fuel & Efficiency card for why"
          : "Set a km/L benchmark first",
      });
    }
    const focus: { type: "bus"; id: string }[] = [];
    const selectedPlate = selectedBus?.plate ? String(selectedBus.plate) : "";
    // A selected bus is current focus only on the actual detail page. Every
    // other screen gets its own visible rows below.
    if (zanaPageContext.entityType === "bus" && selectedPlate) {
      focus.push({ type: "bus", id: selectedPlate });
    }
    // The unread-alert buses are ON SCREEN only where alerts are shown — High
    // Risk and the Dashboard's red card. They used to be sent as focus on every
    // page, so on the Fuel Report Zana said she was "pointing at" the alert
    // buses while the owner looked at a fuel chart (2026-10-26).
    const alertsOnScreen = zanaPageContext.page === "highRiskAlerts" || topLevelTab === "dashboard";
    if (alertsOnScreen) {
      for (const plate of zanaLiveCounts.unreadBusIds.map(plateOf)) {
        if (plate && !focus.some((f) => f.id === plate)) {
          focus.push({ type: "bus", id: plate });
        }
        if (focus.length >= 12) break;
      }
    }
    // On Analytics, name the SUBTAB in the page so the model knows fuel vs
    // routes vs drivers vs incidents — otherwise "why is this high?" on the
    // Drivers tab reads as a fleet-wide question.
    const page = topLevelTab === "analytics"
      ? `analytics:${zanaAnalyticsSubTab || "fuel"}`
      : zanaPageContext.page;
    return {
      ...zanaPageContext,
      page,
      cards,
      focus,
      ...(onFuel ? { window: `last ${fuelCostDays} days` } : {}),
    };
  }, [zanaPageContext, zanaLiveCounts, buses, showFuelReportFullScreen, topLevelTab, zanaAnalyticsSubTab, fuelCostDays, fuelStats, selectedBus, detailView, fleetRefuelCountByBusId]);

  // Takeover gets the same rich context as Ask — cards + focus are what make
  // "I've talked about this, it points to this" possible.
  const takeover = useZanaTakeover({
    orgCode: fleetOrgCode,
    onTab: handleZanaTakeoverTab,
    onExecuteIntent: handleZanaIntent,
    pageContext: zanaAskContext,
    liveCounts: zanaLiveCounts,
  });
  takeoverRef.current = takeover.start;
  jumpRef.current = takeover.jump;

  const fleetActive = buses.filter((b) => b.status === "active").length;

  const saveSchedule = useCallback(async () => {
    if (!selectedBus) return;
    try {
      await updateDoc(doc(db, "buses", selectedBus.id), {
        morningPickupTime: mTime || null,
        eveningPickupTime: eTime || null,
        updatedAt: serverTimestamp(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
    }
  }, [selectedBus, mTime, eTime]);

  const saveBusDetails = useCallback(async () => {
    if (!selectedBus || detailsSaving) return;
    setDetailsSaving(true);
    try {
      const updates = buildFuelBenchmarkUpdates(
        fuelCapacityInput,
        benchmarkInput,
        busYearInput,
        odometerUnit,
      );
      await updateDoc(doc(db, "buses", selectedBus.id), updates);
      customToast.show("Saved", "success");
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
      customToast.show("Failed to save. Check your permissions or try again.", "error");
    } finally {
      setDetailsSaving(false);
    }
  }, [selectedBus, detailsSaving, fuelCapacityInput, benchmarkInput, busYearInput, odometerUnit]);

  const handleDeleteBus = useCallback(async (bus?: Bus | null) => {
    const target = bus ?? selectedBus;
    if (!target || busDeleteBusy) return;
    setBusDeleteBusy(true);
    try {
      const batch = writeBatch(db);
      for (const driverId of target.drivers) {
        batch.update(doc(db, "users", driverId), {
          busId: null,
          isBusOwner: null,
          busNickname: null,
          isLoggedOut: true,
          logoutReason: "bus_deleted",
          logoutTimestamp: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      const parentIds = [...new Set(target.children.map((c) => c.parentId).filter(Boolean))] as string[];
      for (const parentId of parentIds) {
        const parentRef = doc(db, "users", parentId);
        const parentSnap = await getDoc(parentRef);
        if (!parentSnap.exists()) continue;
        const data = parentSnap.data();
        const children = (data.children as Record<string, unknown>[] || []).map((c: Record<string, unknown>) =>
          (c as { busId?: string }).busId === target.id ? { ...c, busId: null } : c
        );
        const busChildren = { ...(data.busChildren as Record<string, string> || {}) };
        Object.keys(busChildren).forEach((k) => { if (busChildren[k] === target.id) delete busChildren[k]; });
        batch.update(parentRef, { children, busChildren, updatedAt: serverTimestamp() });
      }
      await batch.commit();
      await updateDoc(doc(db, "buses", target.id), { isActive: false, updatedAt: serverTimestamp() });
      setSelectedBus((prev) => (prev?.id === target.id ? null : prev));
      setDetailView(false);
      setDetailReturnTo(null);
      customToast.show(`${target.name} removed from the fleet`, "success");
    } catch (e) {
      console.error(e);
      alert("Failed to delete bus");
    } finally {
      setDeleteBusToConfirm(null);
      setBusDeleteBusy(false);
    }
  }, [selectedBus, busDeleteBusy]);

  // Resend an invite to a still-pending email (supersedes the old one + fires a fresh link).
  const handleResendInvite = async (email: string, role: string) => {
    if (!fleetOrgCode || !currentUser || resendingEmail) return;
    setResendingEmail(email);
    try {
      const result = await sendFleetInvite(
        email, fleetOrgCode, orgDisplayName || fleetOrgCode,
        role as 'parent' | 'bus_driver', currentUser.uid,
        { isTester: false, isPilot: orgIsPilot }
      );
      customToast.show(result.ok ? `Invite re-sent to ${email}` : (result.error || 'Could not resend invite'), result.ok ? 'success' : 'error');
    } catch {
      customToast.show('Could not resend invite', 'error');
    }
    setResendingEmail(null);
  };

  const handleBulkInvite = async () => {
    if (!inviteEmails.length || !fleetOrgCode || !currentUser || bulkInviting) return;
    setBulkInviting(true);

    // Skip emails already pending for this role — avoids duplicate invites.
    const pendingSet = new Set(sentInvites.filter(i => !i.superseded && !i.used && i.role === inviteRole).map(i => i.email));
    const toSend = inviteEmails.filter(e => !pendingSet.has(e));
    const skipped = inviteEmails.length - toSend.length;

    /* Which ones failed, not just how many. Without the list there is nothing
       to put back in the box, and the code below cleared the WHOLE box on any
       success — so a run where three sent and two failed reported "3 invites
       sent" and then deleted the two addresses that still needed sending
       (review, 09-06). */
    const failedEmails: string[] = [];
    let sent = 0, failed = 0, lastError = '';
    let stoppedEarly = false;
    for (const email of toSend) {
      const result = await sendFleetInvite(
        email, fleetOrgCode, orgDisplayName || fleetOrgCode,
        inviteRole, currentUser.uid,
        { isTester: false, isPilot: orgIsPilot }
      );
      if (result.ok) {
        sent++;
      } else {
        failed++;
        failedEmails.push(email);
        lastError = result.error || '';
        // Rate-limited: stop early and tell them clearly to wait.
        if (lastError.toLowerCase().includes('too many') || lastError.toLowerCase().includes('too-many-requests')) {
          customToast.show('Too many invites sent right now — please wait a few minutes and try again.', 'error');
          // Everything after this point was never attempted — keep those too.
          const idx = toSend.indexOf(email);
          if (idx >= 0) failedEmails.push(...toSend.slice(idx + 1));
          stoppedEarly = true;
          break;
        }
      }
    }
    setBulkInviting(false);

    if (sent > 0) {
      // Say what actually happened. "3 invites sent" while two silently failed
      // is a report the reader cannot act on.
      customToast.show(
        failedEmails.length
          ? `${sent} invite${sent > 1 ? 's' : ''} sent — ${failedEmails.length} could not be sent and ${failedEmails.length > 1 ? 'are' : 'is'} still in the box.`
          : `${sent} invite${sent > 1 ? 's' : ''} sent`,
        failedEmails.length ? 'info' : 'success',
      );
      // Keep the ones that failed so they can be retried; clear only the rest.
      setInviteEmails(failedEmails);
      if (!failedEmails.length) setInviteDraft('');
    }
    if (skipped > 0 && sent === 0 && failed === 0) {
      customToast.show(skipped === 1 ? 'That email is already invited — use Resend below.' : `${skipped} of those are already invited — use Resend below.`, 'info');
      setInviteEmails([]);
    } else if (skipped > 0) {
      customToast.show(`${skipped} already-invited email${skipped > 1 ? 's' : ''} skipped — use Resend for those.`, 'info');
    }
    if (failed > 0 && sent === 0 && !stoppedEarly) {
      setInviteEmails(failedEmails);
      customToast.show(lastError || `${failed} invite${failed > 1 ? 's' : ''} failed to send. Please try again.`, 'error');
    } else if (stoppedEarly && sent === 0) {
      setInviteEmails(failedEmails);
    }
  };

  const sendAdminInviteTo = async (email: string): Promise<{ ok: boolean; error?: string }> => {
    if (!email || !fleetOrgCode) return { ok: false, error: "No organization." };
    const { isPaymentsConfigured, sendAdminInviteEmailApi } = await import("../services/paymentsApi");
    if (!isPaymentsConfigured()) return { ok: false, error: "Email service not configured." };
    try {
      const orgQ = query(collection(db, "organizations"), where("code", "==", fleetOrgCode), limit(1));
      const orgSnap = await getDocs(orgQ);
      if (orgSnap.docs[0]) {
        await updateDoc(doc(db, "organizations", orgSnap.docs[0].id), {
          pendingAdminInvites: arrayUnion(email),
          updatedAt: new Date(),
        });
      }
    } catch {
      // non-fatal — invite email still goes out
    }
    const settings = adminInviteActionSettings(fleetOrgCode);
    const branded = await sendAdminInviteEmailApi({
      orgCode: fleetOrgCode,
      email,
      continueUrl: settings.url,
    });
    if (!branded.ok) return { ok: false, error: branded.error || "Failed to send invite." };
    return { ok: true };
  };

  const handleInviteAdmin = async () => {
    const email = inviteAdminEmail.trim().toLowerCase();
    if (!email || !fleetOrgCode || adminInviteSending) return;
    setAdminInviteSending(true);
    setAdminInviteStatus("idle");
    setAdminInviteError("");
    try {
      const result = await sendAdminInviteTo(email);
      if (!result.ok) {
        setAdminInviteStatus("error");
        setAdminInviteError(result.error || "Failed to send invite.");
        return;
      }
      setAdminInviteStatus("success");
      setInviteAdminEmail("");
    } catch (e: unknown) {
      setAdminInviteStatus("error");
      setAdminInviteError((e as Error)?.message || "Failed to send invite.");
    } finally {
      setAdminInviteSending(false);
    }
  };

  const handleResendAdminInvite = async (email: string) => {
    const addr = email.trim().toLowerCase();
    if (!addr || !fleetOrgCode || resendingEmail) return;
    setResendingEmail(addr);
    try {
      const result = await sendAdminInviteTo(addr);
      customToast.show(
        result.ok ? `Invite re-sent to ${addr}` : (result.error || "Could not resend invite"),
        result.ok ? "success" : "error",
      );
    } catch (e: unknown) {
      customToast.show((e as Error)?.message || "Could not resend invite", "error");
    } finally {
      setResendingEmail(null);
    }
  };

  const handleRemoveAccount = useCallback(async (user: { id: string; email: string; displayName: string; role: string; busId?: string | null }) => {
    if (user.id === currentUser?.uid) {
      alert("You cannot remove your own account.");
      return;
    }
    if (!window.confirm(`Remove ${user.displayName || user.email} from the fleet? They will be logged out and lose access.`)) return;
    setAccountActionBusy(true);
    try {
      const userRef = doc(db, "users", user.id);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        alert("User not found.");
        return;
      }
      const data = userSnap.data() as Record<string, unknown>;
      const isTempConductor = data.temporaryTripConductor === true
        || String(user.id).startsWith("tripc_")
        || !!(data.username && !user.email);

      // Temp trip conductors: expire bcrypt login + strip bus via backend (Admin SDK).
      // If that succeeds, do NOT follow with a client update — schoolCode is already
      // cleared and fleet-admin rules would fail (user no longer "in" the org).
      if (isTempConductor && fleetOrgCode && isTripsApiConfigured()) {
        try {
          const r = await disableTripConductor({
            orgCode: fleetOrgCode,
            uid: user.id,
            username: typeof data.username === "string" ? data.username : undefined,
          });
          if (r.disabled !== false) {
            customToast.show(`${user.displayName || user.email || "Conductor"} removed from fleet`, "success");
            setSelectedAccount(null);
            return;
          }
        } catch (apiErr) {
          /* 2026-09-15: a 403 is the server refusing this admin (the strict org
             gate), not an outage. The Firestore fallback below cannot expire the
             conductor's login record, so falling back left a live login and hid
             the reason — usually "open the admin invite sent to your email". */
          if ((apiErr as { status?: number })?.status === 403) {
            customToast.show((apiErr as Error).message || "You are not allowed to remove this conductor.", "error");
            return;
          }
          console.warn("[Accounts] conductor-disable API failed, falling back to Firestore:", apiErr);
        }
      }

      const batch = writeBatch(db);
      const updates: Record<string, unknown> = {
        isLoggedOut: true,
        logoutReason: "removed_by_fleet_admin",
        logoutTimestamp: serverTimestamp(),
        updatedAt: serverTimestamp(),
        schoolCode: null,
        organizationCode: null,
        institution: null,
        orgCode: null,
        activeSchoolTripId: null,
        suspended: true,
        suspendedAt: serverTimestamp(),
        suspendReason: "Removed by fleet admin",
      };
      if (isTempConductor) {
        updates.temporaryTripConductor = false;
        updates.tripConductorAccessUntil = Timestamp.fromMillis(Date.now() - 1000);
      }
      const busId = (user.busId ?? data.busId) as string | undefined;
      if (busId) {
        updates.busId = null;
        updates.isBusOwner = null;
        updates.busNickname = null;
        const bus = buses.find((b) => b.id === busId);
        if (bus) {
          const newDriverIds = bus.drivers.filter((id) => id !== user.id);
          batch.update(doc(db, "buses", busId), {
            driverIds: newDriverIds,
            updatedAt: serverTimestamp(),
          });
        } else {
          // Bus may be hired/temp and not in local list — still try direct update.
          try {
            const busSnap = await getDoc(doc(db, "buses", busId));
            if (busSnap.exists()) {
              const ids = ((busSnap.data().driverIds as string[]) || []).filter((id) => id !== user.id);
              batch.update(doc(db, "buses", busId), {
                driverIds: ids,
                temporaryConductorUid: null,
                updatedAt: serverTimestamp(),
              });
            }
          } catch (busErr) {
            console.warn("[Accounts] bus unassign skipped:", busErr);
          }
        }
      }
      // Strip this parent's children off every bus roster too. The roster row
      // freezes parentName at add-time and nothing below rewrites
      // buses/{id}.children[] — so without this, every removed parent left
      // permanent ghost children on the driver's morning list, naming a person
      // the admin could no longer see or restore (Grace Mwangi, 2026-09-04).
      await Promise.all(buses
        .filter((b) => (b.children || []).some((c) => c.parentId === user.id))
        .map(async (b) => {
          try {
            // Fresh read on purpose — the local copy lags the driver's
            // per-child status writes; parallel so N buses cost one round trip.
            const rosterSnap = await getDoc(doc(db, "buses", b.id));
            if (!rosterSnap.exists()) return;
            const rawRoster = (rosterSnap.data().children as Record<string, unknown>[]) || [];
            const keptRoster = rawRoster.filter(
              (c) => (c as { parentId?: string }).parentId !== user.id,
            );
            if (keptRoster.length !== rawRoster.length) {
              batch.update(doc(db, "buses", b.id), {
                children: keptRoster,
                updatedAt: serverTimestamp(),
              });
            }
          } catch (rosterErr) {
            console.warn("[Accounts] roster strip skipped for", b.id, rosterErr);
          }
        }));

      const busChildren = (data.busChildren as Record<string, string>) || {};
      const ourBusIds = new Set(buses.map((b) => b.id));
      const hasBusLink = Object.values(busChildren).some((bid) => ourBusIds.has(bid));
      if (hasBusLink) {
        const children = (data.children as Record<string, unknown>[]) || [];
        const newChildren = children.map((c) => {
          const bid = (c as { busId?: string }).busId;
          if (bid && ourBusIds.has(bid)) return { ...c, busId: null };
          return c;
        });
        const newBusChildren = { ...busChildren };
        Object.keys(newBusChildren).forEach((k) => {
          if (ourBusIds.has(newBusChildren[k])) delete newBusChildren[k];
        });
        updates.children = newChildren;
        updates.busChildren = newBusChildren;
      }
      batch.update(userRef, updates as Record<string, import("firebase/firestore").FieldValue | object | null | undefined>);
      await batch.commit();
      customToast.show(`${user.displayName || user.email || "Account"} removed from fleet`, "success");
      setSelectedAccount(null);
    } catch (e) {
      console.error(e);
      alert("Failed to remove account. If this is a trip conductor, wait for a backend redeploy and try again.");
    } finally {
      setAccountActionBusy(false);
    }
  }, [buses, currentUser?.uid, fleetOrgCode]);

  const softDeleteEmptyBus = useCallback(async (busId: string) => {
    const busRef = doc(db, "buses", busId);
    const snap = await getDoc(busRef);
    if (!snap.exists()) return;
    const data = snap.data();
    const children = (data.children as Record<string, unknown>[]) || [];
    if (children.length > 0) {
      alert("Cannot delete: this bus still has students on the server. Refresh and try again.");
      return;
    }
    const batch = writeBatch(db);
    const driverIds = (data.driverIds as string[]) || [];
    for (const driverId of driverIds) {
      batch.update(doc(db, "users", driverId), {
        busId: null,
        isBusOwner: null,
        busNickname: null,
        isLoggedOut: true,
        logoutReason: "bus_deleted",
        logoutTimestamp: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
    await batch.commit();
    await updateDoc(busRef, { isActive: false, updatedAt: serverTimestamp() });
    setSelectedBus((prev) => (prev?.id === busId ? null : prev));
    setDetailView(false);
    setDetailReturnTo(null);
    customToast.show("Bus removed from the fleet", "success");
  }, []);

  /** Empty bus: set status only. Otherwise open intro, then reassignment modal. */
  const openReassignIntro = useCallback(async (bus: Bus, mode: "maintenance" | "for_hire" | "suspended" | "delete_bus") => {
    if (bus.children.length === 0) {
      const msg =
        mode === "for_hire"
          ? `Mark "${bus.name}" for hire? This bus has no students on board.`
          : mode === "delete_bus"
            ? `Delete "${bus.name}"? This bus has no students on board.`
            : `Place "${bus.name}" in suspend/maintenance? This bus has no students on board.`;
      if (!window.confirm(msg)) return;
      if (mode === "delete_bus") {
        await softDeleteEmptyBus(bus.id);
        return;
      }
      const status: Bus["status"] =
        mode === "for_hire" ? "for_hire" : mode === "suspended" ? "suspended" : "maintenance";
      try {
        await updateDoc(doc(db, "buses", bus.id), { status, updatedAt: serverTimestamp() });
        if (fleetOrgCode) {
          void logBusStatusEvent({
            busId: bus.id,
            orgCode: fleetOrgCode,
            plate: bus.plate,
            busName: bus.name,
            fromStatus: bus.status,
            toStatus: status,
            byUid: currentUser?.uid ?? null,
            byEmail: currentUser?.email ?? null,
            source: "dashboard",
            childCount: 0,
          });
        }
        setBuses((prev) => prev.map((x) => (x.id === bus.id ? { ...x, status } : x)));
        setSelectedBus((prev) => (prev?.id === bus.id ? { ...prev, status } : prev));
      } catch (e) {
        console.error(e);
        alert("Failed to update bus");
      }
      return;
    }
    setReassignIntro({ bus, mode });
  }, [softDeleteEmptyBus, fleetOrgCode, currentUser?.uid, currentUser?.email]);

  zanaLifecycleHelpersRef.current = { openReassignIntro, softDeleteEmptyBus };

  const confirmReassignIntro = useCallback(() => {
    if (!reassignIntro) return;
    const { bus, mode } = reassignIntro;
    const init: Record<string, string> = {};
    for (const c of bus.children) init[c.id] = "";
    setReassignTargets(init);
    setReassignSelection(new Set(bus.children.map((c) => c.id)));
    setReassignFilter("");
    setReassignBulkDest("");
    setReassignHireNickname("");
    setReassignHirePlate("");
    setReassignHireExpiresLocal("");
    setReassignHireCondEmail("");
    setReassignHireCondPassword("");
    setReassignHireCondName("");
    setReassignModal({ bus, mode });
    setReassignIntro(null);
  }, [reassignIntro]);

  const sendFleetParentNotification = useCallback(
    async (parentIds: string[], title: string, body: string, notificationType: string) => {
      if (!fleetOrgCode || parentIds.length === 0) return;
      try {
        const fn = httpsCallable<
          { parentIds: string[]; title: string; body: string; schoolCode: string; notificationType: string },
          { sent: number; total: number }
        >(functionsEu, "notifyFleetRosterParents");
        await fn({ parentIds, title, body, schoolCode: fleetOrgCode, notificationType });
      } catch (e) {
        console.warn("notifyFleetRosterParents failed", e);
      }
    },
    [fleetOrgCode]
  );

  /**
   * Bring a bus back into service AND restore the roster that was parked when
   * it went into maintenance / suspend / hire. Activate used to flip status only,
   * so kids stayed on the host bus forever — Gabriel 2026-08-04.
   */
  /**
   * Close out one incident report from the bus page — the door the High Risk
   * screen has promised since the redesign ("it clears when the incident is
   * resolved on the bus page") without there being one. Until 2026-08-04 the
   * ONLY resolve path was Zana's `resolve_incidents` action, and her chat
   * router swallows the phrase that should reach it (bug 78) — so an owner who
   * didn't stumble on the right words could never clear the red count at all.
   *
   * Writes EXACTLY the four fields the busIssueReports rule allows
   * (`hasOnly(['resolved','resolvedAt','resolvedBy','resolvedNote'])`) — add a
   * fifth here and the rules gate rejects the whole update. Errors surface in
   * an alert, never a silent catch: a denied resolve looking like a dead
   * button is this morning's bug wearing a different shirt.
   */
  const handleResolveIncident = useCallback(async (reportId: string) => {
    if (!window.confirm("Close out this incident? It leaves High Risk and the bus's open count — the report itself stays on file.")) return;
    try {
      await updateDoc(doc(db, "busIssueReports", reportId), {
        resolved: true,
        resolvedAt: serverTimestamp(),
        resolvedBy: currentUser?.uid || null,
        resolvedNote: "Closed from the bus page",
      });
      setFleetIssueReports((prev) => prev.map((r) => (r.id === reportId ? { ...r, resolved: true } : r)));
    } catch (e) {
      alert("Could not close the incident: " + (e as Error).message);
    }
  }, [currentUser?.uid]);

  const handleActivateBus = useCallback(async (bus: Bus) => {
    try {
      const sourceRef = doc(db, "buses", bus.id);
      const snap = await getDoc(sourceRef);
      if (!snap.exists()) throw new Error("Bus not found");
      const data = snap.data() as Record<string, unknown>;
      const parked = Array.isArray(data.parkedRoster)
        ? ([...(data.parkedRoster as Record<string, unknown>[])])
        : [];
      const moves =
        data.parkedRosterMoves && typeof data.parkedRosterMoves === "object"
          ? ({ ...(data.parkedRosterMoves as Record<string, string>) })
          : ({} as Record<string, string>);

      if (parked.length === 0) {
        await updateDoc(sourceRef, {
          status: "active",
          updatedAt: serverTimestamp(),
          parkedRoster: deleteField(),
          parkedRosterMoves: deleteField(),
        });
        if (fleetOrgCode) {
          void logBusStatusEvent({
            busId: bus.id,
            orgCode: fleetOrgCode,
            plate: bus.plate,
            busName: bus.name,
            fromStatus: bus.status,
            toStatus: "active",
            byUid: currentUser?.uid ?? null,
            byEmail: currentUser?.email ?? null,
            source: "dashboard",
            childCount: 0,
          });
        }
        setBuses((prev) =>
          prev.map((b) => (b.id === bus.id ? { ...b, status: "active" as Bus["status"] } : b)),
        );
        setSelectedBus((prev) =>
          prev?.id === bus.id ? { ...prev, status: "active" as Bus["status"] } : prev,
        );
        customToast.show(`${bus.name} is active again`, "success");
        return;
      }

      const removeByHost = new Map<string, Set<string>>();
      for (const [cid, hostId] of Object.entries(moves)) {
        if (!cid || !hostId || hostId === bus.id) continue;
        const set = removeByHost.get(hostId) ?? new Set<string>();
        set.add(cid);
        removeByHost.set(hostId, set);
      }

      const batch = writeBatch(db);
      for (const [hostId, cids] of removeByHost) {
        const hRef = doc(db, "buses", hostId);
        const hSnap = await getDoc(hRef);
        if (!hSnap.exists()) continue;
        const existing = (hSnap.data().children as Record<string, unknown>[]) || [];
        const next = existing.filter((c) => !cids.has(String(c.childId || "")));
        batch.update(hRef, { children: next, updatedAt: serverTimestamp() });
      }

      const restored = parked.map((c) => ({
        ...c,
        status: "not_picked",
        lastUpdated: Timestamp.now(),
        timestamp: null,
      }));
      batch.update(sourceRef, {
        status: "active",
        children: restored,
        parkedRoster: deleteField(),
        parkedRosterMoves: deleteField(),
        updatedAt: serverTimestamp(),
      });

      const parentIdSet = new Set<string>();
      for (const raw of parked) {
        const pid = raw.parentId as string | undefined;
        if (pid) parentIdSet.add(pid);
      }
      for (const parentId of parentIdSet) {
        const parentRef = doc(db, "users", parentId);
        const pSnap = await getDoc(parentRef);
        if (!pSnap.exists()) continue;
        const pdata = pSnap.data() as Record<string, unknown>;
        let pch = Array.isArray(pdata.children) ? [...(pdata.children as Record<string, unknown>[])] : [];
        const busChildren = { ...((pdata.busChildren as Record<string, string>) || {}) };
        for (const raw of parked) {
          if ((raw.parentId as string) !== parentId) continue;
          const cid = (raw.childId as string) || "";
          const cname = String(raw.childName || "").trim();
          if (cname) busChildren[cname] = bus.id;
          pch = pch.map((row) => {
            const matchId = (row.childId as string) === cid;
            const matchName = cname.length > 0 && (row.childName as string) === cname;
            if (matchId || matchName) return { ...row, busId: bus.id };
            return row;
          });
        }
        batch.update(parentRef, { children: pch, busChildren, updatedAt: serverTimestamp() });
      }

      await batch.commit();

      if (fleetOrgCode) {
        void logBusStatusEvent({
          busId: bus.id,
          orgCode: fleetOrgCode,
          plate: bus.plate,
          busName: bus.name,
          fromStatus: bus.status,
          toStatus: "active",
          byUid: currentUser?.uid ?? null,
          byEmail: currentUser?.email ?? null,
          source: "dashboard",
          childCount: restored.length,
        });
      }

      const parentIdsNotify = [
        ...new Set(parked.map((c) => c.parentId as string | undefined).filter(Boolean)),
      ] as string[];
      if (parentIdsNotify.length > 0) {
        void sendFleetParentNotification(
          parentIdsNotify,
          "Bus back in service",
          "Your child’s usual bus is active again. Open PEAR to confirm their assignment.",
          "bus_roster_change",
        );
      }

      const toBusChild = (c: Record<string, unknown>): BusChild => ({
        id: String(c.childId || ""),
        name: String(c.childName || ""),
        parentPhone: String(c.parentPhone || ""),
        status: String(c.status || "not_picked"),
        parentId: (c.parentId as string) || undefined,
        classLabel: (c.classLabel as string) || undefined,
        transportSuspended: c.transportSuspended === true,
      });

      setBuses((prev) =>
        prev.map((b) => {
          if (b.id === bus.id) {
            return {
              ...b,
              status: "active" as Bus["status"],
              children: restored.map(toBusChild),
            };
          }
          const cids = removeByHost.get(b.id);
          if (!cids) return b;
          return { ...b, children: b.children.filter((c) => !cids.has(c.id)) };
        }),
      );
      setSelectedBus((prev) => {
        if (!prev) return prev;
        if (prev.id === bus.id) {
          return {
            ...prev,
            status: "active" as Bus["status"],
            children: restored.map(toBusChild),
          };
        }
        const cids = removeByHost.get(prev.id);
        if (!cids) return prev;
        return { ...prev, children: prev.children.filter((c) => !cids.has(c.id)) };
      });

      customToast.show(
        `${bus.name} is active again — ${restored.length} student${restored.length === 1 ? "" : "s"} restored`,
        "success",
      );
    } catch (e) {
      console.error(e);
      alert("Failed to activate bus: " + (e as Error).message);
    }
  }, [sendFleetParentNotification, fleetOrgCode, currentUser?.uid, currentUser?.email]);

  const dismissFleetAlert = useCallback(async (alertId: string) => {
    try {
      await updateDoc(doc(db, "fleetAlerts", alertId), { read: true });
    } catch (e) {
      console.error(e);
      alert("Could not dismiss alert.");
    }
  }, []);

  const handleReassignStudentsAndSetBusStatus = useCallback(async () => {
    if (!reassignModal || !fleetOrgCode) return;
    const { bus: sourceBus, mode } = reassignModal;
    const missing = sourceBus.children.filter((c) => !reassignTargets[c.id]?.trim());
    if (missing.length) {
      alert("Assign every student to a fleet bus or to a new hired bus.");
      return;
    }

    const hiredIds: string[] = [];
    const fleetByChild = new Map<string, string>();
    for (const c of sourceBus.children) {
      const t = (reassignTargets[c.id] || "").trim();
      if (t === REASSIGN_HIRED_SENTINEL) hiredIds.push(c.id);
      else fleetByChild.set(c.id, t);
    }

    if (hiredIds.length > 0 && !fleetHasFullProductFeatures) {
      customToast.show(FULL_FEATURE_ONLY_MSG, "warning");
      return;
    }

    if (hiredIds.length > 0) {
      if (!reassignHireNickname.trim()) {
        alert("Enter a display name for the hired bus.");
        return;
      }
      if (!reassignHireExpiresLocal) {
        alert("Choose an expiry date and time for the hired bus.");
        return;
      }
      const expMs = new Date(reassignHireExpiresLocal).getTime();
      if (!Number.isFinite(expMs) || expMs <= Date.now()) {
        alert("Hired bus expiry must be in the future.");
        return;
      }
      if (!reassignHireCondEmail.trim() || reassignHireCondPassword.length < 8) {
        alert("Hired bus requires conductor email and password (8+ characters).");
        return;
      }
    }

    setReassignBusy(true);
    try {
      let hiredBusId: string | null = null;
      if (hiredIds.length > 0) {
        const fnCreate = httpsCallable<
          {
            schoolCode: string;
            nickname: string;
            numberPlate?: string;
            expiresAtMillis: number;
            moves: { sourceBusId: string; childId: string }[];
            conductorEmail: string;
            conductorPassword: string;
            conductorDisplayName: string;
          },
          { busId: string; conductorUid: string | null; email: string | null }
        >(functionsEu, "createTemporaryHireBus");
        const hireRes = await fnCreate({
          schoolCode: fleetOrgCode,
          nickname: reassignHireNickname.trim(),
          numberPlate: reassignHirePlate.trim() || undefined,
          expiresAtMillis: new Date(reassignHireExpiresLocal).getTime(),
          moves: hiredIds.map((childId) => ({ sourceBusId: sourceBus.id, childId })),
          conductorEmail: reassignHireCondEmail.trim(),
          conductorPassword: reassignHireCondPassword,
          conductorDisplayName: reassignHireCondName.trim() || "Temporary conductor",
        });
        hiredBusId = hireRes.data?.busId ?? null;
      }

      const sourceRef = doc(db, "buses", sourceBus.id);
      const sourceSnap = await getDoc(sourceRef);
      if (!sourceSnap.exists()) throw new Error("Source bus not found");
      const rawChildren = (sourceSnap.data().children as Record<string, unknown>[]) || [];

      const addsByTarget = new Map<string, Record<string, unknown>[]>();
      for (const raw of rawChildren) {
        const cid = (raw.childId as string) || "";
        const tid = fleetByChild.get(cid);
        if (!tid) continue;
        const moved = {
          ...raw,
          status: "not_picked",
          lastUpdated: Timestamp.now(),
          timestamp: null,
        };
        const list = addsByTarget.get(tid) ?? [];
        list.push(moved);
        addsByTarget.set(tid, list);
      }

      const targetSnapshots = new Map<string, Record<string, unknown>[]>();
      for (const tid of addsByTarget.keys()) {
        const tSnap = await getDoc(doc(db, "buses", tid));
        if (!tSnap.exists()) throw new Error("Target bus not found");
        const existing = (tSnap.data().children as Record<string, unknown>[]) || [];
        targetSnapshots.set(tid, existing);
      }

      const parkedRosterMoves: Record<string, string> = {};
      for (const [cid, tid] of fleetByChild) parkedRosterMoves[cid] = tid;
      if (hiredBusId) {
        for (const cid of hiredIds) parkedRosterMoves[cid] = hiredBusId;
      }

      const batch = writeBatch(db);
      const isDelete = mode === "delete_bus";
      const nextStatus: Bus["status"] =
        mode === "maintenance" ? "maintenance" : mode === "suspended" ? "suspended" : "for_hire";

      if (isDelete) {
        batch.update(sourceRef, {
          children: [],
          updatedAt: serverTimestamp(),
          parkedRoster: deleteField(),
          parkedRosterMoves: deleteField(),
        });
      } else {
        // Park the roster so Activate can pull them back.
        batch.update(sourceRef, {
          children: [],
          status: nextStatus,
          parkedRoster: rawChildren,
          parkedRosterMoves,
          updatedAt: serverTimestamp(),
        });
      }

      for (const [tid, adds] of addsByTarget) {
        const existing = targetSnapshots.get(tid) ?? [];
        const newIds = new Set(adds.map((c) => String(c.childId || "")));
        const merged = [...existing.filter((c) => !newIds.has(String(c.childId || ""))), ...adds];
        batch.update(doc(db, "buses", tid), { children: merged, updatedAt: serverTimestamp() });
      }

      const parentIdSet = new Set<string>();
      for (const raw of rawChildren) {
        const pid = raw.parentId as string | undefined;
        if (pid) parentIdSet.add(pid);
      }
      for (const parentId of parentIdSet) {
        const parentRef = doc(db, "users", parentId);
        const pSnap = await getDoc(parentRef);
        if (!pSnap.exists()) continue;
        const pdata = pSnap.data() as Record<string, unknown>;
        let pch = Array.isArray(pdata.children) ? [...(pdata.children as Record<string, unknown>[])] : [];
        const busChildren = { ...((pdata.busChildren as Record<string, string>) || {}) };
        for (const raw of rawChildren) {
          if ((raw.parentId as string) !== parentId) continue;
          const cid = (raw.childId as string) || "";
          const cname = String(raw.childName || "").trim();
          const tid = fleetByChild.get(cid);
          if (!tid) continue;
          if (cname) busChildren[cname] = tid;
          pch = pch.map((row) => {
            const matchId = (row.childId as string) === cid;
            const matchName = cname.length > 0 && (row.childName as string) === cname;
            if (matchId || matchName) return { ...row, busId: tid };
            return row;
          });
        }
        batch.update(parentRef, { children: pch, busChildren, updatedAt: serverTimestamp() });
      }

      await batch.commit();

      if (!isDelete && fleetOrgCode) {
        void logBusStatusEvent({
          busId: sourceBus.id,
          orgCode: fleetOrgCode,
          plate: sourceBus.plate,
          busName: sourceBus.name,
          fromStatus: sourceBus.status,
          toStatus: nextStatus,
          byUid: currentUser?.uid ?? null,
          byEmail: currentUser?.email ?? null,
          source: "dashboard",
          childCount: rawChildren.length,
        });
      }

      const parentIdsNotify = [...new Set(sourceBus.children.map((c) => c.parentId).filter(Boolean))] as string[];
      if (parentIdsNotify.length > 0) {
        const title =
          mode === "delete_bus"
            ? "Bus assignment updated"
            : mode === "for_hire"
              ? "Bus marked for hire"
              : "Bus under maintenance — your child moved";
        const body =
          mode === "delete_bus"
            ? "Your child’s school has updated their bus assignment after a bus was removed. Open PEAR to see the current bus."
            : mode === "for_hire"
              ? "Your child was moved to another bus because theirs was marked for hire. Open PEAR for details."
              : "Your child was moved while their previous bus was placed under maintenance. Open PEAR to see their new bus.";
        void sendFleetParentNotification(parentIdsNotify, title, body, "bus_roster_change");
      }

      if (isDelete) {
        await softDeleteEmptyBus(sourceBus.id);
      }

      setReassignModal(null);
      setReassignTargets({});
      setReassignSelection(new Set());
      setReassignFilter("");
      setReassignBulkDest("");
      setReassignHireNickname("");
      setReassignHirePlate("");
      setReassignHireExpiresLocal("");
      setReassignHireCondEmail("");
      setReassignHireCondPassword("");
      setReassignHireCondName("");
    } catch (e) {
      console.error(e);
      alert("Failed to reassign: " + formatCallableFailure(e));
    } finally {
      setReassignBusy(false);
    }
  }, [
    reassignModal,
    reassignTargets,
    fleetOrgCode,
    reassignHireNickname,
    reassignHirePlate,
    reassignHireExpiresLocal,
    reassignHireCondEmail,
    reassignHireCondPassword,
    reassignHireCondName,
    fleetHasFullProductFeatures,
    softDeleteEmptyBus,
    sendFleetParentNotification,
    currentUser?.uid,
    currentUser?.email,
  ]);

  const handleSaveSchoolTrip = useCallback(async () => {
    if (!fleetOrgCode) {
      alert("Fleet organisation not loaded.");
      return;
    }
    if (tripLegs.some((l) => l.kind === "hired") && !fleetHasFullProductFeatures) {
      customToast.show(FULL_FEATURE_ONLY_MSG, "warning");
      return;
    }
    if (!tripTitle.trim() || !tripDestination.trim() || !tripWhenLocal) {
      alert("Please enter trip title, destination, and departure time.");
      return;
    }
    if (tripLegs.length === 0) {
      alert("Add at least one vehicle (fleet or hired) to the trip.");
      return;
    }

    const depMs = new Date(tripWhenLocal).getTime();
    if (!Number.isFinite(depMs)) {
      alert("Invalid departure time.");
      return;
    }

    const hasHired = tripLegs.some((l) => l.kind === "hired");
    let tripEndsMs = depMs;
    if (hasHired) {
      if (!tripEndsLocal) {
        alert("Enter when the trip ends (this sets hired-bus expiry and conductor login window).");
        return;
      }
      tripEndsMs = new Date(tripEndsLocal).getTime();
      if (!Number.isFinite(tripEndsMs) || tripEndsMs <= Date.now()) {
        alert("Trip end must be in the future.");
        return;
      }
    }

    const accessFromMs = depMs - 4 * 24 * 60 * 60 * 1000;
    const accessUntilMs = tripEndsMs + 24 * 60 * 60 * 1000;

    const usedChildKeys = new Set<string>();
    for (const leg of tripLegs) {
      if (leg.childKeys.size === 0) {
        alert("Each vehicle must have at least one student selected.");
        return;
      }
      for (const k of leg.childKeys) {
        if (usedChildKeys.has(k)) {
          alert("Each student can only be on one vehicle for this trip.");
          return;
        }
        usedChildKeys.add(k);
      }
      if (leg.kind === "fleet") {
        if (!leg.busId) {
          alert("Select a fleet bus for each fleet vehicle row.");
          return;
        }
        if (!leg.useFleetCrew && (!leg.condEmail.trim() || leg.condPassword.length < 8)) {
          alert("For fleet rows where you do not use existing drivers, enter conductor email and password (8+ characters).");
          return;
        }
        for (const k of leg.childKeys) {
          const p = parseTripChildKey(k);
          if (!p || p.busId !== leg.busId) {
            alert(`Students on "${buses.find((b) => b.id === leg.busId)?.name ?? "bus"}" must be chosen from that bus’s roster only.`);
            return;
          }
        }
      } else {
        if (!leg.nickname.trim()) {
          alert("Enter a display name for each hired bus.");
          return;
        }
        if (!leg.c1Email.trim() || leg.c1Password.length < 8) {
          alert("Each hired bus needs at least one conductor with email and password (8+ characters).");
          return;
        }
        if (leg.c2Email.trim() && leg.c2Password.length < 8) {
          alert("Second conductor password must be at least 8 characters or leave second conductor blank.");
          return;
        }
      }
    }

    const childIdToParent = new Map<string, string | undefined>();
    for (const b of buses) {
      for (const c of b.children) {
        childIdToParent.set(c.id, c.parentId);
      }
    }

    setTripSaving(true);
    try {
      const departureAt = Timestamp.fromDate(new Date(tripWhenLocal));
      const tripEndsAt = Timestamp.fromMillis(tripEndsMs);
      const fnCreateHire = httpsCallable<
        {
          schoolCode: string;
          nickname: string;
          numberPlate?: string;
          expiresAtMillis: number;
          moves: { sourceBusId: string; childId: string }[];
          conductorEmail: string;
          conductorPassword: string;
          conductorDisplayName: string;
          conductorAccessFromMillis?: number;
          conductorAccessUntilMillis?: number;
        },
        { busId: string; conductorUid: string | null; email: string | null }
      >(functionsEu, "createTemporaryHireBus");

      const resolvedBusIds: string[] = [];
      const segments: { busId: string; childIds: string[]; kind: string }[] = [];
      const allChildIds: string[] = [];

      for (const leg of tripLegs) {
        if (leg.kind === "fleet") {
          const childIds = [...leg.childKeys].map((k) => parseTripChildKey(k)!.childId);
          resolvedBusIds.push(leg.busId);
          segments.push({ busId: leg.busId, childIds, kind: "fleet" });
          allChildIds.push(...childIds);
        } else {
          const moves = [...leg.childKeys].map((k) => {
            const p = parseTripChildKey(k)!;
            return { sourceBusId: p.busId, childId: p.childId };
          });
          const hireRes = await fnCreateHire({
            schoolCode: fleetOrgCode,
            nickname: leg.nickname.trim(),
            numberPlate: leg.plate.trim() || undefined,
            expiresAtMillis: tripEndsMs,
            moves,
            conductorEmail: leg.c1Email.trim(),
            conductorPassword: leg.c1Password,
            conductorDisplayName: leg.c1Name.trim() || "Trip conductor",
            conductorAccessFromMillis: accessFromMs,
            conductorAccessUntilMillis: accessUntilMs,
          });
          const newBusId = hireRes.data.busId;
          resolvedBusIds.push(newBusId);
          const childIds = moves.map((m) => m.childId);
          segments.push({ busId: newBusId, childIds, kind: "hired" });
          allChildIds.push(...childIds);
        }
      }

      const parentIds = [...new Set(allChildIds.map((id) => childIdToParent.get(id)).filter(Boolean))] as string[];

      const tripRef = await addDoc(collection(db, "schoolTrips"), {
        schoolCode: fleetOrgCode,
        title: tripTitle.trim(),
        destination: tripDestination.trim(),
        departureAt,
        tripEndsAt: hasHired ? tripEndsAt : null,
        busId: resolvedBusIds[0] ?? "",
        busIds: resolvedBusIds,
        segments,
        childIds: allChildIds,
        parentIds,
        status: "planned",
        reminderDayOfDepartureSent: false,
        reminderOneHourBeforeSent: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const fnCond = httpsCallable<
        {
          email: string;
          password: string;
          displayName: string;
          schoolCode: string;
          busId: string;
          tripId: string;
          conductorAccessFromMillis?: number;
          conductorAccessUntilMillis?: number;
        },
        { uid: string }
      >(functionsEu, "createSchoolTripConductor");

      for (let i = 0; i < tripLegs.length; i++) {
        const leg = tripLegs[i];
        const busId = resolvedBusIds[i];
        if (leg.kind === "fleet") {
          if (leg.useFleetCrew) {
            const driversForBus = await getDocs(
              query(collection(db, "users"), where("busId", "==", busId), where("role", "==", "bus_driver"))
            );
            if (!driversForBus.empty) {
              const assignBatch = writeBatch(db);
              driversForBus.forEach((d) => {
                assignBatch.update(d.ref, { activeSchoolTripId: tripRef.id, updatedAt: serverTimestamp() });
              });
              await assignBatch.commit();
            }
          }
          if (!leg.useFleetCrew) {
            await fnCond({
              email: leg.condEmail.trim(),
              password: leg.condPassword,
              displayName: leg.condName.trim() || "Trip conductor",
              schoolCode: fleetOrgCode,
              busId,
              tripId: tripRef.id,
              conductorAccessFromMillis: accessFromMs,
              conductorAccessUntilMillis: accessUntilMs,
            });
          } else if (leg.condEmail.trim() && leg.condPassword.length >= 8) {
            await fnCond({
              email: leg.condEmail.trim(),
              password: leg.condPassword,
              displayName: leg.condName.trim() || "Extra conductor",
              schoolCode: fleetOrgCode,
              busId,
              tripId: tripRef.id,
              conductorAccessFromMillis: accessFromMs,
              conductorAccessUntilMillis: accessUntilMs,
            });
          }
        } else {
          const driversForBus = await getDocs(
            query(collection(db, "users"), where("busId", "==", busId), where("role", "==", "bus_driver"))
          );
          if (!driversForBus.empty) {
            const assignBatch = writeBatch(db);
            driversForBus.forEach((d) => {
              assignBatch.update(d.ref, { activeSchoolTripId: tripRef.id, updatedAt: serverTimestamp() });
            });
            await assignBatch.commit();
          }
          if (leg.c2Email.trim() && leg.c2Password.length >= 8) {
            await fnCond({
              email: leg.c2Email.trim(),
              password: leg.c2Password,
              displayName: leg.c2Name.trim() || "Trip conductor 2",
              schoolCode: fleetOrgCode,
              busId,
              tripId: tripRef.id,
              conductorAccessFromMillis: accessFromMs,
              conductorAccessUntilMillis: accessUntilMs,
            });
          }
        }
      }

      if (parentIds.length > 0) {
        const fnNotify = httpsCallable<
          { parentIds: string[]; title: string; body: string; schoolCode: string; tripId?: string },
          { sent: number }
        >(functionsEu, "notifySchoolTripParents");
        await fnNotify({
          parentIds,
          schoolCode: fleetOrgCode,
          tripId: tripRef.id,
          title: `School trip: ${tripTitle.trim()}`,
          body: `Destination: ${tripDestination.trim()}. When: ${new Date(tripWhenLocal).toLocaleString()}.`,
        });
      }

      setShowSchoolTripPlanner(false);
      setTripTitle("");
      setTripDestination("");
      setTripWhenLocal("");
      setTripEndsLocal("");
      setTripLegs([]);
      setTripStudentTargetLegId("");
      setTripPlannerStudentFilter("");
      alert("School trip saved and parents notified.");
    } catch (e) {
      console.error(e);
      alert("Failed to save trip: " + formatCallableFailure(e));
    } finally {
      setTripSaving(false);
    }
  }, [fleetOrgCode, fleetHasFullProductFeatures, tripTitle, tripDestination, tripWhenLocal, tripEndsLocal, tripLegs, buses]);

  const openTempHireWizard = useCallback(() => {
    if (!fleetHasFullProductFeatures) {
      customToast.show(FULL_FEATURE_ONLY_MSG, "warning");
      return;
    }
    setTempHireNickname("");
    setTempHirePlate("");
    setTempHireExpiresLocal("");
    setTempHireChildKeys(new Set());
    setTempHireCondEmail("");
    setTempHireCondPassword("");
    setTempHireCondName("");
    setTempHireStudentFilter("");
    setShowTempHireWizard(true);
  }, [fleetHasFullProductFeatures]);

  const handleSaveTemporaryHireBus = useCallback(async () => {
    if (!fleetOrgCode) {
      alert("Fleet organisation not loaded.");
      return;
    }
    if (!fleetHasFullProductFeatures) {
      customToast.show(FULL_FEATURE_ONLY_MSG, "warning");
      return;
    }
    if (!tempHireNickname.trim()) {
      alert("Enter a display name for the temporary bus.");
      return;
    }
    if (!tempHireExpiresLocal) {
      alert("Choose an expiry date and time for the hire.");
      return;
    }
    const expiresMs = new Date(tempHireExpiresLocal).getTime();
    if (!Number.isFinite(expiresMs) || expiresMs <= Date.now()) {
      alert("Expiry must be in the future.");
      return;
    }
    if (tempHireChildKeys.size === 0) {
      alert("Select at least one student to place on the temporary bus.");
      return;
    }
    const moves: { sourceBusId: string; childId: string }[] = [];
    for (const key of tempHireChildKeys) {
      const tab = key.indexOf("\t");
      if (tab < 0) continue;
      moves.push({ sourceBusId: key.slice(0, tab), childId: key.slice(tab + 1) });
    }
    if (moves.length === 0) {
      alert("Invalid student selection.");
      return;
    }
    if (tempHireCondEmail.trim() && tempHireCondPassword.length < 8) {
      alert("Conductor password must be at least 8 characters, or leave conductor fields empty.");
      return;
    }
    setTempHireSaving(true);
    try {
      const keysSnapshot = new Set(tempHireChildKeys);
      const fn = httpsCallable<
        {
          schoolCode: string;
          nickname: string;
          numberPlate?: string;
          expiresAtMillis: number;
          moves: { sourceBusId: string; childId: string }[];
          conductorEmail?: string;
          conductorPassword?: string;
          conductorDisplayName?: string;
        },
        { busId: string; conductorUid: string | null; email: string | null }
      >(functionsEu, "createTemporaryHireBus");
      const res = await fn({
        schoolCode: fleetOrgCode,
        nickname: tempHireNickname.trim(),
        numberPlate: tempHirePlate.trim() || undefined,
        expiresAtMillis: expiresMs,
        moves,
        ...(tempHireCondEmail.trim() && tempHireCondPassword.length >= 8
          ? {
              conductorEmail: tempHireCondEmail.trim(),
              conductorPassword: tempHireCondPassword,
              conductorDisplayName: tempHireCondName.trim() || "Temporary driver",
            }
          : {}),
      });
      const parentIdsFound = new Set<string>();
      for (const key of keysSnapshot) {
        const tab = key.indexOf("\t");
        if (tab < 0) continue;
        const busId = key.slice(0, tab);
        const childId = key.slice(tab + 1);
        const b = buses.find((x) => x.id === busId);
        const ch = b?.children.find((c) => c.id === childId);
        if (ch?.parentId) parentIdsFound.add(ch.parentId);
      }
      void sendFleetParentNotification(
        [...parentIdsFound],
        "Temporary bus for your child",
        `Your child has been placed on temporary bus "${tempHireNickname.trim()}". Open PEAR to see their bus and journey updates.`,
        "temp_hire_bus"
      );
      setShowTempHireWizard(false);
      alert(
        `Temporary bus created (${res.data.busId}).${res.data.email ? ` Conductor login: ${res.data.email}` : ""} Parents were notified. The hire expires automatically per schedule.`
      );
    } catch (e) {
      console.error(e);
      alert("Failed: " + formatCallableFailure(e));
    } finally {
      setTempHireSaving(false);
    }
  }, [
    fleetOrgCode,
    fleetHasFullProductFeatures,
    tempHireNickname,
    tempHirePlate,
    tempHireExpiresLocal,
    tempHireChildKeys,
    tempHireCondEmail,
    tempHireCondPassword,
    tempHireCondName,
    buses,
    sendFleetParentNotification,
  ]);

  if (loading && buses.length === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f8fafc", fontFamily: "Geist, sans-serif" }}>
        <div style={{ color: "#64748b", fontSize: 15 }}>Loading fleet…</div>
      </div>
    );
  }
  if (loadError && buses.length === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f8fafc", fontFamily: "Geist, sans-serif" }}>
        <div style={{ color: "#dc2626", fontSize: 15 }}>{loadError}</div>
      </div>
    );
  }
  const bus = selectedBusWithKm ?? selectedBus;

  const busDetailsEditor = (
    <BusDetailsEditor
      tank={fuelCapacityInput}
      benchmark={benchmarkInput}
      year={busYearInput}
      unit={odometerUnit}
      saving={detailsSaving}
      saved={saved}
      onTank={setFuelCapacityInput}
      onBenchmark={setBenchmarkInput}
      onYear={setBusYearInput}
      onUnit={setOdometerUnit}
      onSave={() => { void saveBusDetails(); }}
    />
  );

  const showDetailView = detailView && selectedBus && bus;
  // High risk = buses with incidents OR buses with at least one unread fleet alert (so "Mark all as read" can reset the alert count)
  const busesWithIncidentIds = new Set(busesWithIncidents.filter((b) => b.crashes.length > 0).map((b) => b.id));
  const busesWithUnreadAlertIds = new Set(fleetAlerts.filter((a) => !a.read).map((a) => a.busId));
  const highRiskCount = new Set([...busesWithIncidentIds, ...busesWithUnreadAlertIds]).size;
  const normalCount = buses.length - highRiskCount;
  const maintenanceCount = buses.filter((b) => b.status === "maintenance" || b.status === "for_hire" || b.status === "suspended").length;

  // ── New nav shell (FleetShell) wiring ──────────────────────────────────────
  // Same destinations/handlers as the old top bar; FleetShell renders them in a
  // responsive top-bar / collapsible-sidebar shell.
  /* Every destination also drops out of the bus detail view. Previously the nav
     only set the tab, so picking Accounts or Analytics while looking at a bus
     changed the state underneath and left the same page on screen. */
  const leaveDetail = () => { setDetailView(false); setDetailReturnTo(null); };
  const navItems: FleetNavItem[] = ([
    { key: "dashboard", label: "Dashboard", onClick: () => setTopLevelTab("dashboard") },
    { key: "fleet", label: "Fleet", onClick: () => setTopLevelTab("fleet") },
    { key: "accounts", label: "Accounts", onClick: () => setTopLevelTab("accounts") },
    { key: "monthlyReport", label: "Monthly Report", onClick: () => setTopLevelTab("monthlyReport") },
    { key: "trips", label: "Trips", onClick: () => { setTopLevelTab("trips"); setTripsDetailTripId(null); } },
    { key: "liveMap", label: "Live Map", dot: true, onClick: () => setTopLevelTab("liveMap") },
    ...(premiumLocked ? [{ key: "fuel", label: "Fuel", onClick: () => setTopLevelTab("fuel") }] : []),
    { key: "fuelPrices", label: "Fuel prices", onClick: () => setTopLevelTab("fuelPrices") },
    { key: "maintenance", label: "Maintenance", onClick: () => setTopLevelTab("maintenance") },
    { key: "analytics", label: "Analytics", locked: premiumLocked, onClick: () => { setTryAnalyticsCoach(false); if (premiumLocked) { openUpgrade(); } else { setAnalyticsInitialBusId(null); setTopLevelTab("analytics"); } } },
    // Zana's own room. The dock stays for quick hops; this is where an owner
    // has the long conversation the corner pill was never sized for.
    { key: "zanaChat", label: "Ask Zana", locked: !sub.isZanaTier, onClick: () => { if (!sub.isZanaTier) { openUpgrade(); } else { setTopLevelTab("zanaChat"); } } },
  ] as FleetNavItem[]).map((it) => ({ ...it, onClick: () => { leaveDetail(); it.onClick?.(); } }));

  const returnFromDetail = () => {
    setDetailView(false);
    if (detailReturnTo === "highRisk") setShowHighRiskFullScreen(true);
    else if (detailReturnTo === "fuelReport") setShowFuelReportFullScreen(true);
    else if (detailReturnTo === "maintenance") setShowMaintenanceFullScreen(true);
    else if (detailReturnTo === "serviceMaintenance") setTopLevelTab("maintenance");
    setDetailReturnTo(null);
  };

  const detailProp = showDetailView && bus
    ? { title: bus.name, subtitle: `${bus.plate} · ${bus.school}`, onBack: returnFromDetail }
    : null;

  // The existing right-side cluster, lifted verbatim so every control keeps its
  // wiring. It already self-adjusts for detail mode (search hidden, Announce shown).
  const headerRight = (
    <>
      {!showDetailView && (
        <div className="relative">
          <FaSearch
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-400 w-4 h-4"
          />
          <Input
            type="search"
            autoComplete="off"
            placeholder="Search by name, plate, or school…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="relative z-[1] w-80 pl-10 bg-gray-50 border-gray-200"
          />
        </div>
      )}
      {showDetailView && bus && (
        <Button size="sm" onClick={() => { setShowAnnounce(true); setAnnounceMsg(""); setAnnounceResult(null); }}>
          Announce
        </Button>
      )}
      {/* The bell used to render the unread dot with NO click handler — it
          flagged a problem and then went nowhere. It now opens the panel. */}
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setShowNotifications((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={showNotifications}
        title="Notifications"
      >
        <span className="fd-bellwrap">
          <FaBell className="w-5 h-5 text-gray-500" />
          {unreadAlertCount > 0 && (
            <span className="fd-belldot">{unreadAlertCount > 99 ? "99+" : unreadAlertCount}</span>
          )}
        </span>
      </Button>
      {sub.status !== 'loading' && (
        premiumLocked ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ea580c', display: 'inline-block' }} />
            Pilot · {sub.pilotDaysLeft} day{sub.pilotDaysLeft === 1 ? '' : 's'} left
          </span>
        ) : (
          <button
            onClick={() => setShowSubManager(true)}
            title={sub.isZanaTier ? 'Zana Mode included — manage subscription' : 'Manage subscription'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', border: 'none', cursor: 'pointer', background: sub.isZanaTier ? 'linear-gradient(135deg,#8a63f0,#5a34c9)' : 'linear-gradient(135deg,#2563eb,#5041BC)', color: '#fff', boxShadow: sub.isZanaTier ? '0 4px 13px rgba(111,79,240,.4)' : '0 4px 13px rgba(80,65,188,.32)' }}
          >
            <span style={{ fontSize: 11 }}>◆</span>{sub.plan === 'enterprise' ? 'Enterprise' : sub.plan === 'basic' ? 'Basic' : 'Premium'}
          </button>
        )
      )}
      {sub.plan === 'enterprise' && (
        <EnterpriseBranchSwitcher
          orgCode={fleetOrgCode}
          onSwitched={async () => { await refreshAdminStatus(); }}
        />
      )}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setShowSettings(true)}
      >
        <FaCog className="w-5 h-5 text-gray-500" />
      </Button>
    </>
  );

  const orgNameForShell = (orgDisplayName || fleetOrgCode || "Fleet").trim();
  const orgFirstWord = orgNameForShell.split(/\s+/)[0] || "Fleet";
  const orgInitials = (() => {
    const parts = orgNameForShell.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return orgFirstWord.slice(0, 2).toUpperCase();
  })();

  return (
    <>
    <FleetShell
      active={topLevelTab}
      navItems={navItems}
      headerRight={headerRight}
      detail={detailProp}
      zana={sub.isZanaTier}
      account={{
        orgLabel: orgFirstWord,
        roleLabel: "Owner",
        initials: orgInitials,
        onLogout: () => logout(),
      }}
    >

      {/* Payment-pending banner: a card charge failed and we're in the grace window.
          The org keeps working; this nudges them to retry the card or pay with M-Pesa. */}
      {sub.paymentPending && (
        <div className="px-6 pt-4">
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <span style={{ fontSize: 16 }}>⚠️</span>
            <span style={{ flex: 1, minWidth: 220 }}>
              We couldn’t process your last payment. Your account is still active{sub.graceEnds ? ` until ${sub.graceEnds.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}` : ''} — please update your payment to avoid interruption.
            </span>
            <button
              onClick={() => openUpgrade('checkout')}
              style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#b45309', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Update payment →
            </button>
          </div>
        </div>
      )}

      {/* Add-on connection notice: any add-on the org wanted but the tech team hasn't wired yet
          runs on default app values. Cleared automatically when status flips to 'connected'. */}
      {orgAddons && (() => {
        const labels: Record<string, string> = { gps: "GPS sensor", fuel: "Fuel sensor", fees: "School fees module" };
        const pending = (["gps", "fuel", "fees"] as const).filter(
          (k) => orgAddons[k]?.wanted && !(orgAddons[k]?.status === "connected" && orgAddons[k]?.verified)
        );
        if (!pending.length) return null;
        return (
          <div className="px-6 pt-4">
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
              <FaExclamationTriangle className="w-4 h-4 shrink-0 text-amber-500" />
              <span className="font-semibold">Using default app values</span>
              <span className="text-amber-700">
                — {pending.map((k) => labels[k]).join(", ")} not connected yet. Your PEAR tech team links these from the admin console.
              </span>
            </div>
          </div>
        );
      })()}

      {canPlanSchoolTrips &&
        subscriptionAllowsReminders(orgDocData) &&
        shouldRemindNextTerm({
          termStart: (orgDocData?.currentTermStartDate as string) || null,
          termEnd: (orgDocData?.currentTermEndDate as string) || null,
          nextTermStart: (orgDocData?.nextTermStartDate as string) || null,
          termCalendar: Array.isArray(orgDocData?.termCalendar) ? (orgDocData?.termCalendar as number[]) : null,
        }) && (
        <TermNextBanner
          currentTermStart={(orgDocData?.currentTermStartDate as string) || null}
          onSave={async (thisTermEnd, nextTermStart) => {
            if (!fleetOrgCode) throw new Error("No organization.");
            const q = query(collection(db, "organizations"), where("code", "==", fleetOrgCode), limit(1));
            const snap = await getDocs(q);
            if (!snap.docs[0]) throw new Error("Organization not found.");
            await updateDoc(doc(db, "organizations", snap.docs[0].id), {
              currentTermEndDate: thisTermEnd,
              nextTermStartDate: nextTermStart,
              updatedAt: serverTimestamp(),
            });
          }}
        />
      )}

      {/* Main content: Dashboard or Fleet list when !detailView */}
      {!showDetailView && (
        <main className="p-6">
          {topLevelTab === "dashboard" && (
            <div className="space-y-6">
              {/* What acting on Zana's findings has been worth. Renders nothing
                  until there is a real figure — a "Ksh 0 saved" tile on a new
                  fleet is worse than no tile at all. */}
              {fleetOrgCode && <SavingsLedgerCard fleetOrgCode={fleetOrgCode} />}

              {/* Risk / stat cards */}
              <div className="grid grid-cols-4 gap-4">
                <FleetCard
                  data-zt="hrCard"
                  className="p-4 bg-red-50 border-red-200 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setShowHighRiskFullScreen(true)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-700">High Risk Alerts</p>
                      <p className="text-2xl font-bold text-red-900">{highRiskCount}</p>
                      <p className="text-xs text-red-600 mt-1">Incidents or alerts · Tap to view</p>
                    </div>
                    <FaExclamationTriangle className="w-8 h-8 text-red-500" />
                  </div>
                </FleetCard>
                <FleetCard
                  className={`p-4 cursor-pointer hover:shadow-md transition-shadow border ${
                    fleetFuelSummary.busesNotRefueledCount >= 3 ? "bg-red-50 border-red-200" :
                    fleetFuelSummary.busesNotRefueledCount >= 1 ? "bg-amber-50 border-amber-200" :
                    "bg-green-50 border-green-200"
                  }`}
                  onClick={() => setShowFuelReportFullScreen(true)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${
                        fleetFuelSummary.busesNotRefueledCount >= 3 ? "text-red-700" :
                        fleetFuelSummary.busesNotRefueledCount >= 1 ? "text-amber-700" : "text-green-700"
                      }`}>Fuel Report</p>
                      <p className={`text-2xl font-bold ${
                        fleetFuelSummary.busesNotRefueledCount >= 3 ? "text-red-900" :
                        fleetFuelSummary.busesNotRefueledCount >= 1 ? "text-amber-900" : "text-green-900"
                      }`}>
                        {fleetFuelSummary.busesNotRefueledCount}
                      </p>
                      <p className={`text-xs mt-1 ${
                        fleetFuelSummary.busesNotRefueledCount >= 3 ? "text-red-600" :
                        fleetFuelSummary.busesNotRefueledCount >= 1 ? "text-amber-600" : "text-green-600"
                      }`}>
                        {fleetFuelSummary.busesNotRefueledCount > 0
                          ? `Bus${fleetFuelSummary.busesNotRefueledCount !== 1 ? "es" : ""} with no refill logged (7d) · Tap`
                          : fleetFuelSummary.totalNeedsRefuel > 0
                            ? `${fleetFuelSummary.totalNeedsRefuel} trip${fleetFuelSummary.totalNeedsRefuel !== 1 ? "s" : ""} flagged — all buses logged a refill · Tap`
                            : "No refuel flags · Tap to view"}
                      </p>
                    </div>
                    <FaGasPump className={`w-8 h-8 ${
                      fleetFuelSummary.busesNotRefueledCount >= 3 ? "text-red-500" :
                      fleetFuelSummary.busesNotRefueledCount >= 1 ? "text-amber-500" : "text-green-500"
                    }`} />
                  </div>
                </FleetCard>
                <FleetCard
                  className="p-4 bg-amber-50 border-amber-200 cursor-pointer hover:shadow-md transition-shadow border"
                  onClick={() => setShowMaintenanceFullScreen(true)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-700">Suspend / maintenance</p>
                      <p className="text-2xl font-bold text-amber-900">{maintenanceCount}</p>
                      <p className="text-xs text-amber-600 mt-1">Includes for hire · Tap to view</p>
                    </div>
                    <FaBus className="w-8 h-8 text-amber-500" />
                  </div>
                </FleetCard>
                <FleetCard
                  className="p-4 cursor-pointer hover:shadow-md transition-shadow border bg-green-50 border-green-200"
                  onClick={() => setShowCostEstimateFullScreen(true)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-700">Cost estimate</p>
                      <p className="text-2xl font-bold text-green-900">
                        {Object.keys(costEstimateByBusId).length > 0
                          ? `${Object.values(costEstimateByBusId).reduce((a, b) => a + b, 0).toLocaleString()} KES`
                          : "—"}
                      </p>
                      <p className="text-xs mt-1 text-green-600">Per-bus fuel cost · Tap to view</p>
                    </div>
                    <FaDollarSign className="w-8 h-8 text-green-500" />
                  </div>
                </FleetCard>
              </div>

              {/* Zana fleet-health summary — live briefing for Premium/Enterprise,
                  "wake Zana" upsell for Basic/Pilot. */}
              {/* Wrapped: a bad briefing field must never take the fleet page
                  down with it. On 2026-08-25 it did exactly that. */}
              {fleetOrgCode && (
                <ErrorBoundary label="Zana's fleet summary">
                <ZanaSummaryCard
                  orgCode={fleetOrgCode}
                  zanaEnabled={sub.isZanaTier}
                  onOpenAnalytics={() => { setAnalyticsInitialBusId(null); setTopLevelTab("analytics"); }}
                  onUpgrade={() => openUpgrade()}
                  onExecuteIntent={handleZanaIntent}
                  liveUnreadAlertCount={fleetAlerts.filter((a) => !a.read).length}
                  liveUnreadBusCount={new Set(fleetAlerts.filter((a) => !a.read).map((a) => a.busId)).size}
                  hasMissingRefuel={(fleetFuelSummary.busesNotRefueledCount || 0) > 0}
                />
                </ErrorBoundary>
              )}

              {fleetOrgCode && (
                <FleetCard className="p-4 border-violet-200 bg-violet-50/40">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">School trips</h3>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Plan a trip, notify parents, and optionally create a temporary conductor login to hand over.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (!canPlanSchoolTrips) {
                          customToast.show(FULL_FEATURE_ONLY_MSG, "warning");
                          return;
                        }
                        if (premiumLocked) {
                          openUpgrade();
                          return;
                        }
                        setShowSchoolTripPlanner(true);
                        return;
                        // eslint-disable-next-line no-unreachable
                        const firstBus = buses[0]?.id ?? "";
                        const lid = newTripLegId();
                        setTripLegs([
                          {
                            lid,
                            kind: "fleet",
                            busId: firstBus,
                            useFleetCrew: true,
                            childKeys: new Set(),
                            condName: "",
                            condEmail: "",
                            condPassword: "",
                          },
                        ]);
                        setTripStudentTargetLegId(lid);
                        setTripEndsLocal("");
                        setTripPlannerStudentFilter("");
                        setShowSchoolTripPlanner(true);
                      }}
                      className="shrink-0 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg"
                    >
                      Plan school trip
                    </button>
                  </div>
                </FleetCard>
              )}
              {/* Fleet status grid */}
              <FleetCard className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">Fleet Status</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {filtered.length === 0 ? (
                    <div className="col-span-full rounded-lg border border-dashed border-gray-200 bg-gray-50/80 px-4 py-10 text-center text-sm text-gray-600">
                      {search.trim()
                        ? <>No buses match &ldquo;{search.trim()}&rdquo;. Try name, plate, or school.</>
                        : "No buses to show."}
                    </div>
                  ) : (
                    filtered.map((b) => {
                      // Alert badge = open work only: unread fleetAlerts, or a
                      // bus that still has logged incidents. Read alerts must
                      // not keep the red pill forever after Zana/triage clears.
                      const riskHigh = (busesWithIncidents.find((x) => x.id === b.id)?.crashes?.length ?? 0) > 0
                        || fleetAlerts.some((a) => a.busId === b.id && !a.read);
                      return (
                        <FleetCard
                          key={b.id}
                          className="p-4 hover:shadow-lg transition-all cursor-pointer border-gray-200"
                          onClick={() => { handleSelect(b); setDetailView(true); }}
                        >
                          <div className="text-center">
                            <div className="w-12 h-12 mx-auto mb-2 rounded-lg flex items-center justify-center bg-gray-100">
                              <FaBus
                                className={`w-6 h-6 ${
                                  riskHigh
                                    ? "text-red-500"
                                    : b.status === "active"
                                      ? "text-green-500"
                                      : b.status === "suspended" || b.status === "maintenance"
                                        ? "text-amber-500"
                                        : "text-gray-500"
                                }`}
                              />
                            </div>
                            <p className="font-medium text-gray-900 text-sm">{b.name}</p>
                            <p className="text-xs text-gray-500 font-mono mb-2">{b.plate}</p>
                            {b.status === "active" && (
                              <Badge variant="outline" className="text-xs bg-green-100 text-green-800 border-green-200">Active</Badge>
                            )}
                            {(b.status === "suspended" || b.status === "maintenance") && (
                              <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-200">Suspend / maintenance</Badge>
                            )}
                            {b.status === "for_hire" && (
                              <Badge className="text-xs bg-violet-100 text-violet-800 border-violet-200">For hire</Badge>
                            )}
                            {riskHigh && b.status === "active" && (
                              <Badge variant="destructive" className="text-xs ml-1">Alert</Badge>
                            )}
                            <p className="text-xs text-gray-500 mt-2 truncate">{b.school || "—"}</p>
                          </div>
                        </FleetCard>
                      );
                    })
                  )}
                </div>
              </FleetCard>
            </div>
          )}
          {topLevelTab === "fleet" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Fleet Overview</h1>
                  <p className="text-sm text-gray-600">
                    {search.trim()
                      ? `Showing ${filtered.length} of ${buses.length} bus${buses.length !== 1 ? "es" : ""}`
                      : `Managing ${buses.length} bus${buses.length !== 1 ? "es" : ""}`}
                  </p>
                </div>
                {fleetOrgCode && (
                  <Button
                    type="button"
                    className="shrink-0 bg-slate-900 text-white hover:bg-slate-800 rounded-xl px-4 py-2 text-sm font-medium"
                    onClick={() => openTempHireWizard()}
                  >
                    + Temporary hire bus
                  </Button>
                )}
              </div>

              <FleetCard className="p-6 border border-gray-200">
                <SectionHead sub="Last 30 days · all buses in this fleet">Fleet activity</SectionHead>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
                  <StatTile label="Total trips" value={fleetStats.trips} sub="Completed routes" accent="#2563eb" />
                  <StatTile label="Total distance" value={formatDistance(fleetStats.totalKm)} sub="Sum from driver app" accent="#0f172a" />
                  <StatTile label="Morning" value={fleetStats.morning} sub="Trips" accent="#d97706" />
                  <StatTile label="Evening" value={fleetStats.evening} sub="Trips" accent="#7c3aed" />
                </div>
                <Card style={{ marginBottom: buses.length > 0 ? 16 : 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Absentees (fleet)</div>
                  <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 32, color: fleetStats.absences > 0 ? "#dc2626" : "#16a34a" }}>{fleetStats.absences}</div>
                  <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>Student slots marked absent or not picked (last 30 days)</div>
                </Card>
                {buses.length > 0 && (() => {
                  const maxTrips = Math.max(...fleetByBusStats.map((r) => r.trips), 1);
                  const tripWord = (n: number) => (n === 1 ? "trip" : "trips");
                  const absenceWord = (n: number) => (n === 1 ? "absence" : "absences");
                  return (
                    <Card>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                        Activity by bus (last 30 days)
                      </div>
                      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 14 }}>
                        Sorted by absences, then trips · tap a row to open that bus
                      </div>
                      <div style={{ overflowX: "auto" }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "minmax(120px, 1.4fr) 72px 88px 96px minmax(80px, 1fr)",
                          gap: "8px 12px",
                          alignItems: "center",
                          padding: "0 0 8px",
                          borderBottom: "1px solid #e2e8f0",
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          minWidth: 480,
                        }}
                      >
                        <span>Bus</span>
                        <span style={{ textAlign: "right" }}>Trips</span>
                        <span style={{ textAlign: "right" }}>Distance</span>
                        <span style={{ textAlign: "right" }}>Absences</span>
                        <span />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", minWidth: 480 }}>
                        {fleetByBusStats.map((row) => {
                          const bus = buses.find((b) => b.id === row.busId);
                          const label = bus?.name || bus?.plate || row.busId;
                          const plate = bus?.plate && bus.plate !== label ? bus.plate : row.busId !== label ? row.busId : "";
                          return (
                            <button
                              key={row.busId}
                              type="button"
                              onClick={() => {
                                const b = bus ?? buses.find((x) => x.id === row.busId);
                                if (!b) return;
                                handleSelect(b);
                                setDetailView(true);
                              }}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(120px, 1.4fr) 72px 88px 96px minmax(80px, 1fr)",
                                gap: "8px 12px",
                                alignItems: "center",
                                padding: "12px 0",
                                borderBottom: "1px solid #f1f5f9",
                                background: "transparent",
                                borderLeft: "none",
                                borderRight: "none",
                                borderTop: "none",
                                cursor: "pointer",
                                textAlign: "left",
                                width: "100%",
                              }}
                            >
                              <span style={{ minWidth: 0 }}>
                                <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {label}
                                </span>
                                {plate ? (
                                  <span style={{ display: "block", fontSize: 11, color: "#94a3b8", fontFamily: "ui-monospace, monospace" }}>{plate}</span>
                                ) : null}
                              </span>
                              <span style={{ fontSize: 13, fontWeight: 600, color: "#334155", textAlign: "right" }}>
                                {row.trips}
                                <span style={{ display: "block", fontSize: 10, fontWeight: 500, color: "#94a3b8" }}>{tripWord(row.trips)}</span>
                              </span>
                              <span style={{ fontSize: 13, fontWeight: 600, color: "#334155", textAlign: "right" }}>
                                {formatDistance(row.trips ? row.totalKm : null)}
                              </span>
                              <span style={{ fontSize: 13, fontWeight: 600, color: row.absences > 0 ? "#dc2626" : "#16a34a", textAlign: "right" }}>
                                {row.absences}
                                <span style={{ display: "block", fontSize: 10, fontWeight: 500, color: "#94a3b8" }}>{absenceWord(row.absences)}</span>
                              </span>
                              <span style={{ minWidth: 0 }}>
                                <MiniBar value={row.trips} max={maxTrips} fill="#0f172a" color="#f1f5f9" />
                                <span style={{ fontSize: 10, color: "#94a3b8", marginTop: 4, display: "block" }}>
                                  {row.lastTripAt
                                    ? `Last ${row.lastTripAt.toISOString().slice(0, 10)}`
                                    : "No trips"}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      </div>
                    </Card>
                  );
                })()}
              </FleetCard>

              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <FleetCard className="p-6 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Vehicles</p>
                      <p className="text-2xl font-semibold text-gray-900">{buses.length}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FaBus className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </FleetCard>
                <FleetCard className="p-6 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Active</p>
                      <p className="text-2xl font-semibold text-green-600">{fleetActive}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <FaMapPin className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </FleetCard>
                <FleetCard className="p-6 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Suspend / maintenance</p>
                      <p className="text-2xl font-semibold text-amber-700">{maintenanceCount}</p>
                      <p className="text-xs text-gray-500 mt-1">Suspended, maintenance, or for hire</p>
                    </div>
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                      <FaBus className="w-6 h-6 text-amber-700" />
                    </div>
                  </div>
                </FleetCard>
                <FleetCard
                  data-zt="hrCard"
                  className="p-6 border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setShowHighRiskFullScreen(true)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">High risk</p>
                      <p className="text-2xl font-semibold text-red-600">{highRiskCount}</p>
                      <p className="text-xs text-gray-500 mt-1">Buses with incidents or unread route alerts · Tap for detail</p>
                    </div>
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <FaExclamationTriangle className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </FleetCard>
              </div>
              <FleetCard className="border border-gray-200">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900">Buses</h3>
                  <p className="text-sm text-gray-500 mt-0.5">Tap a row to view details. Use actions to place a bus in suspend/maintenance, activate, hire out, or remove.</p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bus</TableHead>
                      <TableHead>Plate</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>School</TableHead>
                      <TableHead>Students</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={monthlyShowEfficiencyPercent ? 7 : 6} className="text-center text-sm text-gray-500 py-10">
                          {search.trim()
                            ? `No buses match "${search.trim()}". Try name, plate, or school.`
                            : "No buses to show."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.map((b) => (
                        <TableRow
                          key={b.id}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => { handleSelect(b); setDetailView(true); }}
                        >
                          <TableCell className="font-medium">{b.name}</TableCell>
                          <TableCell className="font-mono text-sm">{b.plate}</TableCell>
                          <TableCell>
                            {b.status === "active" && (
                              <Badge variant="outline" className="bg-green-100 text-green-800 hover:bg-green-100 border-green-200">
                                Active
                              </Badge>
                            )}
                            {(b.status === "suspended" || b.status === "maintenance") && (
                              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200">
                                Suspend / maintenance
                              </Badge>
                            )}
                            {b.status === "for_hire" && (
                              <Badge className="text-xs bg-violet-100 text-violet-800 border-violet-200">For hire</Badge>
                            )}
                          </TableCell>
                          <TableCell>{b.school || "—"}</TableCell>
                          <TableCell>{b.children.length}</TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1 flex-wrap">
                              <Button size="sm" variant="ghost" onClick={() => { handleSelect(b); setDetailView(true); }}>
                                View
                              </Button>
                              {(b.status === "active" || b.status === "maintenance") && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-violet-700 border-violet-200 hover:bg-violet-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void openReassignIntro(b, "for_hire");
                                  }}
                                >
                                  Hire
                                </Button>
                              )}
                              {b.status === "for_hire" ? null : b.status === "active" ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void openReassignIntro(b, "maintenance");
                                  }}
                                >
                                  Suspend / maintenance
                                </Button>
                              ) : (
                                <Button size="sm" variant="outline" onClick={() => handleActivateBus(b)}>
                                  Activate
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() =>
                                  b.children.length > 0 ? void openReassignIntro(b, "delete_bus") : setDeleteBusToConfirm(b)
                                }
                              >
                                Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </FleetCard>
            </div>
          )}
          {topLevelTab === "accounts" && (
            <div className="space-y-6">
              {/* Header */}
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Accounts</h1>
                <p className="text-sm text-gray-600">Parents and bus drivers in your organization. For parents, transport hold keeps login — children stay off the bus until you restore them. Removing an account logs them out and revokes access.</p>
              </div>

              {/* Trasse Families households asking to connect a child to this
                  school. Renders nothing until one actually does. */}
              <SchoolFamilyLinksPanel orgCode={fleetOrgCode} />

              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-4">
                <FleetCard className="p-4 border border-gray-200">
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-semibold text-gray-900">{fleetUsers.length}</p>
                </FleetCard>
                <FleetCard className="p-4 border border-gray-200">
                  <p className="text-sm text-gray-600">Bus drivers</p>
                  <p className="text-2xl font-semibold text-gray-900">{fleetUsers.filter((u) => ["Driver", "bus_driver", "driver"].includes(u.role)).length}</p>
                </FleetCard>
                <FleetCard className="p-4 border border-gray-200">
                  <p className="text-sm text-gray-600">Parents</p>
                  <p className="text-2xl font-semibold text-gray-900">{fleetUsers.filter((u) => ["Parent", "client", "parent"].includes(u.role)).length}</p>
                </FleetCard>
              </div>

              {/* Sub-tabs + invite buttons */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {(["drivers", "parents"] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setAccountsSubTab(t)}
                    style={{
                      padding: "8px 20px", borderRadius: 20, border: "none", cursor: "pointer",
                      fontFamily: "Geist, sans-serif", fontSize: 13, fontWeight: 600,
                      background: accountsSubTab === t ? (t === "drivers" ? "#0f172a" : "#2563eb") : "#f1f5f9",
                      color: accountsSubTab === t ? "#fff" : "#64748b",
                      transition: "all 0.15s",
                    }}
                  >
                    {t === "drivers" ? "🚌 Drivers" : "👨‍👩‍👧 Parents"}
                  </button>
                ))}
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  <button
                    onClick={() => { setInviteRole(accountsSubTab === "drivers" ? "bus_driver" : "parent"); setShowSettings(true); }}
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 20, border: "none", cursor: "pointer", background: accountsSubTab === "drivers" ? "#0f172a" : "#2563eb", color: "#fff", fontFamily: "Geist, sans-serif", fontSize: 13, fontWeight: 600 }}
                  >
                    + Invite {accountsSubTab === "drivers" ? "driver" : "parent"}
                  </button>
                  {accountsSubTab === "drivers" && fleetOrgCode && (
                    <CreateDriverButton
                      orgCode={fleetOrgCode}
                      buses={buses.map((b) => ({ id: b.id, name: b.name, plate: b.plate }))}
                    />
                  )}
                  {orgIsPilot && (
                    <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", padding: "4px 10px", borderRadius: 20, fontWeight: 700, alignSelf: "center" }}>
                      Pilot
                    </span>
                  )}
                </div>
              </div>

              {/* User list — drivers: table; parents (A3): buses as columns */}
              {accountsSubTab === "drivers" ? (
              <FleetCard className="border border-gray-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Linked Bus</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fleetUsers
                      .filter((u) => ["Driver", "bus_driver", "driver"].includes(u.role))
                      .map((u) => (
                        <TableRow
                          key={u.id}
                          style={{ cursor: u.id !== currentUser?.uid ? "pointer" : "default" }}
                          onClick={() => { if (u.id !== currentUser?.uid) { setSelectedAccount(u); setMoveBusInput(""); } }}
                        >
                          <TableCell className="font-medium">{u.displayName || "—"}</TableCell>
                          <TableCell className="text-gray-600">{u.email}</TableCell>
                          <TableCell>{u.busName || <span className="text-gray-400">No bus</span>}</TableCell>
                          <TableCell>
                            {u.suspended
                              ? <span style={{ fontSize: 11, fontWeight: 600, background: "#fef2f2", color: "#dc2626", padding: "2px 8px", borderRadius: 20, border: "1px solid #fecaca" }}>Suspended</span>
                              : u.id === currentUser?.uid
                              ? <span style={{ fontSize: 11, color: "#94a3b8" }}>You</span>
                              : <span style={{ fontSize: 11, fontWeight: 600, background: "#f0fdf4", color: "#16a34a", padding: "2px 8px", borderRadius: 20, border: "1px solid #bbf7d0" }}>Active</span>
                            }
                          </TableCell>
                          <TableCell>
                            {u.id !== currentUser?.uid && (
                              <span style={{ fontSize: 12, color: "#94a3b8" }}>Tap to manage →</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                {fleetUsers.filter((u) => ["Driver", "bus_driver", "driver"].includes(u.role)).length === 0 && (
                  <div className="p-8 text-center text-gray-500 text-sm">
                    No drivers in this organization yet.
                  </div>
                )}
              </FleetCard>
              ) : (
              <>
              {/* Every parent on the fees roster, invited by email in one go (2026-09-29). */}
              <ParentsBulkInvite
                orgCode={fleetOrgCode}
                feesConnected={!!(orgAddons?.fees?.status === "connected" && orgAddons?.fees?.verified)}
              />
              <div style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 8, maxWidth: "100%" }}>
                {(() => {
                  const parents = fleetUsers.filter((u) =>
                    ["Parent", "client", "parent"].includes(u.role)
                  );
                  const parentOnBus = (busId: string) =>
                    parents.filter((u) =>
                      buses.some(
                        (b) =>
                          b.id === busId &&
                          b.children.some((c) => c.parentId === u.id)
                      )
                    );
                  const assignedParentIds = new Set(
                    buses.flatMap((b) =>
                      b.children.map((c) => c.parentId).filter(Boolean) as string[]
                    )
                  );
                  const unassigned = parents.filter((u) => !assignedParentIds.has(u.id));
                  /* Roster rows whose parent is NOT one of this school's parent
                     accounts any more — the parent was removed or suspended
                     (Remove account clears their school code), or the row was
                     written for a uid that never had one. Until 2026-09-28 these
                     children simply vanished from this tab while still riding on
                     the Buses tab ("some children seen on the buses and not
                     here" — Gabriel). They are shown under their bus, flagged. */
                  const parentIds = new Set(parents.map((u) => u.id));
                  type RosterChild = (typeof buses)[number]["children"][number];
                  const columns: { key: string; title: string; sub: string; list: typeof parents; orphans: RosterChild[] }[] = [
                    ...buses.map((b) => ({
                      key: b.id,
                      title: b.name || b.plate || b.id,
                      sub: b.plate && b.name !== b.plate ? b.plate : `${b.children.length} on roster`,
                      list: parentOnBus(b.id),
                      orphans: b.children.filter((c) => !c.parentId || !parentIds.has(c.parentId)),
                    })),
                    ...(unassigned.length > 0
                      ? [{
                          key: "_unassigned",
                          title: "No bus yet",
                          sub: `${unassigned.length} parent${unassigned.length === 1 ? "" : "s"}`,
                          list: unassigned,
                          orphans: [] as RosterChild[],
                        }]
                      : []),
                  ];
                  if (columns.length === 0) {
                    return (
                      <FleetCard className="border border-gray-200 p-8 text-center text-gray-500 text-sm w-full">
                        No buses or parents in this organization yet.
                      </FleetCard>
                    );
                  }
                  const parentStatus = (u: (typeof parents)[0]) => {
                    const kids = buses.flatMap((b) =>
                      b.children.filter((c) => c.parentId === u.id)
                    );
                    const held = kids.filter((c) => c.transportSuspended).length;
                    if (held > 0 && held === kids.length && kids.length > 0) {
                      return { label: "Transport hold", bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" };
                    }
                    if (held > 0) {
                      return { label: `${held}/${kids.length} on hold`, bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" };
                    }
                    return { label: "Active", bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" };
                  };
                  return columns.map((col) => (
                    <div
                      key={col.key}
                      style={{
                        flex: "0 0 280px",
                        maxWidth: 300,
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 14,
                        display: "flex",
                        flexDirection: "column",
                        maxHeight: "50vh",
                        minHeight: 200,
                      }}
                    >
                      <div style={{ padding: "12px 14px", borderBottom: "1px solid #f1f5f9" }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{col.title}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{col.sub}</div>
                      </div>
                      <div style={{ overflowY: "auto", flex: 1, padding: 8 }}>
                        {col.list.length === 0 && col.orphans.length === 0 ? (
                          <div style={{ fontSize: 12, color: "#94a3b8", padding: "16px 8px", textAlign: "center" }}>
                            No parents on this bus
                          </div>
                        ) : (
                          col.list.map((u) => {
                            const st = parentStatus(u);
                            const names = buses
                              .flatMap((b) =>
                                b.children
                                  .filter((c) => c.parentId === u.id && (col.key === "_unassigned" || b.id === col.key))
                                  .map((c) => c.name)
                              )
                              .filter(Boolean);
                            return (
                              <button
                                key={`${col.key}-${u.id}`}
                                type="button"
                                disabled={u.id === currentUser?.uid}
                                onClick={() => {
                                  if (u.id === currentUser?.uid) return;
                                  setSelectedAccount(u);
                                  setMoveBusInput("");
                                }}
                                style={{
                                  width: "100%",
                                  textAlign: "left",
                                  padding: "10px 12px",
                                  marginBottom: 6,
                                  borderRadius: 10,
                                  border: "1px solid #f1f5f9",
                                  background: "#f8fafc",
                                  cursor: u.id === currentUser?.uid ? "default" : "pointer",
                                  opacity: u.id === currentUser?.uid ? 0.7 : 1,
                                }}
                              >
                                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                                  {u.displayName || u.email || "Parent"}
                                </div>
                                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                  {names.length > 0 ? names.join(", ") : u.email}
                                </div>
                                <span
                                  style={{
                                    display: "inline-block",
                                    marginTop: 6,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    background: st.bg,
                                    color: st.color,
                                    padding: "2px 8px",
                                    borderRadius: 20,
                                    border: `1px solid ${st.border}`,
                                  }}
                                >
                                  {st.label}
                                </span>
                              </button>
                            );
                          })
                        )}
                        {col.orphans.length > 0 && (
                          <div
                            style={{
                              padding: "10px 12px",
                              marginBottom: 6,
                              borderRadius: 10,
                              border: "1px solid #fed7aa",
                              background: "#fff7ed",
                            }}
                          >
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#9a3412" }}>
                              {col.orphans.map((c) => c.name).filter(Boolean).join(", ") ||
                                `${col.orphans.length} child${col.orphans.length === 1 ? "" : "ren"}`}
                            </div>
                            <div style={{ fontSize: 11, color: "#c2410c", marginTop: 2 }}>
                              On this bus, but the parent account is not in this school any more
                              (removed, suspended or never linked). Fix the roster row to name a current parent.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ));
                })()}
              </div>
              </>
              )}

              {/* Account action modal — portal to body so overlay covers sticky top bar + sidebar */}
              {selectedAccount && createPortal(
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5000, padding: 16 }}
                  onClick={() => setSelectedAccount(null)}>
                  <div style={{ background: "#fff", borderRadius: 18, padding: 28, maxWidth: 420, width: "100%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 48px rgba(0,0,0,0.18)" }}
                    onClick={(e) => e.stopPropagation()}>

                    {/* Header */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 14, background: ["Driver","bus_driver","driver"].includes(selectedAccount.role) ? "#0f172a" : "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                        {["Driver","bus_driver","driver"].includes(selectedAccount.role) ? "🚌" : "👤"}
                      </div>
                      <div>
                        <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{selectedAccount.displayName || selectedAccount.email}</div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>{selectedAccount.email}</div>
                      </div>
                    </div>

                    {/* Driver-specific: linked bus */}
                    {["Driver","bus_driver","driver"].includes(selectedAccount.role) && selectedAccount.busName && (
                      <div style={{ marginBottom: 16, padding: "10px 14px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13, color: "#475569" }}>
                        🚍 Assigned to: <strong>{selectedAccount.busName}</strong>
                      </div>
                    )}

                    {/* Parent-specific: move each child (or all) to a different bus + per-child transport hold */}
                    {["Parent","client","parent"].includes(selectedAccount.role) && (() => {
                      const inputStyle: React.CSSProperties = { flex: 1, minWidth: 0, padding: "9px 11px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "Geist, sans-serif", outline: "none" };
                      const moveBtnStyle = (enabled: boolean): React.CSSProperties => ({ padding: "9px 14px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", cursor: enabled ? "pointer" : "not-allowed", opacity: enabled ? 1 : 0.5 });
                      // This parent's children, grouped from the org's loaded buses.
                      const parentChildren = buses.flatMap((b) =>
                        b.children
                          .filter((c) => c.parentId === selectedAccount.id)
                          .map((c) => ({
                            childId: c.id,
                            childName: c.name,
                            busId: b.id,
                            busName: b.name || b.plate,
                            plate: b.plate,
                            transportSuspended: !!c.transportSuspended,
                          }))
                      );
                      const fallbackNames = ((selectedAccount as { childNames?: string[] }).childNames || []);
                      const allIds = parentChildren.map((c) => c.childId);
                      const heldCount = parentChildren.filter((c) => c.transportSuspended).length;
                      const allHeld = parentChildren.length > 0 && heldCount === parentChildren.length;
                      return (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginBottom: 8 }}>Transport hold (A1/A2)</div>
                          <p style={{ fontSize: 12, color: "#64748b", marginBottom: 10, lineHeight: 1.45 }}>
                            Parent keeps signing in. Held children are faded on the driver roster and skipped by smart routes / stop geofences.
                          </p>
                          {parentChildren.length === 0 ? (
                            <div style={{ fontSize: 12.5, color: "#94a3b8", padding: "8px 0", marginBottom: 12 }}>
                              No children on a bus roster yet — assign them first.
                            </div>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                              {parentChildren.map((c) => (
                                <div
                                  key={c.childId}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 10,
                                    background: c.transportSuspended ? "#fff7ed" : "#f8fafc",
                                    border: `1px solid ${c.transportSuspended ? "#fed7aa" : "#eef2f7"}`,
                                    borderRadius: 10,
                                    padding: "10px 12px",
                                    opacity: c.transportSuspended ? 0.85 : 1,
                                  }}
                                >
                                  <div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{c.childName || "Child"}</div>
                                    <div style={{ fontSize: 11, color: "#94a3b8" }}>on {c.busName}</div>
                                  </div>
                                  <button
                                    type="button"
                                    disabled={accountActionBusy}
                                    onClick={() =>
                                      void setChildrenTransportSuspend([c.childId], !c.transportSuspended)
                                    }
                                    style={{
                                      padding: "7px 12px",
                                      borderRadius: 8,
                                      border: "none",
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor: accountActionBusy ? "not-allowed" : "pointer",
                                      background: c.transportSuspended ? "#2563eb" : "#f97316",
                                      color: "#fff",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {c.transportSuspended ? "Restore" : "Hold"}
                                  </button>
                                </div>
                              ))}
                              {parentChildren.length > 1 && (
                                <button
                                  type="button"
                                  disabled={accountActionBusy}
                                  onClick={() =>
                                    void setChildrenTransportSuspend(allIds, !allHeld)
                                  }
                                  style={{
                                    padding: "10px 0",
                                    background: allHeld ? "#2563eb" : "#fb923c",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: accountActionBusy ? "not-allowed" : "pointer",
                                  }}
                                >
                                  {allHeld ? "Restore transport for all children" : "Hold transport for all children"}
                                </button>
                              )}
                            </div>
                          )}

                          <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginBottom: 8 }}>Move children to a different bus</div>
                          {parentChildren.length === 0 ? (
                            <div style={{ fontSize: 12.5, color: "#94a3b8", padding: "8px 0" }}>
                              {fallbackNames.length > 0
                                ? `Children on profile (${fallbackNames.join(", ")}) are not linked to a bus roster yet. Assign them from the parent app or move them once they appear on a bus.`
                                : "No children are assigned to a bus yet."}
                            </div>
                          ) : (
                            <>
                              {/* Move ALL */}
                              {parentChildren.length > 1 && (
                                <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
                                  <input
                                    value={moveBusInput}
                                    onChange={(e) => setMoveBusInput(e.target.value)}
                                    placeholder={`Move all ${parentChildren.length} to plate…`}
                                    style={inputStyle}
                                  />
                                  <button
                                    disabled={!moveBusInput.trim() || moveBusSaving}
                                    onClick={() => moveChildrenToBus(allIds, moveBusInput)}
                                    style={moveBtnStyle(!!moveBusInput.trim() && !moveBusSaving)}
                                  >
                                    {moveBusSaving ? "Moving…" : "Move all"}
                                  </button>
                                </div>
                              )}
                              {/* Per-child rows */}
                              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {parentChildren.map((c) => {
                                  const val = childMoveInputs[c.childId] ?? "";
                                  return (
                                    <div key={`move-${c.childId}`} style={{ background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 10, padding: "10px 12px" }}>
                                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{c.childName || "Child"}</span>
                                        <span style={{ fontSize: 11, color: "#94a3b8" }}>on {c.busName}</span>
                                      </div>
                                      <div style={{ display: "flex", gap: 8 }}>
                                        <input
                                          value={val}
                                          onChange={(e) => setChildMoveInputs((m) => ({ ...m, [c.childId]: e.target.value }))}
                                          placeholder="Bus plate (e.g. KCA123A)"
                                          style={inputStyle}
                                        />
                                        <button
                                          disabled={!val.trim() || moveBusSaving}
                                          onClick={() => moveChildrenToBus([c.childId], val)}
                                          style={moveBtnStyle(!!val.trim() && !moveBusSaving)}
                                        >
                                          {moveBusSaving ? "…" : "Move"}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}

                    {/* Action buttons */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {/* Drivers: login Suspend / Unsuspend. Parents: transport hold is above (A1). */}
                      {["Driver","bus_driver","driver"].includes(selectedAccount.role) && (
                        selectedAccount.suspended ? (
                        <button
                          disabled={accountActionBusy}
                          onClick={async () => {
                            setAccountActionBusy(true);
                            try {
                              await updateDoc(doc(db, "users", selectedAccount.id), {
                                suspended: false,
                                suspendedAt: null,
                                suspendReason: null,
                                isLoggedOut: false,
                                logoutReason: null,
                                logoutTimestamp: null,
                              });
                              customToast.show(`${selectedAccount.displayName || selectedAccount.email} unsuspended`, "success");
                              setSelectedAccount(null);
                            } catch (e) { customToast.show("Failed to unsuspend", "error"); }
                            finally { setAccountActionBusy(false); }
                          }}
                          style={{ padding: "12px 0", background: "#2563eb", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                        >
                          ✓ Unsuspend Account
                        </button>
                      ) : (
                        <button
                          disabled={accountActionBusy}
                          onClick={async () => {
                            if (!window.confirm(`Suspend ${selectedAccount.displayName || selectedAccount.email}? They will be logged out and cannot sign in until unsuspended.`)) return;
                            setAccountActionBusy(true);
                            try {
                              await updateDoc(doc(db, "users", selectedAccount.id), {
                                suspended: true,
                                suspendedAt: serverTimestamp(),
                                suspendReason: "Your account has been suspended by your fleet admin. Contact them to restore access.",
                                // Kick any active session immediately (the app listens to
                                // isLoggedOut) and block re-login (AuthService checks `suspended`).
                                isLoggedOut: true,
                                logoutReason: "Your account has been suspended by your fleet admin.",
                                logoutTimestamp: serverTimestamp(),
                              });
                              customToast.show(`${selectedAccount.displayName || selectedAccount.email} suspended`, "success");
                              setSelectedAccount(null);
                            } catch (e) { customToast.show("Failed to suspend", "error"); }
                            finally { setAccountActionBusy(false); }
                          }}
                          style={{ padding: "12px 0", background: "#f97316", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                        >
                          ⏸ Suspend Account
                        </button>
                      )
                      )}
                      {/* Delete */}
                      <button
                        disabled={accountActionBusy}
                        onClick={() => handleRemoveAccount(selectedAccount)}
                        style={{ padding: "12px 0", background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                      >
                        🗑 Delete Account
                      </button>
                      {/* Cancel */}
                      <button
                        onClick={() => setSelectedAccount(null)}
                        style={{ padding: "12px 0", background: "#f1f5f9", color: "#64748b", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>,
                document.body
              )}

            </div>
          )}
          {topLevelTab === "monthlyReport" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center shadow-md">
                    <FaChartBar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Monthly Report</h1>
                    <p className="text-sm text-gray-500">Fuel, incidents, efficiency and pickup by bus</p>
                  </div>
                </div>
                <select
                  value={`${reportMonth.year}-${String(reportMonth.month).padStart(2, "0")}`}
                  onChange={(e) => {
                    const [y, m] = e.target.value.split("-").map(Number);
                    setReportMonth({ year: y, month: m });
                  }}
                  className="px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 text-sm font-medium"
                >
                  {[0, 1, 2].map((i) => {
                    const d = new Date();
                    d.setMonth(d.getMonth() - i);
                    const y = d.getFullYear();
                    const m = d.getMonth() + 1;
                    return (
                      <option key={`${y}-${m}`} value={`${y}-${String(m).padStart(2, "0")}`}>
                        {new Date(y, m - 1).toLocaleString("default", { month: "long" })} {y}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* White-themed report: summary cards (clickable → relevant screens) and per-bus table */}
              <div className="rounded-2xl bg-white border border-gray-200 shadow-sm p-6 space-y-6">
                {/* Summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <button
                    type="button"
                    onClick={() => { setTopLevelTab("dashboard"); setShowFuelReportFullScreen(true); }}
                    className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-left hover:bg-orange-100/80 transition-colors"
                  >
                    <p className="text-xs font-medium text-orange-700 uppercase tracking-wide">Total fuel cost</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {monthlyReportRows.reduce((s, r) => s + r.fuelCost, 0) > 0
                        ? `${monthlyReportRows.reduce((s, r) => s + r.fuelCost, 0).toLocaleString()} KES`
                        : "No data"}
                    </p>
                    <p className="text-xs text-orange-600/80 mt-0.5">
                      {monthlyReportRows.reduce((s, r) => s + r.fuelCost, 0) > 0
                        ? "From driver refuel logs"
                        : "Drivers haven't logged costs yet"}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTopLevelTab("dashboard"); }}
                    className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left hover:bg-blue-100/80 transition-colors"
                  >
                    <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Total km</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {monthlyReportRows.reduce((s, r) => s + r.km, 0) > 0
                        ? `${monthlyReportRows.reduce((s, r) => s + r.km, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                        : "0"}
                    </p>
                    <p className="text-xs text-blue-600/80 mt-0.5">
                      {monthlyReportRows.reduce((s, r) => s + r.km, 0) === 0
                        ? "No routes completed this month"
                        : "Tap to open Dashboard"}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowIncidentsSummaryScreen(true)}
                    className="bg-red-50 border border-red-200 rounded-xl p-4 text-left hover:bg-red-100/80 transition-colors"
                  >
                    <p className="text-xs font-medium text-red-700 uppercase tracking-wide">Incidents</p>
                    <p className="text-2xl font-bold text-red-600">
                      {monthlyReportRows.reduce((s, r) => s + r.incidents, 0)}
                    </p>
                    <p className="text-xs text-red-600/80 mt-0.5">Tap to see all buses & incidents</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTopLevelTab("fleet"); setDetailView(false); }}
                    className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-left hover:bg-emerald-100/80 transition-colors"
                  >
                    <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide">Buses</p>
                    <p className="text-2xl font-bold text-emerald-700">{buses.length}</p>
                    <p className="text-xs text-emerald-600/80 mt-0.5">Tap to open Fleet</p>
                  </button>
                </div>

                {/* Empty state when no route data in the selected month */}
                {monthlyReportRows.every((r) => r.km === 0) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5 shrink-0">⚠️</span>
                    <span>
                      <strong>No route data for this month yet.</strong> Routes completed in previous months are visible when you select those months from the dropdown.
                      Km and fuel efficiency are calculated per period selected.
                    </span>
                  </div>
                )}

                <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50/50">
                  <div className="flex flex-wrap items-center justify-between gap-3 p-4 pb-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Per-bus summary</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Km, fuel cost, fuel alerts (driver post-trip surveys), incidents &amp; attendance for selected month.
                        Fuel cost only appears when drivers log refuel costs in the app.
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={monthlyShowEfficiencyPercent}
                          onChange={(e) => setMonthlyShowEfficiencyPercent(e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        Show fuel efficiency %
                      </label>
                      <button
                        onClick={() => void exportMonthlyReportCsv(
                          `${reportMonth.year}-${String(reportMonth.month).padStart(2, "0")}`,
                          "csv",
                        )}
                        style={{ padding: "6px 14px", background: "#0f172a", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                      >
                        Download report
                      </button>
                    </div>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-200 bg-gray-100/80 hover:bg-gray-100">
                        <TableHead className="text-gray-700 font-medium">Bus</TableHead>
                        <TableHead className="text-right text-gray-700 font-medium">Km</TableHead>
                        <TableHead className="text-right text-gray-700 font-medium">Fuel cost (KES)</TableHead>
                        <TableHead className="text-right text-gray-700 font-medium" title="Post-trip 'needs refuel' alerts from drivers">Fuel alerts</TableHead>
                        <TableHead className="text-right text-gray-700 font-medium">Incidents</TableHead>
                        <TableHead className="text-right text-gray-700 font-medium">L/100 km</TableHead>
                        {monthlyShowEfficiencyPercent && (
                          <TableHead className="text-right text-gray-700 font-medium">Efficiency</TableHead>
                        )}
                        <TableHead className="text-right text-gray-700 font-medium">Attendance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthlyReportRows.map((r) => {
                        const busForRow = buses.find((b) => b.id === r.busId);
                        const attendanceLabel = r.totalSlots != null && r.totalSlots > 0 && r.pickedOrDropped != null
                          ? `${r.pickedOrDropped}/${r.totalSlots} (${r.pickupRatePct}%)`
                          : r.pickupRatePct != null ? `${r.pickupRatePct}%` : "—";
                        return (
                          <TableRow
                            key={r.busId}
                            className="border-gray-200 hover:bg-gray-50 cursor-pointer"
                            onClick={() => busForRow && (handleSelect(busForRow), setTopLevelTab("fleet"), setDetailView(true), setTab("today"))}
                          >
                            <TableCell className="font-medium text-gray-900">{r.busName}</TableCell>
                            <TableCell className="text-right text-gray-700">{r.km > 0 ? r.km.toFixed(1) : <span className="text-gray-400">—</span>}</TableCell>
                            <TableCell className="text-right text-orange-600">
                              {r.fuelCost > 0
                                ? `${r.fuelCost.toLocaleString()} KES`
                                : <span className="text-gray-300" title="Driver hasn't logged refuel cost in the app">—</span>}
                            </TableCell>
                            <TableCell className="text-right">
                              {r.needsRefuelTrips > 0
                                ? <span className="text-amber-600 font-semibold">{r.needsRefuelTrips}</span>
                                : <span className="text-gray-400">0</span>}
                            </TableCell>
                            <TableCell className="text-right text-gray-700">{r.incidents}</TableCell>
                            <TableCell className="text-right text-emerald-600">{r.l100km != null ? r.l100km.toFixed(1) : "—"}</TableCell>
                            {monthlyShowEfficiencyPercent && (
                              <TableCell className="text-right">
                                {r.efficiencyPct != null && r.efficiencyColor ? (
                                  <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: r.efficiencyColor }}>
                                    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: r.efficiencyColor }} aria-hidden />
                                    {r.efficiencyPct}%
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </TableCell>
                            )}
                            <TableCell className="text-right text-gray-700">{attendanceLabel}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
          {topLevelTab === "trips" && (
            <div style={{ maxWidth: 1180, margin: "0 auto" }}>
              {/* header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 26 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 13, background: "linear-gradient(140deg,#8b5cf6,#5b21b6)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 20px rgba(124,58,237,.3)" }}>
                    <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></svg>
                  </div>
                  <div>
                    <h1 style={{ margin: 0, fontSize: 25, fontWeight: 800, letterSpacing: "-.6px", color: "#0f172a" }}>School trips</h1>
                    <p style={{ margin: "3px 0 0", fontSize: 13.5, color: "#64748b" }}>Upcoming, active, and past trips for your organisation</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="pear-plan-btn"
                  onClick={() => {
                    if (!canPlanSchoolTrips) { customToast.show(FULL_FEATURE_ONLY_MSG, "warning"); return; }
                    if (premiumLocked) { openUpgrade(); return; }
                    setShowSchoolTripPlanner(true);
                  }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 17px", borderRadius: 11, border: "none", cursor: "pointer", fontSize: 13.5, fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#7c3aed,#5b21b6)", boxShadow: "0 6px 16px rgba(124,58,237,.3)" }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                  Plan a trip
                </button>
              </div>

              {/* buckets */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: 20 }}>
                {(["upcoming", "active", "past"] as const).map((bucket) => {
                  const meta = bucket === "upcoming"
                    ? { label: "Not started", color: "#d97706", soft: "#fff7ed", emptyText: "No trips waiting to start" }
                    : bucket === "active"
                    ? { label: "Active", color: "#16a34a", soft: "#e8f7ee", emptyText: "No trips running right now" }
                    : { label: "Past", color: "#64748b", soft: "#eef1f6", emptyText: "No past trips yet" };
                  const phaseStyle = (phase: string) => {
                    if (phase === "Active") return { color: "#16a34a", soft: "#e8f7ee" };
                    if (phase === "Pending") return { color: "#d97706", soft: "#fff7ed" };
                    if (phase === "Upcoming") return { color: "#2563eb", soft: "#eef3ff" };
                    return { color: "#64748b", soft: "#eef1f6" };
                  };
                  const list = fleetTripsEnriched.filter((t) => t.bucket === bucket);
                  return (
                    <section key={bucket} style={{ background: "#fff", border: "1px solid #e9edf3", borderRadius: 18, overflow: "hidden", boxShadow: "0 1px 2px rgba(15,23,42,.04)", display: "flex", flexDirection: "column" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", borderBottom: "1px solid #f1f4f9" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                          <span style={{ width: 9, height: 9, borderRadius: "50%", background: meta.color }} />
                          <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".6px", color: "#334155" }}>{meta.label}</h2>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: meta.color, background: meta.soft, padding: "3px 10px", borderRadius: 999 }}>{list.length} trip{list.length === 1 ? "" : "s"}</span>
                      </div>
                      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10, flex: 1, minHeight: 130, maxHeight: "min(70vh, 560px)", overflowY: "auto" }}>
                        {list.length === 0 ? (
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 9, padding: "26px 12px", textAlign: "center" }}>
                            <span style={{ width: 42, height: 42, borderRadius: 12, background: "#f4f6fa", display: "flex", alignItems: "center", justifyContent: "center", color: "#cbd5e1" }}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></svg>
                            </span>
                            <span style={{ fontSize: 12.5, color: "#94a3b8" }}>{meta.emptyText}</span>
                          </div>
                        ) : (
                          list.map((t) => {
                            const ps = phaseStyle(t.phaseLabel);
                            return (
                            <button
                              key={t.id}
                              type="button"
                              onClick={() => setTripsDetailTripId(t.id)}
                              className="pear-trip-card"
                              style={{ textAlign: "left", width: "100%", border: "1px solid #eef1f6", background: "#fcfcfe", borderRadius: 13, padding: 14, cursor: "pointer", display: "flex", flexDirection: "column", gap: 9, font: "inherit" }}
                            >
                              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                                <span style={{ fontSize: 14.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.25 }}>{t.title || "Untitled trip"}</span>
                                <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".4px", color: ps.color, background: ps.soft, padding: "3px 8px", borderRadius: 7, whiteSpace: "nowrap" }}>{t.phaseLabel}</span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#64748b" }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "0 0 13px" }}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                                <span>{t.depD ? t.depD.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}</span>
                              </div>
                              {t.destination ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#64748b" }}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "0 0 13px" }}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></svg>
                                  <span>{t.destination}</span>
                                </div>
                              ) : null}
                              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: ps.color, marginTop: 1 }}>
                                View details
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                              </div>
                            </button>
                            );
                          })
                        )}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Live Map tab ── */}
          {topLevelTab === "liveMap" && (
            <BusLiveMapPanel buses={buses} fleetOrgCode={fleetOrgCode} />
          )}

          {/* ── Fuel tab (pilot only — basic spend overview, efficiency locked) ── */}
          {topLevelTab === "fuelPrices" && fleetOrgCode && (
            <FuelPricesPage
              fleetOrgCode={fleetOrgCode}
              epraManaged={(sub.plan === "premium" || sub.plan === "enterprise") && sub.status !== "locked"}
              onBack={() => setTopLevelTab("dashboard")}
            />
          )}

          {topLevelTab === "fuel" && (
            <FuelOverview buses={buses} fleetOrgCode={fleetOrgCode} onUpgrade={openUpgrade} />
          )}

          {/* ── Maintenance: service intervals, insurance/NTSA expiry, burn drift ── */}
          {topLevelTab === "maintenance" && fleetOrgCode && (
            <MaintenancePage
              fleetOrgCode={fleetOrgCode}
              onBack={() => setTopLevelTab("dashboard")}
              onOpenBus={(busId) => {
                const b = findBusByAnyId(busId);
                if (!b) return;
                setTopLevelTab("dashboard");
                setDetailReturnTo("serviceMaintenance");
                handleSelect(b);
                setDetailView(true);
              }}
            />
          )}

          {/* ── Ask Zana tab (Premium/Enterprise) — the full-page conversation.
              Same endpoint and same allow-listed intents as the dock; it gets
              the screen context so she can still see what the console shows. ── */}
          {topLevelTab === "zanaChat" && sub.isZanaTier && (
            <ZanaChatPage
              orgCode={fleetOrgCode}
              pageContext={zanaAskContext}
              liveCounts={zanaLiveCounts}
              onExecuteIntent={handleZanaIntent}
              liveUnreadAlertCount={zanaLiveCounts.unreadAlertCount}
              liveHighRiskBusCount={zanaLiveCounts.highRiskBusCount}
              takeoverActive={takeover.active}
              onStopGuide={takeover.stopFromChat}
            />
          )}

          {/* ── Analytics tab (Premium) ── */}
          {topLevelTab === "analytics" && (
            premiumLocked ? (
              // Locked-out orgs can't open analytics — show the upgrade trailer.
              <div style={{ padding: "20px 24px 48px" }}>
                <ZanaModeUpsell onUpgrade={() => openUpgrade()} plan={sub.isPilot ? 'pilot' : (sub.plan || 'premium')} />
              </div>
            ) : (
              <BusAnalyticsPage
                buses={buses}
                fleetOrgCode={fleetOrgCode}
                onBack={() => setTopLevelTab("dashboard")}
                initialBusId={analyticsInitialBusId}
                zanaEnabled={sub.isZanaTier}
                onUpgrade={() => openUpgrade()}
                onZanaIntent={handleZanaIntent}
                liveUnreadAlertCount={fleetAlerts.filter((a) => !a.read).length}
                subTab={zanaAnalyticsSubTab}
                subTabNonce={zanaAnalyticsSubTabNonce}
                onSubTabChange={setZanaAnalyticsSubTab}
                currentTermStartDate={(orgDocData?.currentTermStartDate as string) || null}
                currentTermEndDate={(orgDocData?.currentTermEndDate as string) || null}
                nextTermStartDate={(orgDocData?.nextTermStartDate as string) || null}
              />
            )
          )}
        </main>
      )}

      {/* Full-screen school trip detail (from Trips tab) */}
      {tripsDetailTripId && fleetOrgCode && (() => {
        const row = fleetTripsEnriched.find((x) => x.id === tripsDetailTripId);
        if (!row) {
          return (
            <div className="fixed inset-0 z-[128] bg-black/40 flex items-center justify-center p-4">
              <FleetCard className="max-w-md w-full p-6 space-y-4">
                <p className="text-sm text-gray-600">Trip not found or still loading.</p>
                <Button type="button" variant="outline" onClick={() => setTripsDetailTripId(null)}>
                  Close
                </Button>
              </FleetCard>
            </div>
          );
        }
        const d = row.data;
        const busIds = Array.isArray(d.busIds) ? (d.busIds as string[]) : [];
        const childIds = Array.isArray(d.childIds) ? (d.childIds as string[]) : [];
        const parentIds = Array.isArray(d.parentIds) ? (d.parentIds as string[]) : [];
        const segments = Array.isArray(d.segments) ? (d.segments as { busId?: string; childIds?: string[]; kind?: string }[]) : [];
        const conductorUids = Array.isArray(d.tripConductorUids) ? (d.tripConductorUids as string[]) : [];
        const conductorEmails = Array.isArray(d.conductorEmails) ? (d.conductorEmails as string[]) : [];
        const conductorEmail = d.conductorEmail != null ? String(d.conductorEmail) : "";
        const tripEndD = firestoreTimestampToDate(d.tripEndsAt);
        const fmtWhen = (dt: Date | null | undefined) => {
          if (!dt) return "—";
          return dt.toLocaleString("en-GB", {
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
        };

        const childName = (cid: string) => {
          for (const b of buses) {
            const c = b.children.find((ch) => ch.id === cid);
            if (c) return c.name;
          }
          return cid;
        };
        const busLabel = (bid: string) => buses.find((b) => b.id === bid)?.name ?? tripHireLabels[bid] ?? bid;
        // Prefer crew loaded for this trip (hired-bus conductors often missing from Accounts list).
        type CrewRow = { id: string; displayName: string; email: string; username?: string; busName?: string };
        const conductors: CrewRow[] = tripDetailCrew.length > 0
          ? tripDetailCrew
          : conductorUids
              .map((uid) => fleetUsers.find((u) => u.id === uid))
              .filter((u): u is (typeof fleetUsers)[number] => !!u)
              .map((u) => ({
                id: u.id,
                displayName: u.displayName,
                email: u.email,
                username: u.username,
                busName: u.busName,
              }));
        const outsideParents = parentIds
          .map((uid) => fleetUsers.find((u) => u.id === uid))
          .filter((u): u is (typeof fleetUsers)[number] => !!u && (u.temporaryTripParent === true || (u.email || "").includes(".trasseapp@")));
        const phase = row.phaseLabel;
        const phaseColor = phase === "Active" ? "#16a34a" : phase === "Pending" ? "#d97706" : phase === "Upcoming" ? "#2563eb" : "#64748b";

        const copyText = (t: string) => { void navigator.clipboard?.writeText(t); };

        const doReissueParent = async (u: (typeof fleetUsers)[number]) => {
          if (!fleetOrgCode || !isTripsApiConfigured()) {
            window.alert("Trips backend is not configured — cannot reissue credentials.");
            return;
          }
          const phone = window.prompt("Phone for SMS (leave blank to use the number on file):", "") ?? undefined;
          setTripReissueBusy(`p:${u.id}`);
          try {
            const r = await reissueTripParent({
              orgCode: fleetOrgCode,
              tripId: row.id,
              uid: u.id,
              username: u.username || u.email || undefined,
              phone: phone?.trim() || undefined,
            });
            const sms = r.parent.sms;
            let smsNote = "SMS: not attempted";
            if (sms?.ok) smsNote = `SMS sent to ${sms.recipient || phone || "phone on file"}`;
            else if (sms?.skipped) smsNote = `SMS skipped (${sms.error || "Brevo SMS not configured"}) — copy credentials below`;
            else if (sms?.error) smsNote = `SMS failed: ${sms.error}`;
            setTripReissuedCred({
              kind: "parent",
              displayName: r.parent.displayName || u.displayName,
              username: r.parent.username,
              password: r.parent.password,
              revealUrl: r.parent.revealUrl,
              smsNote,
            });
          } catch (err) {
            window.alert(err instanceof Error ? err.message : String(err));
          } finally {
            setTripReissueBusy(null);
          }
        };

        const doReissueConductor = async (u: CrewRow) => {
          if (!fleetOrgCode || !isTripsApiConfigured()) {
            window.alert("Trips backend is not configured — cannot reissue credentials.");
            return;
          }
          const phone = window.prompt("Phone for SMS (leave blank to use the number on file):", "") ?? undefined;
          setTripReissueBusy(`c:${u.id}`);
          try {
            const r = await reissueTripConductor({
              orgCode: fleetOrgCode,
              tripId: row.id,
              uid: u.id,
              username: u.username || u.email || undefined,
              phone: phone?.trim() || undefined,
            });
            const sms = r.conductor.sms;
            let smsNote = "SMS: not attempted";
            if (sms?.ok) smsNote = `SMS sent to ${sms.recipient || phone || "phone on file"}`;
            else if (sms?.skipped) smsNote = `SMS skipped (${sms.error || "Brevo SMS not configured"}) — copy credentials below`;
            else if (sms?.error) smsNote = `SMS failed: ${sms.error}`;
            setTripReissuedCred({
              kind: "conductor",
              displayName: r.conductor.displayName || u.displayName,
              username: r.conductor.username,
              password: r.conductor.password,
              revealUrl: r.conductor.revealUrl,
              smsNote,
            });
          } catch (err) {
            window.alert(err instanceof Error ? err.message : String(err));
          } finally {
            setTripReissueBusy(null);
          }
        };

        return (
          <div className="fixed inset-0 z-[128] flex flex-col bg-slate-50">
            <header className="shrink-0 flex items-center justify-between gap-4 px-6 py-4 border-b bg-white">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  type="button"
                  onClick={() => { setTripsDetailTripId(null); setTripReissuedCred(null); }}
                  className="p-2 hover:bg-gray-100 rounded-xl shrink-0"
                  aria-label="Back"
                >
                  <FaArrowLeft className="w-5 h-5 text-gray-700" />
                </button>
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold text-gray-900 truncate">{row.title || "Trip"}</h2>
                  <p className="text-sm text-gray-500 truncate">
                    {row.destination}
                    {row.depD ? ` · Departs ${fmtWhen(row.depD)}` : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {row.status !== "cancelled" && row.bucket !== "past" && (
                  <button
                    type="button"
                    disabled={cancelTripBusy}
                    onClick={() => handleCancelTrip(row.id, d)}
                    className="px-4 py-2 text-sm font-semibold rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {cancelTripBusy ? "Cancelling…" : "Cancel trip"}
                  </button>
                )}
                {/* "Close" removed — it ran the EXACT same handler as the back
                    arrow three lines up, so the header offered one action
                    twice and left Cancel trip sitting between two ways out
                    (Gabriel, 09-07). Same call as the "View trip dashboard"
                    duplicate deleted on 09-05. */}
              </div>
            </header>
            <div className="flex-1 overflow-auto p-6 max-w-3xl mx-auto w-full space-y-6 pb-16">
              <FleetCard className="p-6 border border-gray-200 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</p>
                <p className="text-sm text-gray-800">
                  <span style={{ color: phaseColor, fontWeight: 700 }}>{phase}</span>
                  {!row.started && row.bucket !== "past" ? (
                    <span className="text-gray-500"> — waiting for driver/conductor to start</span>
                  ) : null}
                  {row.started ? <span className="text-gray-500"> — crew has started</span> : null}
                </p>
                <p className="text-sm text-gray-600">Departs: {fmtWhen(row.depD)}</p>
                {tripEndD && <p className="text-sm text-gray-600">Trip ends: {fmtWhen(tripEndD)}</p>}
              </FleetCard>

              <FleetCard className="p-6 border border-gray-200 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Vehicles</p>
                {busIds.length === 0 ? (
                  <p className="text-sm text-gray-500">—</p>
                ) : (
                  <ul className="space-y-2">
                    {busIds.map((bid) => {
                      const isHired = !!tripHireLabels[bid]
                        || segments.some((s) => String(s.busId) === bid && String(s.kind) === "hired");
                      return (
                        <li key={bid} className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm ${isHired ? "bg-violet-50 border border-violet-200" : "bg-slate-50 border border-slate-100"}`}>
                          <span className="font-medium text-gray-900">{busLabel(bid)}</span>
                          {isHired ? (
                            <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700 bg-violet-100 px-2 py-0.5 rounded-md">Hired bus</span>
                          ) : (
                            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-600 bg-slate-200/70 px-2 py-0.5 rounded-md">Fleet</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </FleetCard>

              {(conductors.length > 0 || conductorEmails.length > 0 || conductorEmail) && (
                <FleetCard className="p-6 border-2 border-amber-200 bg-amber-50/40 space-y-3">
                  <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Conductors (trip login)</p>
                  {conductors.length === 0 && (conductorEmails.length > 0 || conductorEmail) && (
                    <ul className="list-disc pl-5 text-sm text-gray-800 space-y-1">
                      {conductorEmails.map((em) => (<li key={em}>{em}</li>))}
                      {conductorEmail && !conductorEmails.includes(conductorEmail) ? <li>{conductorEmail}</li> : null}
                    </ul>
                  )}
                  {conductors.map((u) => (
                    <div key={u.id} className="rounded-xl border border-amber-200 bg-white px-3 py-3 space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{u.displayName}</p>
                          <p className="text-xs text-gray-500 mt-0.5">Login: <span className="font-mono text-gray-800">{u.username || u.email || "—"}</span></p>
                          {u.busName ? <p className="text-xs text-gray-500">Vehicle: {u.busName}</p> : null}
                        </div>
                        <button
                          type="button"
                          disabled={!!tripReissueBusy || row.bucket === "past"}
                          onClick={() => void doReissueConductor(u)}
                          className="shrink-0 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-amber-300 text-amber-900 hover:bg-amber-50 disabled:opacity-50"
                        >
                          {tripReissueBusy === `c:${u.id}` ? "…" : "Show new password"}
                        </button>
                      </div>
                    </div>
                  ))}
                  <p className="text-[11px] text-amber-900/70">Passwords are one-time. Use &quot;Show new password&quot; to mint fresh credentials for testing or if SMS/reveal was missed.</p>
                </FleetCard>
              )}

              {outsideParents.length > 0 && (
                <FleetCard className="p-6 border-2 border-emerald-200 bg-emerald-50/40 space-y-3">
                  <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Outside parents (temp login)</p>
                  {outsideParents.map((u) => (
                    <div key={u.id} className="rounded-xl border border-emerald-200 bg-white px-3 py-3 space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{u.displayName}</p>
                          <p className="text-xs text-gray-500 mt-0.5">Login: <span className="font-mono text-gray-800">{u.username || u.email}</span></p>
                          {u.childNames?.length ? (
                            <p className="text-xs text-gray-500">Children: {u.childNames.join(", ")}</p>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          disabled={!!tripReissueBusy || row.bucket === "past"}
                          onClick={() => void doReissueParent(u)}
                          className="shrink-0 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-emerald-300 text-emerald-900 hover:bg-emerald-50 disabled:opacity-50"
                        >
                          {tripReissueBusy === `p:${u.id}` ? "…" : "Show new password"}
                        </button>
                      </div>
                    </div>
                  ))}
                  <p className="text-[11px] text-emerald-900/70">Use their login in the parent app (trip parent login) to track the bus for their outside children.</p>
                </FleetCard>
              )}

              {tripReissuedCred && (
                <FleetCard className="p-6 border-2 border-blue-300 bg-blue-50 space-y-3">
                  <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">
                    New {tripReissuedCred.kind === "parent" ? "outside parent" : "conductor"} credentials (shown once)
                  </p>
                  <p className="text-sm font-semibold text-gray-900">{tripReissuedCred.displayName}</p>
                  <div className="grid gap-2 text-sm">
                    <div className="flex items-center justify-between gap-2 rounded-lg bg-white border px-3 py-2">
                      <span><span className="text-xs text-gray-500 block">Login</span><span className="font-mono">{tripReissuedCred.username}</span></span>
                      <button type="button" className="text-xs font-semibold text-blue-700" onClick={() => copyText(tripReissuedCred.username)}>Copy</button>
                    </div>
                    <div className="flex items-center justify-between gap-2 rounded-lg bg-white border px-3 py-2">
                      <span><span className="text-xs text-gray-500 block">Password</span><span className="font-mono">{tripReissuedCred.password}</span></span>
                      <button type="button" className="text-xs font-semibold text-blue-700" onClick={() => copyText(tripReissuedCred.password)}>Copy</button>
                    </div>
                    {tripReissuedCred.revealUrl && (
                      <div className="flex items-center justify-between gap-2 rounded-lg bg-white border px-3 py-2">
                        <span className="min-w-0"><span className="text-xs text-gray-500 block">One-time reveal</span><span className="text-xs break-all">{tripReissuedCred.revealUrl}</span></span>
                        <button type="button" className="text-xs font-semibold text-blue-700 shrink-0" onClick={() => copyText(tripReissuedCred.revealUrl || "")}>Copy</button>
                      </div>
                    )}
                  </div>
                  {tripReissuedCred.smsNote ? <p className="text-xs text-blue-900/80">{tripReissuedCred.smsNote}</p> : null}
                  <button type="button" className="text-xs text-gray-500 underline" onClick={() => setTripReissuedCred(null)}>Dismiss</button>
                </FleetCard>
              )}

              {segments.length > 0 && (
                <FleetCard className="p-6 border border-gray-200 space-y-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">By vehicle</p>
                  {segments.map((seg, i) => {
                    const bid = String(seg.busId ?? "");
                    const kids = Array.isArray(seg.childIds) ? seg.childIds : [];
                    const kind = String(seg.kind ?? "");
                    const isHired = kind === "hired";
                    return (
                      <div key={`${bid}-${i}`} className={`border rounded-xl p-4 ${isHired ? "border-violet-200 bg-violet-50/50" : "border-gray-100 bg-gray-50/50"}`}>
                        <p className="text-sm font-medium text-gray-900">
                          {busLabel(bid)}
                          {isHired ? <span className="ml-2 text-[10px] font-bold uppercase text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">Hired</span> : null}
                          {!isHired && kind ? <span className="text-gray-500 font-normal"> · {kind}</span> : null}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{kids.length} student(s)</p>
                        <ul className="mt-2 max-h-40 overflow-auto text-sm text-gray-700 space-y-0.5 list-disc pl-5">
                          {kids.map((cid) => (
                            <li key={cid}>{childName(String(cid))}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </FleetCard>
              )}
              {segments.length === 0 && childIds.length > 0 && (
                <FleetCard className="p-6 border border-gray-200 space-y-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Students on trip</p>
                  <ul className="max-h-64 overflow-auto text-sm text-gray-800 space-y-1 list-disc pl-5">
                    {childIds.map((cid) => (
                      <li key={cid}>{childName(String(cid))}</li>
                    ))}
                  </ul>
                </FleetCard>
              )}
            </div>
          </div>
        );
      })()}

      {/* Detail view: existing tabbed content */}
      {showDetailView && bus && (
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* What the bus is doing, before what the bus is. */}
          <BusDetailHeader {...busDetailHeaderProps} />

          {/* Four sections. See the tab state for why seven became four. */}
          <div className="fd-subtabs" role="tablist" aria-label="Bus sections">
            {(
              [
                ["today", "Today", 0],
                ...((bus.children?.length ?? 0) > 0
                  ? [["people", "People", 0] as const]
                  : []),
                ["money", "Money", 0],
                ["incidents", "Incidents", detailIncidentCount],
                ["settings", "Settings", 0],
              ] as const
            ).map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className="fd-subtab"
              >
                {label}
                {count > 0 && <span className="fd-cnt">{count}</span>}
              </button>
            ))}
            {tab === "people" && (
              <div className="fd-seg" style={{ marginLeft: "auto", alignSelf: "center" }}>
                {(["children", "attendance", "absentees"] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={peopleSub === k}
                    onClick={() => setPeopleSub(k)}
                  >
                    {k === "children" ? "Roster" : k === "attendance" ? "Attendance" : "Absentees"}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

        {/* Announce modal */}
        {showAnnounce && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 6200 }} onClick={() => !announceSending && setShowAnnounce(false)}>
            <div style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 440, width: "90%", boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }} onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: "Instrument Serif, serif", fontSize: 22, marginBottom: 8 }}>Send Announcement</h3>
              <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>All parents on {busPhrase(bus?.name)} will receive this as a push notification and in their in-app inbox.</p>
              <textarea
                value={announceMsg}
                onChange={(e) => setAnnounceMsg(e.target.value)}
                placeholder="e.g. Bus will be 15 minutes late today."
                rows={4}
                maxLength={500}
                style={{ width: "100%", padding: 12, border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, fontFamily: "Geist, sans-serif", resize: "vertical", marginBottom: 8 }}
              />
              <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 16 }}>{announceMsg.length}/500 characters</div>
              {announceResult && <div style={{ marginBottom: 16, fontSize: 14, color: announceResult.startsWith("Sent") ? "#16a34a" : "#dc2626" }}>{announceResult}</div>}
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={async () => {
                    if (!announceMsg.trim() || !bus || !fleetOrgCode) return;
                    setAnnounceSending(true);
                    setAnnounceResult(null);
                    try {
                      const res = await announceToBusParents(fleetOrgCode, bus.id, announceMsg.trim());
                      if (!res.ok) {
                        setAnnounceResult(res.message || "Failed to send");
                        return;
                      }
                      setAnnounceResult(`Sent to ${res.sent ?? 0} of ${res.total ?? 0} parents`);
                      if ((res.sent ?? 0) > 0) setAnnounceMsg("");
                    } catch (err: unknown) {
                      const e = err as { message?: string };
                      setAnnounceResult(e?.message || "Failed to send");
                    } finally {
                      setAnnounceSending(false);
                    }
                  }}
                  disabled={!announceMsg.trim() || announceSending || !fleetOrgCode}
                  style={{ padding: "10px 20px", background: announceMsg.trim() && !announceSending ? "#2563eb" : "#cbd5e1", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: announceMsg.trim() && !announceSending ? "pointer" : "not-allowed", fontFamily: "Geist, sans-serif" }}
                >
                  {announceSending ? "Sending…" : "Send"}
                </button>
                <button
                  onClick={() => { setShowAnnounce(false); setAnnounceResult(null); }}
                  disabled={announceSending}
                  style={{ padding: "10px 20px", background: "#f1f5f9", color: "#334155", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: announceSending ? "not-allowed" : "pointer", fontFamily: "Geist, sans-serif" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings — flat Aurora (Zana violet) full-screen page */}
        {showSettings && (
          <FleetAdminSettings
            orgCode={fleetOrgCode || ""}
            orgDisplayName={orgDisplayName}
            userEmail={currentUser?.email}
            schoolLocation={schoolHomeLocation}
            buses={buses}
            fleetUsers={fleetUsers}
            nowMs={nowMs}
            signupCode={signupCode}
            signupCodeExpiresAt={signupCodeExpiresAt}
            onGenerateSignupCode={async () => {
              if (!fleetOrgCode || !currentUser?.uid) return;
              const code = String(Math.floor(1000 + Math.random() * 9000));
              const expiresAt = Date.now() + 15 * 60 * 1000;
              try {
                await setDoc(doc(db, "fleet_signup_codes", fleetOrgCode), {
                  code,
                  orgCode: fleetOrgCode,
                  generatedBy: currentUser.uid,
                  expiresAt: Timestamp.fromMillis(expiresAt),
                  createdAt: serverTimestamp(),
                });
                setSignupCode(code);
                setSignupCodeExpiresAt(expiresAt);
              } catch (e) {
                console.warn("Failed to generate signup code:", e);
              }
            }}
            /* No onSaveSchoolLocation, so the campus pin shows view-only. A
               school sets it when the account is created; after that only
               Trasse moves it, and the rules refuse a fleet admin's write
               (Gabriel, 2026-10-26). */
            parentMapEnabled={parentMapEnabled}
            parentMapSaving={parentMapSaving}
            onToggleParentMap={async () => {
              if (!fleetOrgCode || parentMapSaving) return;
              setParentMapSaving(true);
              try {
                const q = query(collection(db, "organizations"), where("code", "==", fleetOrgCode), limit(1));
                const snap = await getDocs(q);
                if (snap.docs[0]) {
                  await updateDoc(doc(db, "organizations", snap.docs[0].id), {
                    parentMapEnabled: !parentMapEnabled,
                  });
                }
              } catch (e) {
                console.warn("Failed to update parentMapEnabled:", e);
              } finally {
                setParentMapSaving(false);
              }
            }}
            legacyFuelFlowEnabled={legacyFuelFlowEnabled}
            tripCompleteSaving={tripCompleteSaving}
            showTermDates={canPlanSchoolTrips}
            termStartDate={(orgDocData?.currentTermStartDate as string) || ""}
            termEndDate={(orgDocData?.currentTermEndDate as string) || ""}
            onSaveTermDates={async (start, end) => {
              if (!fleetOrgCode) throw new Error("No organization.");
              const q = query(collection(db, "organizations"), where("code", "==", fleetOrgCode), limit(1));
              const snap = await getDocs(q);
              if (!snap.docs[0]) throw new Error("Organization not found.");
              await updateDoc(doc(db, "organizations", snap.docs[0].id), {
                currentTermStartDate: start || null,
                currentTermEndDate: end || null,
                updatedAt: serverTimestamp(),
              });
            }}
            onToggleLegacyFuelFlow={async () => {
              if (!fleetOrgCode || tripCompleteSaving) return;
              setTripCompleteSaving(true);
              const next = !legacyFuelFlowEnabled;
              try {
                const q = query(collection(db, "organizations"), where("code", "==", fleetOrgCode), limit(1));
                const snap = await getDocs(q);
                if (snap.docs[0]) {
                  // No optimistic flip: the org onSnapshot listener above owns
                  // this state, so the switch only moves once the write is
                  // actually on the doc. A rules rejection leaves it where it
                  // was, which is the honest outcome.
                  await updateDoc(doc(db, "organizations", snap.docs[0].id), {
                    legacyFuelFlowEnabled: next,
                    updatedAt: serverTimestamp(),
                  });
                }
              } catch (e) {
                console.warn("Failed to update legacyFuelFlowEnabled:", e);
              } finally {
                setTripCompleteSaving(false);
              }
            }}
            inviteAdminEmail={inviteAdminEmail}
            onInviteAdminEmailChange={(v) => {
              setInviteAdminEmail(v);
              setAdminInviteStatus("idle");
            }}
            onInviteAdmin={() => void handleInviteAdmin()}
            adminInviteSending={adminInviteSending}
            adminInviteStatus={adminInviteStatus}
            adminInviteError={adminInviteError}
            pendingAdminInvites={
              (Array.isArray(orgDocData?.pendingAdminInvites)
                ? (orgDocData?.pendingAdminInvites as unknown[])
                : []
              ).map((e) => String(e || "").toLowerCase()).filter(Boolean)
            }
            adminEmails={
              (Array.isArray(orgDocData?.adminEmails)
                ? (orgDocData?.adminEmails as unknown[])
                : []
              ).map((e) => String(e || "").toLowerCase()).filter(Boolean)
            }
            primaryAdminEmail={String(orgDocData?.adminEmail || "").toLowerCase()}
            onResendAdminInvite={(email) => void handleResendAdminInvite(email)}
            inviteRole={inviteRole}
            onInviteRoleChange={setInviteRole}
            inviteEmails={inviteEmails}
            onRemoveInviteEmail={(em) => setInviteEmails((prev) => prev.filter((e) => e !== em))}
            inviteDraft={inviteDraft}
            onInviteDraftChange={setInviteDraft}
            onInviteDraftKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                const addr = inviteDraft.trim().toLowerCase();
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) return;
                if (inviteEmails.includes(addr)) {
                  setInviteDraft("");
                  return;
                }
                const alreadyPending = sentInvites.some(
                  (i) => !i.superseded && !i.used && i.role === inviteRole && i.email === addr,
                );
                if (alreadyPending) {
                  customToast.show(`${addr} is already invited — use Resend below instead.`, "info");
                  setInviteDraft("");
                  return;
                }
                setInviteEmails((prev) => [...prev, addr]);
                setInviteDraft("");
              }
            }}
            onBulkInvite={() => void handleBulkInvite()}
            bulkInviting={bulkInviting}
            sentInvites={sentInvites}
            onResendInvite={(email, role) => void handleResendInvite(email, role)}
            resendingEmail={resendingEmail}
            onBack={() => setShowSettings(false)}
            onSignOut={() => {
              setShowSettings(false);
              logout();
            }}
          />
        )}

        {showDetailView && bus && (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
          {/* OVERVIEW */}
          {tab === "today" && (
            <div style={{ animation: "fadeUp 0.2s ease" }}>
              {/* Where it is, before what it is. Compact on purpose — Live Map
                  remains the fleet view; this is one bus and no chrome. */}
              <div style={{ marginBottom: 20 }}>
                {(() => {
                  /* Live wins until a run is clicked: an admin watching a bus
                     that is out RIGHT NOW should not be shown yesterday. Once
                     a row is clicked, that run's trail is the subject — the
                     live marker stays visible on top if there is one. */
                  const liveNow = bus.liveLocation != null
                    && bus.liveLocationUpdatedAt != null
                    && nowMs - bus.liveLocationUpdatedAt < 60_000;
                  const showLive = liveNow && selectedRunId === null;
                  const trailPts = showLive
                    ? (bus.gpsRoutePoints ?? [])
                    : (runTrail && shownRun && runTrail.forId === shownRun.id ? runTrail.pts : []);
                  return (
                    <BusMiniMap
                      liveLocation={bus.liveLocation ?? null}
                      routePoints={trailPts}
                      fitToRoute={!showLive}
                      emptyMessage={shownRun && !showLive
                        ? 'No trail recorded for this run — the phone was not tracking.'
                        : undefined}
                      stale={
                        !bus.liveLocationUpdatedAt
                        || nowMs - bus.liveLocationUpdatedAt >= 60_000
                      }
                      height={300}
                    />
                  );
                })()}
              </div>

              {/* WHEN, after WHERE. The header counts (3 of 5 aboard) and the
                  map shapes the run; this is the order it happened in, off
                  the same roster rows. Live run from the bus document, so it
                  moves as the driver marks children; parked, the last
                  finished run. */}
              {(() => {
                const onRoute = bus.route?.status === "active" || bus.route?.status === "collecting";
                const last = lastTripRecordByBusId[bus.id];
                if (onRoute) {
                  return (
                    <RunTimeline
                      live
                      leg={bus.route?.type ?? null}
                      started={bus.route?.started ?? null}
                      finished={null}
                      km={null}
                      children={bus.children
                        .filter((c) => !c.transportSuspended)
                        .map((c) => ({ name: c.name, status: c.status, time: c.time ?? null }))}
                      now={nowMs}
                    />
                  );
                }
                if (!last || isRefuelAttendance(last)) return null;
                return (
                  <RunTimeline
                    live={false}
                    leg={last.type ?? null}
                    started={last.started}
                    finished={last.finished}
                    km={last.km}
                    children={(last.children ?? []).map((c) => ({ name: c.name, status: c.status, time: c.time }))}
                    now={nowMs}
                  />
                );
              })()}

              <SectionHead sub={`${bus.plate} · ${bus.school || "—"} · last updated · ${ft(new Date())}`}>Bus overview</SectionHead>

              {/* Fleet alerts (traffic, etc.) */}
              {fleetAlerts.filter((a) => !a.read).length > 0 && (
                <Card style={{ marginBottom: 16, borderLeft: "4px solid #dc2626", background: "#fef2f2" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Recent Alerts</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>Includes route deviation when a trip doesn’t match saved routes.</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {fleetAlerts.filter((a) => !a.read).slice(0, 5).map((alert) => (
                      <div key={alert.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "8px 0", borderBottom: "1px solid #fecaca" }}>
                        <span style={{ fontSize: 18 }}>🚦</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{alert.busName}</div>
                          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{alert.message}</div>
                          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>{ft(alert.timestamp)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>Trip distance from the driver app (all completed routes).</div>
              </div>
              {attendanceError && (
                <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "#fef2f2", color: "#b91c1c", fontSize: 12 }}>
                  Could not load trips: {attendanceError}
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
                <StatTile label="Children" value={bus.children.length} sub={`${bus.children.filter(c => c.status === "picked_up").length} currently aboard`} accent="#2563eb" />
                <StatTile label="Drivers" value={bus.drivers.length} sub={namesForDriverIds(bus.drivers)} accent="#0f172a" />
                <StatTile label="Total Rides" value={busRecords.length} sub="Attendance records from the driver app" accent="#64748b" />
                <StatTile label="Total km" value={formatDistance(busRecords.length ? totalKm : null)} sub={`across ${busRecords.length} trips · ${avgDur} min avg`} accent="#16a34a" />
              </div>

              {/* Recent runs — the per-trip distance that existed nowhere.
                  `distanceKm` has been written on every completed route since
                  Batch 1 and read only in aggregate: "Total km across N trips"
                  and a cost-per-km figure. Nothing ever showed a SINGLE run, so
                  the number feeding fuel economy and route scoring could not be
                  checked against a car odometer — which is bug 35's open half
                  and the thing Gabriel could not find (2026-08-01).
                  Deliberately a short list, not the full run timeline from the
                  Claude Design proposal: that is still to build, and an owner
                  needs to see last week's runs long before they need a timeline. */}
              {busRecords.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Recent runs</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      newest first · distance is the GPS odometer, so treat it as “about”
                    </div>
                  </div>
                  {/* Every run, in one scrolling box — the slice(0, 12) cap is
                      gone (Gabriel, 2026-08-06: "I should be able to scroll
                      down… to see every trip"). ~12 rows tall, sticky header,
                      scroll for the rest; the loader's own fetch window is
                      what bounds the list, not this render. */}
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflowX: "auto", maxHeight: 480, overflowY: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "#f8fafc", textAlign: "left", position: "sticky", top: 0, zIndex: 1 }}>
                          <th style={{ padding: "8px 12px", fontWeight: 600, color: "#475569", background: "#f8fafc" }}>Date</th>
                          <th style={{ padding: "8px 12px", fontWeight: 600, color: "#475569", background: "#f8fafc" }}>Run</th>
                          <th style={{ padding: "8px 12px", fontWeight: 600, color: "#475569", background: "#f8fafc" }}>Distance</th>
                          <th style={{ padding: "8px 12px", fontWeight: 600, color: "#475569", background: "#f8fafc" }}>Time</th>
                          <th style={{ padding: "8px 12px", fontWeight: 600, color: "#475569", background: "#f8fafc" }}>Children</th>
                          <th style={{ padding: "8px 6px", fontWeight: 600, color: "#475569", background: "#f8fafc", textAlign: "center" }} title="Pin a run to find it later under Analytics → Routes">Pin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {busRecords.map((r) => {
                          const kids = r.children ?? [];
                          /* WHAT HAPPENED, not "accounted for". This counted
                             everyone not still aboard — so a run where nobody
                             was collected and both children were marked absent
                             read "2/2", which Gabriel read as two collected
                             (2026-09-28: "I did not collect the students, so
                             why is it saying 2/2?"). Collected over total, and
                             the absences said in words. */
                          const collected = kids.filter(
                            (c) => c.status === "picked_up" || c.status === "dropped_off" || c.status === "dropped",
                          ).length;
                          const absent = kids.filter((c) => c.status === "absent").length;
                          /* The run on the map is FADED here — Gabriel's own
                             design: "fade it to show which one is being shown;
                             the most recent should be faded when they arrive,
                             since it's the one that's there by default." */
                          const shownOnMap = shownRun?.id === r.id;
                          return (
                            <tr
                              key={r.id}
                              onClick={() => setSelectedRunId(r.id)}
                              title={shownOnMap ? 'Showing on the map above' : 'Click to show this run on the map'}
                              style={{
                                borderTop: "1px solid #f1f5f9",
                                cursor: "pointer",
                                opacity: shownOnMap ? 0.55 : 1,
                                background: shownOnMap ? "#f1f5f9" : undefined,
                              }}
                            >
                              <td style={{ padding: "8px 12px", color: "#0f172a" }}>
                                {r.date ? r.date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}
                              </td>
                              <td style={{ padding: "8px 12px", color: "#475569", textTransform: "capitalize" }}>
                                {(r.type || "").replace(/_/g, " ") || "—"}
                                {r.pinned && r.pinnedName ? <span style={{ marginLeft: 6, fontSize: 11, color: "#6d28d9", textTransform: "none" }}>📌 {r.pinnedName}</span> : null}
                              </td>
                              {/* An unfinished or unsynced run has no distance. Showing
                                  "0.0 km" would read as "this bus went nowhere". */}
                              <td style={{ padding: "8px 12px", fontWeight: 600, color: (r.km ?? 0) > 0 ? "#16a34a" : "#94a3b8" }}>
                                {/* "0.0 km" was still reachable: the guard is `> 0` but the
                                    display rounds to one decimal, so a 40-metre shunt printed
                                    0.0 km — the exact "this bus went nowhere" reading the
                                    em-dash exists to avoid (Gabriel, 2026-08-03). */}
                                {(r.km ?? 0) <= 0
                                  ? "—"
                                  : r.km! < 0.05
                                    ? "< 0.1 km"
                                    : `${r.km!.toFixed(1)} km`}
                              </td>
                              <td style={{ padding: "8px 12px", color: "#475569" }}>
                                {r.status === "cancelled" ? "—" : r.duration ? `${Math.round(r.duration)} min` : "—"}
                              </td>
                              {/* A cancelled run used to read "0/5" — which looks like
                                  five children never got home. Say what happened. */}
                              <td style={{ padding: "8px 12px", color: r.status === "cancelled" ? "#b45309" : "#475569", fontWeight: r.status === "cancelled" ? 600 : undefined }}>
                                {r.status === "cancelled"
                                  ? "Cancelled"
                                  : kids.length
                                    ? `${collected}/${kids.length}${absent > 0 ? ` · ${absent} absent` : ""}`
                                    : "—"}
                              </td>
                              <td style={{ padding: "4px 6px", textAlign: "center" }}>
                                <button
                                  type="button"
                                  onClick={(e) => void togglePinRun(r, e)}
                                  title={r.pinned ? `Pinned: ${r.pinnedName || "this run"} — click to unpin` : "Pin this run to find it later under Analytics → Routes"}
                                  aria-label={r.pinned ? "Unpin run" : "Pin run"}
                                  style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 14, opacity: r.pinned ? 1 : 0.3, padding: 2 }}
                                >
                                  📌
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {busRecords.length > 12 && (
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
                      All {busRecords.length} runs — scroll the list for older ones.
                    </div>
                  )}
                </div>
              )}
              {/* Full Fuel & Efficiency Estimate — same card as Analytics tab */}
              {(() => {
                const tankL = bus.fuelTankCapacityL ?? DEFAULT_TANK_CAPACITY_L;
                const isEstimatedTank = bus.fuelTankCapacityL == null;
                const inTerm = (dt: Date | null | undefined) => termStartMs == null || (dt != null && dt.getTime() >= termStartMs);
                const termEvents = refuelEvents.filter((e) => inTerm(e.timestamp));
                const allTimeRefuelEvents = termEvents.length;
                const allTimeSurveyAlerts = busRecords.filter((r) => r.needsRefuelSoon && inTerm(r.date)).length;
                const allTimeRefuelSignals = allTimeRefuelEvents + allTimeSurveyAlerts;
                const allTimeLitresAdded = termEvents.reduce((s, e) => s + (e.litersAdded ?? 0), 0);
                const termKm = busRecords.filter((r) => inTerm(r.date)).reduce((s, r) => s + (r.km || 0), 0);
                /* Clean odometer-bracketed fills: the ring's preferred source. Anything
                   needs_review is not measurement — the review list owns it. */
                const cleanOdoFills = termEvents.filter(
                  (e) => (e.kmSinceLastRefuel ?? 0) > 0 && (e.litersAdded ?? 0) > 0 && e.efficiencyFlag !== "needs_review",
                );
                const odoWindowKm = cleanOdoFills.reduce((s, e) => s + (e.kmSinceLastRefuel ?? 0), 0);
                const odoWindowLitres = cleanOdoFills.reduce((s, e) => s + (e.litersAdded ?? 0), 0);
                const ovEffInput: BusFuelInput = {
                  totalKm30: termKm,
                  refuelCount30: allTimeRefuelSignals,
                  needsRefuelCount: allTimeSurveyAlerts,
                  tankCapacityL: tankL,
                  totalLitresAdded: allTimeLitresAdded,
                  odoWindowKm,
                  odoWindowLitres,
                  benchmarkKmPerL: bus.benchmarkKmPerL ?? undefined,
                  busAgeYears: busAgeYearsFrom(bus.yearOfManufacture),
                };
                const ovEffEst = estimateFuelEfficiency(ovEffInput);
                const ovPieColor = ovEffEst.pct != null
                  ? (ovEffEst.pct <= 30 ? "#dc2626" : ovEffEst.pct <= 70 ? "#f97316" : "#16a34a")
                  : "#e2e8f0";
                const ovPieRows = ovEffEst.pct != null
                  ? [
                      { name: "eff", value: ovEffEst.pct, fill: ovPieColor },
                      { name: "rest", value: Math.max(0, 100 - ovEffEst.pct), fill: "#e2e8f0" },
                    ]
                  : [{ name: "empty", value: 100, fill: "#e2e8f0" }];
                return (
                  <Card style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Fuel &amp; Efficiency Estimate</div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 16 }}>Auto-calculated from total km traveled, all refuel signals &amp; tank capacity</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" }}>
                      <div style={{ flex: "1 1 240px", minWidth: 220 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
                          <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 10px" }}>
                            <div style={{ fontSize: 9, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Total km traveled</div>
                            <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{formatDistance(totalKm)}</div>
                            <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>across {busRecords.length} routes</div>
                          </div>
                          <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 10px" }}>
                            <div style={{ fontSize: 9, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Refuel signals</div>
                            <div style={{ fontSize: 17, fontWeight: 700, color: allTimeRefuelSignals > 0 ? "#0f172a" : "#94a3b8" }}>{allTimeRefuelSignals}</div>
                            <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>
                              {allTimeRefuelEvents > 0 ? `${allTimeRefuelEvents} logged` : "0 logged"}
                              {allTimeSurveyAlerts > 0 ? ` · ${allTimeSurveyAlerts} surveys` : ""}
                            </div>
                          </div>
                          <div
                            style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 10px", cursor: "pointer" }}
                            onClick={() => { if (!editingFuelCapacity) { setFuelCapacityInput(String(tankL)); setBenchmarkInput(String(bus.benchmarkKmPerL ?? "")); setBusYearInput(String(bus.yearOfManufacture ?? "")); setEditingFuelCapacity(true); } }}
                          >
                            <div style={{ fontSize: 9, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, display: "flex", justifyContent: "space-between" }}>
                              <span>Fuel capacity &amp; benchmark</span>
                              <span style={{ color: "#2563eb" }}>Edit</span>
                            </div>
                            {editingFuelCapacity ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }} onClick={(e) => e.stopPropagation()}>
                                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                  <span style={{ fontSize: 9, color: "#64748b", width: 60, flexShrink: 0 }}>Tank (L)</span>
                                  <input type="number" min={50} max={500} value={fuelCapacityInput} onChange={(e) => setFuelCapacityInput(e.target.value)} placeholder="optional" style={{ flex: 1, fontSize: 11, fontWeight: 700, border: "1px solid #2563eb", borderRadius: 6, padding: "2px 4px", background: "#fff", outline: "none" }} autoFocus />
                                </div>
                                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                  <span style={{ fontSize: 9, color: "#dc2626", width: 60, flexShrink: 0 }}>Benchmark*</span>
                                  <input type="number" min={0.1} step={0.1} value={benchmarkInput} onChange={(e) => setBenchmarkInput(e.target.value)} placeholder="km/L e.g. 8" style={{ flex: 1, fontSize: 11, fontWeight: 700, border: "1px solid #dc2626", borderRadius: 6, padding: "2px 4px", background: "#fff", outline: "none" }} />
                                </div>
                                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                  <span style={{ fontSize: 9, color: "#64748b", width: 60, flexShrink: 0 }}>Year</span>
                                  <input type="number" min={1980} max={new Date().getFullYear()} value={busYearInput} onChange={(e) => setBusYearInput(e.target.value)} placeholder="e.g. 2015" style={{ flex: 1, fontSize: 11, fontWeight: 700, border: "1px solid #2563eb", borderRadius: 6, padding: "2px 4px", background: "#fff", outline: "none" }} />
                                </div>
                                <div style={{ fontSize: 9, color: "#94a3b8" }}>* Required for score · Year relaxes the target for an older bus</div>
                                <div style={{ display: "flex", gap: 4 }}>
                                  <button onClick={async (e) => {
                                    e.stopPropagation();
                                    const updates = buildFuelBenchmarkUpdates(fuelCapacityInput, benchmarkInput, busYearInput);
                                    try {
                                      await updateDoc(doc(db, "buses", bus.id), updates);
                                      customToast.show("Saved", "success");
                                    } catch (err) {
                                      console.error("Fuel benchmark save failed:", err);
                                      customToast.show("Failed to save. Check your permissions or try again.", "error");
                                    }
                                    setEditingFuelCapacity(false);
                                  }} style={{ fontSize: 10, fontWeight: 700, background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "3px 7px", cursor: "pointer" }}>Save</button>
                                  <button onClick={(e) => { e.stopPropagation(); setEditingFuelCapacity(false); }} style={{ fontSize: 10, background: "none", border: "none", color: "#94a3b8", cursor: "pointer" }}>✕</button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div style={{ fontSize: 14, fontWeight: 700, color: isEstimatedTank ? "#64748b" : "#0f172a" }}>{isEstimatedTank ? `~${DEFAULT_TANK_CAPACITY_L} L` : `${bus.fuelTankCapacityL} L`}</div>
                                <div style={{ fontSize: 11, marginTop: 2, color: bus.benchmarkKmPerL ? "#0f172a" : "#dc2626", fontWeight: 600 }}>
                                  {bus.benchmarkKmPerL ? `${bus.benchmarkKmPerL} km/L benchmark` : "⚠ No benchmark set"}
                                </div>
                                {bus.yearOfManufacture ? <div style={{ fontSize: 10, marginTop: 1, color: "#94a3b8", fontWeight: 600 }}>{bus.yearOfManufacture} · {busAgeYearsFrom(bus.yearOfManufacture)}yr old</div> : null}
                              </>
                            )}
                          </div>
                        </div>
                        {ovEffEst.confidence === "no_benchmark" ? (
                          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 12px", fontSize: 11, color: "#dc2626", lineHeight: 1.5 }}>
                            <strong>⚠ No benchmark set</strong>
                            <div style={{ marginTop: 4, fontWeight: 400 }}>
                              Click <strong>Edit</strong> above to enter a km/L benchmark for this bus.
                              The benchmark is the efficiency target (e.g. 8 km/L). Once set, the efficiency score is calculated from total km traveled and litres added by drivers.
                              {ovEffEst.kmPerL != null && <span style={{ display: "block", marginTop: 4, color: "#0f172a", fontWeight: 600 }}>Current consumption: {ovEffEst.kmPerL.toFixed(2)} km/L ({ovEffEst.l100km?.toFixed(1)} L/100km)</span>}
                            </div>
                          </div>
                        ) : (
                          <div style={{ background: ovEffEst.confidence === "measured" ? "#f0fdf4" : ovEffEst.confidence === "estimated" ? "#fff7ed" : "#f8fafc", border: `1px solid ${ovEffEst.confidence === "measured" ? "#bbf7d0" : ovEffEst.confidence === "estimated" ? "#fed7aa" : "#e2e8f0"}`, borderRadius: 8, padding: "10px 12px", fontSize: 11, color: ovEffEst.confidence === "measured" ? "#15803d" : ovEffEst.confidence === "estimated" ? "#c2410c" : "#64748b", lineHeight: 1.5 }}>
                            <strong>{ovEffEst.confidenceLabel}</strong>
                            {ovEffEst.kmPerL != null && <span style={{ marginLeft: 6, color: "#0f172a", fontWeight: 600 }}>{ovEffEst.kmPerL.toFixed(2)} km/L ({ovEffEst.l100km?.toFixed(1)} L/100km)</span>}
                            {bus.benchmarkKmPerL && <span style={{ marginLeft: 6, color: "#64748b" }}>· vs {bus.benchmarkKmPerL} km/L target</span>}
                            <div style={{ marginTop: 4, fontWeight: 400, color: "#64748b" }}>
                              {ovEffEst.confidence === "early" ? "Ask drivers to log refuel events (with litres) to calculate a score." : allTimeLitresAdded > 0 ? `${allTimeLitresAdded.toFixed(1)} L added · ${totalKm.toFixed(0)} km (all-time)` : `${allTimeRefuelSignals} refuel signal(s) · ${totalKm.toFixed(0)} km total`}
                            </div>
                          </div>
                        )}
                      </div>
                      <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>Fuel efficiency score</div>
                        <div style={{ position: "relative", width: 148, height: 148 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie data={ovPieRows} dataKey="value" cx="50%" cy="50%" innerRadius={48} outerRadius={64} paddingAngle={ovEffEst.pct != null ? 2 : 0} startAngle={90} endAngle={-270} strokeWidth={0}>
                                {ovPieRows.map((entry, i) => <Cell key={i} fill={entry.fill} stroke="none" />)}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                          <div style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                            {ovEffEst.pct != null ? (
                              <>
                                <span style={{ fontFamily: "Instrument Serif, serif", fontSize: 26, fontWeight: 600, color: ovPieColor, lineHeight: 1 }}>{ovEffEst.pct}%</span>
                                <span style={{ fontSize: 9, color: "#64748b", marginTop: 3, fontWeight: 600 }}>{ovEffEst.pct <= 30 ? "Poor" : ovEffEst.pct <= 70 ? "Fair" : "Efficient"}</span>
                              </>
                            ) : ovEffEst.confidence === "no_benchmark" ? (
                              <span style={{ fontSize: 10, color: "#dc2626", textAlign: "center", padding: "0 10px", fontWeight: 600 }}>Set benchmark</span>
                            ) : (
                              <span style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", padding: "0 12px" }}>Need data</span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, fontSize: 9, color: "#94a3b8" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#dc2626", display: "inline-block" }} />≤30%</span>
                          <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f97316", display: "inline-block" }} />31–70%</span>
                          <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a", display: "inline-block" }} />&gt;70%</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })()}

              {/* Odometer review — flagged readings + Zana's proposed fixes */}
              {(() => {
                const flagged = refuelEvents.filter(
                  (e) => (e.reviewReason ?? "").startsWith("odometer_") && !e.odometerCorrected,
                );
                /* The trace. Approving a fix used to make the whole card vanish
                   — "it told me it did then left" (Gabriel, 2026-08-23). The
                   last corrections stay listed as "Applied · when · new km". */
                const corrected = refuelEvents
                  .filter((e) => e.odometerCorrected)
                  .sort((a, b) => (b.correctedAt?.getTime() ?? b.timestamp.getTime()) - (a.correctedAt?.getTime() ?? a.timestamp.getTime()))
                  .slice(0, 3);
                if (!flagged.length && !corrected.length) return null;
                /* Was a local table of three, worded differently from every
                   other surface and missing spans_unmetered_fill entirely. The
                   reason a fill is flagged is now written once, in alertMeta,
                   and read by this card, the High Risk alert row and (next
                   build) the app's own message — so the owner cannot be told
                   two different things about one number. */
                const reasonCopy = REVIEW_REASON_COPY;
                return (
                  <Card style={{ marginBottom: 16, border: "1px solid #fde68a", background: "#fffbeb" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#b45309", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                      Odometer review · {flagged.length ? flagged.length : "all clear"}
                    </div>
                    {flagged.length > 0 && (
                      <div style={{ fontSize: 12, color: "#92400e", marginBottom: 12 }}>
                        These readings failed a physics check when they were logged. Zana proposes the
                        floor GPS can prove; approving writes the corrected number and keeps the original
                        on the record.
                      </div>
                    )}
                    {corrected.length > 0 && (
                      <div style={{ fontSize: 12, color: "#166534", marginBottom: flagged.length ? 10 : 0, lineHeight: 1.6 }}>
                        {corrected.map((e) => (
                          <div key={`fixed-${e.id}`}>
                            ✓ Applied{e.correctedAt ? ` · ${e.correctedAt.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                            {" — "}now {e.odometerKm != null ? e.odometerKm.toLocaleString() : "—"} km
                            {e.odometerOriginalKm != null ? ` (driver typed ${e.odometerOriginalKm.toLocaleString()} km)` : ""}; the original stays on the record.
                          </div>
                        ))}
                      </div>
                    )}
                    {flagged.map((e) => {
                      const floor = (e.previousOdometerKm ?? 0) + Math.max(1, Math.ceil(e.gpsKmSincePrevRefuel ?? 0));
                      // No tracked km between the pair = GPS proves nothing.
                      const gpsProven = (e.gpsKmSincePrevRefuel ?? 0) > 0;
                      return (
                        <div key={e.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 0", borderTop: "1px solid #fde68a" }}>
                          <div style={{ fontSize: 12.5, color: "#78350f" }}>
                            <div style={{ fontWeight: 700 }}>
                              {e.timestamp.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · typed {e.odometerKm != null ? e.odometerKm.toLocaleString() : "—"} km
                            </div>
                            <div>
                              {reasonCopy[e.reviewReason ?? ""] ?? e.reviewReason}
                              {gpsProven
                                ? ` · GPS proves ≥ ${floor.toLocaleString()} km`
                                : ` · previous reading ${(e.previousOdometerKm ?? 0).toLocaleString()} km · nothing tracked between them, so neither is proven`}
                            </div>
                          </div>
                          <button
                            onClick={() => approveOdometerFix(e.id, gpsProven ? floor : (e.previousOdometerKm ?? floor), e.odometerKm, gpsProven)}
                            disabled={odoFixBusy === e.id || (e.previousOdometerKm ?? 0) <= 0}
                            style={{ border: "1px solid #f59e0b", background: "#fff", color: "#b45309", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: odoFixBusy === e.id ? 0.5 : 1 }}
                          >
                            {odoFixBusy === e.id ? "Saving…" : gpsProven ? "Approve fix" : "Set the right reading"}
                          </button>
                        </div>
                      );
                    })}
                  </Card>
                );
              })()}

              {/* Fuel (last 30 days) — click to open efficiency modal */}
              <div role="button" tabIndex={0} onClick={() => { setEfficiencyModalBus(bus); setEfficiencyTankL(String(bus.fuelTankCapacityL ?? DEFAULT_TANK_CAPACITY_L)); setShowFuelEfficiencyModal(true); }} onKeyDown={e => e.key === "Enter" && (setEfficiencyModalBus(bus), setEfficiencyTankL(String(bus.fuelTankCapacityL ?? DEFAULT_TANK_CAPACITY_L)), setShowFuelEfficiencyModal(true))} style={{ marginBottom: 16, cursor: "pointer" }}>
                <Card style={{ marginBottom: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Fuel (last 30 days) · Click for efficiency</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
                  <div>
                    <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 24, color: "#0f172a" }}>{fuelStats.totalKm30.toFixed(0)}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>km</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 24, color: fuelStats.refuelCount30 >= 1 ? "#16a34a" : fuelStats.needsRefuelCount >= 5 ? "#dc2626" : fuelStats.needsRefuelCount >= 2 ? "#d97706" : "#16a34a" }}>{fuelStats.needsRefuelCount}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>Needs refuel (trips)</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 24, color: "#16a34a" }}>{fuelStats.refuelCount30}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>Refuel events</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 24, color: "#2563eb" }}>{fuelStats.estimatedL100 ?? "—"}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>Est. L/100 km</div>
                  </div>
                </div>
                {fuelStats.estimatedL100 == null && (fuelStats.refuelCount30 > 0 || fuelStats.totalKm30 > 0) && (
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 10 }}>Add at least one refuel event and trips in the last 30 days to see estimated consumption.</div>
                )}
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 12 }}>From driver post-trip surveys (needs refuel) and &quot;I refueled&quot; in driver settings.</div>
              </Card>
              </div>

              {/* Route status */}
              <Card style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Current Route</div>
                {bus.route.status === "active" ? (
                  <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 20, color: "#111", marginBottom: 4 }}>
                        {bus.route.type === "refuel" ? "Refuel trip in progress" : `${bus.route.type === "morning" ? "Morning" : "Evening"} route in progress`}
                      </div>
                      <div style={{ fontSize: 13, color: "#64748b" }}>
                        Started {ft(bus.route.started)} · {bus.children.length} students
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 20 }}>
                      {[
                        { l: "Aboard", v: bus.children.filter(c => c.status === "picked_up").length, c: "#2563eb" },
                        { l: "Dropped", v: bus.children.filter(c => c.status === "dropped_off").length, c: "#16a34a" },
                        { l: "Missed", v: bus.children.filter(c => c.status === "not_picked").length, c: "#dc2626" },
                      ].map(s => (
                        <div key={s.l} style={{ textAlign: "center" }}>
                          <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 28, color: s.c, letterSpacing: "-0.03em" }}>{s.v}</div>
                          <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.l}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ color: "#94a3b8", fontSize: 14 }}>
                    No active route · {bus.morningTime ? `Next morning: ${bus.morningTime}` : "No schedule set"}
                  </div>
                )}
              </Card>

              {/* Last route summary */}
              {busRecords[0] && (
                <Card>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Last Route · {ft(busRecords[0].date, "date")}</div>
                  <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
                    {[
                      { l: "Started", v: ft(busRecords[0].started) },
                      { l: "Finished", v: ft(busRecords[0].finished) },
                      { l: "Duration", v: busRecords[0].duration ? `${Math.round(busRecords[0].duration)} min` : "—" },
                      { l: "Distance", v: formatDistance(busRecords[0].km) },
                      { l: "Route", v: busRecords[0].type === "morning" ? "Morning ☀️" : busRecords[0].type === "refuel" ? "Refuel ⛽" : "Evening 🌙" },
                    ].map(s => (
                      <div key={s.l}>
                        <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>{s.l}</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>{s.v}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* CHILDREN */}
          {tab === "people" && peopleSub === "children" && (
            <div style={{ animation: "fadeUp 0.2s ease" }}>
              <SectionHead sub={`${bus.children.length} students assigned · class/year comes from parent bus signup (yearGroup / class on roster)`}>
                Children
              </SectionHead>
              {bus.children.length === 0 ? (
                <Card><div style={{ color: "#94a3b8", fontSize: 14, textAlign: "center", padding: "40px 0" }}>No children assigned to this bus</div></Card>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {/* Header row */}
                  <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 120px 1fr 120px", gap: 16, padding: "0 16px 8px", fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    <div></div><div>Student</div><div>Class / year</div><div>Parent Contact</div><div>Status</div>
                  </div>
                  {bus.children.map((c, i) => {
                    const s = c.transportSuspended
                      ? { label: "Transport hold", color: "#c2410c", bg: "#fff7ed", border: "#fed7aa" }
                      : STATUS[c.status as StatusKey] || STATUS.not_picked;
                    const phone = (c.parentPhone || "").replace(/\D/g, "");
                    const telHref = phone ? `tel:${c.parentPhone}` : undefined;
                    const smsHref = phone ? `sms:${c.parentPhone}` : undefined;
                    return (
                      <div key={c.id} style={{ background: "#fff", border: "1px solid #f1f5f9", borderRadius: 10, padding: "12px 16px", display: "grid", gridTemplateColumns: "40px 1fr 120px 1fr 120px", gap: 16, alignItems: "center", opacity: c.transportSuspended ? 0.55 : 1 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Instrument Serif, serif", fontSize: 14, color: "#64748b" }}>
                          {c.name.charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{c.name}</div>
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>{c.classLabel || "—"}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12, color: "#94a3b8", fontFamily: "monospace" }}>{c.parentPhone || "—"}</span>
                          {telHref && (
                            <>
                              <a href={telHref} style={{ fontSize: 12, fontWeight: 600, color: "#2563eb", textDecoration: "none" }}>Call</a>
                              <a href={smsHref} style={{ fontSize: 12, fontWeight: 600, color: "#16a34a", textDecoration: "none" }}>SMS</a>
                            </>
                          )}
                        </div>
                        <Pill label={s.label} color={s.color} bg={s.bg} border={s.border} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ATTENDANCE */}
          {tab === "people" && peopleSub === "attendance" && (
            <div style={{ animation: "fadeUp 0.2s ease" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
                <SectionHead sub={`${busRecords.length} routes logged`}>Attendance History</SectionHead>
                {busRecords.length > 0 && (
                  <button
                    onClick={() => {
                      const headers = ["date", "routeType", "busId", "childName", "status", "durationMin", "distanceKm"];
                      const rows = busRecords.flatMap((rec) =>
                        rec.children.map((c) => [
                          rec.date ? new Date(rec.date).toISOString().slice(0, 10) : "",
                          rec.type || "",
                          bus.id,
                          c.name,
                          c.status,
                          String(rec.duration ?? ""),
                          String(rec.km ?? ""),
                        ])
                      );
                      const csv = [headers.join(","), ...rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))].join("\n");
                      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `attendance-${bus.plate}-${new Date().toISOString().slice(0, 10)}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    style={{ padding: "8px 16px", background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Geist, sans-serif" }}
                  >
                    Export CSV
                  </button>
                )}
              </div>
              {attendanceError ? (
                <Card style={{ borderLeft: "4px solid #dc2626", background: "#fef2f2" }}>
                  <div style={{ color: "#dc2626", fontSize: 14, padding: "20px" }}>Could not load attendance: {attendanceError}</div>
                  <div style={{ fontSize: 12, color: "#64748b", padding: "0 20px 20px" }}>Check that the bus has completed at least one trip (start route → finish route in the driver app).</div>
                </Card>
              ) : busRecords.length === 0 ? (
                <Card>
                  <div style={{ color: "#94a3b8", fontSize: 14, textAlign: "center", padding: "24px 20px" }}>No attendance records yet</div>
                  <div style={{ fontSize: 12, color: "#64748b", textAlign: "center", padding: "0 20px 24px" }}>Attendance and distance are recorded when the driver finishes a route in the app. Start and complete a trip to see data here.</div>
                </Card>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {busRecords.map(rec => {
                    const pu = rec.children.filter(c => c.status === "picked_up").length;
                    const do_ = rec.children.filter(c => c.status === "dropped_off").length;
                    const np = rec.children.filter(c => c.status === "not_picked").length;
                    const ab = rec.children.filter(c => c.status === "absent").length;
                    return (
                      <Card key={rec.id}>
                        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
                          <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 18, color: "#111", letterSpacing: "-0.02em" }}>{ft(rec.date, "date")}</div>
                          <Pill
                            label={rec.type === "morning" ? "☀️ Morning" : rec.type === "refuel" ? "⛽ Refuel" : "🌙 Evening"}
                            color={rec.type === "morning" ? "#d97706" : rec.type === "refuel" ? "#166534" : "#7c3aed"}
                            bg={rec.type === "morning" ? "#fffbeb" : rec.type === "refuel" ? "#e6f4ea" : "#faf5ff"}
                            border={rec.type === "morning" ? "#fde68a" : rec.type === "refuel" ? "#bbf7d0" : "#ddd6fe"}
                          />
                          {rec.routeAnomaly && <Pill label="Route deviation" color="#dc2626" bg="#fef2f2" border="#fecaca" />}
                          <div style={{ marginLeft: "auto", display: "flex", gap: 20 }}>
                            {[
                              { l: "Picked up", v: pu, c: "#16a34a" },
                              { l: "Dropped off", v: do_, c: "#2563eb" },
                              { l: "Not picked", v: np, c: "#dc2626" },
                              { l: "Absent", v: ab, c: "#b91c1c" },
                            ].map(s => (
                              <div key={s.l} style={{ textAlign: "center" }}>
                                <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 22, color: s.c, letterSpacing: "-0.03em" }}>{s.v}</div>
                                <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.l}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 20, fontSize: 12, color: "#94a3b8", paddingBottom: 14, borderBottom: "1px solid #f1f5f9", marginBottom: 14, flexWrap: "wrap" }}>
                          <span>Start <b style={{ color: "#334155" }}>{ft(rec.started)}</b></span>
                          <span>End <b style={{ color: "#334155" }}>{ft(rec.finished)}</b></span>
                          <span>Duration <b style={{ color: "#334155" }}>{rec.duration ? `${Math.round(rec.duration)} min` : "—"}</b></span>
                          <span>Distance <b style={{ color: "#334155" }}>{formatDistance(rec.km)}</b></span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {rec.children.map((c, i) => {
                            const s = STATUS[c.status as StatusKey];
                            return (
                              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                                <Dot color={s.color} />
                                <span style={{ flex: 1, color: "#334155", fontWeight: 500 }}>{c.name}</span>
                                <span style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>{s.label}</span>
                                {c.time && <span style={{ fontSize: 11, color: "#cbd5e1", fontFamily: "monospace" }}>{ft(c.time)}</span>}
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ANALYTICS */}
          {tab === "money" && (
            <div style={{ animation: "fadeUp 0.2s ease" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
                <SectionHead sub="Route performance & distance metrics">Analytics</SectionHead>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {busRecords.length > 0 && (
                    <button
                      onClick={() => {
                        const totalRoutes = busRecords.length;
                        const avgKm = totalRoutes ? (totalKm / totalRoutes) : 0;
                        const totalDur = busRecords.reduce((s, r) => s + (r.duration || 0), 0);
                        const avgDurVal = totalRoutes ? Math.round(totalDur / totalRoutes) : 0;
                        const headers = ["date", "routeType", "distanceKm", "durationMin"];
                        const rows = busRecords.map((rec) => [
                          rec.date ? new Date(rec.date).toISOString().slice(0, 10) : "",
                          rec.type || "",
                          String(rec.km ?? ""),
                          String(rec.duration ?? ""),
                        ]);
                        const summaryRows = [
                          ["--- Summary ---", "", "", ""],
                          ["Total Routes", String(totalRoutes), "", ""],
                          ["Total Distance (km)", totalKm.toFixed(1), "", ""],
                          ["Avg Distance per Route (km)", avgKm.toFixed(1), "", ""],
                          ["Avg Duration per Trip (min)", String(avgDurVal), "", ""],
                          ["Total Refuel Events", String(refuelEvents.length), "", ""],
                        ];
                        const allRows = [headers, ...rows, ["", "", "", ""], ...summaryRows];
                        const csv = allRows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
                        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `analytics-${bus.plate}-${new Date().toISOString().slice(0, 10)}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      style={{ padding: "8px 16px", background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Geist, sans-serif" }}
                    >
                      Download analytics
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 24 }}>
                <StatTile label="Total Distance" value={formatDistance(totalKm)} sub={`across ${busRecords.length} routes`} accent="#2563eb" />
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                      {`Fuel Cost (${fuelCostDays}d)`}
                    </span>
                    <div style={{ display: "flex", gap: 4 }}>
                      {([10, 30, 50] as const).map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setFuelCostDays(d)}
                          style={{
                            fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 6, cursor: "pointer",
                            border: fuelCostDays === d ? "1.5px solid #0f172a" : "1.5px solid #e2e8f0",
                            background: fuelCostDays === d ? "#0f172a" : "#f8fafc",
                            color: fuelCostDays === d ? "#fff" : "#64748b",
                          }}
                        >{d}d</button>
                      ))}
                    </div>
                  </div>
                  <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 24, color: "#0f172a", marginTop: 2 }}>
                    {fuelStats.totalCost30 > 0
                      ? `KES ${fuelStats.totalCost30.toLocaleString("en-KE")}`
                      : "—"}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    {fuelStats.refuelCount30 > 0
                      ? `${fuelStats.refuelCount30} refuel event(s)`
                      : "No refuel events logged"}
                  </div>
                </div>
                <StatTile label="Avg Duration" value={`${avgDur} min`} sub="Mean route time" accent="#16a34a" />
              </div>

              {busDetailsEditor}

              {/* Fuel & Efficiency Estimate — uses all-time km + all-time refuel signals */}
              <Card style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Fuel &amp; Efficiency Estimate</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 16 }}>Auto-calculated from total km traveled, all refuel signals &amp; tank capacity</div>
                {(() => {
                  const tankL = bus.fuelTankCapacityL ?? DEFAULT_TANK_CAPACITY_L;
                  const isEstimatedTank = bus.fuelTankCapacityL == null;
                  // Use all-time km (from all loaded routes) + all-time refuel signals for accuracy
                  const inTerm = (dt: Date | null | undefined) => termStartMs == null || (dt != null && dt.getTime() >= termStartMs);
                  const termEvents = refuelEvents.filter((e) => inTerm(e.timestamp));
                  const allTimeRefuelEvents = termEvents.length;
                  const allTimeSurveyAlerts = busRecords.filter((r) => r.needsRefuelSoon && inTerm(r.date)).length;
                  const allTimeRefuelSignals = allTimeRefuelEvents + allTimeSurveyAlerts;
                  const allTimeLitresAdded = termEvents.reduce((s, e) => s + (e.litersAdded ?? 0), 0);
                  const termKm = busRecords.filter((r) => inTerm(r.date)).reduce((s, r) => s + (r.km || 0), 0);
                  /* Clean odometer-bracketed fills: the ring's preferred source. Anything
                     needs_review is not measurement — the review list owns it. */
                  const cleanOdoFills = termEvents.filter(
                    (e) => (e.kmSinceLastRefuel ?? 0) > 0 && (e.litersAdded ?? 0) > 0 && e.efficiencyFlag !== "needs_review",
                  );
                  const odoWindowKm = cleanOdoFills.reduce((s, e) => s + (e.kmSinceLastRefuel ?? 0), 0);
                  const odoWindowLitres = cleanOdoFills.reduce((s, e) => s + (e.litersAdded ?? 0), 0);
                  const effInput: BusFuelInput = {
                    totalKm30: termKm,                // this-term km (all-time when no term set)
                    refuelCount30: allTimeRefuelSignals, // this-term signals
                    needsRefuelCount: allTimeSurveyAlerts,
                    tankCapacityL: tankL,
                    totalLitresAdded: allTimeLitresAdded,
                    odoWindowKm,
                    odoWindowLitres,
                    benchmarkKmPerL: bus.benchmarkKmPerL ?? undefined,
                  busAgeYears: busAgeYearsFrom(bus.yearOfManufacture),
                  };
                  const effEst = estimateFuelEfficiency(effInput);
                  const pieColor = effEst.pct != null
                    ? (effEst.pct <= 30 ? "#dc2626" : effEst.pct <= 70 ? "#f97316" : "#16a34a")
                    : "#e2e8f0";
                  const pieRows = effEst.pct != null
                    ? [
                        { name: "eff", value: effEst.pct, fill: pieColor },
                        { name: "rest", value: Math.max(0, 100 - effEst.pct), fill: "#e2e8f0" },
                      ]
                    : [{ name: "empty", value: 100, fill: "#e2e8f0" }];

                  return (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" }}>
                      {/* Left: stats grid */}
                      <div style={{ flex: "1 1 240px", minWidth: 220 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
                          {/* Total km */}
                          <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 10px" }}>
                            <div style={{ fontSize: 9, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Total km traveled</div>
                            <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{formatDistance(totalKm)}</div>
                          </div>
                          {/* Total refuel events — busRefuelEvents + needsRefuelSoon survey signals */}
                          <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 10px" }}>
                            <div style={{ fontSize: 9, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Refuel signals</div>
                            {(() => {
                              const explicitRefuels = refuelEvents.length;
                              const surveyAlerts = busRecords.filter((r) => r.needsRefuelSoon).length;
                              const total = explicitRefuels + surveyAlerts;
                              return (
                                <>
                                  <div style={{ fontSize: 17, fontWeight: 700, color: total > 0 ? "#0f172a" : "#94a3b8" }}>{total}</div>
                                  <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>
                                    {explicitRefuels > 0 ? `${explicitRefuels} logged` : "0 logged"}
                                    {surveyAlerts > 0 ? ` · ${surveyAlerts} survey alerts` : ""}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                          {/* Fuel capacity — hover to edit */}
                          <div
                            style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 10px", cursor: "pointer", position: "relative" }}
                            title={isEstimatedTank ? "Typical 50-seater bus capacity. Hover and click Edit to enter actual capacity." : "Actual capacity set. Click to update."}
                            onClick={() => {
                              if (!editingFuelCapacity) {
                                setFuelCapacityInput(String(tankL));
                                setBenchmarkInput(String(bus.benchmarkKmPerL ?? ""));
                                setBusYearInput(String(bus.yearOfManufacture ?? ""));
                                setEditingFuelCapacity(true);
                              }
                            }}
                          >
                            <div style={{ fontSize: 9, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span>Fuel capacity &amp; benchmark</span>
                              <span style={{ fontSize: 9, color: "#2563eb", fontWeight: 600 }}>Edit</span>
                            </div>
                            {editingFuelCapacity ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 2 }} onClick={(e) => e.stopPropagation()}>
                                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                  <span style={{ fontSize: 9, color: "#64748b", width: 64, flexShrink: 0 }}>Tank (L)</span>
                                  <input
                                    type="number" min={50} max={500} value={fuelCapacityInput}
                                    onChange={(e) => setFuelCapacityInput(e.target.value)}
                                    placeholder="optional"
                                    style={{ flex: 1, fontSize: 11, fontWeight: 700, border: "1px solid #2563eb", borderRadius: 6, padding: "2px 4px", background: "#fff", outline: "none" }}
                                    autoFocus
                                  />
                                </div>
                                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                  <span style={{ fontSize: 9, color: "#dc2626", width: 64, flexShrink: 0 }}>Benchmark*</span>
                                  <input
                                    type="number" min={0.1} step={0.1} value={benchmarkInput}
                                    onChange={(e) => setBenchmarkInput(e.target.value)}
                                    placeholder="km/L e.g. 8"
                                    style={{ flex: 1, fontSize: 11, fontWeight: 700, border: "1px solid #dc2626", borderRadius: 6, padding: "2px 4px", background: "#fff", outline: "none" }}
                                  />
                                </div>
                                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                  <span style={{ fontSize: 9, color: "#64748b", width: 64, flexShrink: 0 }}>Year</span>
                                  <input
                                    type="number" min={1980} max={new Date().getFullYear()} value={busYearInput}
                                    onChange={(e) => setBusYearInput(e.target.value)}
                                    placeholder="e.g. 2015"
                                    style={{ flex: 1, fontSize: 11, fontWeight: 700, border: "1px solid #2563eb", borderRadius: 6, padding: "2px 4px", background: "#fff", outline: "none" }}
                                  />
                                </div>
                                <div style={{ fontSize: 9, color: "#94a3b8" }}>* Required for score · Year relaxes the target for an older bus</div>
                                <div style={{ display: "flex", gap: 4 }}>
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const updates = buildFuelBenchmarkUpdates(fuelCapacityInput, benchmarkInput, busYearInput);
                                      try {
                                        await updateDoc(doc(db, "buses", bus.id), updates);
                                        customToast.show("Saved", "success");
                                      } catch (err) {
                                        console.error("Fuel benchmark save failed:", err);
                                        customToast.show("Failed to save. Check your permissions or try again.", "error");
                                      }
                                      setEditingFuelCapacity(false);
                                    }}
                                    style={{ fontSize: 10, fontWeight: 700, background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "3px 7px", cursor: "pointer" }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setEditingFuelCapacity(false); }}
                                    style={{ fontSize: 10, background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: "3px 2px" }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div style={{ fontSize: 15, fontWeight: 700, color: isEstimatedTank ? "#64748b" : "#0f172a" }}>
                                  {isEstimatedTank ? `~${DEFAULT_TANK_CAPACITY_L} L` : `${bus.fuelTankCapacityL} L`}
                                </div>
                                <div style={{ fontSize: 11, marginTop: 2, color: bus.benchmarkKmPerL ? "#0f172a" : "#dc2626", fontWeight: 600 }}>
                                  {bus.benchmarkKmPerL ? `${bus.benchmarkKmPerL} km/L benchmark` : "⚠ No benchmark — click Edit"}
                                </div>
                                {bus.yearOfManufacture ? <div style={{ fontSize: 10, marginTop: 1, color: "#94a3b8", fontWeight: 600 }}>{bus.yearOfManufacture} · {busAgeYearsFrom(bus.yearOfManufacture)}yr old</div> : null}
                              </>
                            )}
                          </div>
                        </div>
                        {/* Confidence / info callout */}
                        {effEst.confidence === "no_benchmark" ? (
                          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 12px", fontSize: 11, color: "#dc2626", lineHeight: 1.5 }}>
                            <strong>⚠ Please set a benchmark to get a fuel efficiency score</strong>
                            <div style={{ marginTop: 4, fontWeight: 400 }}>
                              Click <strong>Edit</strong> on the tile above and enter a benchmark in km/L (e.g. 8 km/L means the bus should travel 8 km per litre).
                              The score = actual km/L ÷ benchmark × 100%.
                              {effEst.kmPerL != null && <span style={{ display: "block", marginTop: 4, color: "#0f172a", fontWeight: 600 }}>Current consumption: {effEst.kmPerL.toFixed(2)} km/L · {effEst.l100km?.toFixed(1)} L/100km</span>}
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            background: effEst.confidence === "measured" ? "#f0fdf4" : effEst.confidence === "estimated" ? "#fff7ed" : "#f8fafc",
                            border: `1px solid ${effEst.confidence === "measured" ? "#bbf7d0" : effEst.confidence === "estimated" ? "#fed7aa" : "#e2e8f0"}`,
                            borderRadius: 8, padding: "10px 12px", fontSize: 11,
                            color: effEst.confidence === "measured" ? "#15803d" : effEst.confidence === "estimated" ? "#c2410c" : "#64748b",
                            lineHeight: 1.5,
                          }}>
                            <strong>{effEst.confidenceLabel}</strong>
                            {effEst.kmPerL != null && <span style={{ marginLeft: 6, color: "#0f172a", fontWeight: 600 }}>{effEst.kmPerL.toFixed(2)} km/L · {effEst.l100km?.toFixed(1)} L/100km</span>}
                            {bus.benchmarkKmPerL && <span style={{ marginLeft: 6, color: "#64748b", fontWeight: 400 }}>· vs {bus.benchmarkKmPerL} km/L target</span>}
                            <div style={{ marginTop: 4, fontWeight: 400, color: "#64748b" }}>
                              {effEst.confidence === "early"
                                ? (effEst.confidenceLabel === "Not scored — the numbers don't add up" || effEst.confidenceLabel === "Odometer data needs review"
                                    /* Say WHY — "ask drivers to log litres" was wrong when the
                                       litres were logged and the ratio was the problem
                                       (Gabriel, 2026-08-23: 171.8 km, 18 refuels, "Need data"). */
                                    ? effEst.tooltipNote
                                    : "Ask drivers to log refuel events (with litres added) to calculate a score.")
                                : allTimeLitresAdded > 0
                                  ? `${allTimeLitresAdded.toFixed(1)} L added · ${totalKm.toFixed(0)} km traveled (all-time)`
                                  : `${allTimeRefuelSignals} refuel signal(s) · ${totalKm.toFixed(0)} km total`
                              }
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right: efficiency donut */}
                      <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600, textAlign: "center" }}>Fuel efficiency score</div>
                        <div
                          style={{ position: "relative", width: 148, height: 148 }}
                        >
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={pieRows}
                                dataKey="value"
                                cx="50%"
                                cy="50%"
                                innerRadius={48}
                                outerRadius={64}
                                paddingAngle={effEst.pct != null ? 2 : 0}
                                startAngle={90}
                                endAngle={-270}
                                strokeWidth={0}
                              >
                                {pieRows.map((entry, i) => (
                                  <Cell key={i} fill={entry.fill} stroke="none" />
                                ))}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                          <div style={{
                            position: "absolute", left: 0, right: 0, top: 0, bottom: 0,
                            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                            pointerEvents: "none",
                          }}>
                            {effEst.pct != null ? (
                              <>
                                <span style={{ fontFamily: "Instrument Serif, serif", fontSize: 26, fontWeight: 600, color: pieColor, lineHeight: 1 }}>{effEst.pct}%</span>
                                <span style={{ fontSize: 9, color: "#64748b", marginTop: 3, fontWeight: 600 }}>
                                  {effEst.pct <= 30 ? "Poor" : effEst.pct <= 70 ? "Fair" : "Efficient"}
                                </span>
                              </>
                            ) : effEst.confidence === "no_benchmark" ? (
                              <span style={{ fontSize: 10, color: "#dc2626", textAlign: "center", padding: "0 10px", fontWeight: 600 }}>Set benchmark</span>
                            ) : (
                              <span style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", padding: "0 12px" }}>Need data</span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, fontSize: 9, color: "#94a3b8" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#dc2626", display: "inline-block" }} />≤30%</span>
                          <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f97316", display: "inline-block" }} />31–70%</span>
                          <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a", display: "inline-block" }} />&gt;70%</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </Card>

              {/* Distance per route — one labelled row per trip. Recharts used
                  the date as the category key, so two "31 Jul" mornings became
                  one bar and the rest of the labels floated (Gabriel, Leaf). */}
              <Card style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Distance per route</div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 14 }}>Last 8 trips · oldest at the top · blue is measured, grey is estimated, pale means not recorded</div>
                {busRecords.length === 0 ? (
                  <div style={{ color: "#94a3b8", fontSize: 13 }}>No data available</div>
                ) : (
                  (() => {
                    const rows = lastRouteDistanceRows(busRecords, 8);
                    const maxKm = Math.max(1, ...rows.map((r) => r.km ?? 0));
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {rows.map((row) => {
                          const pct = row.kind === "none" || row.km == null ? 8 : Math.max(8, Math.round((row.km / maxKm) * 100));
                          const fill = row.kind === "measured" ? "#2563eb" : row.kind === "estimated" ? "#94a3b8" : "#e2e8f0";
                          const value =
                            row.kind === "measured" && row.km != null
                              ? formatDistance(row.km)
                              : row.kind === "estimated" && row.km != null
                                ? `≈ ${formatDistance(row.km)}`
                                : "Not recorded";
                          return (
                            <div key={row.id} style={{ display: "grid", gridTemplateColumns: "108px 1fr 96px", gap: 10, alignItems: "center" }}>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{row.day}</div>
                                <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "capitalize" }}>{row.when}</div>
                              </div>
                              <div style={{ height: 10, background: "#f1f5f9", borderRadius: 6, overflow: "hidden" }}>
                                <div style={{ width: `${pct}%`, height: "100%", background: fill, borderRadius: 6 }} />
                              </div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: row.kind === "none" ? "#94a3b8" : "#0f172a", textAlign: "right" }}>{value}</div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
                )}
              </Card>

              {/* "Km per route" card removed 2026-08-24 — it repeated the
                  DISTANCE PER ROUTE chart above with meaningless "Run 7" labels
                  (Gabriel: "I don't get the point of having the two of them"). */}
            </div>
          )}

          {/* ABSENTEES - Chronic absenteeism for this bus only */}
          {tab === "people" && peopleSub === "absentees" && (
            <div style={{ animation: "fadeUp 0.2s ease" }}>
              <SectionHead sub={`${bus.name} · last 30 days · absent or not picked`}>Chronic Absenteeism Report</SectionHead>
              <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
                Students on this bus with repeated absences or missed pickups. Consider follow-up with parents.
              </p>
              {chronicAbsenteesForDetail.length === 0 ? (
                <Card>
                  <div style={{ textAlign: "center", padding: "48px 0", color: "#94a3b8" }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
                    <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 18, color: "#111", marginBottom: 6 }}>No chronic absentees</div>
                    <div style={{ fontSize: 13 }}>No students with absent or not picked status on this bus in the last 30 days</div>
                  </div>
                </Card>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 80px", gap: 16, padding: "0 16px 8px", fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    <div></div><div>Student</div><div>Count</div>
                  </div>
                  {chronicAbsenteesForDetail.map((a, i) => (
                    <div key={`${a.name}-${a.busId}-${i}`} style={{ background: "#fff", border: "1px solid #f1f5f9", borderRadius: 10, padding: "12px 16px", display: "grid", gridTemplateColumns: "40px 1fr 80px", gap: 16, alignItems: "center" }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Instrument Serif, serif", fontSize: 14, color: "#dc2626" }}>
                        {a.name.charAt(0)}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{a.name}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#dc2626" }}>{a.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* INCIDENTS */}
          {tab === "incidents" && (
            <div style={{ animation: "fadeUp 0.2s ease" }}>
              {(() => {
                const busCrashes = busesWithIncidents.find((x) => x.id === bus.id)?.crashes ?? [];
                const refuelAnomalies = fleetAlerts.filter((a) => a.busId === bus.id && a.type === "refuel_risk");
                return (
                  <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
                <SectionHead sub={`${busCrashes.length} incident${busCrashes.length !== 1 ? "s" : ""} recorded for this bus`}>Crash &amp; Incident Reports</SectionHead>
                {busCrashes.length > 0 && (
                  <button
                    onClick={() => {
                      const headers = ["date", "severity", "description", "reported"];
                      const rows = busCrashes.map((c) => [
                        c.date ? new Date(c.date).toISOString().slice(0, 10) : "",
                        c.severity,
                        c.description || "",
                        c.reported ? "Yes" : "No",
                      ]);
                      const csv = [headers, ...rows].map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
                      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `incident-report-${bus.plate}-${new Date().toISOString().slice(0, 10)}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    style={{ padding: "8px 16px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Geist, sans-serif" }}
                  >
                    Download incident report
                  </button>
                )}
              </div>
              {busCrashes.length === 0 ? (
                <Card>
                  <div style={{ textAlign: "center", padding: "48px 0" }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
                    <div style={{ fontFamily: "Instrument Serif, serif", fontSize: 18, color: "#111", marginBottom: 6 }}>Clean record</div>
                    <div style={{ fontSize: 13, color: "#94a3b8" }}>No incidents have been reported for this bus</div>
                  </div>
                </Card>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {busCrashes.map((c, i) => {
                    const sev = CRASH_SEV[c.severity as CrashSevKey];
                    return (
                      <Card key={i} style={{ borderLeft: `3px solid ${sev.color}` }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                              <Pill label={`${sev.label} Incident`} color={sev.color} bg={sev.bg} border={sev.border} />
                              <span style={{ fontSize: 12, color: "#94a3b8" }}>{ft(c.date, "date")}</span>
                              {c.reported && <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 600 }}>✓ Reported</span>}
                            </div>
                            <div style={{ fontSize: 14, color: "#334155", lineHeight: 1.6 }}>{c.description}</div>
                          </div>
                          {c.reportId && (
                            <button
                              type="button"
                              onClick={() => void handleResolveIncident(c.reportId as string)}
                              style={{ flexShrink: 0, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#fff", background: "#16a34a", border: "none", borderRadius: 8, cursor: "pointer" }}
                            >
                              Mark resolved
                            </button>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}

              <div style={{ marginTop: 28 }}>
                <SectionHead sub="Automated checks on refuel frequency vs fleet and past weeks">Fuel & refuel anomalies</SectionHead>
                {refuelAnomalies.length === 0 ? (
                  <Card>
                    <div style={{ textAlign: "center", padding: "28px 0", color: "#94a3b8", fontSize: 13 }}>No active refuel risk alerts for this bus.</div>
                  </Card>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {refuelAnomalies.map((a) => (
                      <Card key={a.id} style={{ borderLeft: "3px solid #f59e0b", background: a.read ? "#fff" : "#fffbeb" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                          <div style={{ flex: 1 }}>
                            <Pill label="Refuel risk" color="#b45309" bg="#fef3c7" border="#fcd34d" />
                            <div style={{ fontSize: 13, color: "#334155", marginTop: 10, lineHeight: 1.5 }}>{a.message}</div>
                            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6 }}>{ft(a.timestamp)}</div>
                          </div>
                          {!a.read && (
                            <button
                              type="button"
                              onClick={() => dismissFleetAlert(a.id)}
                              style={{ flexShrink: 0, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#fff", background: "#64748b", border: "none", borderRadius: 8, cursor: "pointer" }}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Fleet-wide incident summary */}
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Fleet-wide Incident Summary</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
                  <StatTile label="Total Incidents" value={busesWithIncidents.reduce((s, b) => s + b.crashes.length, 0)} sub="" accent="#dc2626" />
                  <StatTile label="Major" value={busesWithIncidents.reduce((s, b) => s + b.crashes.filter(c => c.severity === "major").length, 0)} sub="" accent="#dc2626" />
                  <StatTile label="Minor" value={busesWithIncidents.reduce((s, b) => s + b.crashes.filter(c => c.severity === "minor").length, 0)} sub="" accent="#d97706" />
                </div>
              </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* SETTINGS */}
          {tab === "settings" && (
            <div style={{ animation: "fadeUp 0.2s ease", maxWidth: 500 }}>
              <SectionHead sub="Bus configuration and schedule">Settings</SectionHead>

              <Card style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>Bus Information</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {[
                    { l: "Nickname", v: bus.name },
                    { l: "Plate", v: bus.plate },
                    { l: "School", v: bus.school },
                    { l: "Drivers", v: namesForDriverIds(bus.drivers) },
                    {
                      l: "Status",
                      v:
                        bus.status === "maintenance" || bus.status === "suspended"
                          ? "Suspend / maintenance"
                          : bus.status === "for_hire"
                            ? "For hire"
                            : "Active",
                    },
                  ].map(({ l, v }) => (
                    <div key={l}>
                      <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{l}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{v}</div>
                    </div>
                  ))}
                </div>
              </Card>

              {busDetailsEditor}

              <Card style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Suspend / maintenance</div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
                  {bus.status === "maintenance" || bus.status === "suspended"
                    ? "This bus is out of active service. Return it to active when ready."
                    : "To suspend or place this bus in maintenance, move all students to other buses (use the buttons below). Parents stay aligned when you reassign first."}
                </div>
                {(bus.status === "maintenance" || bus.status === "suspended") && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm("Return this bus to active? Students parked when it went out of service will come back.")) return;
                      await handleActivateBus(bus);
                    }}
                    style={{
                      padding: "9px 20px",
                      background: "#16a34a",
                      border: "none",
                      borderRadius: 8,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 13,
                      fontFamily: "Geist, sans-serif",
                      cursor: "pointer",
                    }}
                  >
                    Return to active
                  </button>
                )}
              </Card>

              <Card style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                  Reassign students (suspend / maintenance or hire)
                </div>
                <p style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16, lineHeight: 1.5 }}>
                  Move every student to other buses in one step, then mark this bus in suspend/maintenance or for hire. Parent apps update automatically.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  <button
                    type="button"
                    disabled={bus.children.length === 0}
                    onClick={() => void openReassignIntro(bus, "maintenance")}
                    style={{
                      padding: "9px 16px",
                      background: bus.children.length === 0 ? "#e2e8f0" : "#d97706",
                      border: "none",
                      borderRadius: 8,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: bus.children.length === 0 ? "not-allowed" : "pointer",
                    }}
                  >
                    Suspend / maintenance + reassign…
                  </button>
                  <button
                    type="button"
                    disabled={bus.children.length === 0}
                    onClick={() => void openReassignIntro(bus, "for_hire")}
                    style={{
                      padding: "9px 16px",
                      background: bus.children.length === 0 ? "#e2e8f0" : "#7c3aed",
                      border: "none",
                      borderRadius: 8,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: bus.children.length === 0 ? "not-allowed" : "pointer",
                    }}
                  >
                    For hire + reassign…
                  </button>
                </div>
                {bus.children.length === 0 && (
                  <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>No students on this bus — use Return to active above when this bus is out of service.</p>
                )}
                {bus.status === "for_hire" && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm("Return this bus from hire to active? Students parked for the hire will come back.")) return;
                      await handleActivateBus(bus);
                    }}
                    style={{
                      marginTop: 12,
                      padding: "9px 16px",
                      background: "#16a34a",
                      border: "none",
                      borderRadius: 8,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    Return from hire (active)
                  </button>
                )}
              </Card>

              <Card style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Pickup Schedule</div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>Drivers see these times when starting a route.</div>
                <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
                  {[
                    { l: "☀️ Morning", v: mTime, set: setMTime },
                    { l: "🌙 Evening", v: eTime, set: setETime },
                  ].map(({ l, v, set }) => (
                    <div key={l} style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>{l}</div>
                      <input type="time" value={v} onChange={e => set(e.target.value)} style={{
                        width: "100%", padding: "9px 12px", border: "1px solid #e2e8f0", borderRadius: 8,
                        fontSize: 15, fontFamily: "monospace", color: "#111", outline: "none", background: "#fff",
                      }} />
                    </div>
                  ))}
                </div>
                <button onClick={saveSchedule} style={{
                  padding: "10px 22px", background: saved ? "#16a34a" : "#0f172a", border: "none",
                  borderRadius: 9, color: "#fff", fontWeight: 600, fontSize: 13, transition: "all 0.2s ease",
                  fontFamily: "Geist, sans-serif",
                }}>
                  {saved ? "✓ Saved" : "Save Schedule"}
                </button>
              </Card>

              <div style={{ background: "#fff", border: "1px solid #fee2e2", borderRadius: 14, padding: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Danger Zone</div>
                <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 16 }}>
                  Deleting removes {bus.drivers.length} driver(s) and notifies {new Set(bus.children.map(c => c.parentId).filter(Boolean)).size} families. Cannot be undone.
                </div>
                <button
                  onClick={() => {
                    if (!bus) return;
                    if (bus.children.length > 0) void openReassignIntro(bus, "delete_bus");
                    else setDeleteBusToConfirm(bus);
                  }}
                  style={{
                  padding: "9px 20px", background: "transparent", border: "1px solid #fecaca",
                  borderRadius: 8, color: "#dc2626", fontWeight: 600, fontSize: 13,
                  fontFamily: "Geist, sans-serif", transition: "all 0.15s ease",
                }}>Delete Bus</button>
              </div>
            </div>
          )}
        </div>
        )}

      {/* Fuel Report full-screen */}
      {/* Fuel Report full-screen — redesigned; see fleet/FuelReportScreen.tsx */}
      {showFuelReportFullScreen && (
        <FuelReportScreen
          rows={fleetFuelSummary.rows}
          busById={fuelBusById}
          lastRefuelAtByBusId={fleetLastRefuelAtByBusId}
          costByBusId={costEstimateByBusId}
          refuelTrips={fleetRefuelTrips}
          onClose={() => setShowFuelReportFullScreen(false)}
          onOpenBus={(busId) => {
            const b = findBusByAnyId(busId);
            if (!b) return;
            setShowFuelReportFullScreen(false);
            setDetailReturnTo("fuelReport");
            handleSelect(b);
            setDetailView(true);
          }}
          onOpenFuelPrices={() => { setShowFuelReportFullScreen(false); setTopLevelTab("fuelPrices"); }}
        />
      )}


      {/* Full-screen: Cost estimate — comprehensive page with charts */}
      {showCostEstimateFullScreen && (
        <div className="fixed inset-0 z-[100] bg-white overflow-auto">
          <BusCostEstimatePage
            buses={buses}
            fleetOrgCode={fleetOrgCode}
            onBack={() => setShowCostEstimateFullScreen(false)}
            onDrillDownBus={(busId) => {
              setShowCostEstimateFullScreen(false);
              setAnalyticsInitialBusId(busId);
              setTopLevelTab("analytics");
            }}
          />
        </div>
      )}
      {showNotifications && (
        <NotificationsPopover
          alerts={fleetAlerts}
          plateByBusId={plateByBusId}
          onClose={() => setShowNotifications(false)}
          onOpenBus={(busId) => {
            const b = findBusByAnyId(busId);
            setShowNotifications(false);
            if (!b) return;
            handleSelect(b);
            setDetailView(true);
          }}
          /* A notification is about an ALERT; open that alert, not the bus's
             Today tab (Gabriel, 2026-08-22: tapped "Fuel needs review" and
             landed on the map with no sign of the fuel issue). */
          onOpenAlert={(alertId) => {
            setShowNotifications(false);
            setHighRiskInitialKey(`a:${alertId}`);
            setShowHighRiskFullScreen(true);
          }}
          onOpenAll={() => { setShowNotifications(false); setHighRiskInitialKey(null); setShowHighRiskFullScreen(true); }}
          onMarkRead={async (alertId) => {
            await updateDoc(doc(db, "fleetAlerts", alertId), { read: true });
          }}
          onMarkAllRead={async () => {
            const unread = fleetAlerts.filter((a) => !a.read);
            if (unread.length === 0) return;
            for (let i = 0; i < unread.length; i += 450) {
              const batch = writeBatch(db);
              for (const alert of unread.slice(i, i + 450)) {
                batch.update(doc(db, "fleetAlerts", alert.id), { read: true });
              }
              await batch.commit();
            }
          }}
        />
      )}

      {/* High Risk Alerts full-screen — redesigned; see fleet/HighRiskScreen.tsx.
          The Zana takeover hooks (alertList / markAll / alert0..7) live inside
          that component and are part of its contract. */}
      {showHighRiskFullScreen && (
        <HighRiskScreen
          buses={highRiskBuses}
          initialSelectedKey={highRiskInitialKey}
          alerts={fleetAlerts}
          incidents={highRiskIncidents}
          kmByBusId={kmByBusId}
          costByBusId={costEstimateByBusId}
          lastTripByBusId={lastTripByBusId}
          onClose={() => setShowHighRiskFullScreen(false)}
          onOpenBus={(busId) => {
            const b = findBusByAnyId(busId);
            if (!b) return;
            setShowHighRiskFullScreen(false);
            setDetailReturnTo("highRisk");
            handleSelect(b);
            setDetailView(true);
          }}
          onSetTank={(busId) => {
            const b = findBusByAnyId(busId);
            if (!b) return;
            setShowHighRiskFullScreen(false);
            setEfficiencyModalBus(b);
            setEfficiencyTankL(String(b.fuelTankCapacityL ?? ""));
            setShowFuelEfficiencyModal(true);
          }}
          onMarkRead={async (alertId) => {
            await updateDoc(doc(db, "fleetAlerts", alertId), { read: true });
          }}
          onMarkAllRead={async () => {
            const unread = fleetAlerts.filter((a) => !a.read);
            if (unread.length === 0) return;
            /* Firestore refuses a batch over 500 writes, and this list is
               capped at 500 upstream — chunk rather than assume it fits. */
            for (let i = 0; i < unread.length; i += 450) {
              const batch = writeBatch(db);
              for (const alert of unread.slice(i, i + 450)) {
                batch.update(doc(db, "fleetAlerts", alert.id), { read: true });
              }
              await batch.commit();
            }
          }}
        />
      )}


      {/* Suspend / maintenance full-screen — redesigned; see fleet/MaintenanceScreen.tsx */}
      {showMaintenanceFullScreen && (
        <MaintenanceScreen
          buses={maintenanceScreenBuses}
          orgCode={fleetOrgCode}
          onClose={() => setShowMaintenanceFullScreen(false)}
          onOpenBus={(busId) => {
            const b = findBusByAnyId(busId);
            if (!b) return;
            setShowMaintenanceFullScreen(false);
            setDetailReturnTo("maintenance");
            handleSelect(b);
            setDetailView(true);
          }}
        />
      )}

      {/* Incidents summary full-screen (from Monthly Report): all buses + incident count for selected month; tap bus → modal with details */}
      {showIncidentsSummaryScreen && reportMonth && (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col">
          <div className="p-4 border-b border-gray-200 flex items-center gap-4 shrink-0">
            <button
              type="button"
              onClick={() => { setShowIncidentsSummaryScreen(false); setIncidentsModalBusId(null); }}
              className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 flex items-center gap-2"
            >
              <FaArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold text-gray-900">
                Incidents — {new Date(reportMonth.year, reportMonth.month - 1).toLocaleString("default", { month: "long" })} {reportMonth.year}
              </h1>
              <p className="text-sm text-gray-500">Tap a bus to see incident details for this month</p>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-6">
            <div className="max-w-2xl mx-auto space-y-3">
              {monthlyReportRows.map((r) => (
                <button
                  key={r.busId}
                  type="button"
                  onClick={() => setIncidentsModalBusId(r.busId)}
                  className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 hover:bg-gray-50 hover:border-gray-300 transition-colors flex items-center justify-between"
                >
                  <span className="font-medium text-gray-900">{r.busName}</span>
                  <span className={`text-lg font-bold ${r.incidents > 0 ? "text-red-600" : "text-gray-400"}`}>
                    {r.incidents} incident{r.incidents !== 1 ? "s" : ""}
                  </span>
                </button>
              ))}
              {monthlyReportRows.length === 0 && (
                <div className="text-center py-12 text-gray-500">No buses in fleet</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Incident detail modal (from Incidents summary): list of incidents for one bus in the selected month */}
      {incidentsModalBusId && reportMonth && (() => {
        const bus = buses.find((b) => b.id === incidentsModalBusId);
        const busName = bus?.name ?? "Bus";
        const start = new Date(reportMonth.year, reportMonth.month - 1, 1);
        const end = new Date(reportMonth.year, reportMonth.month, 0, 23, 59, 59);
        const inMonth = (d: Date) => { const t = d.getTime(); return t >= start.getTime() && t <= end.getTime(); };
        const busCrashes = busesWithIncidents.find((x) => x.id === incidentsModalBusId)?.crashes ?? [];
        const incidentsInMonth = busCrashes.filter((c) => inMonth(c.date));
        return (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50" onClick={() => setIncidentsModalBusId(null)}>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-gray-200 flex items-center justify-between shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">{busName} — Incidents</h3>
                <button type="button" onClick={() => setIncidentsModalBusId(null)} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                {incidentsInMonth.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">No incidents recorded for this bus in this month.</p>
                ) : (
                  <div className="space-y-3">
                    {incidentsInMonth.map((c, i) => {
                      const sev = CRASH_SEV[c.severity as CrashSevKey];
                      return (
                        <div key={i} className="border border-gray-200 rounded-lg p-3" style={{ borderLeftWidth: 3, borderLeftColor: sev.color }}>
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span style={{ fontSize: 11, fontWeight: 600, color: sev.color, background: sev.bg, border: `1px solid ${sev.border}`, borderRadius: 20, padding: "2px 8px" }}>
                              {sev.label} Incident
                            </span>
                            <span className="text-xs text-gray-500">{ft(c.date, "date")}</span>
                            {c.reported && <span className="text-xs text-green-600 font-medium">✓ Reported</span>}
                          </div>
                          <p className="text-sm text-gray-700">{c.description}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* School Trip Planner — multi-vehicle (fleet + temporary hired) */}
      {showSchoolTripPlanner && fleetOrgCode && (
        <SchoolTripComposer
          fleetOrgCode={fleetOrgCode}
          canHire={canPlanSchoolTrips}
          buses={buses.map((b) => {
            /* 2026-09-08: the BUS picker shows the driver. driverIds on the bus
               doc misses drivers who signed up on the phone (the app writes the
               bus onto the user, not always the user onto the bus), so union in
               every org driver whose own busId points here — the same plate-key
               match mapFleetUser uses. Temporary trip conductors also carry a
               busId but are not the bus's driver. Both lists are already in
               memory: zero extra reads. */
            const plateKey = normalizePlateKey(b.plate);
            const idKey = normalizePlateKey(b.id);
            const viaUserDoc = fleetUsers
              .filter((u) => u.role === "Driver" && !u.temporaryTripConductor && !!u.busId
                && (u.busId === b.id || normalizePlateKey(u.busId) === plateKey || normalizePlateKey(u.busId) === idKey))
              .map((u) => u.id);
            const driverIds = [...new Set([...b.drivers, ...viaUserDoc])];
            return {
              id: b.id,
              name: b.name,
              plate: b.plate,
              driverLabel: driverIds.length ? namesForDriverIds(driverIds) : undefined,
              children: b.children
                .filter((c) => !c.temporaryTripChild)
                .map((c) => ({ id: c.id, name: c.name, parentId: c.parentId, classLabel: c.classLabel })),
            };
          })}
          onClose={() => setShowSchoolTripPlanner(false)}
        />
      )}


      {/* Temporary Hired Bus Wizard — redesigned (travel-app style) */}
      {showTempHireWizard && fleetOrgCode && (
        <div className="trip-modal fixed inset-0 z-[125] flex flex-col">
          <header className="trip-modal-header shrink-0 flex items-center justify-between gap-4 px-8 py-5 border-b">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => !tempHireSaving && setShowTempHireWizard(false)}
                disabled={tempHireSaving}
                className="p-2 hover:bg-gray-100 rounded-2xl transition-colors disabled:opacity-50"
              >
                <FaArrowLeft className="w-6 h-6 text-gray-700" />
              </button>
              <div>
                <h2 className="text-3xl font-semibold tracking-tight text-gray-900">Temporary Hire Bus</h2>
                <p className="text-sm text-gray-500">Perfect for school trips • Auto-expires</p>
              </div>
            </div>
            <Button type="button" variant="outline" disabled={tempHireSaving} onClick={() => setShowTempHireWizard(false)}>
              Close
            </Button>
          </header>

          <div className="flex-1 overflow-auto p-8 max-w-4xl mx-auto w-full space-y-8 pb-16">
            <div className="trip-card p-8 space-y-6">
              <p className="form-label">Bus details</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="form-label mb-2">Display name</p>
                  <Input
                    value={tempHireNickname}
                    onChange={(e) => setTempHireNickname(e.target.value)}
                    placeholder="e.g. Zoo Trip Coach"
                    className="rounded-3xl text-lg py-6"
                  />
                </div>
                <div>
                  <p className="form-label mb-2">Plate (optional)</p>
                  <Input
                    value={tempHirePlate}
                    onChange={(e) => setTempHirePlate(e.target.value)}
                    placeholder="TEMP-1234 or leave blank for auto"
                    className="rounded-3xl text-lg py-6"
                  />
                </div>
                <div>
                  <p className="form-label mb-2">Hire ends</p>
                  <Input
                    type="datetime-local"
                    value={tempHireExpiresLocal}
                    onChange={(e) => setTempHireExpiresLocal(e.target.value)}
                    className="rounded-3xl text-lg py-6"
                  />
                </div>
              </div>
            </div>

            <div className="trip-card p-8">
              <div className="mb-4">
                <p className="form-label mb-2">Search (name, class, or year group — e.g. year 6 blue)</p>
                <Input
                  value={tempHireStudentFilter}
                  onChange={(e) => setTempHireStudentFilter(e.target.value)}
                  placeholder="Filter students…"
                  className="rounded-3xl text-base py-4"
                />
              </div>
              <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                <p className="form-label">Move students to this temporary bus</p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setTempHireChildKeys(new Set(tempHireRoster.map((r) => r.key)))}
                    className="text-violet-600 text-sm font-semibold hover:text-violet-700"
                  >
                    Select all in fleet
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setTempHireChildKeys((prev) => {
                        const n = new Set(prev);
                        for (const r of tempHireFilteredRoster) n.add(r.key);
                        return n;
                      })
                    }
                    disabled={tempHireFilteredRoster.length === 0}
                    className="text-violet-600 text-sm font-semibold hover:text-violet-700 disabled:opacity-40"
                  >
                    Select all filtered
                  </button>
                </div>
              </div>

              {tempHireRoster.length === 0 ? (
                <p className="text-center py-12 text-amber-600 bg-amber-50 rounded-3xl">No students available in fleet.</p>
              ) : tempHireFilteredRoster.length === 0 ? (
                <p className="text-center py-12 text-gray-500 bg-gray-50 rounded-3xl">No students match this search.</p>
              ) : (
                <div className="max-h-96 overflow-auto space-y-3 pr-3">
                  {tempHireFilteredRoster.map((r) => {
                    const on = tempHireChildKeys.has(r.key);
                    return (
                      <label
                        key={r.key}
                        className={`student-list-item flex items-center gap-4 px-6 py-5 rounded-3xl border cursor-pointer ${
                          on ? "border-violet-300 bg-violet-50" : "border-transparent"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => {
                            setTempHireChildKeys((prev) => {
                              const n = new Set(prev);
                              if (n.has(r.key)) n.delete(r.key);
                              else n.add(r.key);
                              return n;
                            });
                          }}
                          className="w-5 h-5 accent-violet-600 shrink-0"
                        />
                        <span className="font-medium text-gray-900 text-lg flex-1">{r.studentName}</span>
                        <span className="text-xs text-gray-400 bg-white px-4 py-2 rounded-3xl border border-gray-100">{r.busName}</span>
                        {r.classLabel ? (
                          <span className="text-xs bg-white px-4 py-2 rounded-3xl text-gray-500 border border-gray-100">{r.classLabel}</span>
                        ) : null}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="trip-card p-8 bg-gradient-to-br from-violet-50 to-white">
              <p className="form-label mb-6">Temporary Conductor (optional)</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input placeholder="Name" value={tempHireCondName} onChange={(e) => setTempHireCondName(e.target.value)} className="rounded-3xl bg-white" />
                <Input type="email" placeholder="Email" value={tempHireCondEmail} onChange={(e) => setTempHireCondEmail(e.target.value)} className="rounded-3xl bg-white" />
                <Input type="password" placeholder="Password" value={tempHireCondPassword} onChange={(e) => setTempHireCondPassword(e.target.value)} className="rounded-3xl bg-white" />
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" disabled={tempHireSaving} onClick={() => setShowTempHireWizard(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handleSaveTemporaryHireBus()}
                disabled={tempHireSaving || tempHireChildKeys.size === 0}
                className="primary-cta text-white px-10 py-7 text-lg font-semibold rounded-3xl flex items-center gap-3 border-0"
              >
                {tempHireSaving ? "Creating…" : "Create Temporary Bus"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: explain hire / suspend / maintenance before student picker */}
      {reassignIntro && (
        <div
          className="fixed inset-0 z-[119] flex items-center justify-center p-4 bg-black/45 backdrop-blur-[2px]"
          onClick={() => setReassignIntro(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 tracking-tight">
              {reassignIntro.mode === "for_hire" && "Mark bus for hire"}
              {(reassignIntro.mode === "suspended" || reassignIntro.mode === "maintenance") && "Suspend / maintenance"}
              {reassignIntro.mode === "delete_bus" && "Delete this bus"}
            </h3>
            <p className="text-sm text-gray-600 mt-3 leading-relaxed">
              {reassignIntro.mode === "for_hire" &&
                `To hire out "${reassignIntro.bus.name}", move every student to another active bus or to a new temporary hired bus (with conductor login). Parent apps update automatically.`}
              {(reassignIntro.mode === "suspended" || reassignIntro.mode === "maintenance") &&
                `"${reassignIntro.bus.name}" will leave active service until you return it. Move all students to other fleet buses or a new hired bus; drivers and parents will see the bus as under maintenance.`}
              {reassignIntro.mode === "delete_bus" &&
                `Before deleting "${reassignIntro.bus.name}", move every student to another bus or a temporary hired bus. The bus is removed only after no students remain.`}
            </p>
            <div className="flex justify-end gap-2 mt-8">
              <Button type="button" variant="outline" onClick={() => setReassignIntro(null)}>
                Cancel
              </Button>
              <Button type="button" className="bg-gray-900 text-white hover:bg-gray-800" onClick={() => confirmReassignIntro()}>
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign students then mark bus maintenance / for hire / delete (legacy suspended mode) */}
      {reassignModal && (() => {
        const fleetOpts = buses.filter((b) => b.id !== reassignModal.bus.id && b.status === "active");
        const allAssigned = reassignModal.bus.children.every((c) => (reassignTargets[c.id] || "").trim() !== "");
        const needsFleetSlot = reassignModal.bus.children.some((c) => {
          const t = (reassignTargets[c.id] || "").trim();
          return t !== "" && t !== REASSIGN_HIRED_SENTINEL;
        });
        const anyHired = reassignModal.bus.children.some((c) => reassignTargets[c.id] === REASSIGN_HIRED_SENTINEL);
        const unassignedCount = reassignModal.bus.children.filter((c) => !(reassignTargets[c.id] || "").trim()).length;
        const filterQ = reassignFilter.trim().toLowerCase();
        const filteredStudents = filterQ
          ? reassignModal.bus.children.filter((c) => c.name.toLowerCase().includes(filterQ))
          : reassignModal.bus.children;
        const confirmDisabled =
          reassignBusy || !allAssigned || (needsFleetSlot && fleetOpts.length === 0);
        const busLabel = (id: string) => buses.find((b) => b.id === id)?.name ?? id;

        return (
          <div
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50"
            onClick={() => !reassignBusy && setReassignModal(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-gray-100 shrink-0 bg-gradient-to-b from-slate-50/80 to-white">
                <h3 className="text-lg font-semibold text-gray-900">
                  {(reassignModal.mode === "maintenance" || reassignModal.mode === "suspended") && "Suspend / maintenance"}
                  {reassignModal.mode === "for_hire" && "For hire"}
                  {reassignModal.mode === "delete_bus" && "Delete bus"}
                  <span className="text-gray-400 font-normal"> · </span>
                  {reassignModal.bus.name}
                </h3>
                <p className="text-sm text-gray-500 mt-1.5">
                  {reassignModal.mode === "delete_bus"
                    ? "Move every student off this bus, then it will be permanently removed."
                    : reassignModal.mode === "for_hire"
                      ? "Choose destinations in bulk (large rosters). This bus will be marked for hire and cleared."
                      : "Choose destinations in bulk (large rosters). This bus will be placed under maintenance (not active) and cleared."}
                </p>
                <p className="text-xs text-violet-800 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2 mt-3">
                  {reassignModal.bus.children.length} student{reassignModal.bus.children.length === 1 ? "" : "s"} ·{" "}
                  {unassignedCount === 0 ? "All assigned" : `${unassignedCount} still need a destination`}
                  {anyHired ? " · includes new hired bus" : ""}
                </p>
              </div>
              <div className="p-5 overflow-y-auto flex-1 space-y-4">
                <div className="rounded-xl border border-violet-100 bg-violet-50/50 p-4 space-y-3">
                  <label className="block text-xs font-semibold text-violet-900 uppercase tracking-wide">
                    Bulk assign (select students below, pick destination, apply)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <Input
                      placeholder="Search students…"
                      value={reassignFilter}
                      onChange={(e) => setReassignFilter(e.target.value)}
                      className="max-w-xs rounded-xl text-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      disabled={reassignBusy}
                      onClick={() => setReassignSelection(new Set(filteredStudents.map((c) => c.id)))}
                    >
                      Select all{filterQ ? " (filtered)" : ""}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      disabled={reassignBusy}
                      onClick={() => setReassignSelection(new Set())}
                    >
                      Clear selection
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      disabled={reassignBusy}
                      onClick={() => {
                        const next = { ...reassignTargets };
                        for (const c of reassignModal.bus.children) next[c.id] = "";
                        setReassignTargets(next);
                      }}
                    >
                      Clear all destinations
                    </Button>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs text-gray-500 mb-1">Destination for selected ({reassignSelection.size})</label>
                      <select
                        className="w-full rounded-xl border border-violet-200/80 bg-white px-3 py-2.5 text-sm shadow-sm"
                        value={reassignBulkDest}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === REASSIGN_HIRED_SENTINEL && !fleetHasFullProductFeatures) {
                            customToast.show(FULL_FEATURE_ONLY_MSG, "warning");
                            setReassignBulkDest("");
                            return;
                          }
                          setReassignBulkDest(v);
                        }}
                      >
                        <option value="">Choose fleet bus or hired bus…</option>
                        {fleetOpts.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name} ({b.plate})
                          </option>
                        ))}
                        <option value={REASSIGN_HIRED_SENTINEL}>New temporary hired bus…</option>
                      </select>
                    </div>
                    <Button
                      type="button"
                      className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl shrink-0"
                      disabled={reassignBusy || !reassignBulkDest || reassignSelection.size === 0}
                      onClick={() => {
                        const v = reassignBulkDest;
                        if (!v) return;
                        if (v === REASSIGN_HIRED_SENTINEL && !fleetHasFullProductFeatures) {
                          customToast.show(FULL_FEATURE_ONLY_MSG, "warning");
                          return;
                        }
                        setReassignTargets((prev) => {
                          const next = { ...prev };
                          for (const id of reassignSelection) next[id] = v;
                          return next;
                        });
                      }}
                    >
                      Apply to selected
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1 border-t border-violet-100/80">
                    <span className="text-xs text-violet-800 self-center mr-1">Apply to everyone:</span>
                    <select
                      className="rounded-xl border border-violet-200/80 bg-white px-3 py-2 text-xs shadow-sm max-w-[220px]"
                      value=""
                      disabled={reassignBusy}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) return;
                        if (v === REASSIGN_HIRED_SENTINEL && !fleetHasFullProductFeatures) {
                          customToast.show(FULL_FEATURE_ONLY_MSG, "warning");
                          e.target.value = "";
                          return;
                        }
                        const next = { ...reassignTargets };
                        for (const c of reassignModal.bus.children) next[c.id] = v;
                        setReassignTargets(next);
                        e.target.value = "";
                      }}
                    >
                      <option value="">Set all students to…</option>
                      {fleetOpts.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name} ({b.plate})
                        </option>
                      ))}
                      <option value={REASSIGN_HIRED_SENTINEL}>New temporary hired bus</option>
                    </select>
                  </div>
                </div>

                {anyHired && (
                  <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-4 space-y-3">
                    <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">New hired bus (required for selected students)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Input
                        placeholder="Display name"
                        value={reassignHireNickname}
                        onChange={(e) => setReassignHireNickname(e.target.value)}
                        className="rounded-xl text-sm bg-white"
                      />
                      <Input
                        placeholder="Plate (optional)"
                        value={reassignHirePlate}
                        onChange={(e) => setReassignHirePlate(e.target.value)}
                        className="rounded-xl text-sm bg-white"
                      />
                      <Input
                        type="datetime-local"
                        value={reassignHireExpiresLocal}
                        onChange={(e) => setReassignHireExpiresLocal(e.target.value)}
                        className="rounded-xl text-sm bg-white"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Input
                        placeholder="Conductor name"
                        value={reassignHireCondName}
                        onChange={(e) => setReassignHireCondName(e.target.value)}
                        className="rounded-xl text-sm bg-white"
                      />
                      <Input
                        type="email"
                        placeholder="Conductor email"
                        value={reassignHireCondEmail}
                        onChange={(e) => setReassignHireCondEmail(e.target.value)}
                        className="rounded-xl text-sm bg-white"
                      />
                      <Input
                        type="password"
                        placeholder="Password (min 8)"
                        value={reassignHireCondPassword}
                        onChange={(e) => setReassignHireCondPassword(e.target.value)}
                        className="rounded-xl text-sm bg-white"
                      />
                    </div>
                  </div>
                )}

                {needsFleetSlot && fleetOpts.length === 0 && (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                    No other active fleet buses. Assign everyone to a new hired bus, or add/activate a bus first.
                  </p>
                )}

                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Students (checkbox = selected for bulk)</p>
                  <div className="max-h-[min(360px,45vh)] overflow-y-auto rounded-xl border border-gray-100 divide-y divide-gray-50">
                    {filteredStudents.length === 0 ? (
                      <p className="text-sm text-gray-500 p-4">No students match this search.</p>
                    ) : (
                      filteredStudents.map((c) => {
                        const t = reassignTargets[c.id] ?? "";
                        const destLabel =
                          t === ""
                            ? "—"
                            : t === REASSIGN_HIRED_SENTINEL
                              ? "Hired bus"
                              : busLabel(t);
                        const sel = reassignSelection.has(c.id);
                        return (
                          <div
                            key={c.id}
                            className={`flex items-center gap-3 px-3 py-2.5 text-sm ${sel ? "bg-violet-50/50" : "bg-white"}`}
                          >
                            <input
                              type="checkbox"
                              checked={sel}
                              disabled={reassignBusy}
                              onChange={() => {
                                setReassignSelection((prev) => {
                                  const n = new Set(prev);
                                  if (n.has(c.id)) n.delete(c.id);
                                  else n.add(c.id);
                                  return n;
                                });
                              }}
                              className="w-4 h-4 accent-violet-600 shrink-0"
                            />
                            <span className="font-medium text-gray-900 flex-1 min-w-0 truncate">{c.name}</span>
                            <span className="text-xs text-gray-500 shrink-0 max-w-[140px] truncate" title={destLabel}>
                              → {destLabel}
                            </span>
                            <select
                              className="max-w-[160px] rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs shrink-0"
                              value={t}
                              disabled={reassignBusy}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === REASSIGN_HIRED_SENTINEL && !fleetHasFullProductFeatures) {
                                  customToast.show(FULL_FEATURE_ONLY_MSG, "warning");
                                  return;
                                }
                                setReassignTargets((prev) => ({ ...prev, [c.id]: v }));
                              }}
                            >
                              <option value="">—</option>
                              {fleetOpts.map((b) => (
                                <option key={b.id} value={b.id}>
                                  {b.name}
                                </option>
                              ))}
                              <option value={REASSIGN_HIRED_SENTINEL}>Hired bus</option>
                            </select>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
              <div className="p-4 border-t border-gray-100 flex justify-end gap-3 shrink-0 bg-slate-50/50">
                <Button type="button" variant="outline" disabled={reassignBusy} onClick={() => setReassignModal(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  className={reassignModal.mode === "delete_bus" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-gray-900 text-white hover:bg-gray-800"}
                  disabled={confirmDisabled}
                  onClick={() => handleReassignStudentsAndSetBusStatus()}
                >
                  {reassignBusy ? "Saving…" : reassignModal.mode === "delete_bus" ? "Move all & delete bus" : "Confirm moves"}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete bus confirmation modal */}
      {deleteBusToConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setDeleteBusToConfirm(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete bus</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deleteBusToConfirm.name}</strong>? Drivers and parents will be unlinked. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteBusToConfirm(null)}>Cancel</Button>
              <Button
                variant="destructive"
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={busDeleteBusy}
                onClick={() => handleDeleteBus(deleteBusToConfirm)}
              >
                {busDeleteBusy ? "Deleting…" : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Fuel Efficiency modal (per-bus: tank capacity, MPG, cost, report). Can be opened from bus Overview or from Fleet list. */}
      {showFuelEfficiencyModal && (efficiencyModalBus ?? selectedBus) && (() => {
        const modalBus = efficiencyModalBus ?? selectedBus!;
        const isSelectedBus = selectedBus && modalBus.id === selectedBus.id;
        const stats = isSelectedBus ? fuelStats : (() => {
          const row = fleetFuelSummary.rows.find((r) => r.busId === modalBus.id);
          if (!row) return { totalKm30: 0, needsRefuelCount: 0, refuelCount30: 0, totalLitresAdded30: 0 };
          return { totalKm30: row.km, needsRefuelCount: row.needsRefuel, refuelCount30: row.refuelEvents, totalLitresAdded30: row.litresAdded ?? 0 };
        })();
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => { setShowFuelEfficiencyModal(false); setEfficiencyModalBus(null); }}>
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Fuel Efficiency · {modalBus.name}</h3>
                <button type="button" onClick={() => { setShowFuelEfficiencyModal(false); setEfficiencyModalBus(null); }} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
              </div>
              <div className="p-6 overflow-y-auto flex-1 space-y-4">
                <div className="flex items-end gap-3 flex-wrap">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tank capacity (L)</label>
                    <Input type="number" min={1} value={efficiencyTankL} onChange={e => setEfficiencyTankL(e.target.value)} placeholder={String(DEFAULT_TANK_CAPACITY_L)} className="max-w-[140px]" />
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      const tankL = parseFloat(efficiencyTankL);
                      if (Number.isNaN(tankL) || tankL < 1) return;
                      try {
                        // Both names, deliberately: the app reads
                        // `fuelCapacityLiters` and the Fuel Engine anchors on
                        // it, so writing only the dashboard's name would leave
                        // the driver's phone still guessing. See the mapper.
                        await updateDoc(doc(db, "buses", modalBus.id), { fuelTankCapacityL: tankL, fuelCapacityLiters: tankL, updatedAt: serverTimestamp() });
                        setBuses((prev) => prev.map((b) => b.id === modalBus.id ? { ...b, fuelTankCapacityL: tankL } : b));
                        if (selectedBus?.id === modalBus.id) setSelectedBus((prev) => prev ? { ...prev, fuelTankCapacityL: tankL } : null);
                      } catch (e) { console.error(e); }
                    }}
                    className="px-3 py-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 rounded-lg"
                  >
                    Save tank to bus
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost per liter (optional)</label>
                  <Input type="number" min={0} step={0.01} value={efficiencyCostPerL} onChange={e => setEfficiencyCostPerL(e.target.value)} placeholder="e.g. 1.50" className="max-w-[140px]" />
                </div>
                {(() => {
                  const tankL = parseFloat(efficiencyTankL) || 0;
                  const costPerL = efficiencyCostPerL === "" ? undefined : parseFloat(efficiencyCostPerL);
                  const input: BusFuelInput = {
                    totalKm30: stats.totalKm30,
                    refuelCount30: stats.refuelCount30,
                    needsRefuelCount: stats.needsRefuelCount,
                    tankCapacityL: tankL || DEFAULT_TANK_CAPACITY_L,
                    costPerLiter: costPerL,
                    totalLitresAdded: stats.totalLitresAdded30,
                  };
                  const result = generateEfficiencyReport(input, modalBus.name);
                  return (
                    <div className="space-y-4 pt-2 border-t border-gray-100">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 uppercase font-medium">L/100 km</p>
                          <p className="text-lg font-bold text-slate-900">{result.l100km != null ? result.l100km.toFixed(1) : "—"}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 uppercase font-medium">MPG</p>
                          <p className="text-lg font-bold text-slate-900">{result.mpg != null ? result.mpg.toFixed(1) : "—"}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 uppercase font-medium">Rating</p>
                          <p className="text-lg font-bold text-slate-900">{result.rating}</p>
                        </div>
                        {result.monthlyCost != null && (
                          <div className="bg-slate-50 rounded-lg p-3">
                            <p className="text-xs text-slate-500 uppercase font-medium">Est. monthly cost</p>
                            <p className="text-lg font-bold text-slate-900">{result.monthlyCost.toFixed(2)}</p>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{result.refuelingInsight}</p>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Report</p>
                        <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap font-sans text-gray-700">{result.report}</pre>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })()}

      {showSubManager && (
        <SubscriptionManager
          orgCode={fleetOrgCode ?? ''}
          orgData={orgDocData as Parameters<typeof SubscriptionManager>[0]['orgData']}
          onClose={() => setShowSubManager(false)}
          onBranchCreated={async (branchCode) => {
            const res = await switchBranch(branchCode);
            if (!res.ok) {
              customToast.show(res.message || 'Branch created, but could not open it — use the Branch menu.', 'error');
              return;
            }
            await refreshAdminStatus();
          }}
        />
      )}

      {showUpgrade && (
        <PremiumUpgradeFlow
          orgCode={fleetOrgCode ?? ''}
          orgName={orgDisplayName}
          orgEmail={(orgDocData?.adminEmail as string) || currentUser?.email || ''}
          startAt={upgradeStart}
          onClose={() => setShowUpgrade(false)}
          onUpgraded={() => { setShowUpgrade(false); setTopLevelTab('analytics'); }}
        />
      )}

    </FleetShell>
    {/* Zana docked bottom-left across the whole fleet console — Premium/Enterprise only.
        pageContext tells her which screen the owner is on AND what that screen
        is showing, so "why is this one flagged?" and "sort these out" resolve
        against the rows they can see. Phase-1 navigate intents are executed
        here (allow-listed tabs / alerts / fuel / bus only). Hidden on her own
        tab — two Zanas on one screen is worse than either. */}
    {/* The floating dock renders on EVERY fleet screen, so an error in it is
        an error everywhere. Silent fallback: a missing dock is a degraded
        page, an error panel floating over the map is a broken-looking one. */}
    <ErrorBoundary label="Ask Zana" fallback={null}>
    <ZanaDock
      orgCode={fleetOrgCode}
      premium={sub.isZanaTier}
      hidden={topLevelTab === "zanaChat"}
      pageContext={zanaAskContext}
      liveCounts={zanaLiveCounts}
      onExecuteIntent={handleZanaIntent}
      liveUnreadAlertCount={fleetAlerts.filter((a) => !a.read).length}
      liveHighRiskBusCount={highRiskCount}
      driving={takeover.driving}
      takeoverActive={takeover.active}
      notice={takeover.notice}
      noticeEpisode={takeover.noticeEpisode}
      onNoticeShown={takeover.dismissNotice}
      onStopGuide={takeover.stopFromChat}
    />
    </ErrorBoundary>
    <ZanaTakeoverPanel state={takeover.state} controller={takeover.controller} />
    {fleetOrgCode && sub.isZanaTier ? (
      <ZanaAgentsDrawer
        orgCode={fleetOrgCode}
        onWalkthroughReport={(run) => {
          void takeoverRef.current?.({
            topic: run.topic || 'fleet',
            kind: 'show',
            skipChooser: true,
            findingTitle: run.report?.summary || `What Zana · ${run.name} did`,
          });
        }}
      />
    ) : null}
    </>
  );
}

